import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ACCOUNT_DELETION_GRACE_DAYS } from '../constants/accountDeletion';

const translations = {
  en: {
    // Nav
    home: "Home",
    courses: "Courses",
    dashboard: "Dashboard",
    profile: "Profile",
    login: "Login",
    register: "Register",
    logout: "Logout",
    search: "Search courses...",

    // Profile — Notification Preferences (KVKK / communication consent,
    // bkz. AdminNotificationJobs.js için aynı desen — GET/PUT /me/communication-prefs)
    'profile.notifPrefs.tabLabel': "Notifications",
    'profile.notifPrefs.title': "Notification Preferences",
    'profile.notifPrefs.subtitle': "Choose how we can reach you. Service messages about your courses stay on by default; promotional messages are only sent with your explicit consent.",
    'profile.notifPrefs.serviceTitle': "Service Notifications",
    'profile.notifPrefs.serviceDesc': "Lesson reminders, interview results, payment and account updates. You can turn these off if you prefer.",
    'profile.notifPrefs.serviceEmailLabel': "Service notifications by email",
    'profile.notifPrefs.serviceWhatsappLabel': "Service notifications by WhatsApp",
    'profile.notifPrefs.commercialTitle': "Promotional Messages",
    'profile.notifPrefs.commercialDesc': "Campaigns, discounts and new course announcements. These are off by default and only sent with your explicit consent.",
    'profile.notifPrefs.commercialEmailLabel': "Promotional messages by email",
    'profile.notifPrefs.commercialWhatsappLabel': "Promotional messages by WhatsApp",
    'profile.notifPrefs.consentNote': "You can withdraw this consent at any time — it won't affect your access to courses or support.",
    'profile.notifPrefs.inAppNote': "In-app notifications (like the lesson reminders shown in the bell icon) are always on and can't be turned off — they're part of the core service.",
    'profile.notifPrefs.updatedAt': "Last updated: {{date}}",
    'profile.notifPrefs.loadError': "We couldn't load your notification preferences.",
    'profile.notifPrefs.retry': "Try again",
    'profile.notifPrefs.saveSuccess': "Preference saved",
    'profile.notifPrefs.saveError': "We couldn't save your preference. Please try again.",

    // Profile — "Privacy" tab (holds Dijital Rozet consent; may host more
    // privacy-related settings later, hence the generic tab name).
    'profile.privacyTab.label': "Privacy",

    // Profile — Dijital Rozet (Digital Badge) consent, Privacy tab.
    // TASLAK METIN — KVKK hukuki incelemesi bekliyor. Prod'a cikmadan once
    // hukuk onayi ZORUNLU. Consent is account-level (users.dr_share_consent),
    // not per-certificate — one toggle covers all of the student's certificates
    // (bkz. GET/PUT /users/me/dijital-rozet-consent).
    'profile.drConsent.title': "Digital Badge Sharing",
    'profile.drConsent.subtitle': "Make your certificates verifiable through the official Digital Badge system of the Ministry of Industry and Technology of Türkiye.",
    'profile.drConsent.description': "Turning this on shares your name, email, and education details for completed courses with the Digital Badge platform run by the Ministry of Industry and Technology of Türkiye. The Ministry issues you a government-verified, shareable digital badge and a verification link, which you can share on LinkedIn or with employers. You can withdraw this consent at any time.",
    'profile.drConsent.dataItem.name': "Full name",
    'profile.drConsent.dataItem.email': "Email address",
    'profile.drConsent.dataItem.courses': "Completed course and certificate details",
    'profile.drConsent.toggleLabel': "Share my info with the Ministry for a Digital Badge",
    'profile.drConsent.note': "You can withdraw this consent at any time. Withdrawing stops future certificates from being sent to the Ministry; it doesn't affect your access to courses.",
    'profile.drConsent.withdrawTitle': "Withdraw your consent?",
    'profile.drConsent.withdrawBody': "If you turn this off, your future certificates won't be sent to the Ministry. Badges already issued stay in the Ministry's system — removing them requires a separate request, independent of this setting.",
    'profile.drConsent.withdrawConfirm': "Withdraw consent",
    'profile.drConsent.withdrawCancel': "Cancel",
    'profile.drConsent.loadError': "We couldn't load your Digital Badge sharing preference.",
    'profile.drConsent.retry': "Try again",
    'profile.drConsent.saveSuccessOn': "Digital Badge sharing turned on",
    'profile.drConsent.saveSuccessOff': "Digital Badge sharing turned off",
    'profile.drConsent.saveError': "We couldn't save your preference. Please try again.",
    'profile.drConsent.updatedAt': "Last updated: {{date}}",
    // TASLAK METIN — KVKK hukuki incelemesi bekliyor. Prod'a cikmadan once hukuk onayi ZORUNLU.
    // Hesap silme (DELETE /users/me) sonrasinda da Bakanliga aktarilmis rozet kaydinin ayri bir
    // surec oldugunu acikca belirtir — "otomatik silinir" gibi yanlis bir vaat ICERMEZ.
    // DUZELTME (2026-09-01): eski metin "Removing them is a separate process, started only
    // when you request it" diyordu — bu YANLISTI. services/user_purge.py::
    // queue_dr_revocation_records() hesap silindiginde OTOMATIK cagrilir ve bir geri cekme
    // kaydi olusturur; ancak bu kayit Bakanliga hicbir dis cagri YAPMAZ (hukuki gorus
    // bekleniyor). profile.deleteAccount.bulletMinistry ile AYNI olguyu anlatacak sekilde
    // hizalandi — iki anahtar artik celismiyor.
    'profile.drConsent.accountDeletionTitle': "What happens to your badge if you delete your account?",
    'profile.drConsent.accountDeletionBody': "If your certificates have been shared with the Ministry of Industry and Technology's Digital Badge platform, deleting your account automatically creates a withdrawal request for those records — but since that request isn't processed yet, the records stay in the Ministry's system.",

    // Exam — optional Digital Badge consent step shown before the final exam
    // starts (intro phase, before POST .../final-exam/start). Reuses the
    // profile.drConsent.* legal copy above; only these 5 keys are new.
    'exam.drConsent.title': "Turn your certificate into a Digital Badge?",
    'exam.drConsent.intro': "This is optional and won't affect your exam. Either way, if you pass, your certificate is issued to your account.",
    'exam.drConsent.declineButton': "Not now",
    'exam.drConsent.confirmButton': "I agree",
    'exam.drConsent.declineHint': "You can turn this on anytime later from your profile - it won't change your certificate.",

    // Profile — "Delete Your Account" section, Privacy tab (renders below
    // DijitalRozetConsentSection). Real backend behavior (routes/auth.py
    // DELETE /users/me + services/account_deactivation.py): the account is
    // DEACTIVATED IMMEDIATELY (account_status = "deactivated") and personal
    // data is actually DELETED within 72 hours. There is no 30-day grace
    // window.
    // FIVE exceptions, stated explicitly because omitting any of them would be
    // an incomplete disclosure:
    //   1. Payment / installment / promissory note / invoice AND debt-
    //      enforcement records are kept for their legal retention period AND
    //      the name, address and tax/ID number on them are NOT masked (Turkish
    //      Tax Procedure Law art. 253 and 256, Turkish Commercial Code art. 82,
    //      Turkish Code of Obligations art. 146).
    //   2. The approved distance sales contract copy is kept, together with the
    //      identity data printed in it and the approval IP address.
    //   3. Certificate records are kept INDEFINITELY. /verify/{code} needs no
    //      sign-in, so anyone holding the link keeps seeing the holder's name.
    //   4. The Digital Badge copy held by the Ministry is NOT withdrawn today:
    //      deletion only queues a withdrawal request, the flow is not live yet.
    //   5. Pre-enrollment lead records (name, phone, advisor call notes) are
    //      outside the scope of account deletion.
    // Copy rule: deletion is real, so say it — but it is 72 hours DELAYED, so
    // never use the past tense ("has been deleted"). Use "is deleted / will be
    // deleted ... within 3 days at the latest".
    'profile.deleteAccount.title': "Delete Your Account",
    'profile.deleteAccount.subtitle': "Your account closes immediately, your personal data is deleted, and you won't be able to sign in again. You can't undo this yourself.",
    'profile.deleteAccount.bulletNoLogin': "Access ends immediately: you're signed out the moment you confirm — no signing in again, with email and password or with Google.",
    'profile.deleteAccount.bulletDeleted': `Your personal data is deleted: your profile, notes, favorites, quiz sessions, exam attempts, voice recordings, interview transcripts, notifications, and your health and criminal record declarations are deleted within ${ACCOUNT_DELETION_GRACE_DAYS} days at the latest. The records listed below are not deleted.`,
    'profile.deleteAccount.bulletRetained': "Payment and enforcement records are not deleted: your payment, installment, promissory note, invoice and debt-enforcement records are kept for their legal retention period, together with the name, address and tax/ID number on them. Turkish tax and commercial law requires this: it has to stay provable who an invoice was issued to and what was owed.",
    'profile.deleteAccount.bulletContract': "Your contract record is kept: the copy of the distance sales agreement you approved at enrollment is kept, together with the name, email, phone and address it contains and the IP address you approved it from.",
    'profile.deleteAccount.bulletCertificates': "Your certificate records are kept indefinitely: your certificate stays valid after your account is deleted. Anyone who opens its verification link still sees your name, the course name and the issue date; there is no time limit on this record.",
    'profile.deleteAccount.bulletMinistry': "The Ministry's copy isn't withdrawn yet: if your certificate was transferred to the Ministry of Industry and Technology's Digital Badge platform, your name and email address are on record there too. We create a withdrawal request when you delete your account, but that request isn't being processed yet.",
    'profile.deleteAccount.bulletLeads': "Your pre-enrollment contact record stays: the name and phone number you left when you first contacted us for information, along with your advisor call notes, remain in our sales records.",
    'profile.deleteAccount.cta': "Delete My Account",
    'profile.deleteAccount.dialogA.title': "Are you sure you want to delete your account?",
    'profile.deleteAccount.dialogA.body': `The moment you confirm, your account closes, you're signed out, and your personal data is deleted within ${ACCOUNT_DELETION_GRACE_DAYS} days at the latest. You won't be able to sign in again — not with your email and password, not with Google. Payment, debt-enforcement and contract records are kept as the law requires, and your certificate stays verifiable with your name on it.`,
    'profile.deleteAccount.dialogA.cancel': "Cancel",
    'profile.deleteAccount.dialogA.continue': "Continue",
    'profile.deleteAccount.dialogB.title': "Last step — confirm your decision",
    'profile.deleteAccount.dialogB.body': `The moment you confirm, your account closes, your personal data is deleted within ${ACCOUNT_DELETION_GRACE_DAYS} days at the latest, and you can't undo this yourself. Payment, debt-enforcement and contract records continue to be kept, as Turkish tax and commercial law requires. Your certificate records are also kept indefinitely, and your name stays visible on the verification page.`,
    'profile.deleteAccount.dialogB.checkboxLabel': "I understand that my account will be closed, that my personal data will be deleted, that my payment and contract records will be kept as the law requires, and that my name will stay visible on the certificate verification page.",
    'profile.deleteAccount.dialogB.cancel': "Cancel",
    'profile.deleteAccount.dialogB.confirm': "Yes, Delete My Account",
    'profile.deleteAccount.dialogB.confirming': "Confirming…",
    'profile.deleteAccount.success.title': "Your account is closed",
    'profile.deleteAccount.success.body': `Your account is closed and your personal data will be deleted within ${ACCOUNT_DELETION_GRACE_DAYS} days at the latest. You can no longer sign in to this account. Your payment, debt-enforcement, contract and certificate records stay on file. All the best.`,
    'profile.deleteAccount.success.signOutNote': "You'll be signed out now.",
    'profile.deleteAccount.success.backHome': "Back to Home",
    'profile.deleteAccount.error': "We couldn't complete this. Check your connection and try again.",

    // Public "Account Deletion" page — /account-deletion, no auth required.
    // Used as the Google Play Data Safety "account deletion URL" and as the
    // Apple App Store in-app-initiated deletion reference. Same facts as
    // profile.deleteAccount.* above, written for a reader who may not be
    // signed in (or may be an App/Play reviewer).
    //
    // ⚠️ this page is READ BY STORE REVIEWERS, so it must match the backend
    // exactly — neither overstating nor understating. The student flow
    // deactivates the account immediately and personal data is really deleted
    // within 72 hours. The five retained categories (financial + enforcement
    // records with unmasked identity data; the contract copy with its approval
    // IP; certificate records kept INDEFINITELY and publicly verifiable by
    // name; the Ministry's Digital Badge copy, which is not withdrawn today;
    // pre-enrollment lead records) are each named explicitly — leaving any of
    // them out would be an incomplete disclosure. Because deletion is
    // 72 hours DELAYED, never use the past tense; write "is deleted ... within
    // 3 days at the latest".
    'accountDeletion.meta.title': "Delete Your Account — Kariyer Online",
    'accountDeletion.header.title': "Account Deletion",
    'accountDeletion.header.subtitle': "How to delete your Kariyer Online (Academy Pro) account, and what happens to your data afterwards.",
    'accountDeletion.intro': `This page explains how to delete your account, whether or not you're able to sign in. When you delete your account, your access ends immediately and your personal data is deleted within ${ACCOUNT_DELETION_GRACE_DAYS} days at the latest. Your payment, debt-enforcement, contract and certificate records are kept; below we list exactly what goes, what stays, and why.`,
    'accountDeletion.pathA.title': "If you can sign in",
    'accountDeletion.pathA.step1': "Sign in to your account at kariyeronline.tr.",
    'accountDeletion.pathA.step2': "Open your Profile, then go to the Privacy tab.",
    'accountDeletion.pathA.step3': "Scroll to \"Delete Your Account\" and tap the button.",
    'accountDeletion.pathA.step4': "Confirm twice — first that you understand the consequences, then a checkbox confirming your final decision. Nothing happens until both are done.",
    'accountDeletion.pathA.step5': `The moment you confirm, your account closes, you're signed out, and your personal data is deleted within ${ACCOUNT_DELETION_GRACE_DAYS} days at the latest. You won't be able to sign in again — not with your email and password, not with Google.`,
    'accountDeletion.pathA.mobileNote': "To delete from the mobile app: Profile → Account Settings → Delete account. The confirmation steps and the outcome are the same.",
    'accountDeletion.pathA.cta': "Go to Profile",
    'accountDeletion.pathB.title': "If you can't sign in",
    'accountDeletion.pathB.body': "Even if you can't sign in, you can email us a deletion request under KVKK Article 11 (Türkiye's data protection law). Include your full name and the email address or phone number your account is registered with, so we can verify it's you. The outcome is the same as deleting it yourself: your account is closed and your personal data is deleted.",
    'accountDeletion.pathB.emailLabel': "Email us at:",
    'accountDeletion.pathB.sla': "We resolve such requests within 30 days at the latest, as required by KVKK Article 13/2.",
    'accountDeletion.data.title': "What happens to your account and your data",
    'accountDeletion.data.accessTitle': "Your access ends",
    'accountDeletion.data.accessBody': "Your account closes immediately and you're signed out. You can't sign in again — not with your email and password, not with Google — and you can't reopen a deleted account on your own.",
    'accountDeletion.data.deletedTitle': "The personal data we delete",
    'accountDeletion.data.deletedBody': `Your profile, notes, favorites, quiz sessions, exam attempts, voice recordings, interview transcripts, notifications, and your health and criminal record declarations are deleted within ${ACCOUNT_DELETION_GRACE_DAYS} days at the latest. What's listed here can't be brought back; see the other cards for the records that are not deleted.`,
    'accountDeletion.data.retainedTitle': "Records the law requires us to keep",
    'accountDeletion.data.retainedBody': "Your payment, installment, promissory note, invoice and debt-enforcement records are not deleted. The name, address and tax/ID number on them are not masked either — it has to stay clear who an invoice was issued to and what was owed. This isn't our choice: the Turkish Tax Procedure Law (Art. 253 and 256), the Turkish Commercial Code (Art. 82) and the Turkish Code of Obligations (Art. 146) require these records to be kept.",
    'accountDeletion.data.contractsTitle': "Your contract record is kept",
    'accountDeletion.data.contractsBody': "The copy of the distance sales agreement you approved at enrollment is kept. It carries the full name, email address, phone number and address printed in the contract text; the IP address you approved it from is also kept, as evidence of who gave that approval. The basis is the contract-evidence provisions of the Turkish Code of Obligations and the Turkish Commercial Code.",
    'accountDeletion.data.certificatesTitle': "Your certificate — and your name — stay verifiable",
    'accountDeletion.data.certificatesBody': "Your certificate records are kept indefinitely. After you delete your account, anyone who opens the verification link — no matter who shared it — sees your name, the course name and the issue date. That verification page requires no sign-in, and there is no time limit on the record.",
    'accountDeletion.data.ministryTitle': "The Ministry's copy is not withdrawn today",
    'accountDeletion.data.ministryBody': "If your certificate was transferred to the Ministry of Industry and Technology's Digital Badge platform, your name and email address are held in the Ministry's own record as well. When you delete your account we create a request to withdraw that record; the withdrawal flow isn't live yet, so as of today the Ministry's copy stays in place. Pending requests will be processed once it goes live.",
    'accountDeletion.data.leadsTitle': "Your pre-enrollment contact record",
    'accountDeletion.data.leadsBody': "The name and phone number you left when you first contacted us for information, along with your advisor call notes, remain in our sales records. This record is outside the scope of account deletion; if you want it deleted, you can request that under KVKK Article 11 at the email address below.",
    'accountDeletion.footer.entity': "Kariyer Online Eğitim Hizmetleri",
    'accountDeletion.footer.backHome': "Back to Home",
    'accountDeletion.footer.backLogin': "Sign In",

    // Payment Result — PUBLIC /odeme-sonuc, Moka callback landing page. Note:
    // "completed" is intentionally non-committal — the callback only means
    // verification was TRIGGERED, not that the collection was recorded yet.
    'paymentResult.meta.title': "Payment Result — Kariyer Online",
    'paymentResult.completed.title': "Payment received",
    'paymentResult.completed.body': "Your payment has been received and your record will be updated shortly. If you have any questions, please contact your advisor.",
    'paymentResult.unknown.title': "Payment not recognized",
    'paymentResult.unknown.body': "We couldn't recognize this payment link. Please contact your advisor.",
    'paymentResult.dogrulanamadi.title': "Verification could not be completed",
    'paymentResult.dogrulanamadi.body': "The payment information could not be verified automatically. Please contact your advisor — your payment will be checked manually.",
    'paymentResult.kapali.title': "Payment system currently unavailable",
    'paymentResult.kapali.body': "This payment link is not active right now. Please contact your advisor.",
    'paymentResult.basarisiz.title': "Payment failed",
    'paymentResult.basarisiz.body': "Your payment could not be completed and your card was not charged. You can try again with the same payment link, or contact your advisor.",
    'paymentResult.fallback.title': "Payment status",
    'paymentResult.fallback.body': "Your request has been received. Please contact your advisor if you have any questions.",
    'paymentResult.backHome': "Back to Home",
    'paymentResult.backLogin': "Sign In",

    // Moka United — Admin ödeme linki dialog'u (AdminAccounting.jsx taksit
    // satırı) + eşleştirme kuyruğu (AdminMokaQueue.jsx)
    'admin.moka_row_button': "Payment Link",
    'admin.moka_row_button_disabled_hint': "Moka payment link integration is disabled",
    'admin.moka_link_dialog_title': "Moka Payment Link",
    'admin.moka_disabled_banner': "The Moka payment link integration is currently disabled.",
    'admin.moka_loading': "Loading…",
    'admin.moka_voice_note_label': "Note (optional)",
    'admin.moka_voice_note_placeholder': "Call note…",
    'admin.moka_bank_label': "POS Account",
    'admin.moka_bank_placeholder': "Select a POS account",
    'admin.moka_bank_required': "A POS account must be selected.",
    'admin.moka_no_pos_banks': "No active POS account found. Add one under Accounting > Banks first.",
    'admin.moka_generate_link': "Generate Link",
    'admin.moka_generating': "Generating…",
    'admin.moka_active_link_title': "Active Payment Link",
    'admin.moka_link_created_banner': "Link generated and sent to the student.",
    'admin.moka_link_url_label': "Payment page",
    'admin.moka_copy': "Copy",
    'admin.moka_voice_approval_by': "Approved by",
    'admin.moka_delivery_title': "Delivery Status",
    'admin.moka_channel_sms': "SMS",
    'admin.moka_channel_email': "Email",
    'admin.moka_delivery_status_sent': "Sent",
    'admin.moka_delivery_status_error': "Error",
    'admin.moka_delivery_status_not_sent': "Not sent",
    'admin.moka_delivery_status_skipped': "Skipped (no contact info)",
    'admin.moka_resend': "Resend",
    'admin.moka_resending': "Sending…",
    'admin.moka_resend_success': "Link resent.",
    'admin.moka_cancel_link': "Cancel Link",
    'admin.moka_cancel_confirm_title': "Cancel this payment link?",
    'admin.moka_cancel_confirm_desc': "This link will no longer work. The student won't be able to pay through it.",
    'admin.moka_cancel_success': "Link cancelled.",
    'admin.moka_generate_new_link': "Generate new link (supersede old one)",
    'admin.moka_force_confirm_title': "Supersede the existing link?",
    'admin.moka_force_confirm_desc': "The active link will be cancelled and a new one generated in its place. Make sure the student knows not to use the old link.",
    'admin.moka_back_to_active': "Back to active link",
    'admin.moka_conflict_title': "Someone else just generated a link",
    'admin.moka_conflict_desc': "Another admin generated a payment link for this installment moments ago. Review it below before generating a new one.",
    'admin.moka_conflict_created_by': "Generated by",
    'admin.nav.mokaQueue': "Moka Matching",
    'admin.moka_queue_title': "Moka Matching Queue",
    'admin.moka_queue_subtitle': "Approve or reject Moka collections that could not be auto-matched to an installment.",
    'admin.moka_queue_filter_label': "Status",
    'admin.moka_queue_filter_pending': "Pending",
    'admin.moka_queue_filter_queued': "Queued",
    'admin.moka_queue_filter_unmatched': "Link Not Found",
    'admin.moka_queue_filter_approved': "Approved",
    'admin.moka_queue_filter_rejected': "Rejected",
    'admin.moka_queue_filter_auto': "Auto Collected",
    'admin.moka_queue_filter_ignored': "Unsuccessful",
    'admin.moka_queue_empty_title': "No items in the queue",
    'admin.moka_queue_empty_desc': "There are no Moka collections waiting for review.",
    'admin.moka_queue_error_title': "Failed to load the queue",
    'admin.moka_queue_error_desc': "The Moka matching queue could not be loaded. Please try again.",
    'admin.moka_queue_error_retry': "Retry",
    'admin.moka_queue_candidates_title': "Candidate Installments",
    'admin.moka_queue_no_candidates': "No automatic candidate found — enter the installment ID manually.",
    'admin.moka_queue_manual_installment_placeholder': "installment_id…",
    'admin.moka_queue_compare_title': "Comparison",
    'admin.moka_queue_compare_amount_payment': "Amount Paid",
    'admin.moka_queue_compare_amount_installment': "Installment Amount",
    'admin.moka_queue_compare_name_payer': "Card Holder",
    'admin.moka_queue_compare_name_student': "Student",
    'admin.moka_queue_compare_date': "Payment Date",
    'admin.moka_queue_compare_due': "Due Date",
    'admin.moka_queue_approve_bank_label': "Collection POS Account",
    'admin.moka_queue_approve_button': "Approve & Collect",
    'admin.moka_queue_approve_confirm_title': "Confirm this collection?",
    'admin.moka_queue_approve_confirm_desc': "Approving marks the installment as collected and cannot be undone. Double-check the amount, name and date match before confirming.",
    'admin.moka_queue_approve_success': "Collection approved.",
    'admin.moka_queue_reject_button': "Reject",
    'admin.moka_queue_reject_note_label': "Rejection note (optional)",
    'admin.moka_queue_reject_confirm_title': "Are you sure you want to reject this record?",
    'admin.moka_queue_reject_success': "Record rejected.",
    'admin.moka_queue_select_bank_required': "A POS account must be selected.",
    'admin.moka_queue_select_installment_required': "An installment ID is required.",
    'admin.moka_queue_cancel_selection': "Cancel",
    'admin.moka_queue_status_queued': "Queued",
    'admin.moka_queue_status_unmatched_link_not_found': "Link Not Found",
    'admin.moka_queue_status_approved': "Approved",
    'admin.moka_queue_status_rejected': "Rejected",
    'admin.moka_queue_status_auto_collected': "Auto Collected",
    'admin.moka_queue_status_ignored_not_successful': "Unsuccessful",
    'admin.moka_queue_status_invalid_no_dealer_payment_id': "Invalid Record",
    'admin.moka_queue_status_pending': "Pending",

    // Bank matching pool (Eşleşme Havuzu)
    'admin.nav.bankMatchingPool': "Bank Matching",
    'admin.bank_match_title': "Bank Transaction Matching Pool",
    'admin.bank_match_subtitle': "Match incoming bank transfers to a student's pending payment.",
    'admin.bank_match_auto_button': "Auto-Match",
    'admin.bank_match_auto_confirm_title': "Run the auto-matching engine now?",
    'admin.bank_match_auto_confirm_desc': "Transactions with a reference + amount match (100% confidence) are approved automatically. Lower-confidence matches are added as suggestions for manual review.",
    'admin.bank_match_auto_success': "Auto-matching finished",
    'admin.bank_match_auto_approved': "Auto-approved",
    'admin.bank_match_auto_suggestions': "Suggestions added",
    'admin.bank_match_filter_bank': "Bank",
    'admin.bank_match_filter_direction': "Direction",
    'admin.bank_match_filter_date_from': "From",
    'admin.bank_match_filter_date_to': "To",
    'admin.bank_match_filter_all': "All",
    'admin.bank_match_direction_in': "Incoming",
    'admin.bank_match_direction_out': "Outgoing",
    'admin.bank_match_empty_title': "The matching pool is empty",
    'admin.bank_match_empty_desc': "There are no unmatched bank transactions right now.",
    'admin.bank_match_error_title': "Failed to load the matching pool",
    'admin.bank_match_error_desc': "The bank transaction pool could not be loaded. Please try again.",
    'admin.bank_match_error_retry': "Retry",
    'admin.bank_match_suggestion_title': "System Suggestion",
    'admin.bank_match_suggest_button': "Match Suggestion",
    'admin.bank_match_no_suggestion': "No suggestion yet — run \"Auto-Match\" or select manually.",
    'admin.bank_match_ref_recognized_no_payment': "A reference was recognized but no matching pending payment was found:",
    'admin.bank_match_suggest_confirm_title': "Confirm this match?",
    'admin.bank_match_suggest_confirm_desc': "The transaction will be marked as matched and the student's payment will be completed. Double-check the sender name/description above against the suggested reference before confirming.",
    'admin.bank_match_method_reference': "Reference + amount",
    'admin.bank_match_method_reference_mismatch': "Reference matched, amount differs",
    'admin.bank_match_method_name_amount': "Name + amount",
    'admin.bank_match_method_amount_only': "Amount only",
    'admin.bank_match_manual_button': "Select Manually",
    'admin.bank_match_student_search_label': "Search Student",
    'admin.bank_match_student_search_placeholder': "Name or email…",
    'admin.bank_match_searching': "Searching…",
    'admin.bank_match_no_student_found': "No student found.",
    'admin.bank_match_open_student_detail': "Open student detail",
    'admin.bank_match_pending_payments_loading': "Loading pending items…",
    'admin.bank_match_pending_payments_label': "Pending items",
    'admin.bank_match_pending_payments_error': "This student's pending items could not be loaded.",
    'admin.bank_match_pending_payments_empty': "This student has no pending payments or installments.",
    'admin.bank_match_pending_payment_required': "Select a pending item to match.",
    'admin.bank_match_manual_match_button': "Match",
    'admin.bank_match_manual_confirm_title': "Confirm this manual match?",
    'admin.bank_match_manual_confirm_desc': "The transaction will be marked as matched and the selected item will be completed. This cannot be undone from this screen.",
    'admin.bank_match_cancel_selection': "Cancel",
    'admin.bank_match_success': "Transaction matched.",
    'admin.bank_match_amount_mismatch_warning': "Matched, but the transaction amount and payment amount don't match — please verify.",
    'admin.bank_match_name_match_info': "Sender name matches: {student}",
    'admin.bank_match_name_match_no_amount_match': "The amount doesn't match any open item.",
    'admin.bank_match_name_match_match_button': "Match this item",
    'admin.bank_match_name_conflict_badge': "Mismatch",
    'admin.bank_match_name_conflict_warning': "The sender's name matches a DIFFERENT student than the system suggestion. Double-check before confirming.",
    'admin.bank_match_name_conflict_ack': "I understand the mismatch and want to match anyway.",
    'admin.bank_match_name_conflict_ack_required': "You must check this box before continuing.",
    'admin.bank_match_ignore_button': "Ignore",
    'admin.bank_match_ignore_confirm_title': "Ignore this transaction?",
    'admin.bank_match_ignore_confirm_desc': "The transaction will be soft-deleted and removed from the pool. It can be restored from the database if needed, but not from this screen.",
    'admin.bank_match_ignore_success': "Transaction ignored.",
    'admin.bank_match_tab_pool': "Pool",
    'admin.bank_match_tab_matched': "Matched",
    'admin.bank_matched_error_title': "Failed to load matched transactions",
    'admin.bank_matched_error_desc': "The matched transactions list could not be loaded. Please try again.",
    'admin.bank_matched_empty_title': "No matched transactions",
    'admin.bank_matched_empty_desc': "There are no matched bank transactions for this filter yet.",
    'admin.bank_matched_matched_at': "Matched at",

    // Landing
    hero_title: "Learn Without Limits",
    hero_subtitle: "Interactive courses with AI-powered quizzes. Learn at your own pace, track your progress, and earn rewards.",
    get_started: "Get Started",
    explore_courses: "Explore Courses",
    featured_courses: "Featured Courses",
    all_categories: "All Categories",
    view_all: "View All",
    students: "students",
    lessons: "lessons",
    
    // Course
    enroll_now: "Enroll Now",
    buy_now: "Buy Now",
    coming_soon: "Coming Soon",
    start_learning: "Start Learning",
    continue_learning: "Continue Learning",
    curriculum: "Curriculum",
    reviews: "Reviews",
    about: "About",
    instructor: "Instructor",
    level: "Level",
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
    interactive_course: "Interactive",
    
    // Dashboard
    my_courses: "My Courses",
    your_progress: "Your Progress",
    streak: "Day Streak",
    total_xp: "Total XP",
    courses_enrolled: "Courses Enrolled",
    completion: "Completion",
    no_courses: "You haven't enrolled in any courses yet.",
    browse_courses: "Browse Courses",

    // Dashboard v2 — Faz 4a
    'dashboard.welcomeTitle': "Welcome, {{name}}!",
    'dashboard.defaultName': "Student",
    'dashboard.streakMessage': "{{days}} day streak — keep it up!",
    'dashboard.continuePrograms': "Continue Learning",
    'dashboard.recentActivity': "Recent Activity",
    'dashboard.quickAccess': "Quick Access",
    'dashboard.programs.freeAccess': "Gift Access",
    'dashboard.programs.moduleProgress': "Progress",
    'dashboard.programs.progressAriaLabel': "{{title}} — {{pct}}% complete",
    'dashboard.programs.continue': "Continue",
    'dashboard.programs.emptyTitle': "No courses yet",
    'dashboard.programs.emptySub': "Find courses that match your goals and start learning.",
    'dashboard.activity.emptyTitle': "No recent activity",
    'dashboard.activity.emptySub': "Complete lessons to see your activity here.",
    'dashboard.activity.emptyCta': "Browse Courses",
    'dashboard.activity.errorTitle': "Couldn't load activities",
    'dashboard.milestones.heading': "Your Progress Journey",
    'dashboard.milestones.xpRemaining': "{{xp}} XP to next level",
    'dashboard.milestones.ariaLabel': "Overall milestone progress: {{pct}}%",
    'dashboard.milestones.level1': "Beginner",
    'dashboard.milestones.level2': "Student",
    'dashboard.milestones.level3': "Practitioner",
    'dashboard.milestones.level4': "Expert",
    'dashboard.milestones.level5': "Cabin Crew",
    'dashboard.error.title': "Connection lost",
    'dashboard.error.sub': "Your dashboard couldn't be loaded. Try again.",
    'dashboard.docRejected': "{{count}} document(s) rejected — click to fix",
    'dashboard.docRejected.fix': "Click to review",
    'dashboard.duo.greeting': "Welcome back, {{name}}!",
    'dashboard.duo.streakLabel': "Day streak",
    'dashboard.duo.xpLabel': "Total XP",
    'dashboard.duo.continueLearning': "Pick up where you left off",
    'dashboard.duo.dailyGoalLabel': "Daily goal",
    'dashboard.duo.dailyGoalUnit': "min",
    'dashboard.duo.continueBtn': "Continue",
    'dashboard.duo.lessonLabel': "Lesson",
    'dashboard.duo.myCourse': "My Course",
    'dashboard.duo.lessonProgress': "{{completed}} of {{total}} lessons done, {{pct}}%",
    'dashboard.duo.allLessons': "See all lessons",
    'dashboard.duo.thisWeek': "This Week",
    'dashboard.duo.studied': "Studied",
    'dashboard.duo.notStudied': "Rest day",
    'dashboard.duo.achievements': "Badges",
    'dashboard.duo.empty.title': "No courses enrolled yet",
    'dashboard.duo.empty.sub': "Discover the Cabin Crew course and get started",
    'dashboard.duo.empty.cta': "Browse Courses",
    'dashboard.duo.noEnrollment.title': "You haven't enrolled in any course yet",
    'dashboard.duo.noEnrollment.cta': "Browse courses",
    'dashboard.duo.lessonNow': "Now",
    'dashboard.duo.lessonCompleted': "Completed",
    'dashboard.duo.lessonLocked': "Locked",
    'dashboard.duo.streakUnit': "day",
    'dashboard.duo.xpUnit': "XP",
    'dashboard.duo.badgeUnit': "badge",
    'dashboard.duo.badgesEarnedCount': "{{count}} earned",
    'dashboard.duo.interviewPromo.badge': "New",
    'dashboard.duo.interviewPromo.title': "Interview Simulation",
    'dashboard.duo.interviewPromo.desc': "Get ready for the real cabin crew interview with AI.",
    'dashboard.duo.interviewPromo.cta': "Go to Simulation",
    'dashboard.quickAccess.certificates': "Certificates",
    'dashboard.quickAccess.profile': "Profile",
    'dashboard.quickAccess.documents': "Documents",
    'dashboard.quickAccess.interview': "Interview Practice",
    'dashboard.quickAccess.exams': "My Exams",
    'dashboard.quickAccess.courses': "Courses",
    'dashboard.quickAccess.progress': "My Progress",
    'gamificationBar.ariaLabel': "Your stats: XP, streak and level",
    'gamificationBar.streakAriaLabel': "{{days}} day streak",
    'courseType.aviation': "Aviation",
    'courseType.cabin': "Cabin Crew",
    'courseType.pax': "Passenger Comm.",
    'courseType.general': "General",
    'common.retry': "Retry",
    'common.cancel': "Cancel",
    'common.save': "Save",
    'catalog.filterAll': "All",
    'catalog.sort.newest': "Newest",
    'catalog.sort.popular': "Most popular",
    'catalog.sort.rating': "Highest rated",
    'catalog.sort.priceLow': "Price: low to high",
    'catalog.sort.priceHigh': "Price: high to low",
    'catalog.levelFilter.all': "All levels",
    'catalog.resultCount': "{{count}} courses",
    'catalog.shownCount': "Showing {{shown}} of {{total}} courses",
    'catalog.loadMore': "Show more",
    'catalog.loadMore.loading': "Loading...",
    'catalog.loadMore.error': "Couldn't load more courses. Please try again.",
    'catalog.empty.title': "No courses match your search",
    'catalog.empty.sub': "Try different keywords or remove a filter.",
    'catalog.empty.clearFilters': "Clear filters",
    'catalog.error.title': "Couldn't load courses",
    'catalog.error.sub': "Check your connection and try again.",
    'catalog.loading.ariaLabel': "Loading courses",
    'catalog.search.placeholder': "Search courses...",
    'catalog.heading': "Courses",
    'catalog.subheading': "Shape your career, take the next step",
    'catalog.card.by': "by",

    // ── Programs + ProgramLanding + Certificates — Faz 2.2bc ────────────────
    'programs.pageTitle': "Training Programs",
    'programs.pageTitle.sub': "Which field do you want to grow in? Choose a program and start with courses tailored for you.",
    'programs.loading.ariaLabel': "Loading programs",
    'programs.error.title': "Couldn't load programs",
    'programs.error.sub': "Check your connection and try again.",
    'programs.empty.title': "No programs available yet",
    'programs.empty.sub': "Programs will appear here once they're published.",
    'programs.empty.cta': "Browse Courses",
    'programs.card.cta': "Go to Program",
    'programLanding.loading': "Loading...",
    'programLanding.notFound': "Program not found.",
    'programLanding.allPrograms': "All Programs",
    'programLanding.backAriaLabel': "Back to all programs",
    'programLanding.coursesHeading': "Courses in This Program",
    'programLanding.coursesAll': "View All Courses",
    'programLanding.coursesAll.ariaLabel': "View all courses in {{name}}",
    'programLanding.coursesLoading': "Loading courses",
    'programLanding.lessonsCount': "{{count}} lessons",
    'programLanding.courses.emptyTitle': "No courses published yet",
    'programLanding.courses.emptySub': "Courses will appear here once added.",
    'programLanding.courses.errorTitle': "Couldn't load courses",
    'programLanding.feature.video': "Video",
    'programLanding.feature.pdf': "PDF",
    'programLanding.feature.text_note': "Notes",
    'programLanding.feature.quiz': "Quiz",
    'programLanding.feature.podcast': "Podcast",
    'programLanding.feature.certificate': "Certificate",
    'programLanding.feature.cabin_games': "Interactive Games",
    'programLanding.feature.interview_practice': "Interview Practice",
    'programLanding.feature.level_system': "Level System",
    'programLanding.feature.english_interview': "English Interview",
    'certificates.pageTitle': "My Certificates",
    'certificates.loading.ariaLabel': "Loading certificates",
    'certificates.error.title': "Couldn't load certificates",
    'certificates.error.sub': "Check your connection and try again.",
    'certificates.empty.title': "No certificates yet",
    'certificates.empty.sub': "Complete a course to earn your first certificate.",
    'certificates.empty.cta': "Browse Courses",
    'certificates.card.completionTitle': "Certificate of Completion",
    'certificates.card.issuedLabel': "Issued",
    'certificates.card.verificationLabel': "Verification",
    'certificates.view.ariaLabel': "View PDF certificate",
    'certificates.download.ariaLabel': "Download PDF certificate",
    'certificates.error.downloadFailed': "Could not open certificate. Please try again.",
    'certificates.error.auth': "Session expired. Please log in again.",
    'certificates.share.ariaLabel': "Share certificate",
    'certificates.share.copied': "Copied!",
    'certificates.share.text': "I completed \"{{title}}\" on Kariyer Online! Verification: {{code}}",
    'certificates.status.revoked': "Revoked",
    'certificates.status.verified': "Verified",

    // ── Dijital Rozet (Ministry of Industry and Technology digital badge) ─────
    // Rendered only when cert.dr_status is present (course is enrolled in the
    // digital badge program). Technical status names are never shown to the
    // user — only this human-readable copy (bkz. data-testid for the raw value).
    'certificates.dr.pending.title': "Badge is being prepared",
    'certificates.dr.pending.body': "Preparation for this certificate's digital badge has started. This will update shortly.",
    'certificates.dr.publishing.title': "Badge is publishing",
    'certificates.dr.publishing.body': "Your badge is currently being sent to the Ministry system. This can take a few minutes.",
    'certificates.dr.sent.title': "Your badge is ready",
    'certificates.dr.sent.body': "This certificate is now verifiable through the Digital Badge system of the Ministry of Industry and Technology of Türkiye.",
    'certificates.dr.sent.viewBadge': "View Badge",
    'certificates.dr.sent.shareLinkedin': "Share on LinkedIn",
    'certificates.dr.failed.title': "Badge couldn't be published right now",
    'certificates.dr.failed.body': "This isn't something you did — a technical issue occurred and our team is looking into it. Your certificate remains valid.",
    'certificates.dr.skipped.title': "Badge isn't available right now",
    'certificates.dr.skipped.body': "Digital badge issuance isn't available for this course at the moment. Your certificate remains valid.",
    'certificates.dr.awaitingConsent.title': "Your badge is waiting for consent",
    'certificates.dr.awaitingConsent.body': "We need your consent from Privacy settings to publish your digital badge.",
    'certificates.dr.awaitingConsent.cta': "Give consent",
    'certificates.dr.revoked.title': "Badge was withdrawn",
    'certificates.dr.revoked.body': "The digital badge for this certificate was withdrawn from the Ministry system.",
    'certificates.dr.linkedin.error': "Couldn't start LinkedIn sharing. Please try again.",
    'certificates.dr.linkedin.revokedSrOnly': "This certificate is revoked and can't be shared.",
    'certificates.dr.badgeImageAlt': "Digital badge image",

    // ── Certificate Verification Page (/verify/:code) ─────────────────────────
    'certVerify.subtitle': "Certificate Verification System",
    'certVerify.error.fetch': "Certificate could not be verified",
    'certVerify.error.notFoundTitle': "Certificate Not Found",
    'certVerify.error.notFoundSub': "No certificate found with this code.",
    'certVerify.valid': "Valid Certificate",
    'certVerify.revoked': "Revoked Certificate",
    'certVerify.invalid': "Invalid Certificate",
    'certVerify.student': "Student",
    'certVerify.course': "Course",
    'certVerify.issuedAt': "Issue Date",
    'certVerify.verificationCode': "Verification Code",
    'certVerify.revokedAt': "Revocation Date",
    'certVerify.revokeReason': "Reason:",
    'certVerify.footer.valid': "This certificate is valid. For questions, please contact the Kariyer Online team.",
    'certVerify.footer.invalid': "This certificate is not valid. For questions, please contact the Kariyer Online team.",

    'courseDetail.reviewForm.title': "Write a review",
    'courseDetail.reviewForm.placeholder': "Share your thoughts about this course...",
    'courseDetail.reviewForm.submitted': "Review submitted — thank you!",
    'courseDetail.continueWhereLeft': "Continue where you left off",
    'courseDetail.finalExam.title': "Final exam ready",
    'courseDetail.finalExam.sub': "Complete the exam to earn your certificate.",
    'courseDetail.finalExam.cta': "Take exam",
    'courseDetail.curriculum.assessment.title': "Assessment",
    'courseDetail.curriculum.assessment.unlockedSub': "Ready — take the exam to earn your certificate.",
    'courseDetail.curriculum.assessment.lockedSub': "Unlocks when all lessons are completed",
    'courseDetail.curriculum.assessment.lockedBadge': "Locked",
    'courseDetail.curriculum.assessment.cta': "Go to exam",
    'courseDetail.levelMismatch.title': "Level requirement",
    'courseDetail.levelMismatch.body': "This course requires {{required}} level. Your current level: {{current}}.",
    'courseDetail.lessonType.interactive': "Interactive lesson",
    'courseDetail.lessonType.video': "Video lesson",
    'courseDetail.lessonType.pdf': "PDF",
    'courseDetail.lessonType.text': "Reading",
    'courseDetail.lessonType.quiz': "Quiz",
    'courseDetail.lessonType.exam': "Exam",
    'courseDetail.lessonType.flashcard': "Flashcards",
    'courseDetail.lessonType.default': "Lesson",
    'courseDetail.lesson.locked': "Contact us to unlock",
    'courseDetail.contactToEnroll.title': "Want to enroll?",
    'courseDetail.contactToEnroll.sub': "Chat with our team on WhatsApp for details.",
    'courseDetail.contactToEnroll.cta': "Ask on WhatsApp",
    'courseDetail.contactToEnroll.ctaPrimaryGuest': "Log In",
    'whatsapp.message.courseInquiry': "Hi, I saw your {{courseTitle}} course on your website and I'd like more information.",
    'whatsapp.message.generic': "Hi, I'd like to get information about your courses.",
    'whatsapp.cta.short': "WhatsApp",
    'whatsapp.cta.askOnWhatsapp': "Ask on WhatsApp",
    'whatsapp.floating.ariaLabel': "Ask us on WhatsApp (opens in a new tab)",
    'whatsapp.opensExternal': "Opens in WhatsApp",
    'courseDetail.curriculum.empty': "Lessons coming soon",
    'courseDetail.reviews.empty': "No reviews yet — be the first!",
    'courseDetail.notFound': "Course not found",
    'courseDetail.enrollSuccess': "You're enrolled — start learning!",
    'courseDetail.progress.ariaLabel': "Course progress: {{pct}}%",
    'courseDetail.fav.add': "Add to favorites",
    'courseDetail.fav.remove': "Remove from favorites",
    'nav.cabinMemurlugu': "Cabin Crew",
    'nav.courses': "Courses",
    'nav.myCourses': "My Courses",
    'nav.allCourses': "All Courses",
    'nav.homeLink': "Home",
    'nav.openMenu': "Open menu",
    'nav.closeMenu': "Close menu",
    'nav.mobileMenuLabel': "Mobile navigation",
    'nav.langToggleLabel': "Change language",
    'nav.mainNav': "Main navigation",
    'myCourses.title': "My Courses",
    'myCourses.empty.title': "No enrolled courses yet",
    'myCourses.empty.sub': "Discover courses and start learning today.",
    'myCourses.empty.cta': "Browse Courses",
    'myCourses.progress': "{{pct}}% complete",
    'myCourses.lessons': "{{completed}} / {{total}} lessons",
    'myCourses.continue': "Continue",
    'myCourses.start': "Start Learning",

    // Quiz
    check_answer: "Check Answer",
    next_question: "Next Question",
    correct: "Correct!",
    incorrect: "Incorrect!",
    quiz_complete: "Quiz Complete!",
    xp_earned: "XP Earned",
    
    // Auth
    email: "Email",
    password: "Password",
    name: "Full Name",
    login_with_google: "Continue with Google",
    or: "or",
    no_account: "Don't have an account?",
    have_account: "Already have an account?",
    
    // Payment
    checkout: "Checkout",
    payment_success: "Payment Successful!",
    total: "Total",
    
    // Stats
    total_courses: "Courses",
    total_students: "Students",
    satisfaction: "Satisfaction",
    
    // General
    free: "Free",
    popular: "Popular",
    new_badge: "New",
    loading: "Loading...",

    // Admin primitives
    'admin.select_all': "Select all",
    'admin.select_row': "Select row",
    'admin.empty_table_title': "No records found",
    'admin.empty_table_desc': "Try adjusting your filters or search query.",
    'admin.pagination_info': "{start}–{end} of {total}",
    'admin.prev_page': "Previous page",
    'admin.next_page': "Next page",
    'admin.pagination_label': "Pagination",
    'admin.pagination_empty': "No data",
    'admin.search_placeholder': "Search...",
    'admin.clear_search': "Clear search",
    'admin.clear_filters': "Clear all",
    'admin.filter_all': "All",
    'admin.active_filters': "Active filters",
    'admin.btn_cancel': "Cancel",
    'admin.btn_confirm': "Confirm",
    'admin.btn_loading': "Loading...",
    'admin.btn_delete': "Delete",
    'admin.btn_edit': "Edit",
    'admin.btn_add': "Add",
    'admin.btn_save': "Save",
    'admin.btn_create': "Create",
    'admin.btn_close': "Close",
    'admin.action_menu_label': "Row actions",
    'admin.file_too_large': "File exceeds {max}MB limit.",
    'admin.file_uploaded': "Uploaded",
    'admin.file_remove': "Remove file",
    'admin.file_drop_zone': "File drop zone",
    'admin.file_drag_title': "Drag & drop or click to browse",
    'admin.file_accepted': "Accepted: {types}",
    'admin.file_any_type': "All file types accepted",
    'admin.file_max_size': "Max {max} MB",
    'admin.file_browse': "Browse",
    'admin.file_preview': "File preview",
    'admin.select_placeholder': "Select...",
    'admin.rich_select_search': "Search...",
    'admin.rich_select_no_results': "No options found.",
    'admin.date_today': "Today",
    'admin.date_last7': "Last 7 days",
    'admin.date_last30': "Last 30 days",
    'admin.date_this_month': "This month",
    'admin.date_last_month': "Last month",
    'admin.date_placeholder': "Pick a date range",
    'admin.date_range_label': "Date range",
    'admin.date_clear': "Clear date",

    // ── AdminCourseManager v2 ─────────────────────────────────────────────────
    'admin.courseManager.title': "Course Manager",
    'admin.courseManager.subtitle': "Create and manage courses, modules and lessons",
    'admin.courseManager.breadcrumb_admin': "Admin",
    'admin.courseManager.breadcrumb_courses': "Courses",
    'admin.courseManager.btn_new_course': "New Course",
    'admin.courseManager.filter_search_placeholder': "Search by title...",
    'admin.courseManager.filter_category': "Category",
    'admin.courseManager.filter_level': "Level",
    'admin.courseManager.filter_interactive': "Interactive",
    'admin.courseManager.filter_interactive_yes': "Interactive",
    'admin.courseManager.filter_interactive_no': "Standard",
    'admin.courseManager.col_course': "Course",
    'admin.courseManager.col_category': "Category",
    'admin.courseManager.col_level': "Level",
    'admin.courseManager.col_lessons': "Lessons",
    'admin.courseManager.col_enrolled': "Enrolled",
    'admin.courseManager.col_price': "Price",
    'admin.courseManager.col_actions': "Actions",
    'admin.courseManager.action_edit': "Edit",
    'admin.courseManager.action_modules': "Modules",
    'admin.courseManager.action_delete': "Delete",
    'admin.courseManager.delete_title': "Delete Course",
    'admin.courseManager.delete_desc': "This action cannot be undone. All lessons will also be deleted.",
    'admin.courseManager.empty_title': "No courses yet",
    'admin.courseManager.empty_desc': "Create your first course to get started.",
    'admin.courseManager.draft_badge': "DRAFT",
    // Dialog — general
    'admin.courseManager.dialog_new_title': "New Course",
    'admin.courseManager.dialog_edit_title': "Edit Course",
    'admin.courseManager.tab_general': "General",
    'admin.courseManager.tab_modules': "Modules",
    'admin.courseManager.tab_lessons': "Lessons",
    'admin.courseManager.tab_quiz': "Quiz",
    'admin.courseManager.tab_certificate': "Certificate",
    // General tab
    'admin.courseManager.section_thumbnail': "Thumbnail",
    'admin.courseManager.section_basic': "Course Info",
    'admin.courseManager.section_settings': "Settings",
    'admin.courseManager.field_title_en': "Title (EN)",
    'admin.courseManager.field_title_tr': "Title (TR)",
    'admin.courseManager.field_desc_en': "Description (EN)",
    'admin.courseManager.field_desc_tr': "Description (TR)",
    'admin.courseManager.field_category': "Category",
    'admin.courseManager.field_subcategory': "Subcategory",
    'admin.courseManager.field_level': "Level",
    'admin.courseManager.field_price': "Price",
    'admin.courseManager.field_currency': "Currency",
    'admin.courseManager.field_language': "Language",
    'admin.courseManager.field_interactive': "Interactive Course",
    'admin.courseManager.field_vimeo_intro': "Vimeo Intro URL",
    'admin.courseManager.field_level_required': "Required Level (Cabin Crew)",
    'admin.courseManager.btn_upload_thumb': "Upload Image",
    'admin.courseManager.btn_save': "Save",
    'admin.courseManager.btn_saving': "Saving...",
    'admin.courseManager.save_success_create': "Course created",
    'admin.courseManager.save_success_update': "Course updated",
    'admin.courseManager.validation_title_required': "Course title is required",
    // Modules tab
    'admin.courseManager.section_modules': "Modules",
    'admin.courseManager.section_modules_desc': "Drag to reorder. Click to expand and manage lessons inside each module.",
    'admin.courseManager.btn_add_module': "Add Module",
    'admin.courseManager.module_title_en': "Module Title (EN)",
    'admin.courseManager.module_title_tr': "Module Title (TR)",
    'admin.courseManager.module_description': "Description",
    'admin.courseManager.module_order': "Order",
    'admin.courseManager.module_edit': "Edit Module",
    'admin.courseManager.module_delete': "Delete Module",
    'admin.courseManager.module_delete_title': "Delete Module",
    'admin.courseManager.module_delete_desc': "Module will be deleted. Lessons inside may become orphaned.",
    'admin.courseManager.module_save': "Save Module",
    'admin.courseManager.module_cancel': "Cancel",
    'admin.courseManager.module_empty': "No modules yet",
    'admin.courseManager.module_empty_desc': "Add the first module to organize lessons.",
    'admin.courseManager.drag_hint': "Drag to reorder",
    // Lessons tab
    'admin.courseManager.section_lessons': "Lessons",
    'admin.courseManager.btn_add_lesson': "Add Lesson",
    'admin.courseManager.lesson_title_en': "Lesson Title (EN)",
    'admin.courseManager.lesson_title_tr': "Lesson Title (TR)",
    'admin.courseManager.lesson_desc_en': "Description (EN)",
    'admin.courseManager.lesson_desc_tr': "Description (TR)",
    'admin.courseManager.lesson_type': "Type",
    'admin.courseManager.lesson_module': "Module",
    'admin.courseManager.lesson_order': "Order",
    'admin.courseManager.lesson_vimeo': "Vimeo URL",
    'admin.courseManager.lesson_vimeo_or': "or",
    'admin.lessons.vimeoPicker.button': "Pick from Vimeo Library",
    'admin.lessons.vimeoPicker.title': "Vimeo Library",
    'admin.lessons.vimeoPicker.loading': "Loading folders...",
    'admin.lessons.vimeoPicker.loadingVideos': "Loading videos...",
    'admin.lessons.vimeoPicker.empty': "No videos in this folder",
    'admin.lessons.vimeoPicker.emptyFolders': "No folders found",
    'admin.lessons.vimeoPicker.search': "Search all videos...",
    'admin.lessons.vimeoPicker.backToFolders': "Folders",
    'admin.lessons.vimeoPicker.selected': "Selected",
    'admin.lessons.vimeoPicker.confirm': "Use This Video",
    'admin.courseManager.lesson_content_en': "Content (EN)",
    'admin.courseManager.lesson_content_tr': "Content (TR)",
    'admin.courseManager.lesson_duration': "Duration (min)",
    'admin.courseManager.lesson_watch_percent': "Min Watch %",
    'admin.courseManager.lesson_passing_score': "Passing Score",
    'admin.courseManager.lesson_edit': "Edit Lesson",
    'admin.courseManager.lesson_delete': "Delete Lesson",
    'admin.courseManager.lesson_delete_title': "Delete Lesson",
    'admin.courseManager.lesson_delete_desc': "This lesson will be permanently deleted.",
    'admin.courseManager.lesson_save': "Save Lesson",
    'admin.courseManager.lesson_validation_title': "Lesson title is required",
    'admin.courseManager.lesson_validation_course': "Please select a course first",
    'admin.courseManager.lesson_empty': "No lessons yet",
    'admin.courseManager.lesson_empty_desc': "Add lessons to build your course content.",
    // Quiz tab
    'admin.courseManager.section_quiz': "Quiz Questions",
    'admin.courseManager.section_quiz_desc': "Define quiz questions for interactive lessons.",
    'admin.courseManager.quiz_question': "Question",
    'admin.courseManager.quiz_options': "Options (one per line)",
    'admin.courseManager.quiz_correct': "Correct Answer",
    'admin.courseManager.quiz_add': "Add Question",
    'admin.courseManager.quiz_remove': "Remove",
    'admin.courseManager.quiz_empty': "No quiz questions",
    'admin.courseManager.quiz_select_lesson': "Select an interactive lesson to configure its quiz.",
    // Certificate tab
    'admin.courseManager.section_cert': "Certificate Info",
    'admin.courseManager.cert_title': "Certificate Title",
    'admin.courseManager.cert_subtitle': "Subtitle",

    // ── Lesson Content Editors — Faz 4e ─────────────────────────────────────
    // Common
    'lessonEditor.common.contentSection': "Content",
    'lessonEditor.common.emptyList': "No items yet. Click the button below to add one.",
    'lessonEditor.common.add': "Add",
    'lessonEditor.common.remove': "Remove",
    'lessonEditor.common.moveUp': "Move up",
    'lessonEditor.common.moveDown': "Move down",
    'lessonEditor.common.preview': "Preview",
    'lessonEditor.common.save': "Save",
    'lessonEditor.common.cancel': "Cancel",
    // Flashcard
    'lessonEditor.flashcard.front': "Front (term)",
    'lessonEditor.flashcard.frontPlaceholder': "e.g. Gate",
    'lessonEditor.flashcard.back': "Back (translation/definition)",
    'lessonEditor.flashcard.backPlaceholder': "e.g. Kapi",
    'lessonEditor.flashcard.example': "Example sentence",
    'lessonEditor.flashcard.examplePlaceholder': "e.g. Please proceed to gate 12.",
    'lessonEditor.flashcard.addCard': "Add Card",
    'lessonEditor.flashcard.removeCard': "Remove card",
    // Matching
    'lessonEditor.matching.left': "Left item",
    'lessonEditor.matching.leftPlaceholder': "e.g. Bread",
    'lessonEditor.matching.right': "Right item (match)",
    'lessonEditor.matching.rightPlaceholder': "e.g. Ekmek",
    'lessonEditor.matching.addPair': "Add Pair",
    'lessonEditor.matching.removePair': "Remove pair",
    'lessonEditor.matching.mode': "Game mode",
    'lessonEditor.matching.mode_two_column': "Two Columns",
    'lessonEditor.matching.mode_memory': "Memory Cards",
    // Drag & Drop
    'lessonEditor.dragDrop.sentence': "Correct sentence",
    'lessonEditor.dragDrop.sentenceHint': "words separated by spaces",
    'lessonEditor.dragDrop.sentencePlaceholder': "e.g. the cat is on the mat",
    'lessonEditor.dragDrop.hint': "Hint (optional)",
    'lessonEditor.dragDrop.hintPlaceholder': "e.g. cat, mat rhyme",
    'lessonEditor.dragDrop.addSentence': "Add Sentence",
    'lessonEditor.dragDrop.removeSentence': "Remove sentence",
    'lessonEditor.dragDrop.scrambledPreview': "Scrambled preview",
    'lessonEditor.dragDrop.rescramble': "Rescramble",
    // Dialogue
    'lessonEditor.dialogue.scenario': "Scenario description",
    'lessonEditor.dialogue.scenarioPlaceholder': "e.g. A passenger asks for water during the flight.",
    'lessonEditor.dialogue.speaker': "Speaker name",
    'lessonEditor.dialogue.speakerPlaceholder': "e.g. Passenger",
    'lessonEditor.dialogue.text': "Dialogue text",
    'lessonEditor.dialogue.textPlaceholder': "What does this speaker say?",
    'lessonEditor.dialogue.textUserPlaceholder': "Leave blank — user selects from options below",
    'lessonEditor.dialogue.isUser': "User's turn",
    'lessonEditor.dialogue.isUserYes': "You",
    'lessonEditor.dialogue.isUserNo': "Other",
    'lessonEditor.dialogue.options': "Answer options",
    'lessonEditor.dialogue.addOption': "Add Option",
    'lessonEditor.dialogue.removeOption': "Remove option",
    'lessonEditor.dialogue.isCorrect': "Correct answer",
    'lessonEditor.dialogue.isCorrectYes': "Correct",
    'lessonEditor.dialogue.isCorrectNo': "Wrong",
    'lessonEditor.dialogue.optionTextPlaceholder': "Option text...",
    'lessonEditor.dialogue.feedbackPlaceholder': "Feedback for this option...",
    'lessonEditor.dialogue.noOptions': "No options yet.",
    'lessonEditor.dialogue.addStep': "Add Dialogue Step",
    'lessonEditor.dialogue.removeStep': "Remove step",
    // Fill in Blank
    'lessonEditor.fillInBlank.sentence': "Sentence with blank",
    'lessonEditor.fillInBlank.sentenceHint': "use ___ for the blank",
    'lessonEditor.fillInBlank.sentencePlaceholder': "e.g. I ___ to the airport.",
    'lessonEditor.fillInBlank.correct': "Correct answer",
    'lessonEditor.fillInBlank.correctPlaceholder': "e.g. went",
    'lessonEditor.fillInBlank.mode': "Input mode",
    'lessonEditor.fillInBlank.mode_word_pool': "Word Pool",
    'lessonEditor.fillInBlank.mode_text_input': "Text Input",
    'lessonEditor.fillInBlank.alternatives': "Alternative words (distractors)",
    'lessonEditor.fillInBlank.noAlternatives': "No alternatives — add some distractors.",
    'lessonEditor.fillInBlank.addQuestion': "Add Question",
    'lessonEditor.fillInBlank.removeQuestion': "Remove question",
    // Swipe
    'lessonEditor.swipe.word': "Word / Term",
    'lessonEditor.swipe.wordPlaceholder': "e.g. Boarding",
    'lessonEditor.swipe.translation': "Translation",
    'lessonEditor.swipe.translationPlaceholder': "e.g. Binis",
    'lessonEditor.swipe.example': "Example sentence",
    'lessonEditor.swipe.examplePlaceholder': "e.g. Boarding starts at 7 AM.",
    'lessonEditor.swipe.addItem': "Add Vocabulary Item",
    'lessonEditor.swipe.removeItem': "Remove item",
    // Time Race
    'lessonEditor.timeRace.duration': "Duration (seconds)",
    'lessonEditor.timeRace.question': "Question",
    'lessonEditor.timeRace.questionPlaceholder': "e.g. How do you greet a passenger?",
    'lessonEditor.timeRace.options': "Answer options",
    'lessonEditor.timeRace.option': "Option",
    'lessonEditor.timeRace.correctIndex': "Correct answer",
    'lessonEditor.timeRace.correctIndexHint': "Click the radio button next to the correct answer.",
    'lessonEditor.timeRace.addQuestion': "Add Question",
    'lessonEditor.timeRace.removeQuestion': "Remove question",

    // ── AdminStudentHub (Student 360) ────────────────────────────────────────
    'admin.studentHub.title': "Students",
    'admin.studentHub.subtitle': "360-degree student management",
    'admin.studentHub.breadcrumb_admin': "Admin",
    'admin.studentHub.breadcrumb_students': "Students",
    'admin.studentHub.btn_new_user': "New User",
    'admin.studentHub.btn_bulk_enroll': "Bulk Enroll",
    'admin.studentHub.filter_search': "Search by name or email...",
    'admin.studentHub.filter_role': "Role",
    'admin.studentHub.filter_level': "Level",
    'admin.studentHub.filter_pending': "Pending Deletion",
    'admin.studentHub.col_user': "User",
    'admin.studentHub.col_role': "Role",
    'admin.studentHub.col_activity': "Last Activity",
    'admin.studentHub.select_user': "Select a user to view details",
    'admin.studentHub.tab_profile': "Profile",
    'admin.studentHub.tab_enrollments': "Enrollments",
    'admin.studentHub.tab_certificates': "Certificates",
    'admin.studentHub.tab_activity': "Activity",
    'admin.studentHub.tab_payments': "Payments",
    'admin.studentHub.tab_kvkk': "KVKK",
    'admin.studentHub.tab_security': "Security",
    'admin.studentHub.action_message': "Send Message",
    'admin.studentHub.action_reset_pw': "Reset Password",
    'admin.studentHub.action_freeze': "Freeze Account",
    'admin.studentHub.action_unfreeze': "Unfreeze Account",
    'admin.studentHub.action_cancel_deletion': "Cancel Deletion",
    'admin.studentHub.action_delete': "Delete Account",
    'admin.studentHub.action_ban': "Ban User",
    'admin.studentHub.action_unban': "Unban User",
    'admin.studentHub.action_deactivate': "Deactivate",
    'admin.studentHub.action_reactivate': "Reactivate",
    'admin.studentHub.profile_name': "Full Name",
    'admin.studentHub.profile_email': "Email",
    'admin.studentHub.profile_phone': "Phone",
    'admin.studentHub.profile_cabin_level': "Cabin Crew Level",
    'admin.studentHub.profile_address': "Address",
    'admin.studentHub.profile_city': "City",
    'admin.studentHub.profile_district': "District",
    'admin.studentHub.profile_postal_code': "Postal Code",
    'admin.studentHub.profile_tc_no': "TC Identity No",
    'admin.studentHub.profile_birth_date': "Birth Date",
    'admin.studentHub.profile_emergency_contact': "Emergency Contact",
    'admin.studentHub.profile_emergency_phone': "Emergency Phone",
    'admin.studentHub.profile_notes': "Notes",
    'admin.studentHub.profile_save': "Save Profile",
    'admin.studentHub.profile_saving': "Saving...",
    'admin.studentHub.profile_saved': "Profile updated",
    'admin.studentHub.enroll_col_course': "Course",
    'admin.studentHub.enroll_col_progress': "Progress",
    'admin.studentHub.enroll_col_status': "Status",
    'admin.studentHub.enroll_action_delete': "Remove Enrollment",
    'admin.studentHub.enroll_action_complete': "Mark Complete",
    'admin.studentHub.enroll_delete_title': "Remove Enrollment",
    'admin.studentHub.enroll_delete_desc': "This will remove the student from the course.",
    'admin.studentHub.enroll_complete_title': "Mark as Complete",
    'admin.studentHub.enroll_complete_desc': "This will manually mark the enrollment as completed.",
    'admin.studentHub.btn_assign_course': "Assign Course",
    'admin.studentHub.assign_course_title': "Assign New Course",
    'admin.studentHub.assign_course_placeholder': "Select a course...",
    'admin.studentHub.assign_course_label': "Course",
    'admin.studentHub.assign_course_success': "Course assigned successfully.",
    'admin.studentHub.assign_course_already': "Student is already enrolled in this course.",
    'admin.studentHub.assign_course_payment_method': "Payment method",
    'admin.studentHub.assign_course_method_cash': "Cash",
    'admin.studentHub.assign_course_method_installments': "Installments",
    'admin.studentHub.assign_course_method_free': "Free",
    // FAZ 1 karma ödeme turu (2026-09-25) — eski Nakit/Taksitli ikilisi
    // "paid" (Peşinat + Kalan, bkz. components/sales/PaymentPlanFields.jsx)
    // olarak birleşti.
    'admin.studentHub.assign_course_method_paid': "Paid",
    'admin.studentHub.assign_course_discount': "Discount (%)",
    'admin.studentHub.assign_course_installments_count': "Number of installments",
    'admin.studentHub.assign_course_down_payment': "Down payment",
    'admin.studentHub.assign_course_price_override': "Manual price (optional)",
    'admin.studentHub.assign_course_notes': "Notes (optional)",
    'admin.studentHub.cert_col_course': "Course",
    'admin.studentHub.cert_col_issued': "Issued",
    'admin.studentHub.cert_col_verify': "Verify URL",
    'admin.studentHub.btn_issue_certificate': "Issue Certificate",
    'admin.studentHub.issue_certificate_title': "Issue Certificate",
    'admin.studentHub.issue_certificate_desc': "Manually issue a certificate for this student. Choose the course — the certificate is generated only if enrollment is completed (and the exam is passed, if required).",
    'admin.studentHub.issue_certificate_label': "Course",
    'admin.studentHub.issue_certificate_placeholder': "Select a course...",
    'admin.studentHub.issue_certificate_already_suffix': "certificate exists",
    'admin.studentHub.issue_certificate_empty': "This student has no enrollments.",
    'admin.studentHub.issue_certificate_confirm': "Issue Certificate",
    'admin.studentHub.issue_certificate_success': "Certificate issued successfully.",
    'admin.studentHub.issue_certificate_already_exists': "Certificate already exists for this course — returned the existing one.",
    'admin.studentHub.activity_empty': "No activity records",
    'admin.studentHub.action.register': "Registered",
    'admin.studentHub.action.login': "Logged in",
    'admin.studentHub.action.logout': "Logged out",
    'admin.studentHub.action.password_changed': "Password changed",
    'admin.studentHub.action.password_reset_requested': "Password reset requested",
    'admin.studentHub.action.password_reset_completed': "Password reset completed",
    'admin.studentHub.action.password_reset_by_admin': "Password reset by admin",
    'admin.studentHub.action.account_deletion_requested': "Account deletion requested",
    'admin.studentHub.action.account_deletion_cancelled': "Account deletion cancelled",
    'admin.studentHub.action.data_exported': "Data exported",
    'admin.studentHub.action.role_changed': "Role changed",
    'admin.studentHub.action.student_registered': "Student registered",
    'admin.studentHub.action.onay_kodu_dogrulandi': "Verification code confirmed",
    'admin.studentHub.action.courses_enrolled': "Enrolled in courses",
    'admin.studentHub.action.courses_assigned_free': "Free courses assigned",
    'admin.studentHub.action.ek_ders_hakki_set': "Extra lesson quota set",
    'admin.studentHub.action.ek_ders_hakki_kullanildi': "Extra lesson quota used",
    'admin.studentHub.action.status_changed': "Status changed",
    'admin.studentHub.action.manual_payment_added': "Manual payment added",
    'admin.studentHub.action.enrollment_updated': "Enrollment updated",
    'admin.studentHub.action.enrollment_cancelled': "Enrollment cancelled",
    'admin.studentHub.action.meb_doc_set': "MEB document set",
    'admin.studentHub.action.students_export': "Students exported",
    'admin.studentHub.action.student_note_added': "Student note added",
    'admin.studentHub.action.lesson_updated': "Lesson updated",
    'admin.studentHub.action.lesson_deleted': "Lesson deleted",
    'admin.studentHub.action.lesson_completed': "Lesson completed",
    'admin.studentHub.action.cabin_lesson_complete': "Cabin lesson completed",
    'admin.studentHub.action.cabin_progress.reset': "Cabin progress reset",
    'admin.studentHub.action.profile_updated': "Profile updated",
    'admin.studentHub.action.certificate_shared': "Certificate shared",
    'admin.studentHub.action.course_created': "Course created",
    'admin.studentHub.action.contract_signed': "Contract signed",
    'admin.studentHub.action.teyit_guncellendi': "Verification updated",
    'admin.studentHub.action.ogrenci_info_sms_gonderildi': "Info SMS sent",
    'admin.studentHub.action.notification_sent': "Notification sent",
    'admin.studentHub.action.reminders_sent': "Reminders sent",
    'admin.studentHub.action.bug_report_create': "Bug report created",
    'admin.studentHub.action.bug_report_update': "Bug report updated",
    'admin.studentHub.action.definition_created': "Definition created",
    'admin.studentHub.action.definition_updated': "Definition updated",
    'admin.studentHub.action.definition_deleted': "Definition deleted",
    'admin.studentHub.action.exam_group_created': "Exam group created",
    'admin.studentHub.action.exam_group_deleted': "Exam group deleted",
    'admin.studentHub.action.exam_group_members_added': "Exam group members added",
    'admin.studentHub.action.forum_thread_created': "Forum thread created",
    'admin.studentHub.action.forum_thread_deleted': "Forum thread deleted",
    'admin.studentHub.action.forum_post_created': "Forum reply posted",
    'admin.studentHub.action.forum_post_deleted': "Forum reply deleted",
    'admin.studentHub.action.forum_accept_answer': "Forum answer accepted",
    'admin.studentHub.action.admin_forum_moderate_thread': "Forum thread moderated",
    'admin.studentHub.action.admin_forum_moderate_post': "Forum reply moderated",
    'admin.studentHub.action.lead_created': "Lead created",
    'admin.studentHub.action.lead_deleted': "Lead deleted",
    'admin.studentHub.action.lead_converted': "Lead converted to student",
    'admin.studentHub.action.leads_imported': "Leads imported",
    'admin.studentHub.action.lead_status_updated': "Lead status updated",
    'admin.studentHub.action.leads_bulk_assigned': "Leads bulk assigned",
    'admin.studentHub.action.installment_collected': "Installment collected",
    'admin.studentHub.action.promissory_note_created': "Promissory note created",
    'admin.studentHub.action.promissory_collected': "Promissory note collected",
    'admin.studentHub.action.taksit_guncellendi': "Installment updated",
    'admin.studentHub.action.referans_kodu_olusturuldu': "Referral code created",
    'admin.studentHub.action.referans_kodu_guncellendi': "Referral code updated",
    'admin.studentHub.action.session_revoked': "Session revoked",
    'admin.studentHub.action.sessions_revoked': "Sessions revoked",
    'admin.studentHub.action.account_frozen': "Account frozen",
    'admin.studentHub.action.video_interview_start': "Video interview started",
    'admin.studentHub.action.video_interview_delete': "Video interview deleted",
    'admin.studentHub.action.video_access_denied': "Video access denied",
    'admin.studentHub.action.embed_token_issued': "Embed token issued",
    'admin.studentHub.action.vimeo_assigned': "Vimeo account assigned",
    'admin.studentHub.action.voice_practice_evaluated': "Voice practice evaluated",
    'admin.studentHub.action.voice_scenario_created': "Voice scenario created",
    'admin.studentHub.action.voice_scenario_updated': "Voice scenario updated",
    'admin.studentHub.action.voice_scenario_deleted': "Voice scenario deleted",
    'admin.studentHub.action.placement_test_submitted': "Placement test submitted",
    'admin.studentHub.action.bank_auto_match_triggered': "Bank auto-match triggered",
    'admin.studentHub.action.bank_transaction_added': "Bank transaction added",
    'admin.studentHub.action.bank_transaction_matched': "Bank transaction matched",
    'admin.studentHub.action.admin_create_cabin_module': "Cabin module created",
    'admin.studentHub.action.admin_update_cabin_module': "Cabin module updated",
    'admin.studentHub.action.impersonate_issued': "Impersonation started",
    'admin.studentHub.action.impersonate_exchange': "Logged in as student",
    'admin.studentHub.action.magic_link_issued': "Magic link issued",
    'admin.studentHub.payment_col_amount': "Amount",
    'admin.studentHub.payment_col_status': "Status",
    'admin.studentHub.payment_col_course': "Course",
    'admin.studentHub.payment_col_date': "Date",
    'admin.studentHub.kvkk_pending_label': "Deletion Requested",
    'admin.studentHub.kvkk_scheduled': "Scheduled For",
    'admin.studentHub.kvkk_grace': "Grace Until",
    'admin.studentHub.kvkk_cancel_btn': "Cancel Deletion Request",
    'admin.studentHub.kvkk_delete_btn': "Delete Immediately",
    'admin.studentHub.kvkk_delete_title': "Delete Account Permanently",
    // KVKK GERCEK DAVRANIS (dogrulandi, backend/services/user_purge.py + admin_bulk_delete_students
    // docstring'i, 2026-08-31): kisisel veriler kalicidir silinir, geri alinamaz (purged_at
    // damgasi dustukten sonra reactivate_account() AccountPurgedError firlatir — bkz.
    // account_deactivation.py:428). Ancak "TUM veri" YANLIS: mali/yasal kayitlar (odeme,
    // taksit, senet, icra, fatura, banka hareketi, iade) VUK/TTK/TBK geregi HICBIR ISLEME
    // TABI TUTULMADAN saklanir (USER_RELATED_RETAIN_COLLECTIONS).
    'admin.studentHub.kvkk_delete_desc': "This permanently deletes the user's personal data (profile, activity, learning records) — this cannot be undone. Payment, installment, invoice and debt-enforcement records are kept as required by tax and commercial law; they are not deleted.",
    'admin.studentHub.kvkk_cancel_title': "Cancel Deletion Request",
    'admin.studentHub.kvkk_cancel_desc': "This will cancel the user's account deletion request.",
    'admin.studentHub.kvkk_no_pending': "No pending deletion request",
    'admin.studentHub.empty_title': "No students found",
    'admin.studentHub.empty_desc': "Try adjusting your search or filters.",
    'admin.studentHub.loading': "Loading students...",
    'admin.studentHub.new_user_title': "Create New User",
    'admin.studentHub.new_user_name': "Full Name",
    'admin.studentHub.new_user_email': "Email",
    'admin.studentHub.new_user_phone': "Phone",
    'admin.studentHub.new_user_password': "Password",
    'admin.studentHub.new_user_role': "Role",
    'admin.studentHub.new_user_save': "Create User",
    'admin.studentHub.new_user_saving': "Creating...",
    'admin.studentHub.new_user_created': "User created",
    'admin.studentHub.pw_reset_title': "Reset Password",
    'admin.studentHub.pw_reset_new': "New Password",
    'admin.studentHub.pw_reset_save': "Reset",
    'admin.studentHub.pw_reset_success': "Password reset",
    'admin.studentHub.ban_confirm_title': "Ban User",
    'admin.studentHub.ban_confirm_desc': "Banned users cannot log in.",
    'admin.studentHub.unban_confirm_title': "Unban User",
    'admin.studentHub.unban_confirm_desc': "This will restore login access.",
    'admin.studentHub.deactivate_confirm_title': "Deactivate Account",
    'admin.studentHub.deactivate_confirm_desc': "The student will not be able to log in. No data is deleted.",
    'admin.studentHub.reactivate_confirm_title': "Reactivate Account",
    'admin.studentHub.reactivate_confirm_desc': "This will restore login access for the student.",
    'admin.studentHub.status_reason_label': "Reason (required)",
    'admin.studentHub.status_reason_placeholder': "Enter a reason for this status change",
    'admin.studentHub.action_assign_level': "Assign Level",
    'admin.studentHub.level_modal_title': "Assign Level",
    'admin.studentHub.level_modal_desc': "Manually set the student's cabin crew and/or English level. A reason is required for audit purposes.",
    'admin.studentHub.level_cabin_crew_label': "Cabin Crew Level",
    'admin.studentHub.level_english_label': "English Level",
    'admin.studentHub.level_keep_current': "Keep current",
    'admin.studentHub.level_beginner': "Beginner",
    'admin.studentHub.level_intermediate': "Intermediate",
    'admin.studentHub.level_advanced': "Advanced",
    'admin.studentHub.level_reason_label': "Reason (required)",
    'admin.studentHub.level_reason_placeholder': "Why is this level being assigned?",
    'admin.studentHub.level_save': "Save Level",
    'admin.studentHub.level_saved': "Level updated",
    'admin.studentHub.level_current_badge_cabin': "Cabin",
    'admin.studentHub.level_current_badge_english': "English",
    'admin.studentHub.cabin_program_badge_label': "Cabin Program",
    'admin.studentHub.cabin_program_registered': "Enrolled",
    'admin.studentHub.cabin_program_not_registered': "Not enrolled",
    'admin.studentHub.action_cabin_enroll': "Enroll in Cabin Program",
    'admin.studentHub.action_cabin_end': "End Cabin Program Enrollment",
    'admin.studentHub.cabin_enroll_title': "Enroll in Cabin Program",
    'admin.studentHub.cabin_enroll_desc': "The student will be enrolled in the cabin crew training program.",
    'admin.studentHub.cabin_enroll_notes_label': "Note (optional)",
    'admin.studentHub.cabin_enroll_notes_placeholder': "Add an internal note about this enrollment",
    'admin.studentHub.cabin_enroll_no_level_hint': "The student also needs a level assigned to access the modules.",
    'admin.studentHub.cabin_enroll_confirm': "Enroll",
    'admin.studentHub.cabin_enroll_success': "Student enrolled in the cabin program",
    'admin.studentHub.cabin_end_title': "End Cabin Program Enrollment",
    'admin.studentHub.cabin_end_desc': "The student will lose access to the cabin crew training program.",
    'admin.studentHub.cabin_end_reason_label': "Reason (required)",
    'admin.studentHub.cabin_end_reason_placeholder': "Why is this enrollment being ended?",
    'admin.studentHub.cabin_end_confirm': "End Enrollment",
    'admin.studentHub.cabin_end_success': "Cabin program enrollment ended",
    'admin.studentHub.videoProgress.title': "Video Watch Detail",
    'admin.studentHub.videoProgress.openBtn': "Video Detail",
    'admin.studentHub.videoProgress.lessonHeader': "Lesson",
    'admin.studentHub.videoProgress.percentHeader': "Watched",
    'admin.studentHub.videoProgress.lastWatchedHeader': "Last Watched",
    'admin.studentHub.videoProgress.completedBadge': "Completed",
    'admin.studentHub.videoProgress.notStarted': "Not started",
    'admin.studentHub.videoProgress.summary': "Total: {{completed}}/{{total}} lessons · avg {{avg}}%",
    'admin.studentHub.videoProgress.empty': "No watch records in this course yet",
    'admin.studentHub.videoProgress.error': "Couldn't load data",

    // ── StudentHub — Görüşmeler sekmesi ──────────────────────────────────────
    'admin.studentHub.tab_gorusmeler': "Interviews",
    'admin.studentHub.tab_sinav': "Exam Tracking",
    'admin.studentHub.gorusme_add_title': "New Interview",
    'admin.studentHub.gorusme_kanal': "Channel",
    'admin.studentHub.gorusme_konu': "Subject",
    'admin.studentHub.gorusme_not': "Note",
    'admin.studentHub.gorusme_tarih': "Date / Time",
    'admin.studentHub.gorusme_btn_add': "Add Interview",
    'admin.studentHub.gorusme_adding': "Adding...",
    'admin.studentHub.gorusme_added': "Interview added",
    'admin.studentHub.gorusme_empty': "No interview records yet",
    'admin.studentHub.gorusme_load_more': "Load more",
    'admin.studentHub.sms_history_title': "Sent SMS messages",
    'admin.studentHub.sms_history_empty': "No SMS sent yet",
    'admin.studentHub.sms_purpose_otp': "Verification Code",
    'admin.studentHub.sms_purpose_credentials': "Login Details",
    'admin.studentHub.sms_purpose_info': "Information",
    'admin.studentHub.sms_purpose_reminder': "Reminder",
    'admin.studentHub.sms_purpose_campaign': "Campaign",
    'admin.studentHub.sms_purpose_lead': "Lead",
    'admin.studentHub.sms_purpose_test': "Test",
    'admin.studentHub.sms_status_sent': "Delivered",
    'admin.studentHub.sms_status_failed': "Failed",
    'admin.studentHub.sms_status_skipped': "Skipped",
    'admin.studentHub.sms_status_pending': "Pending",
    'admin.studentHub.gorusme_lead_badge': "Lead period",
    'admin.studentHub.gorusme_kanal_telefon': "Phone",
    'admin.studentHub.gorusme_kanal_whatsapp': "WhatsApp",
    'admin.studentHub.gorusme_kanal_yuz_yuze': "Face to Face",
    'admin.studentHub.gorusme_kanal_sms': "SMS",
    'admin.studentHub.gorusme_kanal_email': "Email",

    // ── StudentHub — Profil teyit rozetleri ──────────────────────────────────
    'admin.studentHub.teyit_edildi': "Verified",
    'admin.studentHub.teyit_edilmedi': "Not Verified!",
    'admin.studentHub.teyit_confirm_title': "Update Verification",
    'admin.studentHub.teyit_confirm_desc': "Do you want to change the verification status?",
    'admin.studentHub.teyit_tc': "TC ID",
    'admin.studentHub.teyit_adres': "Address",
    'admin.studentHub.teyit_dogum_tarihi': "Birth Date",
    'admin.studentHub.badge_active': "Active",
    'admin.studentHub.badge_inactive': "Inactive",
    'admin.studentHub.badge_verified': "ID Verified",
    'admin.studentHub.badge_ek_ders': "Extra Lesson: {{n}}",

    // ── StudentHub — Aksiyon butonları ────────────────────────────────────────
    'admin.studentHub.btn_send_sms': "Send SMS",
    'admin.studentHub.btn_pdf_form': "Registration PDF",
    'admin.studentHub.btn_ek_ders': "Use Extra Lesson",
    'admin.studentHub.sms_confirm_title': "Send Info SMS",
    'admin.studentHub.sms_confirm_desc': "A registration info SMS will be sent to the student.",
    'admin.studentHub.sms_sent': "SMS sent",
    'admin.studentHub.ek_ders_confirm_title': "Use Extra Lesson Right",
    'admin.studentHub.ek_ders_confirm_desc': "One extra lesson right will be consumed.",
    'admin.studentHub.ek_ders_used': "Extra lesson right used. Remaining: {{n}}",

    // ── StudentHub — Sınav sekmesi ────────────────────────────────────────────
    'admin.studentHub.sinav_my_groups': "Exam Groups",
    'admin.studentHub.sinav_suggestions': "Suggested Groups",
    'admin.studentHub.sinav_no_groups': "Not in any exam group",
    'admin.studentHub.sinav_no_suggestions': "No suggested groups",
    'admin.studentHub.sinav_no_suggestions_desc': "No exam groups to suggest — this happens when the student has no MEB-approved main training, or is already a member of all available groups.",
    'admin.studentHub.sinav_btn_add_group': "Add to Group",
    'admin.studentHub.sinav_adding': "Adding...",
    'admin.studentHub.sinav_added': "Added to group",
    'admin.studentHub.sinav_members': "{{n}} members",
    'admin.studentHub.sinav_date': "Exam: {{date}}",

    // ── AdminStudentDetailPage ─────────────────────────────────────────────────
    'admin.studentDetail.title': "Student Detail",
    'admin.studentDetail.back_to_list': "Student List",
    'admin.studentDetail.search_placeholder': "Search by name, TC, phone, email...",
    'admin.studentDetail.tab_kimlik': "Identity",
    'admin.studentDetail.tab_enrollments': "Training",
    'admin.studentDetail.tab_payments': "Accounting",
    'admin.studentDetail.tab_payments_count_hint': "Number of installments, not the number of collections",
    'admin.studentDetail.tab_activity': "Statistics",
    'admin.studentDetail.tab_certificates': "Certificates",
    'admin.studentDetail.tab_gorusmeler': "Interviews",
    'admin.studentDetail.tab_sinav': "Exam",
    'admin.studentDetail.tab_kvkk': "KVKK",
    'admin.studentDetail.tab_security': "Security",

    // ── AdminEnrollments ─────────────────────────────────────────────────────
    'admin.enrollments.title': "Enrollments",
    'admin.enrollments.subtitle': "All student course enrollments",
    'admin.enrollments.breadcrumb_admin': "Admin",
    'admin.enrollments.breadcrumb': "Enrollments",
    'admin.enrollments.btn_bulk_enroll': "Bulk Enroll",
    'admin.enrollments.filter_search': "Search user or course...",
    'admin.enrollments.filter_course': "Course",
    'admin.enrollments.filter_status': "Status",
    'admin.enrollments.col_user': "User",
    'admin.enrollments.col_course': "Course",
    'admin.enrollments.col_progress': "Progress",
    'admin.enrollments.col_status': "Status",
    'admin.enrollments.col_started': "Started",
    'admin.enrollments.action_delete': "Remove",
    'admin.enrollments.action_complete': "Mark Complete",
    'admin.enrollments.bulk_delete': "Delete Selected",
    'admin.enrollments.bulk_complete': "Complete Selected",
    'admin.enrollments.delete_title': "Remove Enrollment",
    'admin.enrollments.delete_desc': "This will remove the student from the course.",
    'admin.enrollments.empty_title': "No enrollments found",
    'admin.enrollments.empty_desc': "Try adjusting your filters.",
    'admin.enrollments.aggregate_note': "Showing course-level aggregates (per-student list requires backend /admin/enrollments endpoint)",
    'admin.enrollments.enrolled_count': "Enrolled",
    'admin.enrollments.est_revenue': "Est. Revenue",

    // ── AdminAnnouncements ────────────────────────────────────────────────────
    'admin.ann_page_title': "Announcements",
    'admin.ann_page_subtitle': "Create and manage platform-wide announcements",
    'admin.ann_new': "New Announcement",
    'admin.ann_title_en': "Title (EN)",
    'admin.ann_title_tr': "Title (TR)",
    'admin.ann_content': "Content...",
    'admin.ann_target_all': "Everyone",
    'admin.ann_target_students': "Students",
    'admin.ann_target_instructors': "Instructors",
    'admin.ann_priority_low': "Low",
    'admin.ann_priority_normal': "Normal",
    'admin.ann_priority_high': "High",
    'admin.ann_publish': "Publish",
    'admin.ann_publishing': "Publishing...",
    'admin.ann_published': "Announcement published",
    'admin.ann_deleted': "Announcement deleted",
    'admin.ann_activated': "Activated",
    'admin.ann_deactivated': "Deactivated",
    'admin.ann_validation': "Title and content required",
    'admin.ann_empty_title': "No announcements yet",
    'admin.ann_empty_desc': "Create the first announcement to inform your users.",
    'admin.ann_delete_title': "Delete Announcement",
    'admin.ann_delete_desc': "Are you sure you want to delete this announcement? This action cannot be undone.",
    'admin.ann_active': "Active",
    'admin.ann_inactive': "Inactive",

    // ── AdminCampaigns ────────────────────────────────────────────────────────
    'admin.camp_page_title': "Campaigns",
    'admin.camp_page_subtitle': "Manage coupon codes and discount campaigns",
    'admin.camp_new': "New Campaign",
    'admin.camp_name': "Campaign name",
    'admin.camp_code': "Coupon code (e.g. NEW2025)",
    'admin.camp_description': "Description",
    'admin.camp_discount': "Discount %",
    'admin.camp_start': "Start",
    'admin.camp_end': "End",
    'admin.camp_max_usage': "Max Usage",
    'admin.camp_create': "Create",
    'admin.camp_creating': "Creating...",
    'admin.camp_created': "Campaign created",
    'admin.camp_activated': "Activated",
    'admin.camp_deactivated': "Deactivated",
    'admin.camp_validation': "Name and coupon code required",
    'admin.camp_empty_title': "No campaigns yet",
    'admin.camp_empty_desc': "Create your first discount campaign to attract students.",
    'admin.camp_usage': "Usage",
    'admin.camp_active': "Active",
    'admin.camp_inactive': "Inactive",

    // ── AdminCampaigns — WhatsApp ─────────────────────────────────────────────
    'admin.wa.tab_discount': "Discount Campaigns",
    'admin.wa.tab_whatsapp': "WhatsApp Campaigns",
    'admin.wa.step1_title': "1. Select Template",
    'admin.wa.step2_title': "2. Select Recipients",
    'admin.wa.step3_title': "3. Confirm & Send",
    'admin.wa.step_result': "Result",
    'admin.wa.template_search': "Search templates...",
    'admin.wa.template_empty': "No approved templates found.",
    'admin.wa.template_load_error': "Templates could not be loaded. Please try again.",
    'admin.wa.template_preview': "Message Preview",
    'admin.wa.template_selected': "Selected Template",
    'admin.wa.recipients_tab_leads': "Leads",
    'admin.wa.recipients_tab_students': "Students",
    'admin.wa.recipients_tab_manual': "Manual",
    'admin.wa.leads_status_filter': "Filter by status",
    'admin.wa.leads_all_status': "All statuses",
    'admin.wa.search_placeholder': "Search...",
    'admin.wa.manual_placeholder': "One phone number per line (e.g. +905551234567)",
    'admin.wa.selected_count': "Selected",
    'admin.wa.confirm_summary': "template will be sent to",
    'admin.wa.confirm_summary2': "recipient(s).",
    'admin.wa.confirm_checkbox': "I have reviewed the recipient list",
    'admin.wa.send_button': "Send",
    'admin.wa.sending': "Sending...",
    'admin.wa.count_mismatch': "Recipient list has changed. Please refresh and try again.",
    'admin.wa.no_recipients': "No valid recipients found.",
    'admin.wa.too_many': "Too many recipients (max 500).",
    'admin.wa.service_unavailable': "WhatsApp service is currently unavailable.",
    'admin.wa.send_error': "An error occurred while sending.",
    'admin.wa.result_title': "Send Result",
    'admin.wa.result_sent': "Sent",
    'admin.wa.result_failed': "Failed",
    'admin.wa.result_optout': "Opted Out",
    'admin.wa.result_deduped': "Duplicates Removed",
    'admin.wa.result_invalid': "Invalid Phones",
    'admin.wa.poll_status': "Monitoring delivery...",
    'admin.wa.view_report': "View Report",
    'admin.wa.report_phone': "Phone",
    'admin.wa.report_status': "Status",
    'admin.wa.report_sent_at': "Sent At",
    'admin.wa.report_delivered_at': "Delivered At",
    'admin.wa.progress_sent': "Sent",
    'admin.wa.progress_delivered': "Delivered",
    'admin.wa.progress_read': "Read",
    'admin.wa.progress_failed': "Failed",
    'admin.wa.new_campaign': "New Campaign",
    'admin.wa.step_back': "Back",
    'admin.wa.step_next': "Next",
    'admin.wa.no_template_selected': "Please select a template to continue.",
    'admin.wa.no_recipients_selected': "Please select at least one recipient.",
    'admin.wa.loading_leads': "Loading leads...",
    'admin.wa.loading_students': "Loading students...",
    'admin.wa.select_all': "Select All",
    'admin.wa.deselect_all': "Deselect All",
    'admin.wa.approved_badge': "APPROVED",
    'admin.wa.language_badge': "Language",
    'admin.wa.category_badge': "Category",
    'admin.wa.manual_count_suffix': "numbers",
    'admin.wa.result_send_initiated': "Send initiated to {count} recipients",
    'admin.wa.result_invalid_count': "Invalid Numbers",
    'admin.wa.poll_error': "Status could not be retrieved. Please check back later.",
    'admin.wa.no_tracking': "Submission accepted. Status tracking is not available for this campaign.",
    'admin.wa.students_course_filter': "All courses",
    'admin.wa.leads_education_filter': "Education of interest",
    'admin.wa.leads_education_placeholder': "e.g. Cabin Crew",
    'admin.wa.no_phone_badge': "No phone",
    'admin.wa.select_all_with_phone': "Select all ({count})",
    'admin.wa.deselect_all_filtered': "Deselect all",
    'admin.wa.filter_changed_reset': "Filter changed — selection reset.",
    'admin.wa.selection_limit_warning': "Selection exceeds 500 recipients. Please narrow your filter.",
    'admin.wa.phone_masked_label': "Phone",
    'admin.wa.recipient_detail_label': "Details",
    'admin.wa.recipients_total': "Total",
    'admin.wa.recipients_with_phone': "With phone",
    'admin.wa.truncation_warning': "Showing first {shown} of {total} records — narrow your filter",
    'admin.wa.select_all_disabled_truncated': "Select All is disabled when the list is truncated",

    // ── AdminCampaigns — Meta Templates tab ──────────────────────────────────
    'admin.wa.tab_templates': "Templates",
    'admin.wa.tpl_list_title': "WhatsApp Templates",
    'admin.wa.tpl_list_subtitle': "Manage Meta-registered message templates",
    'admin.wa.tpl_refresh': "Refresh",
    'admin.wa.tpl_refreshing': "Refreshing...",
    'admin.wa.tpl_status_approved': "Approved",
    'admin.wa.tpl_status_pending': "Pending Meta approval",
    'admin.wa.tpl_status_rejected': "Rejected",
    'admin.wa.tpl_body_preview': "Message preview",
    'admin.wa.tpl_body_expand': "Show full text",
    'admin.wa.tpl_body_collapse': "Collapse",
    'admin.wa.tpl_delete': "Delete",
    'admin.wa.tpl_delete_confirm_title': "Delete template?",
    'admin.wa.tpl_delete_confirm_body': "This action cannot be undone and a new template with the same name cannot be created for 30 days (Meta policy).",
    'admin.wa.tpl_delete_confirm_btn': "Yes, delete",
    'admin.wa.tpl_delete_cancel': "Cancel",
    'admin.wa.tpl_deleting': "Deleting...",
    'admin.wa.tpl_deleted_ok': "Template deleted.",
    'admin.wa.tpl_empty': "No templates found.",
    'admin.wa.tpl_load_error': "Templates could not be loaded.",
    'admin.wa.tpl_503': "WhatsApp service is unavailable. Please check your Meta API credentials.",
    'admin.wa.tpl_create_title': "Create New Template",
    'admin.wa.tpl_name_label': "Template name",
    'admin.wa.tpl_name_hint': "Lowercase letters, digits and underscores only",
    'admin.wa.tpl_name_preview': "Preview",
    'admin.wa.tpl_category_label': "Category",
    'admin.wa.tpl_category_marketing': "MARKETING – Promotional",
    'admin.wa.tpl_category_utility': "UTILITY – Transactional",
    'admin.wa.tpl_category_authentication': "AUTHENTICATION – Verification",
    'admin.wa.tpl_language_label': "Language",
    'admin.wa.tpl_header_label': "Header text (optional)",
    'admin.wa.tpl_header_hint': "{count}/60 characters",
    'admin.wa.tpl_body_label': "Body",
    'admin.wa.tpl_body_hint': "{count}/1024 characters",
    'admin.wa.tpl_footer_label': "Footer text (optional)",
    'admin.wa.tpl_footer_hint': "{count}/60 characters",
    'admin.wa.tpl_submit': "Submit for Meta approval",
    'admin.wa.tpl_submitting': "Submitting...",
    'admin.wa.tpl_submit_ok': "Template submitted for Meta approval — approval usually takes minutes to hours. Track status in the list.",
    'admin.wa.tpl_submit_ok_short': "Template submitted to Meta",
    'admin.wa.tpl_error_invalid_name': "Template name is invalid. Use only lowercase letters, digits and underscores.",
    'admin.wa.tpl_error_invalid_category': "Invalid category.",
    'admin.wa.tpl_error_body_too_long': "Body text exceeds the 1024-character limit.",
    'admin.wa.tpl_info_note': "Approved templates will appear in the campaign wizard (same WhatsApp Business account). Note: the wizard template list comes from a separate source — newly created templates may take some time to appear there.",
    'admin.wa.tpl_has_buttons_badge': "Has Buttons",
    'admin.wa.tpl_name_required': "Template name is required.",
    'admin.wa.tpl_body_required': "Body text is required.",
    'admin.wa.tpl_header_type_label': "Header type",
    'admin.wa.tpl_header_type_none': "None",
    'admin.wa.tpl_header_type_text': "Text",
    'admin.wa.tpl_header_type_image': "Image",
    'admin.wa.tpl_header_image_hint': "JPEG or PNG, max 5 MB",
    'admin.wa.tpl_header_image_uploading': "Uploading...",
    'admin.wa.tpl_header_image_uploaded': "Uploaded",
    'admin.wa.tpl_header_image_too_large': "Image exceeds 5 MB limit.",
    'admin.wa.tpl_header_image_invalid_type': "Only JPEG and PNG files are accepted.",
    'admin.wa.tpl_header_image_upload_error': "Image could not be uploaded.",
    'admin.wa.tpl_header_image_app_id_missing': "WhatsApp App ID is not configured — image headers are unavailable.",
    'admin.wa.tpl_header_image_badge': "Image header",
    'admin.wa.tpl_image_faz_b_note': "Image template sending requires Meta direct-send integration (Faz B) to be fully supported.",

    // ── AdminCampaigns — Kariyer Takvimi Sharing tab ─────────────────────────
    'admin.wa.tab_kariyertakvimi': "Kariyer Takvimi Sharing",
    'admin.ext.info_title': "External Audience API",
    'admin.ext.info_endpoint': "Endpoint",
    'admin.ext.info_key_note': "API key is defined as EXTERNAL_AUDIENCE_API_KEY in .env (value not shown here).",
    'admin.ext.info_prod_note': "External access becomes active in production deployment.",
    'admin.ext.preview_title': "Shared Data Preview",
    'admin.ext.logs_title': "Access Log",
    'admin.ext.type_label': "Data type",
    'admin.ext.type_leads': "Leads",
    'admin.ext.type_students': "Students",
    'admin.ext.status_label': "Status",
    'admin.ext.status_all': "All statuses",
    'admin.ext.education_placeholder': "Education of interest (e.g. Cabin Crew)",
    'admin.ext.course_label': "All courses",
    'admin.ext.search_placeholder': "Search name / phone...",
    'admin.ext.summary': "Total {total} records, {with_phone} with phone",
    'admin.ext.col_name': "Name",
    'admin.ext.col_phone': "Phone",
    'admin.ext.col_status': "Status / Detail",
    'admin.ext.col_created': "Registration Date",
    'admin.ext.info_note': "Kariyer Takvimi can pull the same list from the API using these filters.",
    'admin.ext.prev_btn': "Previous",
    'admin.ext.next_btn': "Next",
    'admin.ext.log_col_time': "Time",
    'admin.ext.log_col_ip': "IP",
    'admin.ext.log_col_auth': "Auth",
    'admin.ext.log_col_filters': "Filters",
    'admin.ext.log_col_count': "Records",
    'admin.ext.log_col_duration': "Duration (ms)",
    'admin.ext.log_auth_ok': "OK",
    'admin.ext.log_auth_fail': "Failed",
    'admin.ext.log_empty_title': "Kariyer Takvimi has not pulled data yet.",
    'admin.ext.log_empty_desc': "Access records will appear here once external requests are made.",
    'admin.ext.refresh': "Refresh",
    'admin.ext.refreshing': "Refreshing...",

    // ── AdminReviews ──────────────────────────────────────────────────────────
    'admin.rev_page_title': "Reviews",
    'admin.rev_page_subtitle': "Moderate student course reviews",
    'admin.rev_delete_title': "Delete Review",
    'admin.rev_delete_desc': "Are you sure you want to delete this review?",
    'admin.rev_deleted': "Review deleted",
    'admin.rev_empty_title': "No reviews yet",
    'admin.rev_empty_desc': "Student course reviews will appear here.",

    // ── AdminMessages ─────────────────────────────────────────────────────────
    'admin.msg_page_title': "Messages",
    'admin.msg_page_subtitle': "Respond to student contact messages",
    'admin.msg_reply': "Reply",
    'admin.msg_reply_placeholder': "Type reply...",
    'admin.msg_sent': "Reply sent",
    'admin.msg_reply_label': "Reply",
    'admin.msg_validation': "Reply text required",
    'admin.msg_empty_title': "No messages yet",
    'admin.msg_empty_desc': "Student contact messages will appear here.",

    // ── AdminLogs ─────────────────────────────────────────────────────────────
    'admin.log_page_title': "Activity Logs",
    'admin.log_page_subtitle': "Browse admin and user action history",
    'admin.log_filter': "Filter actions...",
    'admin.log_date': "Date",
    'admin.log_user': "User",
    'admin.log_action': "Action",
    'admin.log_details': "Details",
    'admin.log_empty_title': "No logs found",
    'admin.log_empty_desc': "Activity logs will appear here.",

    // ── AdminCertificates ─────────────────────────────────────────────────────
    'admin.cert_page_title': "Certificate Manager",
    'admin.cert_page_subtitle': "Manage certificate types and providers",
    'admin.cert_new': "Add Certificate",
    'admin.cert_types': "Certificate Types",
    'admin.cert_name_tr': "Certificate Name (TR) *",
    'admin.cert_name_en': "Certificate Name (EN)",
    'admin.cert_provider': "Institution *",
    'admin.cert_exam_fee': "Exam Fee (TRY) *",
    'admin.cert_save': "Save",
    'admin.cert_update': "Update",
    'admin.cert_added': "Certificate added",
    'admin.cert_updated': "Certificate updated",
    'admin.cert_deleted': "Certificate type deleted",
    'admin.cert_delete_title': "Delete Certificate Type",
    'admin.cert_delete_desc': "Are you sure? Students with this certificate type will be affected.",
    'admin.cert_empty_title': "No certificate types yet",
    'admin.cert_empty_desc': "Add certificate types that students can earn.",
    'admin.cert_stat_types': "Certificate Types",
    'admin.cert_stat_institutions': "Institutions",
    'admin.cert_stat_avg_fee': "Avg Exam Fee (TRY)",
    'admin.cert_edit': "Edit Certificate",
    'admin.cert_new_form': "New Certificate Type",

    // ── AdminPayments ─────────────────────────────────────────────────────────
    'admin.pay_page_title': "Payments",
    'admin.pay_page_subtitle': "View all payment transactions",
    'admin.pay_revenue': "Revenue",
    'admin.pay_transactions': "Transactions",
    'admin.pay_avg': "Avg Transaction",
    'admin.pay_col_user': "User",
    'admin.pay_col_course': "Course",
    'admin.pay_col_amount': "Amount",
    'admin.pay_col_status': "Status",
    'admin.pay_col_date': "Date",
    'admin.pay_empty_title': "No payments yet",
    'admin.pay_empty_desc': "Payment records will appear here.",

    // ── AdminAccounting v2 ────────────────────────────────────────────────────
    'admin.acc_page_title': "Financial Management",
    'admin.acc_page_subtitle': "Accounting, installments, promissory notes and bank accounts",
    'admin.acc_tab_overview': "Overview",
    'admin.acc_tab_entries': "Transactions",
    'admin.acc_tab_installments': "Installments",
    'admin.acc_tab_notes': "Promissory Notes",
    'admin.acc_tab_banks': "Bank Accounts",
    'admin.acc_kpi_revenue': "Total Revenue",
    'admin.acc_kpi_expense': "Expenses",
    'admin.acc_kpi_net': "Net",
    'admin.acc_kpi_pending': "Pending",
    'admin.acc_kpi_bank_total': "Bank Balance",
    'admin.acc_kpi_overdue': "Overdue",
    'admin.acc_col_user': "Student",
    'admin.acc_col_amount': "Amount",
    'admin.acc_col_status': "Status",
    'admin.acc_col_date': "Date",
    'admin.acc_col_due': "Due Date",
    'admin.acc_col_note_no': "Note No",
    'admin.acc_col_bank': "Bank",
    'admin.acc_col_iban': "IBAN",
    'admin.acc_col_type': "Type",
    'admin.acc_col_category': "Category",
    'admin.acc_col_desc': "Description",
    'admin.acc_col_installment_no': "Inst. #",
    'admin.acc_collect': "Collect",
    // B2 fix — tooltip/label for the disabled "Collect" indicator on a cancelled installment.
    'admin.acc_collect_disabled_cancelled_hint': "This installment was cancelled; it cannot be collected.",
    'admin.acc_collected': "Installment collected",
    'admin.acc_note_collected': "Note collected",
    'admin.acc_note_deleted': "Note deleted",
    'admin.acc_bank_saved': "Account saved",
    'admin.acc_bank_updated': "Account updated",
    'admin.acc_entry_saved': "Entry saved",
    'admin.acc_add_bank': "Add Account",
    'admin.acc_add_note': "Add Note",
    'admin.acc_new_entry': "New Entry",
    'admin.acc_collect_payment': "Collect Payment",
    'admin.acc_select_student': "Select Student",
    'admin.acc_note_number': "Note Number",
    'admin.acc_due_date': "Due Date",
    'admin.acc_bank_name': "Bank Name *",
    'admin.acc_account_name': "Account Name *",
    'admin.acc_iban_placeholder': "TR00 0000 0000 0000 0000 0000 00",
    'admin.acc_account_no': "Account No",
    'admin.acc_branch': "Branch",
    'admin.acc_opening_balance': "Opening Balance",
    'admin.acc_iban_hint': "IBAN must start with TR, total 26 characters",
    'admin.acc_iban_hint_optional': "Optional — if entered, must start with TR, total 26 characters",
    'admin.acc_account_type': "Account Type",
    'admin.acc_account_type_cash': "Cash Register",
    'admin.acc_account_type_bank': "Bank",
    'admin.acc_account_type_pos': "POS (Virtual POS, Moka)",
    'admin.acc_currency': "Currency",
    'admin.acc_delete_note_title': "Delete Promissory Note",
    'admin.acc_delete_note_desc': "Delete this note? This action cannot be undone.",
    'admin.acc_collect_note_title': "Mark as Collected",
    'admin.acc_collect_note_desc': "Mark this promissory note as collected?",
    'admin.acc_note_invoice_id': "Debt Record",
    'admin.acc_note_invoice_loading': "Loading debt records...",
    'admin.acc_note_invoice_select_student_first': "Select a student first",
    'admin.acc_note_invoice_none': "This student has no debt records",
    'admin.acc_note_invoice_select': "Select debt record",
    'admin.acc_note_validation_invoice': "Select the debt record this note belongs to",
    'admin.acc_note_collect_modal_title': "Collect Promissory Note",
    'admin.acc_note_collect_bank_placeholder': "Select bank/cash register",
    'admin.acc_note_collect_bank_required': "Bank/cash register selection is required for collection",
    'admin.acc_collect_amount_locked_hint': "The collected amount must equal the installment amount (interest included); partial collection is not supported.",
    'admin.acc_collect_note_synced': "The linked promissory note was also closed as collected ({n}).",
    'admin.acc_collect_note_skipped': "{n} linked note(s) could not be closed; accounting reconciliation is required.",
    'admin.acc_collect_installment_title': "Collect Installment",
    'admin.acc_collect_installment_desc': "Collect this installment payment?",
    'admin.acc_payment_method': "Payment Method",
    'admin.acc_cash': "Cash",
    'admin.acc_card': "Credit Card",
    'admin.acc_transfer': "Bank Transfer",
    'admin.acc_cash_register': "Cash Register",
    'admin.acc_no_banks': "No banks added",
    'admin.acc_no_notes': "No promissory notes",
    'admin.acc_no_installments': "No installments",
    'admin.acc_no_entries': "No accounting entries",
    'admin.acc_income': "Income",
    'admin.acc_expense': "Expense",
    'admin.acc_validation_category': "Category and description required",
    'admin.acc_validation_amount': "Amount must be positive",
    'admin.acc_validation_student': "Select a student",
    'admin.acc_validation_due_date': "Due date required",
    'admin.acc_validation_bank_name': "Account name required",
    'admin.acc_validation_iban': "IBAN required",
    'admin.acc_validation_iban_format': "Enter valid Turkish IBAN (must start with TR, 26 chars total)",
    'admin.acc_new_bank': "New Account",
    'admin.acc_edit_bank': "Edit Account",
    'admin.acc_saving': "Saving...",
    'admin.acc_save': "Save",
    'admin.acc_update': "Update",
    'admin.acc_creating': "Creating...",
    'admin.acc_create': "Create",
    'admin.acc_collecting': "Collecting...",
    'admin.acc_net': "Net",
    'admin.acc_banks': "Banks",
    'admin.acc_overdue_label': "Overdue",
    'admin.acc_overdue_banner': "{count} overdue — total {amount} TRY",
    'admin.acc_no_overdue': "No overdue payments",
    'admin.acc_no_payments': "No payments found",
    'admin.acc_status_paid': "Paid",
    'admin.acc_status_pending': "Pending",
    'admin.acc_status_overdue': "Overdue",
    'admin.acc_status_collected': "Collected",
    'admin.acc_status_cancelled': "Cancelled",
    'admin.acc_status_active': "Active",
    'admin.acc_overdue_tab': "Overdue ({count})",

    // ── AdminAccounting — new tabs (Expenses / Refunds / Interest Settings) ──
    'admin.acc_tab_expenses': "Expenses",
    'admin.acc_tab_refunds': "Refunds",
    'admin.acc_tab_interest': "Interest Settings",

    // KPI new cards
    'admin.acc_kpi_gross_income': "Gross Income",
    'admin.acc_kpi_refunded': "Refunded",
    'admin.acc_kpi_net_income': "Net Income",
    'admin.acc_kpi_net_profit': "Net Profit",
    // FAZ 1 alignment — shown instead of acc_kpi_net_profit when net_profit < 0
    // (mirrors profit/loss accounting convention; see balanceToneClass note).
    'admin.acc_kpi_net_loss': "Net Loss",
    'admin.acc_kpi_overdue_count': "Overdue Installments",
    'admin.acc_kpi_interest_count': "With Interest",

    // Expenses tab
    'admin.acc_expense_new': "New Expense",
    'admin.acc_expense_category': "Category",
    'admin.acc_expense_vendor': "Vendor",
    'admin.acc_expense_doc_ref': "Document Ref",
    'admin.acc_expense_recurring': "Recurring",
    'admin.acc_expense_saved': "Expense saved",
    'admin.acc_expense_updated': "Expense updated",
    'admin.acc_expense_deleted': "Expense deleted",
    'admin.acc_expense_summary': "Category Summary",
    'admin.acc_no_expenses': "No expenses recorded",
    'admin.acc_expense_delete_title': "Delete Expense",
    'admin.acc_expense_delete_desc': "Delete this expense? This cannot be undone.",
    'admin.acc_expense_edit': "Edit Expense",
    'admin.acc_cat_rent': "Rent",
    'admin.acc_cat_bank_fee': "Bank Fee",
    'admin.acc_cat_salary': "Salary",
    'admin.acc_cat_utility': "Utility",
    'admin.acc_cat_marketing': "Marketing",
    'admin.acc_cat_tax': "Tax",
    'admin.acc_cat_office': "Office",
    'admin.acc_cat_other': "Other",

    // Refunds tab
    'admin.acc_refund_new': "New Refund",
    'admin.acc_refund_amount': "Refund Amount",
    'admin.acc_refund_reason': "Reason",
    'admin.acc_refund_method': "Refund Method",
    'admin.acc_refund_receipt': "Transfer Receipt",
    'admin.acc_refund_payment_id': "Payment ID",
    'admin.acc_refund_invoice_id': "Debt Record ID",
    'admin.acc_refund_or': "OR",
    'admin.acc_refund_saved': "Refund request created",
    'admin.acc_no_refunds': "No refund records",
    'admin.acc_refund_moka_notice': "Moka automatic refund is not yet active. This request will be processed manually by the finance team.",
    'admin.acc_refund_receipt_required': "Bank transfer refund requires receipt upload",
    'admin.acc_refund_validation_amount': "Refund amount must be greater than 0",
    'admin.acc_refund_validation_reason': "Reason required",
    'admin.acc_refund_validation_method': "Method required",
    'admin.acc_refund_validation_ref': "Payment ID or Invoice ID required",
    'admin.acc_reason_student_request': "Student Request",
    'admin.acc_reason_course_cancel': "Course Cancelled",
    'admin.acc_reason_content_change': "Content Changed",
    'admin.acc_reason_other': "Other",
    'admin.acc_method_transfer': "Bank Transfer",
    'admin.acc_method_cash': "Cash",
    'admin.acc_method_moka_refund': "Moka Refund (Auto)",
    'admin.acc_refund_status_requested': "Requested",
    'admin.acc_refund_status_approved': "Approved",
    'admin.acc_refund_status_completed': "Completed",
    'admin.acc_refund_status_rejected': "Rejected",
    'admin.acc_refund_col_amount': "Amount",
    'admin.acc_refund_col_reason': "Reason",
    'admin.acc_refund_col_method': "Method",
    'admin.acc_refund_col_status': "Status",
    'admin.acc_refund_col_date': "Date",
    'admin.acc_refund_col_user': "Student",

    // Interest settings tab
    'admin.acc_interest_title': "Interest & Escalation Settings",
    'admin.acc_interest_enabled': "Enable Interest Calculation",
    'admin.acc_interest_annual_rate': "Annual Rate (%)",
    'admin.acc_interest_grace_period': "Grace Period (days)",
    'admin.acc_interest_warning_day_1': "Threshold Day 1 (days) — Transitions to Overdue status",
    'admin.acc_interest_warning_day_2': "Threshold Day 2 (days) — Transitions to Warning status",
    'admin.acc_interest_start_day': "Interest Start Day",
    'admin.acc_interest_legal_day': "Legal Start Day",
    'admin.acc_interest_min_amount': "Min. Interest Amount (TRY)",
    'admin.acc_interest_saved': "Interest settings saved",
    'admin.acc_interest_job_run': "Interest accrual process started successfully",
    'admin.acc_interest_job_running': "Running...",
    'admin.acc_interest_run_job': "Run Interest Accrual Now",
    'admin.acc_interest_run_job_desc': "Manually starts the daily interest accrual process for all overdue installments.",
    'admin.acc_interest_rate_hint': "Example: 36 = 36% annual rate. Daily rate: annual_rate / 365",
    'admin.acc_interest_loading': "Loading interest settings...",
    // #9: error states
    'admin.acc_expense_load_error': "Could not load expenses. Please try again.",
    'admin.acc_refund_load_error': "Could not load refunds. Please try again.",
    'admin.acc_interest_load_error': "Could not load interest settings. Please try again.",
    'admin.acc_retry': "Retry",
    // #14: expense description validation
    'admin.acc_validation_description': "Description is required",
    // #1: refund invoice required
    'admin.acc_refund_validation_invoice': "Debt Record ID is required",
    // #16: payment_id optional label
    'admin.acc_refund_optional': "optional",
    // invoice dropdown UX
    'admin.acc_refund_invoice_select': "Select Debt Record",
    'admin.acc_refund_invoice_loading': "Loading debt records...",
    'admin.acc_refund_no_invoices': "No debt records found for this student",
    'admin.acc_refund_select_student_first': "Select a student first",
    // #17: breadcrumb
    'admin.acc_breadcrumb_admin': "Admin",

    // Installment extended columns
    'admin.acc_col_interest': "Interest",
    'admin.acc_col_total_due': "Total Due",
    'admin.acc_col_days_overdue': "Days Overdue",
    'admin.acc_status_overdue_warn': "Warning",
    'admin.acc_status_interest': "With Interest",
    'admin.acc_status_legal': "Legal",

    // ── AdminAccounting — Match Pool (Eşleşme Havuzu) ────────────────────────
    'admin.acc_tab_match_pool': "Match Pool",
    'admin.acc_match_pool_title': "Bank Transaction Match Pool",
    'admin.acc_match_pool_subtitle': "Match wire transfers to pending payments",
    'admin.acc_match_new_tx': "New Bank Transaction",
    'admin.acc_match_csv_import': "CSV Import",
    'admin.acc_match_col_date': "Date",
    'admin.acc_match_col_sender': "Sender",
    'admin.acc_match_col_amount': "Amount",
    'admin.acc_match_col_bank': "Bank",
    'admin.acc_match_col_desc': "Description",
    'admin.acc_match_col_suggested': "Suggested Match",
    'admin.acc_match_col_actions': "Actions",
    'admin.acc_match_btn_match': "Match",
    'admin.acc_match_btn_delete': "Delete",
    'admin.acc_match_no_records': "No unmatched transactions",
    'admin.acc_match_no_records_desc': "All wire transfers have been matched.",
    'admin.acc_match_has_more': "There are more than 200 unmatched transactions. Only the first 200 are shown.",
    'admin.acc_match_loading': "Loading transactions...",
    'admin.acc_match_load_error': "Could not load transactions. Please try again.",
    'admin.acc_match_form_title': "New Bank Transaction",
    'admin.acc_match_form_amount': "Amount",
    'admin.acc_match_form_direction': "Direction",
    'admin.acc_match_form_direction_in': "Incoming (Deposit)",
    'admin.acc_match_form_direction_out': "Outgoing (Withdrawal)",
    'admin.acc_match_form_sender_name': "Sender Name",
    'admin.acc_match_form_sender_iban': "Sender IBAN",
    'admin.acc_match_form_description': "Description",
    'admin.acc_match_form_date': "Transaction Date",
    'admin.acc_match_form_bank': "Bank Name",
    'admin.acc_match_form_ext_ref': "External Ref (optional)",
    'admin.acc_match_form_save': "Save Transaction",
    'admin.acc_match_form_saving': "Saving...",
    'admin.acc_match_saved': "Transaction saved",
    'admin.acc_match_val_amount': "Amount must be greater than 0",
    'admin.acc_match_val_sender': "Sender name is required",
    'admin.acc_match_val_date': "Transaction date is required",
    'admin.acc_match_val_bank': "Bank name is required",
    'admin.acc_match_val_payment': "Please select a payment to match",
    'admin.acc_match_csv_title': "CSV Import",
    'admin.acc_match_csv_hint': "Upload a CSV file with bank transactions",
    'admin.acc_match_csv_btn': "Import",
    'admin.acc_match_csv_importing': "Importing...",
    'admin.acc_match_csv_result': "Imported: {{imported}} | Skipped (dedup): {{skipped}} | Errors: {{errors}}",
    'admin.acc_match_csv_error_details': "Error details",
    'admin.acc_match_modal_title': "Match Transaction to Payment",
    'admin.acc_match_modal_suggested': "Suggested",
    'admin.acc_match_modal_payment_id': "Payment ID / Reference",
    'admin.acc_match_modal_placeholder': "Enter payment ID or reference...",
    'admin.acc_match_modal_confirm': "Confirm Match",
    'admin.acc_match_modal_confirming': "Matching...",
    'admin.acc_match_success': "Transaction matched successfully",
    'admin.acc_match_mismatch_warn': "Amount mismatch — Bank: {{bank}}, Payment: {{payment}}, Difference: {{diff}}",
    'admin.acc_match_delete_title': "Delete Transaction",
    'admin.acc_match_delete_desc': "Delete this bank transaction? Matched transactions cannot be deleted.",
    'admin.acc_match_deleted': "Transaction deleted",
    'admin.acc_match_delete_409': "Cannot delete a matched transaction",
    'admin.acc_match_dir_in': "In",
    'admin.acc_match_dir_out': "Out",
    // ── Match Modal FIX 1 (id vs ref) ────────────────────────────────────────
    'admin.acc_match_modal_use_suggestion': "Match with this payment",
    'admin.acc_match_modal_payment_id_manual': "Payment ID (manual entry)",
    'admin.acc_match_modal_id_hint': "Internal pay_xxx ID. Use the suggestion above when available.",
    // ── Havale Talebi Oluştur (FIX 2) ────────────────────────────────────────
    'admin.acc_havale_btn': "Create Wire Transfer Request",
    'admin.acc_havale_form_title': "Create Wire Transfer Request",
    'admin.acc_havale_form_desc': "Admin-initiated. A pending payment (KO-STD reference) will be created and shared with the student.",
    'admin.acc_havale_course': "Course",
    'admin.acc_havale_course_select': "Select course...",
    'admin.acc_havale_courses_loading': "Loading courses...",
    'admin.acc_havale_bank_optional': "optional",
    'admin.acc_havale_bank_auto': "Auto-select bank",
    'admin.acc_havale_val_course': "Please select a course",
    'admin.acc_havale_create': "Create Request",
    'admin.acc_havale_creating': "Creating...",
    'admin.acc_havale_created': "Wire transfer request created",
    'admin.acc_havale_copy': "Copy",
    'admin.acc_havale_result_title': "Wire Transfer Request Created",
    'admin.acc_havale_result_ref': "KO-STD Reference (share with student)",
    'admin.acc_havale_result_ref_hint': "The student must write this reference in the bank transfer description.",
    'admin.acc_havale_result_bank_info': "Bank account to transfer to",
    'admin.acc_havale_result_instruction': "Share the reference code and IBAN with the student. When the bank transaction arrives, enter it in 'New Bank Transaction' and match using this payment ID.",

    // ── FIX FAZA-3: Güven Skoru + Otomatik Eşleştir + Banka Connector ────────
    'admin.acc_match_confidence_full': "Exact Match",
    'admin.acc_match_confidence_name_amount': "Suggested — Name+Amount",
    'admin.acc_match_confidence_amount_only': "Possible — Amount Only",
    'admin.acc_match_confidence_unknown': "Undefined",
    'admin.acc_match_col_confidence': "Confidence",
    'admin.acc_auto_match_btn': "Auto Match",
    'admin.acc_auto_match_running': "Running...",
    'admin.acc_auto_match_result': "{{approved}} auto-approved, {{suggested}} suggestions created ({{processed}} processed)",
    'admin.acc_auto_match_error': "Auto match failed",
    'admin.acc_connector_panel_title': "Bank API Connections",
    'admin.acc_connector_panel_desc': "When bank API access is granted, automatic transaction fetching will be activated. Currently CSV/manual feed + auto match is running.",
    'admin.acc_connector_status_connected': "Connected",
    'admin.acc_connector_status_pending': "Pending — API Application",

    // ── FIX 1: Birleşik Defter (Ledger) ──────────────────────────────────────
    'admin.acc_ledger_loading': "Loading ledger...",
    'admin.acc_ledger_error': "Could not load ledger. Please try again.",
    'admin.acc_ledger_empty': "No ledger entries",
    'admin.acc_ledger_current_balance': "Ledger Balance (Net Flow)",
    // FAZ 1 alignment — shown under the ledger balance card when it's negative.
    // NOTE: unlike the per-student debt ledger, a negative value here is NOT
    // money owed to a student — it means recorded outflows (expenses + refunds)
    // exceed recorded inflows across all sources. Do not reuse
    // accounting.debt_ledger.balance_creditor wording here.
    'admin.acc_ledger_balance_deficit_hint': "Recorded outflows (expenses + refunds) exceed recorded inflows across all sources.",
    'admin.acc_ledger_from': "From",
    'admin.acc_ledger_to': "To",
    'admin.acc_ledger_col_date': "Date",
    'admin.acc_ledger_col_type': "Type",
    'admin.acc_ledger_col_desc': "Description",
    'admin.acc_ledger_col_debit': "Debit (−)",
    'admin.acc_ledger_col_credit': "Credit (+)",
    'admin.acc_ledger_col_amount': "Amount",
    'admin.acc_ledger_col_balance': "Balance",
    'admin.acc_ledger_prev': "Previous page",
    'admin.acc_ledger_next': "Next page",

    // ── Mali İşler liste sekmeleri — server-side "List" (feature/mali-isler-liste-web) ──
    'admin.acc_list_date_range': "Date Range",
    'admin.acc_list_btn_list': "List",
    'admin.acc_list_date_required': "Select a date range and press List.",
    'admin.acc_list_empty_no_range': "Select a date range and press List to see results.",
    'admin.acc_list_truncated_warning': "Too many results — the list was truncated. Narrow the date range.",

    // ── Overview akış özeti (feature/mali-isler-ozet-tarih-web) ─────────────
    'admin.acc_overview_flow_title': "Flow Summary — Date-Range Based",
    'admin.acc_overview_range_label': "Selected Range",

    // ── İade öğrenci arama (feature/mali-isler-ozet-tarih-web) ──────────────
    'admin.acc_refund_student_search_placeholder': "Search by name or email (min 2 characters)",
    'admin.acc_refund_student_search_searching': "Searching…",
    'admin.acc_refund_student_search_no_results': "No results found",

    // ── FIX 2: Banka Ekstre Modal ────────────────────────────────────────────
    'admin.acc_statement_title': "Bank Statement",
    'admin.acc_statement_opening': "Opening Balance",
    'admin.acc_statement_balance': "Current Balance",
    'admin.acc_statement_open': "View Statement",
    // FAZ 1 alignment — badge shown next to a bank/cash balance when it's
    // negative. A negative bank balance is an overdraft (institution's own
    // cash position), not a debt owed to a student — separate from
    // accounting.debt_ledger.balance_creditor by design.
    'admin.acc_balance_deficit_badge': "Cash Deficit",

    // ── FIX 3: Tıklanabilir KPI ──────────────────────────────────────────────
    'admin.acc_kpi_go_tab': "Click to view",

    // ── Checkout — Wire Transfer ──────────────────────────────────────────────
    'checkout.payment_method_group': "Payment Method",
    'checkout.method_wire': "Wire Transfer / EFT",
    'checkout.method_wire_sub': "Pay by bank wire transfer",
    'checkout.wire_ref_title': "Wire Transfer Instructions",
    'checkout.wire_bank_name': "Bank",
    'checkout.wire_iban': "IBAN",
    'checkout.wire_account_no': "Account No",
    'checkout.wire_ref_no': "Reference No",
    'checkout.wire_amount': "Amount",
    'checkout.wire_notice': "Important: Include the reference number in the wire transfer description.",
    'checkout.wire_notice_sub': "Enrollment activates once payment is confirmed (1-2 business days).",
    'checkout.wire_copy': "Copy",
    'checkout.wire_copied': "Copied!",
    'checkout.wire_no_bank': "Wire transfer is currently unavailable. Please choose another payment method.",
    'checkout.wire_loading': "Loading payment details...",
    'checkout.wire_error': "Could not load wire transfer details. Please try again.",
    'checkout.wire_retry': "Retry",

    // ── AdminBulkOps ────────────────���────────────────────────────────────────
    'admin.bulk.dashboard.title': "Bulk Operations",
    'admin.bulk.dashboard.subtitle': "Manage students, courses and payments in bulk",
    'admin.bulk.dashboard.lastAction': "Last action: {{action}} — {{time}}",

    'admin.bulk.tabs.students': "Students",
    'admin.bulk.tabs.courses': "Courses",
    'admin.bulk.tabs.payments': "Payments",
    'admin.bulk.tabs.export': "Export",

    'admin.bulk.actions.enroll': "Enroll in Course",
    'admin.bulk.actions.unenroll': "Remove Enrollment",
    'admin.bulk.actions.roleChange': "Change Role",
    'admin.bulk.actions.paymentUpdate': "Update Payment",
    'admin.bulk.actions.notify': "Send Notification",
    'admin.bulk.actions.certificate': "Issue Certificate",
    'admin.bulk.actions.export': "Export",
    'admin.bulk.actions.delete': "Delete Selected",

    'admin.bulk.bar.selected': "{{count}} records selected",
    'admin.bulk.bar.selected_zero': "No records selected",
    'admin.bulk.bar.clearSelection': "Clear selection",

    'admin.bulk.confirm.cancel': "Cancel",
    'admin.bulk.confirm.confirm': "Confirm",

    'admin.bulk.confirm.deleteTitle': "Bulk Delete",
    'admin.bulk.confirm.deleteDesc': "You are about to permanently delete **{{count}} records**. This cannot be undone.",
    'admin.bulk.confirm.deleteStep1': "Yes, continue",
    'admin.bulk.confirm.deleteStep2Title': "Final Confirmation",
    'admin.bulk.confirm.deleteStep2Desc': "Type **DELETE** below to confirm",
    'admin.bulk.confirm.deleteStep2Placeholder': "DELETE",
    'admin.bulk.confirm.deleteConfirmBtn': "Permanently Delete",

    'admin.bulk.enroll.confirmTitle': "Bulk Enroll",
    'admin.bulk.enroll.confirmDesc': "**{{count}} students** will be enrolled in \"{{courseName}}\".",
    'admin.bulk.enroll.confirmBtn': "Enroll",
    'admin.bulk.enroll.courseLabel': "Select course",
    'admin.bulk.enroll.typeLabel': "Enrollment type",
    'admin.bulk.enroll.typeFree': "Free",
    'admin.bulk.enroll.typePaid': "Paid",

    'admin.bulk.roleChange.confirmTitle': "Bulk Role Change",
    'admin.bulk.roleChange.confirmDesc': "**{{count}} users** will have their role changed to \"{{newRole}}\".",
    'admin.bulk.roleChange.confirmBtn': "Change Role",
    'admin.bulk.roleChange.roleLabel': "New role",
    'admin.bulk.roleChange.adminWarning': "Admin role grants full system access. Proceed with caution.",

    'admin.bulk.notify.sheetTitle': "Bulk Notification",
    'admin.bulk.notify.titleLabel': "Notification title",
    'admin.bulk.notify.titlePlaceholder': "e.g. Exam reminder",
    'admin.bulk.notify.titleRequired': "Title is required",
    'admin.bulk.notify.messageLabel': "Message",
    'admin.bulk.notify.messagePlaceholder': "Message to students...",
    'admin.bulk.notify.channelsLabel': "Channels",
    'admin.bulk.notify.channelInApp': "In-app",
    'admin.bulk.notify.channelEmail': "Email",
    'admin.bulk.notify.channelWhatsapp': "WhatsApp",
    'admin.bulk.notify.previewCount': "Will be sent to {{count}} students",
    'admin.bulk.notify.sendBtn': "Preview & Send",
    'admin.bulk.notify.confirmTitle': "Send Bulk Notification",
    'admin.bulk.notify.confirmDesc': "Notification will be sent to **{{count}} students** via {{channels}}.",
    'admin.bulk.notify.confirmBtn': "Send",
    // URL onay uyarısı (security denetimi, 2026-08-04) — bkz.
    // utils/urlDetection.js. isSafeInternalDeeplink SADECE ayrı `deeplink`
    // alanını korur, serbest başlık/mesaj metnini KAPSAMAZ.
    'admin.bulk.notify.confirmTitleUrl': "Your message contains a link",
    'admin.bulk.notify.urlWarningDesc': "A link/address was detected: \"{{url}}\". On some devices, links in notification text become automatically clickable — are you sure you want to send it?",
    'admin.bulk.notify.urlWarningBtn': "Send Anyway",

    'admin.bulk.export.entityLabel': "Data type",
    'admin.bulk.export.entityUsers': "Students",
    'admin.bulk.export.entityCourses': "Courses",
    'admin.bulk.export.entityPayments': "Payments",
    'admin.bulk.export.fieldsLabel': "Fields",
    'admin.bulk.export.formatLabel': "Format",
    'admin.bulk.export.previewCount': "Filter result: {{count}} records",
    'admin.bulk.export.btn': "Export",
    'admin.bulk.export.downloadReady': "File ready — download started",

    'admin.bulk.progress.title': "Processing...",
    'admin.bulk.progress.status': "{{done}} / {{total}} completed",
    'admin.bulk.progress.ariaLabel': "Bulk operation progress",
    'admin.bulk.progress.cancelBtn': "Cancel",

    'admin.bulk.result.title': "Operation Complete",
    'admin.bulk.result.successCount': "{{count}} succeeded",
    'admin.bulk.result.failedCount': "{{count}} failed",
    'admin.bulk.result.errorListTitle': "Error details",
    'admin.bulk.result.errorItem': "{{id}}: {{error}}",
    'admin.bulk.result.closeBtn': "Close",
    'admin.bulk.result.newActionBtn': "New Action",

    'admin.bulk.result.pushTitle': "Push notification result",
    'admin.bulk.result.pushSent': "Push sent to {{count}} device(s).",
    'admin.bulk.result.pushFailed': "Push failed for {{count}} device(s).",
    'admin.bulk.result.pushSkippedNoPref': "{{count}} recipient(s) did not receive push — their push preference is off (the in-app notification was still sent).",
    'admin.bulk.result.pushSkippedNoTokens': "{{count}} recipient(s) have no active registered device — no push was sent to them (the in-app notification was still sent).",
    'admin.bulk.result.pushSkippedCommercial': "{{count}} recipient(s) received no push because the message is categorized as commercial — this is an intentional limitation (no separate commercial push opt-in infrastructure yet), not an error. The in-app notification was still sent.",

    'admin.bulk.empty.title': "No records found",
    'admin.bulk.empty.desc': "Try adjusting your filters",

    'admin.bulk.error.title': "Connection lost",
    'admin.bulk.error.desc': "Data could not be loaded — try again",
    'admin.bulk.error.retryBtn': "Retry",

    // ── Admin Security ────────────────────────────────────────────────────────
    'admin.security.tabTitle': "Security",
    'admin.security.activeSessions.title': "Active Sessions",
    'admin.security.activeSessions.desc': "View and revoke active user sessions",
    'admin.security.activeSessions.colUser': "User",
    'admin.security.activeSessions.colDevice': "Device",
    'admin.security.activeSessions.colPlatform': "Platform",
    'admin.security.activeSessions.colIp': "IP Address",
    'admin.security.activeSessions.colLastSeen': "Last Seen",
    'admin.security.activeSessions.actionRevoke': "Revoke All Sessions",
    'admin.security.activeSessions.filterSearch': "Search user...",
    'admin.security.activeSessions.filterPlatform': "Platform",
    'admin.security.activeSessions.filterCountry': "Country",
    'admin.security.activeSessions.emptyTitle': "No active sessions",
    'admin.security.activeSessions.emptyDesc': "All users are currently offline.",
    'admin.security.activeSessions.loading': "Loading sessions...",
    'admin.security.anomalies.title': "Suspicious Activity",
    'admin.security.anomalies.desc': "Detected anomalies and security alerts",
    'admin.security.anomalies.colRule': "Rule",
    'admin.security.anomalies.colSeverity': "Severity",
    'admin.security.anomalies.colDate': "Date",
    'admin.security.anomalies.colStatus': "Status",
    'admin.security.anomalies.filterStatus': "Status",
    'admin.security.anomalies.filterSeverity': "Severity",
    'admin.security.anomalies.filterRule': "Rule",
    'admin.security.anomalies.emptyTitle': "No anomalies detected",
    'admin.security.anomalies.emptyDesc': "No suspicious activity found for the current filters.",
    'admin.security.anomalies.loading': "Loading anomalies...",
    'admin.security.anomalies.detailTitle': "Anomaly Details",
    'admin.security.anomalies.rawDetails': "Raw Details (JSON)",
    'admin.security.anomalies.severity.low': "Low",
    'admin.security.anomalies.severity.medium': "Medium",
    'admin.security.anomalies.severity.high': "High",
    'admin.security.anomalies.rule.high_frequency': "High Frequency",
    'admin.security.anomalies.rule.multi_ip': "Multiple IPs",
    'admin.security.anomalies.rule.impossible_travel': "Impossible Travel",
    'admin.security.anomalies.rule.rapid_sequential': "Rapid Sequential",
    'admin.security.anomalies.rule.off_hours': "Off Hours Access",
    'admin.security.anomalies.status.open': "Open",
    'admin.security.anomalies.status.reviewed': "Reviewed",
    'admin.security.anomalies.status.dismissed': "Dismissed",
    'admin.security.action.detail': "Details",
    'admin.security.action.dismiss': "Dismiss",
    'admin.security.action.freezeAccount': "Freeze Account",
    'admin.security.action.revokeSessions': "Revoke Sessions",
    'admin.security.action.investigate': "Mark for Investigation",
    'admin.security.confirm.freezeTitle': "Freeze Account",
    'admin.security.confirm.freezeMessage': "The user will be unable to log in. This action can be reversed from the Students page.",
    'admin.security.confirm.revokeAllTitle': "Revoke All Sessions",
    'admin.security.userDetail.tabSecurity': "Security",
    'admin.security.userDetail.accessPattern.title': "Hourly Access Pattern",
    'admin.security.userDetail.accessPattern.noData': "No access data available",

    // ── AdminVoicePracticeScenarios ────────────────────────────────────────────
    'admin.voicePractice.title': "Voice Practice Scenarios",
    'admin.voicePractice.subtitle': "Manage spoken practice scenarios for cabin crew training",
    'admin.voicePractice.createBtn': "New Scenario",
    'admin.voicePractice.createTitle': "Create Scenario",
    'admin.voicePractice.editTitle': "Edit Scenario",
    'admin.voicePractice.deleteTitle': "Delete Scenario",
    'admin.voicePractice.deleteDesc': "This scenario will be permanently deleted and removed from all active sessions.",
    'admin.voicePractice.createSuccess': "Scenario created",
    'admin.voicePractice.updateSuccess': "Scenario updated",
    'admin.voicePractice.deleteSuccess': "Scenario deleted",
    'admin.voicePractice.validationTitleRequired': "Title (TR) and Title (EN) are required",
    'admin.voicePractice.validationCategoryRequired': "Category is required",
    'admin.voicePractice.colTitle': "Title",
    'admin.voicePractice.colCategory': "Category",
    'admin.voicePractice.colLevel': "Level",
    'admin.voicePractice.colDuration': "Duration",
    'admin.voicePractice.colActive': "Status",
    'admin.voicePractice.colActions': "Actions",
    'admin.voicePractice.filterCategory': "Category",
    'admin.voicePractice.filterLevel': "Level",
    'admin.voicePractice.filterStatus': "Status",
    'admin.voicePractice.fieldCategory': "Category",
    'admin.voicePractice.fieldLevel': "Level",
    'admin.voicePractice.fieldTitleTr': "Title (TR)",
    'admin.voicePractice.fieldTitleEn': "Title (EN)",
    'admin.voicePractice.fieldDescTr': "Description (TR)",
    'admin.voicePractice.fieldDescEn': "Description (EN)",
    'admin.voicePractice.fieldRefTextTr': "Reference Text (TR)",
    'admin.voicePractice.fieldRefTextEn': "Reference Text (EN)",
    'admin.voicePractice.fieldDuration': "Target Duration (seconds)",
    'admin.voicePractice.fieldDurationHint': "Recommended practice duration in seconds",
    'admin.voicePractice.fieldTags': "Tags",
    'admin.voicePractice.tagsPlaceholder': "Add tag (Enter or comma)",
    'admin.voicePractice.fieldActive': "Active",
    'admin.voicePractice.fieldInactive': "Inactive",
    'admin.voicePractice.emptyTitle': "No scenarios yet",
    'admin.voicePractice.emptyDesc': "Create the first voice practice scenario.",
    // categories
    'admin.voicePractice.category.announce': "Announcements",
    'admin.voicePractice.category.safety': "Safety",
    'admin.voicePractice.category.service': "Passenger Service",
    'admin.voicePractice.category.emergency': "Emergency",
    'admin.voicePractice.category.general': "General",
    'admin.voicePractice.category.turbulence': "Turbulence",
    'admin.voicePractice.category.landing': "Landing",
    'admin.voicePractice.category.delay': "Delay",
    // new form fields
    'admin.voicePractice.fieldOrder': "Order",
    'admin.voicePractice.fieldOrderTooltip': "Lower order appears first within the same category",
    'admin.voicePractice.fieldTipsTr': "Tips (TR)",
    'admin.voicePractice.fieldTipsEn': "Tips (EN)",
    'admin.voicePractice.tipsTrPlaceholder': "Add tip in Turkish (Enter or comma)",
    'admin.voicePractice.tipsEnPlaceholder': "Add tip in English (Enter or comma)",
    'admin.voicePractice.tipsMaxError': "Maximum 10 tips allowed",
    'admin.voicePractice.fieldPassingScore': "Passing Score",
    'admin.voicePractice.fieldPassingScoreHint': "Minimum score to pass (0–100)",
    'admin.voicePractice.validationPassingScore': "Passing score must be between 0 and 100",
    'admin.voicePractice.fieldIsDialogCapable': "Dialog Mode",
    'admin.voicePractice.fieldDialogRole': "Dialog Role",
    'admin.voicePractice.dialogRoleNone': "None",
    'admin.voicePractice.dialogRolePassenger': "Passenger",
    'admin.voicePractice.dialogRoleSupervisor': "Supervisor",
    'admin.voicePractice.dialogRoleInterviewer': "Interviewer",
    'admin.voicePractice.validationDialogRole': "Dialog role cannot be set when dialog mode is disabled",
    'admin.voicePractice.fieldAudioUrl': "TTS Audio URL (optional)",
    'admin.voicePractice.fieldAudioUrlHint': "Will be auto-filled in M4 milestone, do not enter manually.",
    'admin.voicePractice.validationAudioUrl': "Must be a valid URL",
    'admin.voicePractice.fieldUnitId': "Curriculum Unit ID (optional)",
    'admin.voicePractice.fieldUnitIdHint': "Will be linked to curriculum system in the future.",
    'admin.voicePractice.sectionAdvanced': "Advanced Settings",
    'admin.voicePractice.sectionDialog': "Dialog Settings",
    'admin.voicePractice.fieldDialogOpenerTr': "Dialog Opener (TR)",
    'admin.voicePractice.fieldDialogOpenerEn': "Dialog Opener (EN)",
    'admin.voicePractice.fieldDialogOpenerHint': "The opening sentence AI will use to start the conversation. Leave empty — AI auto-generates (not recommended).",
    'admin.voicePractice.fieldDialogContextTr': "Dialog Context (TR)",
    'admin.voicePractice.fieldDialogContextEn': "Dialog Context (EN)",
    'admin.voicePractice.fieldDialogContextHint': "Scenario context for AI. Leave empty — uses description as fallback.",
    'admin.voicePractice.fieldDialogMaxTurnsOverride': "Max Turns Override (Per Scenario)",
    'admin.voicePractice.fieldDialogMaxTurnsPlaceholder': "7 (default)",
    'admin.voicePractice.fieldDialogMaxTurnsHint': "Maximum dialog turns allowed for this scenario (2-20). Leave empty — uses global default (7).",
    'admin.voicePractice.validationDialogOpener': "Opener cannot exceed 500 characters",
    'admin.voicePractice.validationDialogContext': "Context cannot exceed 2000 characters",
    'admin.voicePractice.validationDialogMaxTurns': "Max turns must be between 2-20",
    // new table columns
    'admin.voicePractice.colOrder': "Order",
    'admin.voicePractice.colDialogCapable': "Dialog",
    'admin.voicePractice.colPassingScore': "Pass Score",

    // ── AdminVoiceInterviewQuestions ───────────────────────────────────────────
    'admin.voiceInterview.title': "Voice Interview Questions",
    'admin.voiceInterview.subtitle': "Manage spoken interview question bank for cabin crew candidates",
    'admin.voiceInterview.createBtn': "New Question",
    'admin.voiceInterview.createTitle': "Create Question",
    'admin.voiceInterview.editTitle': "Edit Question",
    'admin.voiceInterview.deleteTitle': "Delete Question",
    'admin.voiceInterview.deleteDesc': "This question will be permanently deleted.",
    'admin.voiceInterview.createSuccess': "Question created",
    'admin.voiceInterview.updateSuccess': "Question updated",
    'admin.voiceInterview.deleteSuccess': "Question deleted",
    'admin.voiceInterview.validationRequired': "Question text (TR and EN) is required",
    'admin.voiceInterview.colQuestion': "Question",
    'admin.voiceInterview.colType': "Type",
    'admin.voiceInterview.colDifficulty': "Difficulty",
    'admin.voiceInterview.colDuration': "Duration",
    'admin.voiceInterview.colActive': "Status",
    'admin.voiceInterview.colActions': "Actions",
    'admin.voiceInterview.filterType': "Question Type",
    'admin.voiceInterview.filterDifficulty': "Difficulty",
    'admin.voiceInterview.filterStatus': "Status",
    'admin.voiceInterview.fieldType': "Question Type",
    'admin.voiceInterview.fieldDifficulty': "Difficulty",
    'admin.voiceInterview.fieldQuestionTr': "Question (TR)",
    'admin.voiceInterview.fieldQuestionEn': "Question (EN)",
    'admin.voiceInterview.fieldDuration': "Expected Duration (seconds)",
    'admin.voiceInterview.fieldSampleAnswerTr': "Sample Answer (TR)",
    'admin.voiceInterview.fieldSampleAnswerEn': "Sample Answer (EN)",
    'admin.voiceInterview.fieldActive': "Active",
    'admin.voiceInterview.emptyTitle': "No questions yet",
    'admin.voiceInterview.emptyDesc': "Create interview questions for practice sessions.",

    // ── AdminAviationEnglishScenarios ──────────────────────────────────────────
    'admin.aviationEnglish.title': "Aviation English",
    'admin.aviationEnglish.subtitle': "Manage aviation English scenarios and quiz questions",
    'admin.aviationEnglish.tabScenarios': "Scenarios",
    'admin.aviationEnglish.tabQuestions': "Questions",
    'admin.aviationEnglish.createScenarioBtn': "New Scenario",
    'admin.aviationEnglish.createQuestionBtn': "New Question",
    'admin.aviationEnglish.createScenarioTitle': "Create Scenario",
    'admin.aviationEnglish.editScenarioTitle': "Edit Scenario",
    'admin.aviationEnglish.createQuestionTitle': "Create Question",
    'admin.aviationEnglish.editQuestionTitle': "Edit Question",
    'admin.aviationEnglish.deleteScenarioTitle': "Delete Scenario",
    'admin.aviationEnglish.deleteScenarioDesc': "Deleting this scenario will also remove associated questions.",
    'admin.aviationEnglish.deleteQuestionTitle': "Delete Question",
    'admin.aviationEnglish.deleteQuestionDesc': "This question will be permanently deleted.",
    'admin.aviationEnglish.scenarioCreateSuccess': "Scenario created",
    'admin.aviationEnglish.scenarioUpdateSuccess': "Scenario updated",
    'admin.aviationEnglish.scenarioDeleteSuccess': "Scenario deleted",
    'admin.aviationEnglish.questionCreateSuccess': "Question created",
    'admin.aviationEnglish.questionUpdateSuccess': "Question updated",
    'admin.aviationEnglish.questionDeleteSuccess': "Question deleted",
    'admin.aviationEnglish.validationTitleRequired': "Title (TR and EN) is required",
    'admin.aviationEnglish.validationQuestionRequired': "Question text (TR and EN) is required",
    'admin.aviationEnglish.validationScenarioRequired': "Please select a scenario",
    'admin.aviationEnglish.colTitle': "Title (TR)",
    'admin.aviationEnglish.colTitleEn': "Title (EN)",
    'admin.aviationEnglish.colLevel': "Level",
    'admin.aviationEnglish.colQuestions': "Questions",
    'admin.aviationEnglish.colQuestion': "Question",
    'admin.aviationEnglish.colQuestionType': "Type",
    'admin.aviationEnglish.colCorrectAnswer': "Correct Answer",
    'admin.aviationEnglish.filterLevel': "Level",
    'admin.aviationEnglish.filterScenario': "Scenario",
    'admin.aviationEnglish.fieldTitleTr': "Title (TR)",
    'admin.aviationEnglish.fieldTitleEn': "Title (EN)",
    'admin.aviationEnglish.fieldDescTr': "Description (TR)",
    'admin.aviationEnglish.fieldDescEn': "Description (EN)",
    'admin.aviationEnglish.fieldLevel': "Level",
    'admin.aviationEnglish.fieldActive': "Active",
    'admin.aviationEnglish.fieldScenario': "Scenario",
    'admin.aviationEnglish.fieldQuestionType': "Question Type",
    'admin.aviationEnglish.fieldQuestionTr': "Question (TR)",
    'admin.aviationEnglish.fieldQuestionEn': "Question (EN)",
    'admin.aviationEnglish.fieldChoices': "Answer Choices",
    'admin.aviationEnglish.fieldSampleAnswerTr': "Sample Answer (TR)",
    'admin.aviationEnglish.fieldSampleAnswerEn': "Sample Answer (EN)",
    'admin.aviationEnglish.emptyScenarios': "No scenarios yet",
    'admin.aviationEnglish.emptyQuestions': "No questions yet",
    'admin.aviationEnglish.emptyDesc': "Add content to get started.",
    'admin.aviationEnglish.colOrder': "Order",
    'admin.aviationEnglish.colPoints': "Points",
    'admin.aviationEnglish.colActive': "Status",
    'admin.aviationEnglish.fieldOrder': "Order",
    'admin.aviationEnglish.fieldPoints': "Points",
    'admin.aviationEnglish.fieldContextTr': "Context (TR)",
    'admin.aviationEnglish.addOption': "Add Option",
    'admin.aviationEnglish.correctAnswerHint': "Correct Answer (must match one of the options above)",

    // ── AdminAnnounceTemplates ─────────────────────────────────────────────────
    'admin.announceTemplates.title': "Announcement Templates",
    'admin.announceTemplates.subtitle': "Manage bilingual announcement scripts with slot variables",
    'admin.announceTemplates.createBtn': "New Template",
    'admin.announceTemplates.createTitle': "Create Template",
    'admin.announceTemplates.editTitle': "Edit Template",
    'admin.announceTemplates.deleteTitle': "Delete Template",
    'admin.announceTemplates.deleteDesc': "This template will be permanently deleted.",
    'admin.announceTemplates.createSuccess': "Template created",
    'admin.announceTemplates.updateSuccess': "Template updated",
    'admin.announceTemplates.deleteSuccess': "Template deleted",
    'admin.announceTemplates.validationTitleRequired': "Title (TR and EN) is required",
    'admin.announceTemplates.validationCategoryRequired': "Category is required",
    'admin.announceTemplates.colTitle': "Title",
    'admin.announceTemplates.colCategory': "Category",
    'admin.announceTemplates.colSlots': "Slots",
    'admin.announceTemplates.colActive': "Status",
    'admin.announceTemplates.filterCategory': "Category",
    'admin.announceTemplates.filterStatus': "Status",
    'admin.announceTemplates.fieldCategory': "Category",
    'admin.announceTemplates.fieldTitleTr': "Title (TR)",
    'admin.announceTemplates.fieldTitleEn': "Title (EN)",
    'admin.announceTemplates.fieldTemplateTr': "Template Text (TR)",
    'admin.announceTemplates.fieldTemplateEn': "Template Text (EN)",
    'admin.announceTemplates.fieldSlots': "Slot Definitions",
    'admin.announceTemplates.fieldActive': "Active",
    'admin.announceTemplates.emptyTitle': "No templates yet",
    'admin.announceTemplates.emptyDesc': "Create announcement templates for cabin crew practice.",

    // ── AdminPaxCommSituations ─────────────────────────────────────────────────
    'admin.paxComm.title': "Passenger Communication",
    'admin.paxComm.subtitle': "Manage passenger interaction scenarios with tone-specific responses",
    'admin.paxComm.createBtn': "New Situation",
    'admin.paxComm.createTitle': "Create Situation",
    'admin.paxComm.editTitle': "Edit Situation",
    'admin.paxComm.deleteTitle': "Delete Situation",
    'admin.paxComm.deleteDesc': "This situation will be permanently deleted.",
    'admin.paxComm.createSuccess': "Situation created",
    'admin.paxComm.updateSuccess': "Situation updated",
    'admin.paxComm.deleteSuccess': "Situation deleted",
    'admin.paxComm.validationTitleRequired': "Title (TR and EN) is required",
    'admin.paxComm.validationCategoryRequired': "Category is required",
    'admin.paxComm.validationResponseRequired': "At least one response is required",
    'admin.paxComm.colTitle': "Title",
    'admin.paxComm.colCategory': "Category",
    'admin.paxComm.colResponses': "Responses",
    'admin.paxComm.colActive': "Status",
    'admin.paxComm.filterCategory': "Category",
    'admin.paxComm.filterStatus': "Status",
    'admin.paxComm.fieldCategory': "Category",
    'admin.paxComm.fieldTitleTr': "Title (TR)",
    'admin.paxComm.fieldTitleEn': "Title (EN)",
    'admin.paxComm.fieldContextTr': "Context / Situation (TR)",
    'admin.paxComm.fieldContextEn': "Context / Situation (EN)",
    'admin.paxComm.fieldResponses': "Responses (per tone)",
    'admin.paxComm.fieldTips': "Communication Tips",
    'admin.paxComm.fieldActive': "Active",
    'admin.paxComm.emptyTitle': "No situations yet",
    'admin.paxComm.emptyDesc': "Create passenger communication situations for training.",

    // ── CabinCrew AviationEnglish Hybrid ─────────────────────────────────────
    'cabinCrew.aviationEnglish.staticContentHeading': "Templates",
    'cabinCrew.aviationEnglish.staticContentHeadingPax': "Scenarios",
    'cabinCrew.aviationEnglish.generateAi': "Generate AI Exercises",
    'cabinCrew.aviationEnglish.noStaticContent': "No templates yet, start with AI",
    'cabinCrew.aviationEnglish.loadingStatic': "Loading content...",
    'cabinCrew.aviationEnglish.staticCategory': "Category",
    'cabinCrew.aviationEnglish.templateText': "Announcement Text",
    'cabinCrew.aviationEnglish.contextLabel': "Situation",
    'cabinCrew.aviationEnglish.tipsLabel': "Tips",
    'cabinCrew.aviationEnglish.responsesLabel': "Sample Responses",
    'cabinCrew.aviationEnglish.orDivider': "or",
    'cabinCrew.aviationEnglish.aiSectionHeading': "AI Exercises",
    'cabinCrew.aviationEnglish.backToTemplates': "Back to Templates",

    // ── CabinCrew Announce Practice ───────────────────────────────────────────
    'cabinCrew.announce.listen': "Listen",
    'cabinCrew.announce.stopListen': "Stop",
    'cabinCrew.announce.practice': "Practice",
    'cabinCrew.announce.recording': "Recording...",
    'cabinCrew.announce.stopRecord': "Stop Recording",
    'cabinCrew.announce.evaluating': "Evaluating...",
    'cabinCrew.announce.score': "Score",
    'cabinCrew.announce.tryAgain': "Try Again",
    'cabinCrew.announce.transcript': "Your Speech",
    'cabinCrew.announce.startRecord': "Click to start recording",
    'cabinCrew.announce.pronunciationLabel': "Pronunciation",
    'cabinCrew.announce.clarityLabel': "Clarity",
    'cabinCrew.announce.paceLabel': "Pace",
    'cabinCrew.announce.accuracyLabel': "Accuracy",
    'cabinCrew.announce.overallLabel': "Overall",
    'cabinCrew.announce.aiFeedback': "AI Feedback",
    'cabinCrew.announce.micError': "Microphone permission required!",
    'cabinCrew.announce.evalError': "Evaluation failed. Please try again.",
    'cabinCrew.announce.practiceTitle': "Announce Practice",
    'cabinCrew.announce.noTrVoice': "No Turkish voice found in this browser; falling back to the nearest available language.",
    'cabinCrew.announce.fillSlots': "Fill in the blanks",
    'cabinCrew.announce.slotPlaceholder': "enter value",

    // ── VoicePracticeModule — feedback actions ────────────────────────────────
    'cabinCrew.voice.tryAgain': "Try Again",
    'cabinCrew.voice.nextExercise': "Next Exercise",
    'cabinCrew.voice.close': "Close",
    'cabinCrew.voice.allDone': "You finished all exercises!",
    'cabinCrew.voice.finishAll': "Finish All",

    // ── AviationEnglishModule — filter bar ───────────────────────────────────
    'common.all': "All",
    'common.back': "Back",
    'cabinCrew.filterBy': "Filter",
    'cabinCrew.categories.welcome': "Welcome",
    'cabinCrew.categories.safety': "Safety",
    'cabinCrew.categories.emergency': "Emergency",
    'cabinCrew.categories.weather': "Weather",
    'cabinCrew.categories.landing': "Landing",
    'cabinCrew.categories.service': "Service",
    'cabinCrew.categories.greeting': "Greeting",
    'cabinCrew.categories.request': "Request",
    'cabinCrew.levelGate.title': "Your Level Has Not Been Assigned Yet",
    'cabinCrew.levelGate.desc': "Your level has not been assigned yet. Please contact Kariyer Online.",

    // ── AdminCabinAcademyHub ──────────────────────────────────────────────────
    'admin.cabinAcademy.title': "Cabin Academy Management",
    'admin.cabinAcademy.subtitle': "Centralized management for all cabin crew training modules",
    'admin.cabinAcademy.modulesSection': "Modules",
    'admin.cabinAcademy.metric.activeModules': "Active Modules",
    'admin.cabinAcademy.metric.totalAcrossModules': "Total Content",
    'admin.cabinAcademy.metric.mostActive': "Most Active Module",
    'admin.cabinAcademy.metric.lastUpdated': "Last Updated",
    'admin.cabinAcademy.level.beginner': "Beg",
    'admin.cabinAcademy.level.intermediate': "Int",
    'admin.cabinAcademy.level.advanced': "Adv",
    'admin.cabinAcademy.module.voicePractice.title': "Voice Practice",
    'admin.cabinAcademy.module.voicePractice.desc': "Spoken practice scenarios for cabin crew training sessions",
    'admin.cabinAcademy.module.voicePractice.totalLabel': "scenarios",
    'admin.cabinAcademy.module.voicePractice.manageBtn': "Manage",
    'admin.cabinAcademy.module.voicePractice.quickCreateBtn': "New Scenario",
    'admin.cabinAcademy.module.voiceInterview.title': "Voice Interview",
    'admin.cabinAcademy.module.voiceInterview.desc': "Interview question bank for cabin crew candidate assessment",
    'admin.cabinAcademy.module.voiceInterview.totalLabel': "questions",
    'admin.cabinAcademy.module.voiceInterview.manageBtn': "Manage",
    'admin.cabinAcademy.module.voiceInterview.quickCreateBtn': "New Question",
    'admin.cabinAcademy.module.aviationEnglish.title': "Aviation English",
    'admin.cabinAcademy.module.aviationEnglish.desc': "Aviation English scenarios and quiz questions",
    'admin.cabinAcademy.module.aviationEnglish.totalLabel': "scenarios",
    'admin.cabinAcademy.module.aviationEnglish.questionCount': "{n} quiz questions",
    'admin.cabinAcademy.module.aviationEnglish.manageBtn': "Manage",
    'admin.cabinAcademy.module.aviationEnglish.quickCreateBtn': "New Scenario",
    'admin.cabinAcademy.module.announceTemplates.title': "Announce Templates",
    'admin.cabinAcademy.module.announceTemplates.desc': "Bilingual announcement scripts with slot variables for practice",
    'admin.cabinAcademy.module.announceTemplates.totalLabel': "templates",
    'admin.cabinAcademy.module.announceTemplates.manageBtn': "Manage",
    'admin.cabinAcademy.module.announceTemplates.quickCreateBtn': "New Template",
    'admin.cabinAcademy.module.paxComm.title': "Pax Communication",
    'admin.cabinAcademy.module.paxComm.desc': "Passenger interaction scenarios with tone-specific responses",
    'admin.cabinAcademy.module.paxComm.totalLabel': "situations",
    'admin.cabinAcademy.module.paxComm.manageBtn': "Manage",
    'admin.cabinAcademy.module.paxComm.quickCreateBtn': "New Situation",
    'admin.cabinAcademy.module.cabinAiCoach.title': "Cabin AI Coach",
    'admin.cabinAcademy.module.cabinAiCoach.desc': "AI-powered coaching module for cabin crew (static content, Faz 2)",
    'admin.cabinAcademy.cabinAiCoach.faz2Notice': "This module will be opened for admin management in Faz 2",

    // Flashcard Lesson
    'flashcardLesson.iKnow': "I Know",
    'flashcardLesson.iDontKnow': "I Don't Know",
    'flashcardLesson.next': "Next",
    'flashcardLesson.prev': "Previous",
    'flashcardLesson.completed': "All Done!",
    'flashcardLesson.restart': "Restart",
    'flashcardLesson.wordCount': "words",
    'flashcardLesson.progressLabel': "Progress",
    'flashcardLesson.tapToFlip': "Tap card to reveal",
    'flashcardLesson.score': "Score",
    'flashcardLesson.knownCount': "Known",
    'flashcardLesson.unknownCount': "Unknown",
    'flashcardLesson.noContent': "No flashcard content found for this lesson.",

    // Matching Lesson
    'matchingLesson.matched': "matched",
    'matchingLesson.mistakes': "Mistakes",
    'matchingLesson.completed': "All Matched!",
    'matchingLesson.restart': "Play Again",
    'matchingLesson.hint': "Hint",
    'matchingLesson.score': "Score",
    'matchingLesson.noContent': "No matching pairs found for this lesson.",
    'matchingLesson.selectLeft': "Select a word from the left",
    'matchingLesson.selectRight': "Now select the matching word",

    // DragDrop Lesson
    'dragDropLesson.check': "Check",
    'dragDropLesson.correct': "Correct!",
    'dragDropLesson.incorrect': "Try Again",
    'dragDropLesson.nextSentence': "Next Sentence",
    'dragDropLesson.retry': "Retry",
    'dragDropLesson.submitAll': "Submit",
    'dragDropLesson.completed': "All Sentences Done!",
    'dragDropLesson.score': "Score",
    'dragDropLesson.noContent': "No drag-drop content found for this lesson.",
    'dragDropLesson.dropHere': "Drop words here...",
    'dragDropLesson.sentence': "Sentence",

    // ── DialogueLesson (Faz 4d) ──────────────────────────────────────────────
    'dialogueLesson.yourTurn': "Your Turn",
    'dialogueLesson.correct': "Correct!",
    'dialogueLesson.incorrect': "Not quite.",
    'dialogueLesson.continue': "Continue",
    'dialogueLesson.score': "Score",
    'dialogueLesson.correctAnswers': "correct",
    'dialogueLesson.completed': "Dialogue Complete!",
    'dialogueLesson.restart': "Try Again",
    'dialogueLesson.step': "Step",
    'dialogueLesson.noContent': "No dialogue content found for this lesson.",

    // ── FillInBlankLesson (Faz 4d) ───────────────────────────────────────────
    'fillInBlankLesson.check': "Check",
    'fillInBlankLesson.correct': "Correct!",
    'fillInBlankLesson.incorrect': "Incorrect",
    'fillInBlankLesson.correctAnswerLabel': "Correct answer",
    'fillInBlankLesson.next': "Next",
    'fillInBlankLesson.retry': "Retry",
    'fillInBlankLesson.score': "Score",
    'fillInBlankLesson.hint': "Show Hint",
    'fillInBlankLesson.completed': "All Done!",
    'fillInBlankLesson.question': "Question",
    'fillInBlankLesson.noContent': "No fill-in-blank content found for this lesson.",

    // ── SwipeVocabularyLesson (Faz 4d) ───────────────────────────────────────
    'swipeLesson.iKnow': "I Know",
    'swipeLesson.reviewLater': "Review Later",
    'swipeLesson.known': "Known",
    'swipeLesson.remaining': "remaining",
    'swipeLesson.swipeInstruction': "Swipe right if you know it, left to review later",
    'swipeLesson.completed': "All Done!",
    'swipeLesson.score': "Score",
    'swipeLesson.wordsKnown': "words known",
    'swipeLesson.restart': "Try Again",
    'swipeLesson.noContent': "No vocabulary content found for this lesson.",

    // ── TimeRaceLesson (Faz 4d) ──────────────────────────────────────────────
    'timeRaceLesson.title': "Time Race!",
    'timeRaceLesson.questions': "questions",
    'timeRaceLesson.start': "Start Race",
    'timeRaceLesson.perCorrect': "per correct",
    'timeRaceLesson.perWrong': "per wrong",
    'timeRaceLesson.timeLeft': "Time Left",
    'timeRaceLesson.correct': "Correct",
    'timeRaceLesson.incorrect': "Wrong",
    'timeRaceLesson.points': "pts",
    'timeRaceLesson.timeUp': "Time's Up!",
    'timeRaceLesson.allDone': "All Done!",
    'timeRaceLesson.timeUsed': "Time Used",
    'timeRaceLesson.finalScore': "Final Score",
    'timeRaceLesson.restart': "Play Again",
    'timeRaceLesson.noContent': "No time race content found for this lesson.",

    // Common lesson keys
    'common.completeLesson': "Complete Lesson",
    'common.score': "Score",
    'common.tryAgain': "Try Again",
    'common.lessonCompleted': "Lesson Completed",

    // Admin nav
    'admin.nav.dashboard': "Dashboard",
    'admin.nav.education': "Education",
    'admin.nav.courses': "Courses",
    'admin.nav.vimeo': "Vimeo Library",
    'admin.nav.placementTests': "Placement Tests",
    'admin.nav.reviews': "Reviews",
    'admin.nav.students': "Students",
    'admin.nav.danismanData': "Advisor & Data",
    // "Advisor & Data" altindaki 4 alt drawer basligi
    'admin.nav.danismanGunlukIs': "Daily Work",
    'admin.nav.danismanDataOnay': "Data & Approval",
    'admin.nav.danismanRaporlar': "Reports",
    'admin.nav.danismanEkip': "Team Management",
    'admin.nav.studentHub': "Student Hub (360)",
    'admin.nav.studentsPage': "Students",
    'admin.nav.usersPage': "Users",
    'admin.nav.enrollments': "All Enrollments",
    'admin.newUser.title': "Create New User",
    'admin.newUser.subtitle': "Select the type of user to create",
    'admin.newUser.role.student': "Student",
    'admin.newUser.role.instructor': "Instructor",
    'admin.newUser.role.admin': "Admin",
    'admin.newUser.role.student.desc': "Full enrollment with course, certificate and payment flow",
    'admin.newUser.role.instructor.desc': "Personal info only — no course flow",
    'admin.newUser.role.admin.desc': "Create user with admin privileges",
    'admin.newUser.step.personalInfo': "Personal Info",
    'admin.newUser.step.mainCourse': "Main Course",
    'admin.newUser.step.additional': "Additional",
    'admin.newUser.step.certificate': "Certificate",
    'admin.newUser.step.summary': "Summary",
    'admin.newUser.success.title': "Registration Complete!",
    'admin.newUser.success.newRegistration': "New Registration",
    'admin.newUser.success.backToStudents': "Student List",
    'admin.newUser.success.backToUsers': "User List",
    'admin.nav.cabinCrewEnrollments': "Cabin Crew Programs",
    'admin.nav.cabinCrewGroups': "Groups",
    'admin.nav.bulkOps': "Bulk Operations",
    'admin.nav.cabinAcademy': "Cabin Academy",
    'admin.nav.cabinHub': "Academy Hub",
    'admin.nav.cabinCurriculum': "Curriculum",
    'admin.nav.voicePractice': "Voice Practice",
    'admin.nav.voiceInterview': "Voice Interview Q&A",
    'admin.nav.aviationEnglish': "Aviation English",
    'admin.nav.announceTemplates': "Announce Templates",
    'admin.nav.paxComm': "Pax Communication",
    'admin.nav.documents': "Documents & Legal",
    'admin.nav.certificates': "Certificates",
    'admin.nav.documentsList': "Documents",
    'admin.nav.contracts': "Contracts",
    'admin.nav.kvkk': "KVKK Requests",
    'admin.nav.legalContent': "Legal Content",
    'admin.nav.geoContent': "GEO Content",
    'admin.nav.finance': "Finance",
    'admin.nav.payments': "Payments",
    'admin.nav.accounting': "Accounting",
    'admin.nav.invoices': "Sales Records",

    // ── AdminInvoices — detail modal & PDF ───────────────────────────────────
    'admin.inv.detail_title': "Debt Record Detail",
    'admin.inv.close': "Close",
    'admin.inv.type_sale': "Sale",
    'admin.inv.type_refund': "Refund",
    'admin.inv.type_proforma': "Proforma",
    'admin.inv.field_invoice_no': "Record No",
    'admin.inv.field_type': "Type",
    'admin.inv.field_status': "Status",
    'admin.inv.field_customer': "Customer",
    'admin.inv.field_tc': "T.C. No",
    'admin.inv.field_issued_at': "Issue Date",
    'admin.inv.field_payment_method': "Payment Method",
    'admin.inv.lines_title': "Line Items",
    'admin.inv.col_desc': "Description",
    'admin.inv.col_qty': "Qty",
    'admin.inv.col_unit_price': "Unit Price",
    'admin.inv.col_vat_rate': "VAT %",
    'admin.inv.col_vat_amount': "VAT",
    'admin.inv.col_line_total': "Total",
    'admin.inv.subtotal': "Subtotal",
    'admin.inv.vat_total': "VAT Total",
    'admin.inv.grand_total': "Grand Total",
    'admin.inv.paid_amount': "Paid",
    'admin.inv.remaining_amount': "Remaining",
    'admin.inv.payments_title': "Payments",
    'admin.inv.installments_title': "Installments",
    'admin.inv.no_lines': "No line items.",
    'admin.inv.no_payments': "No payments recorded.",
    'admin.inv.no_installments': "No installments.",
    'admin.inv.pdf_download': "Download PDF",
    'admin.inv.pdf_error': "PDF could not be downloaded.",
    'admin.inv.loading': "Loading record...",
    'admin.inv.load_error': "Record could not be loaded.",
    'admin.inv.detail_btn': "Detail",
    'admin.inv.pay_date': "Date",
    'admin.inv.pay_amount': "Amount",
    'admin.inv.pay_method': "Method",
    'admin.inv.inst_no': "#",
    'admin.inv.inst_due': "Due Date",
    'admin.inv.inst_amount': "Amount",
    'admin.inv.inst_status': "Status",

    // ── AdminMuhasebeListesi — Student Accounting List (Faz L) ───────────────
    'admin.nav.muhasebeListesi': "Student Accounting List",
    'admin.nav.parasutMapping': "Parasut Mapping",
    'admin.muhasebeListesi.page_title': "Student Accounting List",
    'admin.muhasebeListesi.page_subtitle': "Filterable list of installments (debts) and their matching payments.",
    'admin.muhasebeListesi.filter_date_type': "Date Type",
    'admin.muhasebeListesi.date_type_odeme': "By Payment Date",
    'admin.muhasebeListesi.date_type_vade': "By Due Date",
    'admin.muhasebeListesi.date_type_kayit': "By Registration Date",
    'admin.muhasebeListesi.filter_senet_durumu': "Note Status",
    'admin.muhasebeListesi.senet_durumu_tum': "All",
    'admin.muhasebeListesi.senet_durumu_borclu': "Debtor",
    'admin.muhasebeListesi.senet_durumu_odenen': "Paid",
    'admin.muhasebeListesi.filter_payment_method': "Payment Method",
    'admin.muhasebeListesi.payment_method_hepsi': "All",
    'admin.muhasebeListesi.payment_method_cash': "Cash",
    'admin.muhasebeListesi.payment_method_credit_card': "Credit Card",
    'admin.muhasebeListesi.payment_method_bank_transfer': "Bank Transfer",
    'admin.muhasebeListesi.filter_only_moka': "Moka payments only",
    'admin.muhasebeListesi.filter_only_legal': "Under legal follow-up only",
    'admin.muhasebeListesi.filter_course': "Course",
    'admin.muhasebeListesi.filter_certificate': "Certificate",
    'admin.muhasebeListesi.filter_danisman': "Advisor",
    'admin.muhasebeListesi.filter_bank': "Bank",
    'admin.muhasebeListesi.filter_muhasebe_tipi': "Accounting Type",
    'admin.muhasebeListesi.filter_all': "All",
    'admin.muhasebeListesi.filter_date_range': "Start / End Date",
    'admin.muhasebeListesi.btn_list': "List",
    'admin.muhasebeListesi.btn_export': "Export to Excel",
    'admin.muhasebeListesi.summary_total_debt': "Total Debt",
    'admin.muhasebeListesi.summary_total_paid': "Total Paid",
    'admin.muhasebeListesi.summary_total_transferred': "Total Paid (Transferred)",
    'admin.muhasebeListesi.summary_remaining': "Remaining",
    'admin.muhasebeListesi.summary_total_vat': "Total VAT",
    'admin.muhasebeListesi.summary_vat_free_transferred': "VAT-Free Transferred",
    'admin.muhasebeListesi.col_student': "Student",
    'admin.muhasebeListesi.col_debt_date': "Debt Date",
    'admin.muhasebeListesi.col_debt_amount': "Debt Amount",
    'admin.muhasebeListesi.col_payment_date': "Payment Date",
    'admin.muhasebeListesi.col_payment_amount': "Payment Amount",
    'admin.muhasebeListesi.col_payment_transferred': "Payment Amount (Transferred)",
    'admin.muhasebeListesi.col_vat_amount': "VAT Amount",
    'admin.muhasebeListesi.col_bank': "Bank",
    'admin.muhasebeListesi.col_senet_no': "Note No",
    'admin.muhasebeListesi.col_lawyer': "Lawyer",
    'admin.muhasebeListesi.empty_title': "No records found",
    'admin.muhasebeListesi.empty_desc': "No rows match the selected filters.",
    'admin.muhasebeListesi.empty_desc_no_range': "Select a date range and click List.",
    'admin.muhasebeListesi.warning_date_range_required': "Select a start and end date to list or export.",

    'admin.nav.definitions': "Definitions",
    'admin.nav.examGroups': "Exam Groups",
    'admin.nav.certPrint': "Cert Print & Ship",
    'admin.nav.dijitalRozet': "Digital Badge",
    'admin.nav.leads': "Leads",
    'admin.nav.dataManagement': "Data Management",
    'admin.nav.personelYonetimi': "Personnel Management",
    'admin.nav.danismanYonetimi': "Consultant Management",
    'admin.nav.satisOnay': "Profile Approvals",
    'admin.nav.ciroDashboard': "Revenue Dashboard",
    'admin.nav.satisRaporlama': "Sales Reporting",
    'admin.nav.primKurallari': "Commission Rules",
    'admin.nav.izinTalepleri': "Leave Requests",
    'admin.nav.campaigns': "Campaigns",
    'admin.nav.reports': "Reports",
    'admin.nav.system': "System & Security",
    'admin.nav.messages': "Messages",
    'admin.nav.announcements': "Announcements",
    'admin.nav.logs': "Activity Logs",
    'admin.nav.activeSessions': "Active Sessions",
    'admin.nav.anomalies': "Suspicious Activity",
    'admin.nav.bugReports': "Bug Reports",
    'admin.nav.danismanCockpit': "Advisor Panel",
    'admin.nav.danismanAnaSayfa': "Advisor Home",
    'admin.nav.danismanIstatistik': "Consultant Statistics",
    'admin.nav.mesaiMolaTakip': "Shift & Break Tracking",
    'admin.nav.notificationJobs': "Notification Jobs",
    'admin.nav.appVersion': "App Version",

    // ── Admin notification scheduled jobs (on/off) ──────────────────────────
    'admin.notifJobs.title': "Notification Jobs",
    'admin.notifJobs.subtitle': "Turn scheduled in-app notification jobs on or off.",
    'admin.notifJobs.loadError': "Could not load notification jobs.",
    'admin.notifJobs.retry': "Retry",
    'admin.notifJobs.emptyTitle': "No configurable jobs yet",
    'admin.notifJobs.emptyDesc': "Jobs with a real on/off flag will appear here as they're added.",
    'admin.notifJobs.lastRun': "Last run",
    'admin.notifJobs.lastRunNever': "Never run yet",
    'admin.notifJobs.notifiedOf': "{{notified}} notified / {{checked}} checked",
    'admin.notifJobs.statusDisabled': "Skipped (disabled)",
    'admin.notifJobs.toggleOn': "Enabled",
    'admin.notifJobs.toggleOff': "Disabled",
    'admin.notifJobs.toggleSuccess': "Job updated",
    'admin.notifJobs.toggleError': "Could not update job",
    'admin.notifJobs.infoNote': "Only jobs with a real backend flag are listed here. Other scheduled reminders (e.g. study inactivity, interview availability) always run and can't be toggled yet.",

    // ── Admin mobile app version management (force-update gate) ─────────────
    'admin.appVersion.title': "Mobile App Version Management",
    'admin.appVersion.subtitle': "Manage the minimum supported version and the latest version per platform.",
    'admin.appVersion.loadError': "Could not load app version settings.",
    'admin.appVersion.retry': "Retry",
    'admin.appVersion.tabAndroid': "Android",
    'admin.appVersion.tabIos': "iOS",
    'admin.appVersion.sectionVersions': "Versions",
    'admin.appVersion.sectionVersionsDesc': "Semver format (e.g. 1.2.0 or 1.2).",
    'admin.appVersion.fieldMinVersion': "Minimum Supported Version",
    'admin.appVersion.fieldMinVersionHelp': "Users on a version below this are hard-locked out of the app until they update.",
    'admin.appVersion.fieldLatestVersion': "Latest Version",
    'admin.appVersion.fieldLatestVersionHelp': "Users on a version below this see a dismissible update reminder.",
    'admin.appVersion.fieldStoreUrl': "Store URL",
    'admin.appVersion.fieldStoreUrlPlaceholder': "https://play.google.com/store/apps/details?id=...",
    'admin.appVersion.fieldStoreUrlHint': "Leave blank, or use an https:// link to one of: play.google.com, apps.apple.com, itunes.apple.com, kariyeronline.tr.",
    'admin.appVersion.errorInvalidSemver': "Must be a valid version number (e.g. 1.2.0)",
    'admin.appVersion.errorInvalidStoreUrl': "Store URL must be an https:// link to play.google.com, apps.apple.com, itunes.apple.com or kariyeronline.tr — this link opens on a screen users cannot close.",
    'admin.appVersion.errorMinGtLatest': "Minimum supported version cannot be greater than the latest version",
    'admin.appVersion.warningLockTitle': "Warning — this will lock out users",
    'admin.appVersion.warningLockBody': "Changing the minimum supported version immediately hard-locks every user running a version below {{version}} out of the app until they update.",
    'admin.appVersion.sectionOverride': "Optional Update Message Override",
    'admin.appVersion.sectionOverrideDesc': "Leave blank to use the app's default localized update message.",
    'admin.appVersion.fieldTitle': "Title",
    'admin.appVersion.fieldMessage': "Message",
    'admin.appVersion.fieldReleaseNotes': "Release Notes",
    'admin.appVersion.confirmTitle': "Confirm minimum version change",
    'admin.appVersion.confirmBody': "Type the new minimum version below to confirm you understand this will lock every user running a version below {{version}} out of the app.",
    'admin.appVersion.confirmInputLabel': "Type the version to confirm",
    'admin.appVersion.confirmMismatch': "Doesn't match — type exactly \"{{version}}\"",
    'admin.appVersion.confirmButton': "I understand, lock this version",
    'admin.appVersion.cancel': "Cancel",
    'admin.appVersion.saveButton': "Save",
    'admin.appVersion.saving': "Saving...",
    'admin.appVersion.saveSuccess': "App version settings updated.",
    'admin.appVersion.saveError': "Could not update app version settings.",
    'admin.appVersion.lastUpdated': "Last updated",
    'admin.appVersion.lastUpdatedNever': "Never updated yet (using safe defaults).",
    'admin.appVersion.sectionDistribution': "Version Distribution",
    'admin.appVersion.sectionDistributionDesc': "Client versions actually observed over the last {{days}} days.",
    'admin.appVersion.window7d': "7 days",
    'admin.appVersion.window30d': "30 days",
    'admin.appVersion.windowRange': "{{from}} – {{to}}",
    'admin.appVersion.totalRequests': "{{count}} requests",
    'admin.appVersion.daysSeenLabel': "Seen {{days}}/{{window}} days",
    'admin.appVersion.otherVersionsLabel': "Other ({{count}} versions)",
    'admin.appVersion.configuredBadge': "Configured",
    'admin.appVersion.observedMaxBadge': "Suggested",
    'admin.appVersion.distributionLoadError': "Could not load version distribution.",
    'admin.appVersion.distributionEmptyTitle': "No data in this window yet",
    'admin.appVersion.distributionEmptyBody': "No client version data was recorded in the selected date range.",
    'admin.appVersion.staleConfirmedTitle': "Configured latest version is out of date",
    'admin.appVersion.staleConfirmedBody': "The highest observed version is {{observedVersion}} (%{{percent}}, {{days}} days, {{requests}} requests) — the configured latest version is {{configuredVersion}}.",
    'admin.appVersion.staleConfirmedFillCta': "Fill suggested version",
    'admin.appVersion.staleFillPendingHint': "Filled — remember to click Save",
    'admin.appVersion.staleHintBody': "A small amount of usage was seen on {{observedVersion}} (%{{percent}}, {{days}} days) — likely a single test device or early-access install. No action needed for now.",
    'admin.appVersion.dataQualityCapHit': "On {{days}} day(s) in this window the daily version-variety limit ({{max}}) was reached — the distribution for those days may be incomplete.",
    'admin.appVersion.dataQualityCapHitDates': "Affected days: {{dates}}",

    // ── Cabin Module — M5.2 ──────────────────────────────────────────────────
    'cabin.nav.aria_label': "Cabin module navigation",
    'cabin.nav.toggle_label': "Toggle menu",
    'cabin.nav.dashboard': "Dashboard",
    'cabin.nav.modules': "Modules",
    'cabin.nav.roadmap': "Roadmap",
    'cabin.nav.progress': "Progress",
    'cabin.nav.achievements': "Achievements",
    'cabin.nav.leaderboard': "Leaderboard",
    'cabin.nav.video_lessons': "Video Lessons",
    'cabin.nav.interview': "Interview",
    'cabin.nav.spinWheel': "Daily Spin",
    'cabin.nav.more': "More",
    'cabin.nav.back_to_academy': "Back to Kariyer Online",

    // ── Lesson page (cabin mode) ──────────────────────────────────────────────
    'cabin.lesson.description_title': "Lesson Description",
    'cabin.lesson.description_empty': "No description added for this lesson yet.",

    // ── Cabin Udemy layout ────────────────────────────────────────────────────
    'cabin.lesson.tab_overview': "Overview",
    'cabin.lesson.tab_notes': "Notes",
    'cabin.lesson.progress_label': "{done}/{total} completed",
    'cabin.lesson.sidebar_toggle_open': "Open course content",
    'cabin.lesson.sidebar_toggle_close': "Close course content",
    'cabin.lesson.course_content': "Course Content",
    'cabin.lesson.duration_min': "{n} min",
    'cabin.lesson.students': "{n} students",
    'cabin.lesson.notes_title': "My Notes",
    'cabin.lesson.notes_empty': "No notes yet for this lesson.",
    'cabin.lesson.notes_placeholder': "Write your note...",
    'cabin.lesson.notes_save': "Save",
    'cabin.lesson.notes_delete': "Delete",
    'cabin.lesson.notes_loading': "Loading notes...",
    'cabin.lesson.notes_error': "Could not load notes.",
    'cabin.lesson.back_to_course': "Back to course",

    // ── Video Lessons page ────────────────────────────────────────────────────
    'cabin.video_lessons.title': "Video Lessons",
    'cabin.video_lessons.subtitle': "Cabin Crew Training",
    'cabin.video_lessons.done_label': "completed",
    'cabin.video_lessons.completed_label': "Completed",
    'cabin.video_lessons.empty.title': "No lessons added yet",
    'cabin.video_lessons.empty.sub': "Lessons will be published soon.",
    'cabin.video_lessons.empty.cta': "Go to Modules",

    // ── Notifications ─────────────────────────────────────────────────────────
    'cabin.notifications.title': "Notifications",
    'cabin.notifications.panel_label': "Notifications panel",
    'cabin.notifications.bell_label': "Notifications",
    'cabin.notifications.bell_label_count': "Notifications, {count} unread",
    'cabin.notifications.sr_unread': "unread notifications",
    'cabin.notifications.mark_all_read': "Mark all as read",
    'cabin.notifications.unread_dot': "Unread",
    'cabin.notifications.empty': "No notifications yet",
    'cabin.notifications.error': "Notifications could not be loaded",
    'cabin.notifications.markAllError': "Could not mark all as read",
    'cabin.notifications.bucket.today': "Today",
    'cabin.notifications.bucket.this_week': "This Week",
    'cabin.notifications.bucket.older': "Earlier",
    'cabin.notifications.empty.title': "No notifications yet",
    'cabin.notifications.empty.sub': "Lesson reminders, interview results, and announcements will show up here.",
    'cabin.notifications.empty_filtered.title': "You're all caught up",
    'cabin.notifications.empty_filtered.sub': "New notifications will appear here.",
    'cabin.notifications.filter.all': "All",
    'cabin.notifications.filter.unread': "Unread",
    'cabin.notifications.filter.aria_label': "Notification filter",
    'cabin.notifications.mark_read_one': "Mark as read",
    'cabin.notifications.view_all': "View All",
    'cabin.notifications.page.title': "Notifications",
    'cabin.notifications.page.subtitle': "Your lesson reminders, interview results, and announcements.",
    'cabin.notifications.sr_live_unread': "{count} unread notifications",
    'cabin.notifications.sr_live_all_read': "No unread notifications",
    'cabin.notifications.list_aria_label': "Notification list",
    'cabin.notifications.time.just_now': "just now",

    'cabin.spinWheel.title': "Daily Spin",
    'cabin.spinWheel.subtitle': "Spin once a day, grab your reward!",
    'cabin.spinWheel.spin': "Spin",
    'cabin.spinWheel.spinning': "Spinning...",
    'cabin.spinWheel.alreadyUsed': "Already used today!",
    'cabin.spinWheel.comeBack': "Come back when the timer runs out.",
    'cabin.spinWheel.congrats': "Congratulations!",
    'cabin.spinWheel.youWon': "You won a reward! Keep it up.",
    'cabin.spinWheel.awesome': "Awesome!",

    'cabin.dashboard.greeting': "Hello, {name}!",
    'cabin.dashboard.subtitle': "Cabin Crew Training",
    'cabin.dashboard.current_level': "Current Level",
    'cabin.dashboard.target_level': "Target",
    'cabin.dashboard.progress_label': "{completed}/{total} lessons done",
    'cabin.dashboard.progress_pct': "{pct}% to Cabin Crew",
    'cabin.dashboard.footer_quote': "Every lesson gets you one step closer to the cabin door.",
    'cabin.dashboard.modules_title': "Training Modules",
    'cabin.dashboard.view_all': "View All",
    'cabin.dashboard.show_more': "Show {count} more modules",
    'cabin.dashboard.show_less': "Show less",
    'cabin.dashboard.roadmap_banner_title': "Learning Roadmap",
    'cabin.dashboard.roadmap_banner_sub': "3 Stages: Beginner → Intermediate → Advanced · 16 Modules",
    'cabin.dashboard.roadmap_banner_cta': "View Roadmap",

    'cabin.stats.total_miles': "Total Miles",
    'cabin.stats.streak': "Day Streak",
    'cabin.stats.completed': "Completed",
    'cabin.stats.level': "Level",
    'cabin.stats.streak_days': "{count}-day streak",

    'cabin.continue.title': "Continue Where You Left Off",
    'cabin.continue.remaining': "~{min} min left",

    'cabin.module.cta.start': "Start",
    'cabin.module.cta.continue': "Continue",
    'cabin.module.cta.retry': "Retry",
    'cabin.module.cta.locked': "Locked",
    'cabin.module.locked_tooltip': "Unlocks after {prerequisite}",
    'cabin.module.hours': "{time} hours",
    'cabin.module.miles_reward': "{miles} Miles",

    'cabin.daily_tasks.title': "Daily Tasks",
    'cabin.daily_tasks.coming_soon': "Coming soon",

    'cabin.leaderboard.title': "Weekly Leaders",
    'cabin.leaderboard.you': "You",
    'cabin.leaderboard.rank': "Rank: {rank}",

    'cabin.activity.title': "Recent Activity",

    'cabin.roadmap.title': "Learning Roadmap",
    'cabin.roadmap.subtitle': "Step by step to cabin crew",
    'cabin.roadmap.stage_counter': "Stage {current}/{total}",
    'cabin.roadmap.stage_required': "{count} modules · {pct}% completion required",
    'cabin.roadmap.locked_tooltip': "Unlocks after completing {prerequisite}",
    'cabin.roadmap.current_badge': "You Are Here",
    'cabin.roadmap.module_time': "Estimated: {time}",
    'cabin.roadmap.module_miles': "{miles} Miles reward",
    'cabin.roadmap.module_units': "{units} units · {lessons} lessons",
    'cabin.roadmap.detail_sheet_title': "Module Detail",
    'cabin.roadmap.empty.title': "Loading roadmap...",
    'cabin.roadmap.error.title': "Couldn't load roadmap",
    'cabin.roadmap.error.sub': "Check your connection",
    'cabin.roadmap.theme.foundations': "Foundations",
    'cabin.roadmap.theme.interview': "Interview Prep",
    'cabin.roadmap.theme.sector': "Sector & Expertise",
    'cabin.roadmap.theme.foundations.sub': "Modules 1-4 · Core concepts",
    'cabin.roadmap.theme.interview.sub': "Modules 5-10 · Preparation skills",
    'cabin.roadmap.theme.sector.sub': "Modules 11-16 · Industry expertise",
    'cabin.roadmap.theme_progress': "{pct}% completed",
    'cabin.roadmap.modules_count': "{completed}/{total} modules",
    'cabin.roadmap.hero.total_miles': "Total Miles",
    'cabin.roadmap.hero.overall_progress': "Overall Progress",
    'cabin.roadmap.hero.stage_chip_pct': "{pct}%",

    'cabin.progress.title': "My Progress",
    'cabin.progress.subtitle': "Track your cabin crew training progress",
    'cabin.progress.level_card.current': "Current Level",
    'cabin.progress.level_card.next': "Next Level",
    'cabin.progress.level_card.miles_to_next': "Earn {miles} more Miles",
    'cabin.progress.level_card.max': "You've reached the max level!",
    'cabin.progress.next_level': "Next Level",
    'cabin.progress.next_level_miles': "{miles} Miles to next level",
    'cabin.progress.tabs.aria_label': "Progress tabs",
    'cabin.progress.tabs.overview': "Overview",
    'cabin.progress.tabs.modules': "Modules",
    'cabin.progress.tabs.skills': "Skills",
    'cabin.progress.tabs.roadmap': "Roadmap",
    'cabin.progress.stats.total_miles': "Total Miles",
    'cabin.progress.stats.streak': "Daily Streak",
    'cabin.progress.stats.completed': "Lessons Done",
    'cabin.progress.stats.time': "Total Time",
    'cabin.progress.weekly.title': "Weekly Lessons",
    'cabin.progress.weekly.aria_label': "Weekly lessons chart",
    'cabin.progress.weekly.tooltip_label': "Lessons",
    'cabin.progress.module.completed_label': "Completed",
    'cabin.progress.module.in_progress_label': "In Progress",
    'cabin.progress.attempt.score': "{score}% score",
    'cabin.progress.attempt.date': "{date}",
    'cabin.progress.skills.title': "Skill Areas",
    'cabin.progress.achievements.title': "My Achievements",
    'cabin.progress.achievements.coming_soon': "Coming with M5.4",
    'cabin.progress.export.title': "Download PDF Report",
    'cabin.progress.export.coming_soon': "Coming soon",
    'cabin.progress.empty.title': "No completed lessons yet",
    'cabin.progress.empty.sub': "Pick a lesson from Dashboard to see your progress",
    'cabin.progress.empty.cta': "Go to Dashboard",
    'cabin.progress.tab.overview': "Overview",
    'cabin.progress.tab.modules': "Modules",
    'cabin.progress.tab.themes': "Themes",
    'cabin.progress.total_completed': "{completed}/{total} lessons completed",
    'cabin.progress.weekly_activity': "Weekly Activity",
    'cabin.progress.today_summary': "Today's Summary",
    'cabin.progress.top_modules': "Most Progress",
    'cabin.progress.empty': "No lessons completed yet. Start your first module!",
    'cabin.progress.completion_ring.label': "Overall Completion",
    'cabin.progress.today.xp': "XP Today",
    'cabin.progress.today.miles': "Miles Today",
    'cabin.progress.today.lessons': "Lessons Today",
    'cabin.progress.theme.foundations': "Foundations",
    'cabin.progress.theme.interview': "Interview Prep",
    'cabin.progress.theme.sector': "Sector & Expertise",
    'cabin.progress.theme.modules_done': "{done}/{total} modules",
    'cabin.progress.last_activity': "Last active {time}",
    'cabin.progress.streak.current': "Current streak",
    'cabin.progress.streak.longest': "Longest streak",
    'cabin.progress.badges.unlocked': "{count} badges unlocked",
    'cabin.progress.go_to_module': "Continue",

    'cabin.empty.title': "Start your cabin journey!",
    'cabin.empty.sub': "Pick your first lesson and earn your first mile.",
    'cabin.empty.cta': "Start First Lesson",

    'cabin.error.title': "Connection lost",
    'cabin.error.sub': "Couldn't load data. Try again.",
    'cabin.error.cta': "Try Again",

    'cabin.milestone.beginner': "Beginner",
    'cabin.milestone.student': "Student",
    'cabin.milestone.practitioner': "Practitioner",
    'cabin.milestone.master': "Master",
    'cabin.milestone.expert': "Expert",

    'cabin.level.beginner': "Beginner",
    'cabin.level.student': "Student",
    'cabin.level.candidate': "Candidate",
    'cabin.level.intermediate': "Intermediate",
    'cabin.level.advanced': "Advanced",
    'cabin.level.expert': "Expert",
    'cabin.level.master': "Master",
    'cabin.level.professional': "Professional",

    'cabin.a11y.total_miles': "{count} miles earned",
    'cabin.a11y.streak': "{days}-day streak active",
    'cabin.a11y.module_card': "Module {order}, {title}, {progress}% complete",
    'cabin.a11y.module_card_locked': "Module {order}, {title}, unlocks after {prerequisite}",
    'cabin.a11y.level_progress': "Level progress: {pct}%",
    'cabin.a11y.sidebar_expanded': "Menu expanded",
    'cabin.a11y.sidebar_collapsed': "Menu collapsed",

    // ── M5.3 Lesson Player + Exercises ───────────────────────────────────────
    'lesson.loading': "Loading lesson...",
    'lesson.error_title': "Lesson could not be loaded",
    'lesson.error_desc': "Check your connection and try again.",
    'lesson.exit': "Exit lesson",
    'lesson.start': "Start",
    'lesson.resume_notice': "Resuming where you left off",
    'lesson.key_takeaways': "In this lesson you will learn:",
    'lesson.intro_image_alt': "Lesson image",
    'lesson.locked': "Locked",
    'lesson.minutes': "min",
    'lesson.coming_soon_title': "Coming Soon",
    'lesson.coming_soon_desc': "This exercise type will be added in the M5.5 update.",
    'lesson.skip_phase': "Skip and Continue",
    'lesson.exitConfirm.title': "Exit Lesson?",
    'lesson.exitConfirm.desc': "Your progress has been saved. You can continue from where you left off.",
    'lesson.exitConfirm.cancel': "Continue",
    'lesson.exitConfirm.exit': "Exit",

    'exercise.question_counter': "Question {current} / {total}",
    'exercise.max_xp': "Max. {xp} XP",
    'exercise.submit': "Check",
    'exercise.checking': "Checking...",
    'exercise.next': "Next Question",
    'exercise.finish': "Finish",
    'exercise.finish_lesson': "Finish Lesson",
    'exercise.correct': "Correct!",
    'exercise.wrong': "Wrong!",
    'exercise.correct_answer': "Correct answer:",
    'exercise.correct_translation': "Correct translation:",
    'exercise.correct_order': "Correct order:",
    'exercise.hint': "Hint",
    'exercise.hint_used': "Hint used — score may be lower.",
    'exercise.fill_placeholder': "Type your answer...",
    'exercise.fill_input_label': "Answer input",
    'exercise.blank_label': "Blank",
    'exercise.word_pool_label': "Word pool",
    'exercise.match_instruction': "Click matching items from both columns",
    'exercise.match_aria_label': "Matching exercise",
    'exercise.matched': "matched",
    'exercise.all_matched': "All matches complete!",
    'exercise.drag_instruction': "Drag words to the correct order",
    'exercise.word_order_area': "Word ordering area",
    'exercise.reset': "Reset",
    'exercise.card_front': "Card front",
    'exercise.card_back': "Card back",
    'exercise.tap_to_flip': "Click to flip",
    'exercise.flip': "Flip",
    'exercise.prev_card': "Previous card",
    'exercise.next_card': "Next card",
    'exercise.play_audio': "Play audio",
    'exercise.image_question_alt': "Question image",
    'exercise.card_image': "Card image",
    'exercise.card_image_upload': "Upload image",
    'exercise.card_image_remove': "Remove image",
    'exercise.known_btn': "I Know",
    'exercise.unknown_btn': "I Don't Know",
    'exercise.known': "Known",
    'exercise.unknown': "Unknown",
    'exercise.flashcard_done': "Card deck complete!",
    'exercise.flashcard_known': "Known",
    'exercise.flashcard_total': "Total",
    'exercise.progress_label': "Card progress",
    'exercise.language_tr': "Turkish",
    'exercise.language_en': "English",
    'exercise.translate_to': "Translate to {lang}",
    'exercise.translation_placeholder': "Write your translation...",
    'exercise.translation_input_label': "Translation input",
    'exercise.enter_to_submit': "Enter: submit, Shift+Enter: new line",
    'exercise.continue': "Continue",
    'teaching.continue': "Continue",

    'complete.title': "Lesson Complete!",
    'complete.score_label': "Success Score",
    'complete.xp_label': "Experience Points",
    'complete.miles_label': "Aviation Miles",
    'complete.badges_unlocked': "New Badges!",
    'complete.continue': "Continue to Next Lesson",
    'complete.next_lesson': "Next Lesson",
    'complete.backToModule': "Back to Module",
    'complete.failTitle': "Not this time",
    'complete.failDesc': "You need at least %{{threshold}} to pass. Try again!",
    'complete.retry': "Try Again",
    'complete.module_finished_title': "Module Complete!",
    'complete.module_finished_desc': "You've finished all lessons in this module.",

    'module.units': "units",
    'module.lessons': "lessons",
    'module.hours': "hours",
    'module.miles': "Miles",
    'module.no_units_title': "No units yet",
    'module.no_units_desc': "Units and lessons for this module will be added soon.",
    'unit.lessons_done': "lessons done",
    'unit.no_lessons': "No lessons in this unit yet.",
    'common.go_back': "Go Back",
    'common.close': "Close",
    'common.loading': "Loading...",

    // ── M5.4 Gamification ────────────────────────────────────────────────────
    'cabin.gamification.streak.label': "Daily Streak",
    'cabin.gamification.streak.tooltip': "{count}-day streak active, last activity {time} ago",
    'cabin.gamification.streak.fireModeLabel': "Fire Mode",
    'cabin.gamification.streak.aria_updated': "Streak updated: {days} days",
    'cabin.gamification.streak.tooltip_active': "{days}-day streak active",
    'cabin.gamification.streak.tooltip_last': "Last activity: {when}",
    'cabin.gamification.streak.tooltip_longest': "Longest: {days} days",
    'cabin.gamification.streak.last_activity_now': "Just now",
    'cabin.gamification.streak.last_activity_hours': "{hours} hours ago",
    'cabin.gamification.dailyTasks.title': "Daily Tasks",
    'cabin.gamification.dailyTasks.allCompleted': "All today's tasks completed!",
    'cabin.gamification.dailyTasks.claim': "Claim Reward",
    'cabin.gamification.dailyTasks.claimed': "Claimed",
    'cabin.gamification.dailyTasks.progress': "{current}/{total} tasks",
    'cabin.gamification.leaderboard.title': "Leaderboard",
    'cabin.gamification.leaderboard.weekly': "Weekly",
    'cabin.gamification.leaderboard.monthly': "Monthly",
    'cabin.gamification.leaderboard.alltime': "All Time",
    'cabin.gamification.leaderboard.yourRank': "Your rank: #{rank}",
    'cabin.gamification.leaderboard.empty': "No rankings yet",
    'cabin.gamification.leaderboard.my_rank_label': "Your Ranking",
    'cabin.gamification.leaderboard.miles_unit': "Miles",
    'cabin.gamification.leaderboard.level': "Level",
    'cabin.gamification.leaderboard.you': "You",
    'cabin.gamification.leaderboard.error': "Could not load rankings",
    'cabin.gamification.leaderboard.top_n': "Showing top {n} users",
    'cabin.gamification.leaderboard.period_tabs_aria': "Period selection",
    'cabin.gamification.achievements.title': "Achievements",
    'cabin.gamification.achievements.filterAll': "All",
    'cabin.gamification.achievements.filterUnlocked': "Unlocked",
    'cabin.gamification.achievements.filterLocked': "Locked",
    'cabin.gamification.achievements.unlockedOn': "Earned on {date}",
    'cabin.gamification.achievements.progress': "{current}/{threshold}",
    'cabin.gamification.achievements.allUnlocked': "Congratulations! You unlocked all badges!",
    'cabin.gamification.achievements.empty.title': "No badges yet",
    'cabin.gamification.achievements.empty.sub': "Complete your first lesson to unlock your first badge!",
    'cabin.modules.empty.title': "No modules added yet",
    'cabin.modules.empty.sub': "Modules will be published soon.",
    'cabin.gamification.badgeUnlock.congrats': "Congratulations!",
    'cabin.gamification.badgeUnlock.newBadge': "You earned a new badge!",
    'cabin.gamification.badgeUnlock.continue': "Awesome!",
    'cabin.sidebar.leaderboard': "Leaderboard",
    'cabin.sidebar.achievements': "Achievements",

    // ── M5.6 Interview System ─────────────────────────────────────────────────
    'cabin.interview.hub.title': "Interview Practice",
    'cabin.interview.hub.subtitle': "Practice cabin crew interviews with AI assistance",
    'cabin.interview.hub.difficulty': "Difficulty",
    'cabin.interview.hub.language': "Language",
    'cabin.interview.hub.category': "Category",
    'cabin.interview.hub.select_mode': "Select Mode",
    'cabin.interview.hub.text_mode_title': "Text Interview",
    'cabin.interview.hub.text_mode_desc': "Written interview practice, AI-powered conversation",
    'cabin.interview.hub.voice_mode_title': "Voice Interview",
    'cabin.interview.hub.voice_mode_desc': "Voice-based simulation, realistic practice",
    'cabin.interview.hub.video_mode_title': "Video Interview",
    'cabin.interview.hub.video_mode_desc': "Self-record video + AI evaluation",
    'cabin.interview.hub.video_badge': "KVKK",
    'cabin.interview.hub.recent_sessions': "Recent Sessions",
    'cabin.interview.hub.view_all': "View All",
    'cabin.interview.hub.quick_access': "Preparation",
    'cabin.interview.hub.cv_profile_title': "My CV Profile",
    'cabin.interview.hub.cv_profile_desc': "Complete your profile for tailored interview questions",
    'cabin.interview.hub.readiness_title': "My Readiness Report",
    'cabin.interview.hub.readiness_desc': "Review your interview readiness score and tips",
    'cabin.interview.hub.last_session': "LAST SESSION",
    'cabin.interview.hub.difficulty_short': "Difficulty",
    'cabin.interview.hub.pending_eval': "Awaiting evaluation",
    'cabin.interview.hub.progress_summary': "MY PROGRESS",
    'cabin.interview.hub.stat_total': "Total",
    'cabin.interview.hub.stat_scored': "Scored",
    'cabin.interview.hub.stat_pending': "Pending",
    'cabin.interview.hub.avg_score': "Avg. score",
    'cabin.interview.session.status.scored': "Scored",
    'cabin.interview.session.status.pending_review': "Pending Review",
    'cabin.interview.hub.badge.pending': "Pending Review",
    'cabin.interview.hub.badge.in_progress': "In Progress",
    'cabin.interview.hub.pending_hint': "Being reviewed by instructor",
    'cabin.interview.hub.in_progress_hint': "Session not completed",
    'cabin.interview.hub.result.positive': "Positive Result",
    'cabin.interview.hub.result.reserve': "Reserve Result",
    'cabin.interview.hub.result.negative': "Negative Result",
    'cabin.interview.hub.no_session_title': "No interviews yet",
    'cabin.interview.hub.no_session_sub': "Start your first simulation to track readiness",

    'cabin.interview.text.loading': "Starting interview...",
    'cabin.interview.text.back': "Back to hub",
    'cabin.interview.text.turns': "turns",
    'cabin.interview.text.score': "Score",
    'cabin.interview.text.end': "Finish",
    'cabin.interview.text.typing_label': "AI is evaluating...",
    'cabin.interview.text.chat_aria': "Interview chat",
    'cabin.interview.text.textarea_aria': "Your answer",
    'cabin.interview.text.placeholder': "Type your answer...",
    'cabin.interview.text.send': "Send answer",
    'cabin.interview.text.send_hint': "Ctrl+Enter to send",
    'cabin.interview.text.start_error': "Could not start interview",
    'cabin.interview.text.turn_error': "Could not submit answer",

    'cabin.interview.voice.loading': "Preparing voice interview...",
    'cabin.interview.voice.start_error': "Could not start voice interview",
    'cabin.interview.voice.turn_error': "Could not submit recording",
    'cabin.interview.voice.tts_play': "Read question aloud",
    'cabin.interview.voice.record': "Record",
    'cabin.interview.voice.stop': "Stop",
    'cabin.interview.voice.retry': "Try again",
    'cabin.interview.voice.submit': "Submit Answer",
    'cabin.interview.voice.transcribing': "AI is processing...",
    'cabin.interview.voice.perm_denied': "Microphone access is required for this mode",
    'cabin.interview.voice.transcript_label': "Transcript",
    'cabin.interview.voice.feedback_label': "AI Feedback",

    'cabin.interview.video.loading': "Preparing video interview...",
    'cabin.interview.video.start_error': "Could not start video interview",
    'cabin.interview.video.turn_error': "Could not upload recording",
    'cabin.interview.video.kvkk_title': "Video Recording Notice",
    'cabin.interview.video.kvkk_body1': "Your video will be recorded and processed by AI for evaluation purposes.",
    'cabin.interview.video.kvkk_body2': "By proceeding you consent to this recording as per our privacy policy (KVKK).",
    'cabin.interview.video.kvkk_accept': "I Accept",
    'cabin.interview.video.kvkk_decline': "Cancel",
    'cabin.interview.video.record': "Start Recording",
    'cabin.interview.video.stop': "Stop",
    'cabin.interview.video.retry': "Record Again",
    'cabin.interview.video.submit': "Submit & Continue",
    'cabin.interview.video.uploading': "Uploading...",
    'cabin.interview.video.perm_denied': "Camera and microphone access required",
    'cabin.interview.video.question_list': "Questions",
    'cabin.interview.video.done': "Completed",

    'cabin.interview.final.title': "Interview Complete!",
    'cabin.interview.final.strengths': "Strengths",
    'cabin.interview.final.improvements': "Areas to Improve",
    'cabin.interview.final.badges': "Badges Unlocked",
    'cabin.interview.final.retry': "Try Again",
    'cabin.interview.final.history': "History",

    'cabin.interview.history.title': "Interview History",
    'cabin.interview.history.no_sessions': "No sessions yet",
    'cabin.interview.history.start_first': "Start Your First Interview",
    'cabin.interview.history.detail_title': "Session Detail",
    'cabin.interview.history.close': "Close",
    'cabin.interview.history.no_detail': "Detail not available",
    'cabin.interview.history.duration': "Duration",
    'cabin.interview.history.turns': "Turns",
    'cabin.interview.history.status_active': "In Progress",
    'cabin.interview.history.pending_hint': "Awaiting review — your result will appear once the instructor completes the evaluation",

    // ── Interview Simulation (feat/interview-simulation-web) ─────────────────
    'interview.sim.title': "Interview Simulation",
    'interview.sim.hub.subtitle': "Record your answers for instructor evaluation",
    'interview.sim.begin_btn': "Start Simulation",
    'interview.sim.start_error': "Could not start session",
    'interview.sim.submit_error': "Could not submit video",

    'interview.sim.instructions.title': "Before You Begin",
    'interview.sim.instructions.subtitle': "Please read these instructions carefully",
    'interview.sim.instructions.dress': "Wear professional, smart attire",
    'interview.sim.instructions.camera': "Position your camera so your face is clearly visible",
    'interview.sim.instructions.lighting': "Choose a well-lit environment",
    'interview.sim.instructions.quiet': "Find a quiet place with minimal background noise",
    'interview.sim.instructions.natural': "Maintain eye contact and be natural",
    'interview.sim.kvkk_notice': "Your video recording will be stored securely and reviewed only by instructors. By proceeding, you consent to this recording per our privacy policy (KVKK).",
    'interview.sim.question_count': "This simulation includes {{n}} questions",
    'interview.sim.camera_ready': "Camera ready",
    'interview.sim.camera_preview_label': "Camera Preview",
    'interview.sim.no_questions': "Loading questions…",
    'interview.sim.camera_checking': "Checking camera…",
    'interview.sim.camera_denied': "Camera access is required. Please allow camera access in your browser settings and reload the page.",
    'interview.sim.start_interview': "Start Interview",
    'interview.sim.flow_note': "You will answer all 5 questions in a single continuous recording session. Press 'Stop Recording' when you have finished all questions.",
    'interview.sim.question_lang_label': "Question Language",
    'interview.sim.cat.experience': "Experience",
    'interview.sim.cat.motivation': "Motivation",
    'interview.sim.cat.teamwork': "Teamwork",
    'interview.sim.cat.stress': "Stress Management",
    'interview.sim.cat.customer_service': "Customer Service",
    'interview.sim.cat.general': "General",
    'interview.sim.cat.english': "English",
    'interview.sim.cat.safety': "Safety",
    'interview.sim.cat.communication': "Communication",
    'interview.sim.cat.professionalism': "Professionalism",
    'interview.sim.cat.scenario': "Scenario",
    'interview.sim.cat.aviation_specific': "Aviation",
    'interview.sim.cat.situational': "Situational",
    'interview.sim.cat.emergency': "Emergency",
    'interview.sim.cat.introduction': "Introduction",

    // Kariyer Online Shell i18n keys
    'kariyer.lang.tr': "Türkçe",
    'kariyer.lang.en': "English",

    // Interview band labels (standalone — mirrors instructor.reviews.f4.band_* but under kariyer.* namespace)
    'kariyer.band_ccri_very_high': "Very High Readiness",
    'kariyer.band_ccri_high': "High Readiness",
    'kariyer.band_ccri_mid': "Moderate Readiness",
    'kariyer.band_ccri_low': "Low Readiness",
    'kariyer.band_ars_outstanding': "Outstanding",
    'kariyer.band_ars_strong': "Strong Candidate",
    'kariyer.band_ars_adequate': "Adequate Candidate",
    'kariyer.band_ars_developing': "Needs Development",
    'kariyer.band_ars_insufficient': "Insufficient",

    'interview.sim.countdown': "Get ready!",
    'interview.sim.recording': "Recording",
    'interview.sim.finish_recording': "Stop Recording",
    'interview.sim.retake': "Record Again",
    'interview.sim.submit': "Submit Video",

    'interview.sim.preview_title': "Review Your Recording",
    'interview.sim.preview_subtitle': "Watch your recording and submit or record again",

    'interview.sim.submitted_title': "Interview Submitted!",
    'interview.sim.waiting_review': "Your interview has been sent for instructor review. You will see the results here once evaluated.",
    'interview.sim.pending': "Awaiting Review",
    'interview.sim.pending_desc': "Your instructor will review your video and provide a score and feedback. This may take 1-3 business days.",
    'interview.sim.view_history': "View My Sessions",

    'interview.sim.status_pending': "Awaiting Review",
    'interview.sim.status_scored': "Evaluated",

    'interview.sim.result.total': "Total Score",
    'interview.sim.result.criteria_title': "EVALUATION CRITERIA",
    'interview.sim.result.feedback': "Instructor Feedback",
    'interview.sim.result.scored_by': "Evaluated by {{name}}",
    'interview.sim.result.criteria.communication': "Communication",
    'interview.sim.result.criteria.professionalism': "Professionalism",
    'interview.sim.result.criteria.content': "Content",
    'interview.sim.result.criteria.confidence': "Confidence",
    'interview.sim.result.criteria.overall_impression': "Overall Impression",

    // ── EnglishInterviewPlayer keys ───────────────────────────────────────────
    'ei.question_counter': "Question {current} / {total}",
    'ei.textarea_placeholder': "Type your English answer...",
    'ei.word_count': "{count} words",
    'ei.char_count': "{count}/5 chars",
    'ei.submit_btn': "Submit",
    'ei.result.correct': "Well done!",
    'ei.result.incorrect': "Room for improvement",
    'ei.result.grammar': "Grammar",
    'ei.result.relevance': "Relevance",
    'ei.result.tone': "Tone",
    'ei.result.better_version_label': "IMPROVED VERSION",
    'ei.next_question': "Next Question",
    'ei.see_results': "See Results",
    'ei.completion.interview_complete': "Interview Complete!",
    'ei.completion.out_of_lives': "Out of Lives",
    'ei.completion.summary': "Summary",
    'ei.completion.correct_count': "{correct} / {total} correct",
    'ei.completion.finish': "Finish",

    'instructor.reviews.title': "Interview Reviews",
    'instructor.reviews.subtitle': "Review student interview simulations and provide scores",
    'instructor.reviews.stats.pending': "Awaiting Review",
    'instructor.reviews.stats.total': "Total Sessions",
    'instructor.reviews.tab_pending': "Pending",
    'instructor.reviews.tab_all': "All Sessions",
    'instructor.reviews.no_pending': "No pending reviews",
    'instructor.reviews.no_sessions': "No sessions yet",
    'instructor.reviews.inspect': "Review",
    'instructor.reviews.detail_title': "Review Session",
    'instructor.reviews.video_label': "Student interview video",
    'instructor.reviews.video_unavailable': "Video not available yet",
    'instructor.reviews.video_loading': "Loading video...",
    'instructor.reviews.video_error': "Could not load video",
    'interview.sim.result.total_score_label': "Total Score",

    // ── Interview Simulation — Cooldown UI ────────────────────────────────────
    'interview.sim.cooldown.banner_title': "Simulation complete — well done!",
    'interview.sim.cooldown.banner_sub': "Next interview: {{date}}",
    'interview.sim.cooldown.relative': "in {{days}} days",
    'interview.sim.cooldown.relative_today': "Opens today — almost there!",
    'interview.sim.cooldown.btn_caption': "In cooldown period",
    'interview.sim.cooldown.toast': "Your next interview opens on {{date}}",
    'interview.sim.cooldown.a11y_hint': "Not available until {{date}}",

    // ── Interview Simulation — Admin cooldown reset ───────────────────────────
    'interview.sim.admin.reset_cooldown_btn': "Reset Cooldown",
    'interview.sim.admin.reset_cooldown_confirm_title': "Reset Interview Cooldown",
    'interview.sim.admin.reset_cooldown_confirm_desc': "This student will be able to start a new interview immediately.",
    'interview.sim.admin.reset_cooldown_success': "Cooldown reset. The student can start a new interview now.",
    'interview.sim.admin.reset_cooldown_error': "Could not reset cooldown. Please try again.",

    'instructor.reviews.criteria_title': "Scoring Criteria (1–5 each)",
    'instructor.reviews.feedback_label': "Notes & Justification",
    'instructor.reviews.feedback_placeholder': "Write your notes and justification for the score...",
    'instructor.reviews.submit_score': "Score & Submit",
    'instructor.reviews.scoring': "Submitting score...",
    'instructor.reviews.score_success': "Score submitted successfully",
    'instructor.reviews.score_error': "Could not submit score",
    'instructor.reviews.result_required': "Please select a result (Positive / Reserve / Negative) before submitting.",

    // ── Rubric v2 — section headings ─────────────────────────────────────────
    'instructor.reviews.section1_title': "SECTION I — GENERAL IMPRESSION",
    'instructor.reviews.section2_title': "SECTION II — COMMUNICATION & LANGUAGE SKILLS",
    'instructor.reviews.section3_title': "SECTION III — PROFESSIONAL COMPETENCY & ADAPTABILITY",
    'instructor.reviews.section4_title': "Section IV — Overall Evaluation",

    // ── Rubric v2 — criteria labels ──────────────────────────────────────────
    'instructor.reviews.criteria.appearance_hygiene': "Appearance & Hygiene",
    'instructor.reviews.criteria.posture_body_language': "Professional Posture & Body Language",
    'instructor.reviews.criteria.friendliness': "Friendliness / Positive Attitude",
    'instructor.reviews.criteria.first_impression': "First Impression",
    'instructor.reviews.criteria.turkish_communication': "Turkish Communication Skills",
    'instructor.reviews.criteria.english_level': "English Level",
    'instructor.reviews.criteria.voice_diction': "Voice Tone / Diction",
    'instructor.reviews.criteria.empathy_customer_focus': "Empathy & Customer Focus",
    'instructor.reviews.criteria.teamwork': "Teamwork Aptitude",
    'instructor.reviews.criteria.stress_performance': "Performance Under Stress",
    'instructor.reviews.criteria.flexible_hours': "Adaptability to Flexible Hours",
    'instructor.reviews.criteria.passenger_satisfaction': "Approach to Passenger Satisfaction",
    'instructor.reviews.criteria.difficult_passengers': "Handling Difficult Passengers",

    // ── Rubric v2 — result options ───────────────────────────────────────────
    'instructor.reviews.result_label': "Decision",
    'instructor.reviews.result.positive': "Positive",
    'instructor.reviews.result.reserve': "Reserve Candidate",
    'instructor.reviews.result.negative': "Negative",

    // ── Rubric v2 — textarea labels ──────────────────────────────────────────
    'instructor.reviews.strengths_label': "Observed Strengths",
    'instructor.reviews.strengths_placeholder': "Note the candidate's strong points...",
    'instructor.reviews.development_areas_label': "AREAS FOR DEVELOPMENT",
    'instructor.reviews.development_areas_placeholder': "Note areas where the candidate needs improvement...",

    // ── F4 24-criteria scoring — EN ──────────────────────────────────────────
    // Criteria labels
    'instructor.reviews.f4.criteria.kp1': "Eye contact with camera",
    'instructor.reviews.f4.criteria.kp2': "Smile",
    'instructor.reviews.f4.criteria.kp3': "Posture",
    'instructor.reviews.f4.criteria.kp4': "Body language",
    'instructor.reviews.f4.criteria.kp5': "Energy",
    'instructor.reviews.f4.criteria.kp6': "Professional appearance",
    'instructor.reviews.f4.criteria.il1': "Diction",
    'instructor.reviews.f4.criteria.il2': "Voice tone",
    'instructor.reviews.f4.criteria.il3': "Fluency",
    'instructor.reviews.f4.criteria.il4': "Clear answering",
    'instructor.reviews.f4.criteria.il5': "Conciseness",
    'instructor.reviews.f4.criteria.il6': "Persuasiveness",
    'instructor.reviews.f4.criteria.ck1': "Understanding the question",
    'instructor.reviews.f4.criteria.ck2': "Answering with examples",
    'instructor.reviews.f4.criteria.ck3': "Professional awareness",
    'instructor.reviews.f4.criteria.ck4': "Self-confidence",
    'instructor.reviews.f4.criteria.ck5': "Sincerity",
    'instructor.reviews.f4.criteria.ck6': "Non-memorised answers",
    'instructor.reviews.f4.criteria.in1': "Pronunciation",
    'instructor.reviews.f4.criteria.in2': "Vocabulary",
    'instructor.reviews.f4.criteria.in3': "Sentence construction",
    'instructor.reviews.f4.criteria.in4': "English Fluency",
    'instructor.reviews.f4.criteria.in5': "Intelligibility",
    'instructor.reviews.f4.criteria.in6': "English confidence",
    // Group titles
    'instructor.reviews.f4.group_kp_title': "CAMERA PERFORMANCE",
    'instructor.reviews.f4.group_il_title': "COMMUNICATION",
    'instructor.reviews.f4.group_ck_title': "ANSWER QUALITY",
    'instructor.reviews.f4.group_in_title': "ENGLISH",
    // Score badges / notes
    'instructor.reviews.f4.score_excluded_badge': "Not scored",
    'instructor.reviews.f4.score_excluded_hint': "Used in evaluation but not added to CCRI/ARS score",
    'instructor.reviews.f4.score_preview_note': "CCRI & ARS calculated after submit",
    'instructor.reviews.f4.unscored_warning': "{{count}} criteria are still at the default value (5) — please review before submitting.",
    // CV panel
    'instructor.reviews.f4.cv_panel_label': "CV Summary",
    'instructor.reviews.f4.cv_approved_badge': "Approved",
    'instructor.reviews.f4.cv_panel_empty': "CV not yet approved",
    'instructor.reviews.f4.cv_section_kisisel': "PERSONAL",
    'instructor.reviews.f4.cv_section_egitim': "EDUCATION",
    'instructor.reviews.f4.cv_section_deneyim': "EXPERIENCE",
    'instructor.reviews.f4.cv_section_dil': "LANGUAGES",
    'instructor.reviews.f4.cv_section_motivasyon': "MOTIVATION",
    'instructor.reviews.f4.cv_section_no_exp': "No experience",
    // CV score section
    'instructor.reviews.f4.cv_score_section_title': "CV / CAREER SCORE",
    'instructor.reviews.f4.cv_score_label': "Score based on approved CV",
    'instructor.reviews.f4.cv_score_hint': "Review the student's approved CV and assign a score from 0–10",
    'instructor.reviews.f4.cv_score_ars_note': "This score is included in the CCRI calculation (CV/Career area)",
    // Red-flag section
    'instructor.reviews.f4.flag_section_title': "Negative Observations",
    'instructor.reviews.f4.flag_disclaimer': "These observations are recorded as notes and do not directly affect CCRI or ARS scores.",
    'instructor.reviews.f4.flag_note_label': "Additional note (optional)",
    'instructor.reviews.f4.flag_note_placeholder': "Write your observations here...",
    // Red-flag enum labels
    'instructor.reviews.f4.flag.poor_posture': "Poor posture",
    'instructor.reviews.f4.flag.grooming': "Poor grooming",
    'instructor.reviews.f4.flag.unprofessional_appearance': "Unprofessional appearance",
    'instructor.reviews.f4.flag.eye_contact_avoidance': "Avoiding eye contact",
    'instructor.reviews.f4.flag.low_energy': "Low energy",
    'instructor.reviews.f4.flag.monotone_voice': "Monotone voice",
    'instructor.reviews.f4.flag.language_barrier': "Language barrier",
    'instructor.reviews.f4.flag.memorized_answers': "Memorised answers",
    'instructor.reviews.f4.flag.nervousness_excessive': "Excessive nervousness",
    'instructor.reviews.f4.flag.attitude': "Negative attitude",
    'instructor.reviews.f4.flag.dishonesty_signs': "Signs of dishonesty",
    'instructor.reviews.f4.flag.safety_unawareness': "Lack of safety awareness",
    'instructor.reviews.f4.flag.customer_service_gap': "Customer service gap",
    'instructor.reviews.f4.flag.teamwork_concern': "Teamwork concern",
    'instructor.reviews.f4.flag.weak_motivation': "Weak motivation",
    'instructor.reviews.f4.flag.punctuality': "Punctuality issue",
    // Result panel
    'instructor.reviews.f4.result_ccri_title': "CCRI — Cabin Crew Readiness Index",
    'instructor.reviews.f4.result_ars_title': "ARS — Applicant Rating Score",
    'instructor.reviews.f4.result_out_of_100': "out of 100",
    'instructor.reviews.f4.result_breakdown_title': "BREAKDOWN BY AREA",
    // CCRI bands
    'instructor.reviews.f4.band_ccri_very_high': "Very High Readiness",
    'instructor.reviews.f4.band_ccri_high': "High Readiness",
    'instructor.reviews.f4.band_ccri_mid': "Moderate Readiness",
    'instructor.reviews.f4.band_ccri_low': "Low Readiness",
    // ARS bands
    'instructor.reviews.f4.band_ars_outstanding': "Outstanding Performance",
    'instructor.reviews.f4.band_ars_strong': "Strong Candidate",
    'instructor.reviews.f4.band_ars_adequate': "Adequate Candidate",
    'instructor.reviews.f4.band_ars_developing': "Needs Development",
    'instructor.reviews.f4.band_ars_insufficient': "Insufficient",
    // Breakdown area keys
    'instructor.reviews.f4.breakdown.fiziksel_sunum': "Physical Presentation",
    'instructor.reviews.f4.breakdown.iletisim_diksiyon': "Communication & Diction",
    'instructor.reviews.f4.breakdown.ingilizce': "English",
    'instructor.reviews.f4.breakdown.motivasyon': "Motivation",
    'instructor.reviews.f4.breakdown.kriz': "Crisis Management",
    'instructor.reviews.f4.breakdown.ekip': "Teamwork",
    'instructor.reviews.f4.breakdown.cv_kariyer': "CV / Career",
    'instructor.reviews.f4.breakdown.kamera_durusu': "Camera & Posture",
    'instructor.reviews.f4.breakdown.cevap_kalitesi': "Answer Quality",
    'instructor.reviews.f4.breakdown.ozguven': "Self-confidence",
    'instructor.reviews.f4.breakdown.havayolu_kulturu': "Airline Culture",
    'instructor.reviews.f4.breakdown.ingilizce_cevap': "English Answer",
    'instructor.reviews.f4.breakdown.stres': "Stress Management",
    'instructor.reviews.f4.breakdown.profesyonel_imaj': "Professional Image",
    // Read-only legacy view
    'instructor.reviews.f4.legacy_readonly_title': "Scoring details (read-only)",
    'instructor.reviews.f4.legacy_v1_label': "5-criteria score",
    'instructor.reviews.f4.legacy_v2_label': "13-criteria score",
    'instructor.reviews.unknown_schema_title': "Unknown assessment schema",
    'instructor.reviews.unknown_schema_desc': "This session was recorded with an unrecognized scoring schema. Contact your administrator for details.",
    // v2 edit guard
    'instructor.reviews.v2_edit_disabled_title': "This evaluation cannot be edited",
    'instructor.reviews.v2_edit_disabled_desc': "This evaluation was saved in the old format (v2) and does not contain F4 criteria. Editing would overwrite the original scores with defaults. To re-evaluate, use the score again option.",
    'instructor.reviews.v2_edit_rescore_hint': "To re-score with the current F4 rubric, go back to the review list and select \"Score\" for this session.",
    // Common boolean
    'cabin.common.yes': "Yes",
    'cabin.common.no': "No",

    // ── M5.5 Lesson phases ───────────────────────────────────────────────────
    'lesson.teaching_title': "Lesson",
    'lesson.teaching_header_label': "Lesson content",
    'lesson.sections': "{count} sections",
    'lesson.read_and_continue': "Read and continue",
    'lesson.no_content': "No content found.",
    'lesson.intro_header_label': "Lesson intro",
    'lesson.takeaway_label_fact': "Fact",
    'lesson.takeaway_label_item': "Item",
    'exercise.mini_question': "Quick Question",

    // ── M5.5 VocabularyTimeRace ──────────────────────────────────────────────
    'cabin.exercise.timeRace.title': "Time Race",
    'cabin.exercise.timeRace.cards': "{count} cards",
    'cabin.exercise.timeRace.rule1': "{time}-second race",
    'cabin.exercise.timeRace.rule2': "Each correct answer earns points",
    'cabin.exercise.timeRace.rule3': "You have {lives} lives",
    'cabin.exercise.timeRace.rule4': "Speed wins",
    'cabin.exercise.timeRace.start': "Start",
    'cabin.exercise.timeRace.time_left': "Time left",
    'cabin.exercise.timeRace.lives': "Lives",
    'cabin.exercise.timeRace.score': "Score",
    'cabin.exercise.timeRace.finish': "Finish",
    'cabin.exercise.timeRace.combo': "Combo",
    'cabin.exercise.timeRace.great': "Excellent!",
    'cabin.exercise.timeRace.good': "Well done!",
    'cabin.exercise.timeRace.tryAgain': "Keep practicing!",
    'cabin.exercise.timeRace.gameOver': "Race over — here are your results",
    'cabin.exercise.timeRace.correct': "Correct",
    'cabin.exercise.timeRace.maxCombo': "Max Combo",
    'cabin.exercise.timeRace.accuracy': "Accuracy",
    'cabin.exercise.timeRace.playAgain': "Play Again",

    // ── M5.5 SwipeVocabularyCard ─────────────────────────────────────────────
    'cabin.exercise.swipeCard.perfect': "Perfect!",
    'cabin.exercise.swipeCard.great': "Great!",
    'cabin.exercise.swipeCard.keepGoing': "Keep going!",
    'cabin.exercise.swipeCard.resultDesc': "{known}/{total} cards known",
    'cabin.exercise.swipeCard.knownIt': "I Know It",
    'cabin.exercise.swipeCard.dontKnow': "I Don't Know",
    'cabin.exercise.swipeCard.accuracy': "Accuracy",
    'cabin.exercise.swipeCard.swipeHint': "Swipe or use buttons",

    // ── M5.5 StoryMode ───────────────────────────────────────────────────────
    'cabin.exercise.storyMode.outcome': "Outcome",
    'cabin.exercise.storyMode.points': "Points",
    'cabin.exercise.storyMode.scenesVisited': "{n} scenes visited",
    'cabin.exercise.storyMode.finish': "Finish",
    'cabin.exercise.storyMode.scene': "Scene {n}",
    'cabin.exercise.storyMode.playAudio': "Play audio",
    'cabin.exercise.storyMode.choose': "Choose a response",
    'cabin.exercise.storyMode.next': "Next",

    // ── M5.5 WordHunt ────────────────────────────────────────────────────────
    'cabin.exercise.wordHunt.title': "Word Hunt",
    'cabin.exercise.wordHunt.sentences': "sentences",
    'cabin.exercise.wordHunt.howToPlay': "How to play",
    'cabin.exercise.wordHunt.rule1': "Read the sentence and find the missing word",
    'cabin.exercise.wordHunt.rule2': "Type the answer in the box",
    'cabin.exercise.wordHunt.rule3': "You have {time} seconds",
    'cabin.exercise.wordHunt.startBtn': "Start",
    'cabin.exercise.wordHunt.finished': "Finished!",
    'cabin.exercise.wordHunt.wordsFound': "words found",
    'cabin.exercise.wordHunt.points': "Points",
    'cabin.exercise.wordHunt.foundAll': "All words found!",
    'cabin.exercise.wordHunt.keepPracticing': "Keep practicing!",
    'cabin.exercise.wordHunt.find': "Find",
    'cabin.exercise.wordHunt.hint': "Hint",
    'cabin.exercise.wordHunt.sentenceLabel': "Sentence {n}",
    'cabin.exercise.wordHunt.correctWas': "Correct was",
    'cabin.exercise.wordHunt.nextSentence': "Next Sentence",
    'cabin.exercise.wordHunt.seeResults': "See Results",
    // -- Landing v2 (Duo redesign) --------------------------------------
    'landing.duo.hero.eyebrow': "Your cabin crew career starts here.",
    'landing.duo.hero.title': "Interactive training that gets you ready for the industry.",
    'landing.duo.hero.subtitle': "Voice practice, mock interviews, expert feedback. Everything you need. Join 5,000+ candidates already training.",
    'landing.duo.hero.ctaPrimary': "Get Started",
    'landing.duo.hero.ctaSecondary': "Log in",
    'landing.duo.hero.statsCourses': "courses",
    'landing.duo.hero.statsStudents': "students",
    'landing.duo.hero.statsLessons': "lessons",
    'landing.duo.hero.mascotAlt': "Academy mascot",
    'landing.duo.feature1.title': "Lifetime access.",
    'landing.duo.feature1.desc': "Pay once, keep every lesson forever. New content, updates and features included. No recurring fee.",
    'landing.duo.feature1.alt': "Lifetime access illustration",
    'landing.duo.feature2.title': "Backed by science.",
    'landing.duo.feature2.desc': "Spaced repetition and AI feedback that adapts to you. Built with cabin crew industry experts so every lesson counts.",
    'landing.duo.feature2.alt': "Science-based learning illustration",
    'landing.duo.feature3.title': "Stay motivated.",
    'landing.duo.feature3.desc': "Daily streaks, XP points and leaderboards keep you on track. Show up every day, and show up ready on interview day.",
    'landing.duo.feature3.alt': "Motivation and streaks illustration",
    'landing.duo.feature4.title': "Personalised learning.",
    'landing.duo.feature4.desc': "A smart placement test puts you at the right level from day one. Practice what you need, skip what you don't, and get interview-ready faster.",
    'landing.duo.feature4.alt': "Personalised learning illustration",
    'landing.duo.cta.title': "Your path to a cabin crew career starts right here.",
    'landing.duo.cta.desc': "Over 5,000 candidates are already training. Join them.",
    'landing.duo.cta.button': "Get Started",
    'landing.duo.langStrip.title': "our programs",
    'landing.duo.langStrip.cabinCrew': "Cabin Crew",
    'landing.duo.langStrip.aviationEnglish': "Aviation English",
    'landing.duo.langStrip.groundOps': "Ground Ops",
    'landing.duo.langStrip.interview': "Interview Prep",
    'landing.duo.langStrip.hr': "HR Management",
    'landing.duo.langStrip.signLanguage': "Sign Language",
    'landing.duo.langStrip.spiritualCoaching': "Spiritual Counseling",
    'landing.duo.footer.tagline': "The smarter way to prepare for cabin crew.",
    'landing.duo.footer.product': "Product",
    'landing.duo.footer.company': "Company",
    'landing.duo.footer.support': "Support",
    'landing.duo.footer.about': "About",
    'landing.duo.footer.careers': "Careers",
    'landing.duo.footer.contact': "Contact",
    'landing.duo.footer.faq': "FAQ",
    'landing.duo.footer.privacy': "Privacy",
    'landing.duo.footer.terms': "Terms",
    'landing.duo.footer.rights': "All rights reserved.",
    // -- Landing Interview Prova section ------------------------------------
    'landing.interview.eyebrow': "Interview Prep",
    'landing.interview.title.line1': "With industry experts,",
    'landing.interview.title.highlight': "Mock Interviews",
    'landing.interview.subtitle': "Experience the real interview environment. Test yourself with one-on-one interview simulations alongside professional instructors, and boost your confidence.",
    'landing.interview.feature1': "Realistic interview scenarios",
    'landing.interview.feature2': "One-on-one feedback",
    'landing.interview.feature3': "Body language & communication coaching",
    'landing.interview.feature4': "Video recording & analysis",
    'landing.interview.cta': "Get Details",
    'landing.interview.video.title': "Mock Interview",
    'landing.interview.video.subtitle': "Promotional Video",
    'landing.interview.video.badge': "Interview Training",
    'landing.interview.live': "LIVE",
    'landing.interview.scene1.q': "Why do you want to be a cabin crew?",
    'landing.interview.scene1.a': "I love serving people and travel.",
    'landing.interview.scene2.q': "How do you handle stress?",
    'landing.interview.scene2.a': "I stay calm and focused under pressure.",
    'landing.interview.scene3.q': "Tell me about a teamwork experience.",
    'landing.interview.scene3.a': "In my last role, I led a group project successfully.",
    // -- Auth v2 (Duo redesign) -----------------------------------------
    'auth.duo.brand': "Kariyer Online",
    'auth.duo.welcomeBack': "Welcome back!",
    'auth.duo.createAccount': "Create your account",
    'auth.duo.login.title': "Log in",
    'auth.duo.register.title': "Sign up",
    'auth.duo.register.intro': "Free, fun and effective.",
    'auth.duo.forgot.title': "Forgot your password?",
    'auth.duo.forgot.subtitle': "Enter your email and we will send you a reset link.",
    'auth.duo.forgot.cta': "Send reset link",
    'auth.duo.forgot.sending': "Sending...",
    'auth.duo.forgot.sentTitle': "Check your email",
    'auth.duo.forgot.sentDesc': "We sent a reset link to {email}. Do not forget the spam folder.",
    'auth.duo.forgot.backToLogin': "Back to log in",
    'auth.duo.forgot.rememberedQ': "Remembered your password?",
    'auth.duo.reset.title': "Set a new password",
    'auth.duo.reset.subtitle': "Choose a password you will actually remember this time.",
    'auth.duo.reset.newPassword': "New password",
    'auth.duo.reset.confirmPassword': "Repeat password",
    'auth.duo.reset.placeholderNew': "at least 8 characters",
    'auth.duo.reset.placeholderConfirm': "repeat password",
    'auth.duo.reset.cta': "Update password",
    'auth.duo.reset.saving': "Saving...",
    'auth.duo.reset.doneTitle': "All set!",
    'auth.duo.reset.doneDesc': "Your password has been updated. Time to log in with the new one.",
    'auth.duo.reset.goToLogin': "Go to log in",
    'auth.duo.reset.invalid': "This reset link is invalid or has expired.",
    'auth.duo.reset.passwordsDontMatch': "Passwords do not match",
    'auth.duo.reset.passwordTooShort': "Password must be at least 8 characters",
    'auth.duo.email': "Email",
    'auth.duo.emailPlaceholder': "you@example.com",
    'auth.duo.password': "Password",
    'auth.duo.passwordPlaceholder': "Password",
    'auth.duo.name': "Full name",
    'auth.duo.namePlaceholder': "Your name",
    'auth.duo.continueGoogle': "Continue with Google",
    'auth.duo.or': "or",
    'auth.duo.forgotLink': "Forgot password?",
    'auth.duo.haveAccount': "Already have an account?",
    'auth.duo.noAccount': "Don't have an account?",
    'auth.duo.signUpLink': "Sign up",
    'auth.duo.noAccountInfo': "Don't have an account? Please contact your training administrator.",
    'auth.duo.loginLink': "Log in",
    'auth.duo.submitting': "Just a sec...",
    'auth.duo.success': "Welcome aboard!",
    'auth.duo.termsHint': "By signing up you agree to our Terms and Privacy Policy.",
    'auth.duo.login.toastSuccess': "Logged in",
    'auth.duo.login.googleDisabled': "Google sign-in is unavailable right now. Please log in with email and password.",
    'auth.duo.register.toastSuccess': "Account created",
    'auth.duo.register.googleDisabled': "Google sign-up is unavailable right now. Please register with email and password.",
    'auth.duo.forgot.emailRequired': "Email is required",
    // -- Navbar v2 (Duo) -------------------------------------------------
    'navDuo.brand': "Kariyer Online",
    'navDuo.ctaStart': "Get started",
    'navDuo.ctaLogin': "Log in",

    // ── StudentRegistrationWizard ─────────────────────────────────────────────
    'admin.wizard.title': "New Student Registration",
    'admin.wizard.stepsAriaLabel': "Registration wizard steps",
    'admin.wizard.stepOf': "Step {current} of {total}",
    'admin.wizard.close': "Close wizard",
    'admin.wizard.loading': "Loading data...",

    'admin.wizard.step1.short': "Info",
    'admin.wizard.step2.short': "Main Course",
    'admin.wizard.step3.short': "Add-ons",
    'admin.wizard.step4.short': "Certificates",
    'admin.wizard.step5.short': "Payment",

    'admin.wizard.step1.title': "Personal Information",
    'admin.wizard.step2.title': "Main Course",
    'admin.wizard.step3.title': "Additional Courses",
    'admin.wizard.step4.title': "Certificate Selection",
    'admin.wizard.step5.title': "Summary & Payment",

    'admin.wizard.step1.alreadyRegistered': "Student was already registered in Step 1. Changes here will NOT update the saved record.",
    'admin.wizard.step2.hint': "Select one primary course for the student.",
    'admin.wizard.step3.hint': "Optionally add extra courses. You may skip this step.",
    'admin.wizard.step4.hint': "Optionally assign a certificate type per course.",
    'admin.wizard.step4.noCourses': "No courses selected yet.",
    'admin.wizard.step4.certLabel': "Certificate Type",
    'admin.wizard.step4.certNone': "No certificate",
    'admin.wizard.step4.examFeeInfo': "Default exam fee",

    'admin.wizard.step5.studentSection': "Student",
    'admin.wizard.step5.coursesSection': "Courses & Certificates",
    'admin.wizard.step5.paymentSection': "Payment",
    'admin.wizard.step5.courseTotal': "Course total",
    'admin.wizard.step5.discount': "Discount",
    'admin.wizard.step5.certFeeTotal': "Certificate fees",
    'admin.wizard.step5.grandTotal': "Grand total",
    'admin.wizard.step5.certFeeAmount': "Exam fee",
    'admin.wizard.step5.certFeeInclude': "Include in payment",

    'admin.wizard.field.firstName': "First Name",
    'admin.wizard.field.lastName': "Last Name",
    'admin.wizard.field.email': "Email",
    'admin.wizard.field.phone': "Phone",
    'admin.wizard.field.tcNo': "TC Identity No",
    'admin.wizard.field.birthDate': "Birth Date",
    'admin.wizard.field.address': "Address",
    'admin.wizard.field.city': "City",
    'admin.wizard.field.district': "District",
    'admin.wizard.field.postalCode': "Postal Code",
    'admin.wizard.field.emergencyContact': "Emergency Contact",
    'admin.wizard.field.emergencyPhone': "Emergency Phone",
    'admin.wizard.field.notes': "Notes",
    'admin.wizard.field.password': "Password",

    'admin.wizard.pw.autoGen': "Auto-generate",
    'admin.wizard.pw.manual': "Set manually",
    'admin.wizard.pw.regenerate': "Generate new password",
    'admin.wizard.pw.placeholder': "Enter password...",
    'admin.wizard.pw.show': "Show password",
    'admin.wizard.pw.hide': "Hide password",

    'admin.wizard.kvkk.label': "Student consents to KVKK data processing (required)",

    'admin.wizard.search.placeholder': "Search courses...",
    'admin.wizard.courses.empty': "No courses found",
    'admin.wizard.courses.free': "Free",
    'admin.wizard.courses.primaryLabel': "PRIMARY",
    'admin.wizard.courses.secondaryLabel': "ADD-ON",
    'admin.wizard.courses.secondarySelected': "courses selected",
    'admin.wizard.courses.priceLabel': "Price (TRY)",

    'admin.wizard.payment.free': "Free",
    'admin.wizard.payment.paid': "Paid",
    'admin.wizard.payment.method': "Payment Method",
    'admin.wizard.payment.cash': "Cash",
    'admin.wizard.payment.card': "Credit Card",
    'admin.wizard.payment.transfer': "Bank Transfer",
    'admin.wizard.payment.installments': "Installments",
    'admin.wizard.payment.discount': "Discount (%)",
    'admin.wizard.payment.notes': "Payment Notes",
    'admin.wizard.payment.notesPlaceholder': "Optional note...",

    'admin.wizard.btn.back': "Back",
    'admin.wizard.btn.cancel': "Cancel",
    'admin.wizard.btn.next': "Next",
    'admin.wizard.btn.registerAndNext': "Register & Continue",
    'admin.wizard.btn.registering': "Registering...",
    'admin.wizard.btn.enrolling': "Enrolling...",
    'admin.wizard.btn.complete': "Complete Registration",

    'admin.wizard.val.required': "This field is required",
    'admin.wizard.val.email': "Enter a valid email address",
    'admin.wizard.val.tcNo': "TC Identity No must be 11 digits",
    'admin.wizard.val.kvkk': "KVKK consent is required",
    'admin.wizard.val.primaryRequired': "Please select a primary course",

    'admin.wizard.registerSuccess': "Student registered successfully",
    'admin.wizard.enrollSuccess': "Enrollments saved successfully",
    'admin.wizard.error.register': "Registration failed",
    'admin.wizard.error.enroll': "Enrollment failed",
    'admin.wizard.error.loadData': "Could not load course data",
    'admin.wizard.error.noUserId': "Registration step not completed",

    'admin.wizard.success.title': "Student Registration Complete!",
    'admin.wizard.success.sub': "The student has been registered and enrolled in the selected courses.",
    'admin.wizard.success.cta': "Close & Refresh",

    'admin.wizard.openBtn': "New Student (Wizard)",

    // ── AdminLegal — Hukuk & İcra ─────────────────────────────────────────────
    'admin.nav.legal': "Legal & Enforcement",
    'admin.legal.subtitle': "Manage legal cases, enforcement files and lawyers",
    'admin.legal.tab.cases': "Enforcement Files",
    'admin.legal.tab.ready': "Ready for Enforcement",
    'admin.legal.tab.lawyers': "Lawyers",

    'admin.legal.col.caseNo': "File #",
    'admin.legal.col.student': "Student",
    'admin.legal.col.totalDebt': "Total Debt",
    'admin.legal.col.status': "Status",
    'admin.legal.col.lawyer': "Lawyer",
    'admin.legal.col.date': "Date",
    'admin.legal.col.installment': "Installment",
    'admin.legal.col.amount': "Amount",
    'admin.legal.col.dueDate': "Due Date",
    'admin.legal.col.overdueDays': "Overdue",

    'admin.legal.status.pending': "Pending",
    'admin.legal.status.notice_sent': "Notice Sent",
    'admin.legal.status.legal_started': "Legal Started",
    'admin.legal.status.payment_order': "Payment Order",
    'admin.legal.status.seizure': "Seizure",
    'admin.legal.status.collected': "Collected",
    'admin.legal.status.closed': "Closed",

    'admin.legal.filter.status': "Filter by status",
    'admin.legal.filter.lawyer': "Filter by lawyer",
    'admin.legal.filter.allStatuses': "All Statuses",
    'admin.legal.filter.allLawyers': "All Lawyers",
    'admin.legal.filter.selectStatus': "Select next status…",

    'admin.legal.btn.detail': "Detail",
    'admin.legal.btn.downloadNotice': "Download Notice PDF",
    'admin.legal.btn.updateStatus': "Update Status",
    'admin.legal.btn.uploadDoc': "Upload Document",
    'admin.legal.btn.addNote': "Add Note",
    'admin.legal.btn.addPayment': "Add Payment",
    'admin.legal.btn.openCase': "Open Enforcement File",
    'admin.legal.btn.addLawyer': "Add Lawyer",
    'admin.legal.btn.viewDoc': "View document",

    'admin.legal.detail.title': "Enforcement File Detail",
    'admin.legal.detail.overview': "Overview",
    'admin.legal.detail.idMissing': "The case id could not be read for this record, so status, note, document and payment actions are disabled. Close the window, refresh the list and try again.",
    'admin.legal.detail.docs': "Documents",
    'admin.legal.detail.notes': "Notes",
    'admin.legal.detail.payments': "Collections",
    'admin.legal.detail.debtBreakdown': "Debt Breakdown",
    'admin.legal.detail.updateStatus': "Update Status",
    'admin.legal.detail.uploadDoc': "Upload Document",
    'admin.legal.detail.addPayment': "Record Collection",

    'admin.legal.debt.principal': "Principal Debt",
    'admin.legal.debt.interest': "Interest",
    'admin.legal.debt.fees': "Legal Fees",
    'admin.legal.debt.total': "Total",
    'admin.legal.debt.collected': "Collected",
    'admin.legal.debt.remaining': "Remaining",

    'admin.legal.doc.type': "Document Type",
    'admin.legal.doc.typePlaceholder': "e.g. notice, court order",
    'admin.legal.doc.desc': "Description",
    'admin.legal.doc.descPlaceholder': "Brief description",
    'admin.legal.doc.selectFile': "Select file to upload",

    'admin.legal.note.label': "Note",
    'admin.legal.note.placeholder': "Enter note text…",

    'admin.legal.pay.amount': "Amount (₺)",
    'admin.legal.pay.date': "Payment Date",
    'admin.legal.pay.method': "Method",
    'admin.legal.pay.method.transfer': "Bank Transfer",
    'admin.legal.pay.method.cash': "Cash",
    'admin.legal.pay.method.card': "Credit Card",
    'admin.legal.pay.notes': "Notes",
    'admin.legal.pay.notesPlaceholder': "Payment notes…",

    'admin.legal.cases.empty': "No enforcement files found",
    'admin.legal.cases.idMissing': "Case id could not be read",
    'admin.legal.docs.empty': "No documents uploaded",
    'admin.legal.notes.empty': "No notes added",
    'admin.legal.payments.empty': "No collections recorded",
    'admin.legal.lawyers.empty': "No lawyers defined",
    'admin.legal.ready.empty': "No installments ready for enforcement",
    'admin.legal.ready.emptySub': "Installments with status 'legal' and not yet in a case will appear here",
    'admin.legal.ready.hint': "Select installments to open an enforcement file",
    'admin.legal.ready.selectedCount': "{n} installments selected",
    'admin.legal.ready.days': "days",
    'admin.legal.ready.selectAll': "Select all",
    'admin.legal.ready.selectRow': "Select row",

    'admin.legal.openCase.title': "Open Enforcement File",
    'admin.legal.openCase.selectedInstallments': "Selected Installments",
    'admin.legal.openCase.noLawyer': "No lawyer (assign later)",
    'admin.legal.openCase.debtDerivedHint': "The principal debt is derived from the selected installments by the server; it cannot be entered manually.",
    'admin.legal.openCase.installmentIdMissing': "The installment id could not be read for at least one selected row, so the file cannot be opened. Refresh the list and try again.",
    'admin.legal.openCase.lawyerIdMissing': "{n} lawyer records are not listed because their id could not be read. Fix those records on the Lawyers tab.",

    'admin.legal.lawyer.name': "Full Name",
    'admin.legal.lawyer.firm': "Law Firm",
    'admin.legal.lawyer.phone': "Phone",
    'admin.legal.lawyer.email': "Email",
    'admin.legal.lawyer.barNumber': "Bar Number",
    'admin.legal.lawyer.specialization': "Specialization",
    'admin.legal.lawyer.commissionRate': "Commission %",
    'admin.legal.lawyer.status': "Status",
    'admin.legal.lawyer.statusActive': "Active",
    'admin.legal.lawyer.statusInactive': "Inactive",
    'admin.legal.lawyer.nameRequired': "Lawyer name is required",
    'admin.legal.lawyer.deleteConfirm': "Are you sure you want to delete this lawyer?",
    'admin.legal.lawyer.idMissing': "Lawyer id could not be read",
    'admin.legal.lawyer.addTitle': "Add Lawyer",
    'admin.legal.lawyer.editTitle': "Edit Lawyer",

    'admin.legal.msg.statusUpdated': "Status updated",
    'admin.legal.msg.noteAdded': "Note added",
    'admin.legal.msg.docUploaded': "Document uploaded",
    'admin.legal.msg.paymentAdded': "Payment recorded",
    'admin.legal.msg.caseOpened': "Enforcement file opened",
    'admin.legal.msg.lawyerAdded': "Lawyer added",
    'admin.legal.msg.lawyerUpdated': "Lawyer updated",
    'admin.legal.msg.lawyerDeleted': "Lawyer deleted",
    'admin.legal.msg.invalidAmount': "Please enter a valid amount greater than zero",

    'admin.legal.pagination': "{from}–{to} of {total}",
    'admin.legal.prev': "Previous",
    'admin.legal.next': "Next",

    // ── AdminLegalContent (KVKK/Privacy/Terms editor) ───────────────────────────
    'admin.legalContent.subtitle': "Manage the KVKK, privacy policy and terms of service content shown to users",
    'admin.legalContent.matrix.docType': "Document",
    'admin.legalContent.docType.kvkk': "KVKK Disclosure",
    'admin.legalContent.docType.privacy': "Privacy Policy",
    'admin.legalContent.docType.terms': "Terms of Service",
    'admin.legalContent.lang.tr': "Turkish",
    'admin.legalContent.lang.en': "English",
    'admin.legalContent.status.draft': "Draft",
    'admin.legalContent.status.published': "Published",
    'admin.legalContent.status.empty': "No content yet",
    'admin.legalContent.field.title': "Title",
    'admin.legalContent.field.titlePlaceholder': "e.g. Personal Data Protection Disclosure",
    'admin.legalContent.field.format': "Format",
    'admin.legalContent.field.content': "Content",
    'admin.legalContent.field.contentPlaceholder': "Write the legal text here (markdown or HTML)...",
    'admin.legalContent.field.effectiveDate': "Effective date",
    'admin.legalContent.format.markdown': "Markdown",
    'admin.legalContent.format.html': "HTML",
    'admin.legalContent.btn.save': "Save draft",
    'admin.legalContent.btn.publish': "Publish",
    'admin.legalContent.btn.unpublish': "Unpublish",
    'admin.legalContent.btn.preview': "Preview",
    'admin.legalContent.btn.hidePreview': "Hide preview",
    'admin.legalContent.preview.title': "Preview",
    'admin.legalContent.preview.empty': "No content to preview yet",
    'admin.legalContent.hint.needContent': "Add content before publishing",
    'admin.legalContent.hint.contract': "doc_type: kvkk / privacy / terms · lang: tr / en — saving keeps the document as draft, use Publish to make it live",
    'admin.legalContent.msg.saved': "Draft saved",
    'admin.legalContent.msg.published': "Document published",
    'admin.legalContent.msg.unpublished': "Document unpublished",

    // ── AdminGeoContent / AdminGeoContentDetail (GEO article review) ───────────
    'admin.geoContent.pageTitle': "GEO Content Review",
    'admin.geoContent.pageSubtitle': "Review, publish, or reject articles produced by the automation",
    'admin.geoContent.col.title': "Title",
    'admin.geoContent.col.slug': "Slug",
    'admin.geoContent.col.pillar': "Pillar",
    'admin.geoContent.col.status': "Status",
    'admin.geoContent.col.createdAt': "Received",
    'admin.geoContent.col.updatedAt': "Updated",
    'admin.geoContent.col.source': "Source service",
    'admin.geoContent.status.draft': "Draft",
    'admin.geoContent.status.review': "In review",
    'admin.geoContent.status.published': "Published",
    'admin.geoContent.status.rejected': "Rejected",
    'admin.geoContent.status.archived': "Archived",
    'admin.geoContent.empty.title': "No articles to review",
    'admin.geoContent.empty.desc': "New articles will appear here once the automation sends them. If it hasn't connected yet, check the GEO content API key.",
    'admin.geoContent.emptyFiltered.title': "No records found",
    'admin.geoContent.emptyFiltered.desc': "No articles match this filter",
    'admin.geoContent.btn.publish': "Publish",
    'admin.geoContent.btn.reject': "Reject",
    'admin.geoContent.btn.archive': "Unpublish",
    'admin.geoContent.btn.viewLive': "View live page",
    'admin.geoContent.btn.retry': "Retry",
    'admin.geoContent.btn.viewDetail': "View detail",
    'admin.geoContent.publishConfirm.title': "Publish article",
    'admin.geoContent.publishConfirm.desc': "This article will become publicly visible at kariyeronline.tr/makale/{slug} to everyone, including AI search engines. Make sure you've read it fully before continuing.",
    'admin.geoContent.archiveConfirm.title': "Unpublish",
    'admin.geoContent.archiveConfirm.desc': "The article will be removed from kariyeronline.tr/makale/{slug}. This cannot be undone — republishing isn't supported in this version.",
    'admin.geoContent.reject.title': "Reject article",
    'admin.geoContent.reject.desc': "This article won't be published. Write a rejection reason the producing automation can read.",
    'admin.geoContent.reject.label': "Rejection reason",
    'admin.geoContent.reject.placeholder': "e.g. Source could not be verified, statistic is outdated",
    'admin.geoContent.reject.confirm': "Reject",
    'admin.geoContent.toast.published': "Article published",
    'admin.geoContent.toast.rejected': "Article rejected",
    'admin.geoContent.toast.archived': "Article unpublished",
    'admin.geoContent.toast.conflict': "This article was updated in another tab — refreshing",
    'admin.geoContent.toast.rateLimited': "Too many actions — wait a few seconds and try again",
    'admin.geoContent.signal.tldr': "TL;DR",
    'admin.geoContent.signal.tldrYes': "Present",
    'admin.geoContent.signal.tldrNo': "Missing",
    'admin.geoContent.signal.h2': "H2 headings",
    'admin.geoContent.signal.table': "Table",
    'admin.geoContent.signal.sources': "Sources",
    'admin.geoContent.signal.dataPoints': "Data points",
    'admin.geoContent.signal.targetQuestions': "Target questions",
    'admin.geoContent.signal.entities': "Entities",
    'admin.geoContent.tab.rendered': "Published view",
    'admin.geoContent.tab.markdown': "Raw markdown",
    'admin.geoContent.draftNote': "This article hasn't been submitted for review yet",
    'admin.geoContent.rejectedNote': "This article was rejected — re-evaluation isn't supported in this version",
    'admin.geoContent.archivedNote': "This article is archived — republishing isn't supported in this version",
    'admin.geoContent.pillar.cabinCrew': "Cabin crew",
    'admin.geoContent.pillar.career': "Career",
    'admin.geoContent.meta.description': "Meta description",
    'admin.geoContent.meta.chars': "characters",
    'admin.geoContent.meta.targetQuestions': "Target questions",
    'admin.geoContent.meta.sources': "Sources",
    'admin.geoContent.meta.dataPoints': "Data points",
    'admin.geoContent.meta.entities': "Entities",
    'admin.geoContent.meta.technicalDetails': "Technical details",
    'admin.geoContent.meta.authorName': "Author",
    'admin.geoContent.meta.subtopic': "Subtopic",
    'admin.geoContent.meta.topicId': "Topic ID",
    'admin.geoContent.meta.idempotencyKey': "Idempotency key",
    'admin.geoContent.meta.contentHash': "Content hash",

    // Content editing — AdminGeoContentDetail.jsx Edit Mode (görev 2026-07-30)
    'admin.geoContent.btn.edit': "Edit",
    'admin.geoContent.btn.cancelEdit': "Discard",
    'admin.geoContent.btn.save': "Save",
    'admin.geoContent.saving': "Saving…",
    'admin.geoContent.editRejectedNote': "Rejected articles can't be edited in this version — the automation must regenerate it.",
    'admin.geoContent.editArchivedNote': "Archived articles can't be edited — republishing isn't supported in this version.",
    'admin.geoContent.field.title': "Title",
    'admin.geoContent.field.metaDescription': "Meta description",
    'admin.geoContent.field.metaDescriptionHint': "150–160 characters is ideal for search snippets",
    'admin.geoContent.field.bodyMd': "Article body (Markdown)",
    'admin.geoContent.field.entities': "Entities — one per line",
    'admin.geoContent.field.targetQuestions': "Target questions — one per line",
    'admin.geoContent.field.sources': "Sources — one URL per line",
    'admin.geoContent.field.internalLinks': "Internal links — one per line",
    'admin.geoContent.field.pillar': "Pillar",
    'admin.geoContent.field.dataPoints': "Data points",
    'admin.geoContent.field.dataPointLabel': "Label",
    'admin.geoContent.field.dataPointValue': "Value",
    'admin.geoContent.field.dataPointSource': "Source URL (optional)",
    'admin.geoContent.btn.addDataPoint': "Add data point",
    'admin.geoContent.edit.dataPoints.removeAria': "Remove data point {label}",
    'admin.geoContent.edit.publishedWarning': "This article is currently live at kariyeronline.tr/makale/{slug} and visible to everyone. Saving will change the live content immediately.",
    'admin.geoContent.editPublishConfirm.title': "Update the live article",
    'admin.geoContent.editPublishConfirm.desc': "Changes will go live immediately. Continue?",
    'admin.geoContent.editPublishConfirm.confirm': "Save & publish",
    'admin.geoContent.unsavedConfirm.title': "Unsaved changes",
    'admin.geoContent.unsavedConfirm.desc': "Your edits will be lost if you leave this page. Leave anyway?",
    'admin.geoContent.unsavedConfirm.confirm': "Leave",
    'admin.geoContent.unsavedConfirm.cancel': "Stay here",
    'admin.geoContent.toast.saved': "Article saved",
    'admin.geoContent.editConflict.banner': "This article was updated elsewhere. Review your changes and save again — note that saving will overwrite the other update.",
    'admin.geoContent.edit.previewUpdating': "Updating preview…",
    'admin.geoContent.edit.previewUnavailable': "Preview couldn't be generated right now — the renderer is temporarily unavailable. You can still save; preview will retry once it's back.",
    'admin.geoContent.edit.previewToggleShow': "Show preview",
    'admin.geoContent.edit.previewToggleHide': "Hide preview",
    'admin.geoContent.signal.directAnswer': "Direct answer",

    // PUBLIC legal document page — /legal/:docType (LegalPage.jsx)
    'legal.docType.kvkk': "KVKK Disclosure",
    'legal.docType.privacy': "Privacy Policy",
    'legal.docType.terms': "Terms of Service",
    'legal.loading': "Loading document...",
    'legal.error.title': "Something went wrong",
    'legal.error.retry': "Try again",
    'legal.notFound.title': "Document not found",
    'legal.notFound.body': "This legal document link does not exist. Please check the URL.",
    'legal.unavailable.body': "This content will be published soon. Please check back later.",
    'legal.effectiveDate': "Effective date",
    'legal.updatedAt': "Last updated",
    'legal.backHome': "Back to home",
    'legal.langToggle.label': "Language",
    'legal.langToggle.tr': "TR",
    'legal.langToggle.en': "EN",

    // ── AdminVimeo ─────────────────────────────────────────────────────────────
    'admin.vimeo_page_title': "Vimeo Library",
    'admin.vimeo_page_subtitle': "Manage video folders and assign them to courses",

    // ── AdminContracts ─────────────────────────────────────────────────────────
    'admin.con_page_title': "Contracts & Compliance",
    'admin.con_page_subtitle': "Vendor contracts, student agreements, cancellations and inactive students",

    // ── AdminReviews extra columns ─────────────────────────────────────────────
    'admin.rev_col_rating': "Rating",
    'admin.rev_col_comment': "Comment",

    // ── Announcements enum labels ──────────────────────────────────────────────
    'admin.ann_label_all': "Everyone",
    'admin.ann_label_students': "Students",
    'admin.ann_label_instructors': "Instructors",
    'admin.ann_label_normal': "Normal",
    'admin.ann_label_high': "High",
    'admin.ann_label_low': "Low",

    // ── AdminNewIdentityPage ───────────────────────────────────────────────────
    'admin.identity.title': "Create New Identity",
    'admin.identity.subtitle': "Create a new student identity and system record",
    'admin.identity.breadcrumb': "New Identity",
    'admin.identity.section.identity': "Identity Information",
    'admin.identity.section.contact': "Contact Information",
    'admin.identity.section.education': "Education & Profession",
    'admin.identity.section.system': "System Settings",
    'admin.identity.field.ad': "First Name",
    'admin.identity.field.soyad': "Last Name",
    'admin.identity.field.cinsiyet': "Gender",
    'admin.identity.field.tc_vatandasi': "Citizenship",
    'admin.identity.field.tc_no': "TC ID No",
    'admin.identity.field.dogum_tarihi': "Birth Date",
    'admin.identity.field.dogum_yeri': "Birth Place",
    'admin.identity.field.anne_adi': "Mother's Name",
    'admin.identity.field.baba_adi': "Father's Name",
    'admin.identity.field.email': "E-mail (Optional)",
    'admin.identity.field.telefon': "Mobile Phone",
    'admin.identity.field.telefon2': "Mobile Phone 2",
    'admin.identity.field.yurtdisi_tel': "International Phone",
    'admin.identity.field.sehir': "City",
    'admin.identity.field.adres': "Address",
    'admin.identity.field.meslek': "Profession",
    'admin.identity.field.egitim_durumu': "Education Level",
    'admin.identity.field.lisans_bolumu': "University Department",
    'admin.identity.field.egitim_aciklama': "Education Notes",
    'admin.identity.field.reklam_turu': "Ad Source",
    'admin.identity.field.ogrenci_kodu_ozel': "Custom Student Code",
    'admin.identity.field.aktiflik_suresi': "Active Until",
    'admin.identity.field.aktiflik_suresi_hint': "Optional — determined at sale based on program",
    'admin.identity.field.aciklama': "Notes",
    'admin.identity.field.pasif': "Inactive Student",
    'admin.identity.field.fatura_talebi': "Invoice Request",
    'admin.identity.verify.btn': "Verify Identity",
    'admin.identity.verify.hint': "TC No (11 digits), First Name, Last Name and Birth Date required",
    'admin.identity.verify.success': "Verified",
    'admin.identity.verify.fail': "Could not verify",
    'admin.identity.submit': "Create Identity",
    'admin.identity.submitting': "Saving...",
    'admin.identity.success.title': "Identity Created!",
    'admin.identity.success.subtitle': "Student successfully registered in the system.",
    'admin.identity.success.warning': "The information below will not be shown again. Please copy or send via SMS.",
    'admin.identity.success.ogrenci_kodu': "Student No",
    'admin.identity.success.gecici_sifre': "Temp Password",
    'admin.identity.success.onay_kodu': "Approval Code",
    'admin.identity.success.send_approval': "Send Approval Code SMS",
    'admin.identity.success.send_creds': "Send Login Credentials SMS",
    'admin.identity.success.profile': "Go to Student Profile",
    'admin.identity.success.new': "New Record",
    'admin.identity.prefill_notice': "Pre-filled from lead",
    // ── ChangePasswordPage ────────────────────────────────────────────────────
    'auth.changePw.title': "Change Your Password",
    'auth.changePw.subtitle': "You must change your password on your first login.",
    'auth.changePw.current': "Current Password",
    'auth.changePw.new': "New Password",
    'auth.changePw.confirm': "Confirm New Password",
    'auth.changePw.minLength': "(min. 8 characters)",
    'auth.changePw.mismatch': "Passwords do not match",
    'auth.changePw.tooShort': "New password must be at least 8 characters",
    'auth.changePw.submit': "Change Password",
    'auth.changePw.submitting': "Saving...",
    'auth.changePw.done.title': "Password Changed!",
    'auth.changePw.done.redirect': "Redirecting...",
    // ── AutoLoginPage ─────────────────────────────────────────────────────────
    'auth.autoLogin.loading.title': "Logging in...",
    'auth.autoLogin.loading.subtitle': "Please wait.",
    'auth.autoLogin.success.title': "Login successful!",
    'auth.autoLogin.success.subtitle': "Redirecting...",
    'auth.autoLogin.error.title': "Login Failed",
    'auth.autoLogin.error.backToLogin': "Back to Login",
    // ── StudentSecurityTab — impersonate ──────────────────────────────────────
    'admin.security.access.title': "Admin Access Tools",
    'admin.security.access.impersonate': "Impersonate Student",
    'admin.security.access.magicLink': "Create Login Link",
    'admin.security.access.copy': "Copy",
    'admin.security.access.copied': "Copied",
    'admin.security.access.sendSms': "Send via SMS",
    'admin.security.access.smsSent': "SMS Sent ✓",
    'admin.security.access.validFor': "Valid for:",

    // ── AdminStudentSalePage ──────────────────────────────────────────────────
    'admin.sale.title': "Sale",
    'admin.sale.breadcrumb': "Sale",
    'admin.sale.subtitle': "Sale page — course selection, payment plan and confirmation",
    'admin.sale.btn_go_to_sale': "Start Sale",
    'admin.sale.btn_new_sale': "New Sale",
    'admin.sale.panel_courses': "Course Selection",
    'admin.sale.panel_sales_info': "Sales Information",
    'admin.sale.panel_payment': "Payment Plan",
    'admin.sale.panel_approval': "Confirmation",
    'admin.sale.course_type_main': "MAIN",
    'admin.sale.course_type_extra': "EXTRA",
    'admin.sale.meb_warning': "MEB-approved — main only",
    'admin.sale.cart_empty': "No courses selected yet",
    'admin.sale.search_placeholder': "Search courses...",
    'admin.sale.label_referral_code': "Referral Code (Optional)",
    'admin.sale.btn_query': "Query",
    'admin.sale.referral_valid': "Valid code",
    'admin.sale.referral_invalid': "Invalid code",
    'admin.sale.label_discount': "Discount",
    'admin.sale.label_list_price': "List Price",
    'admin.sale.label_sale_total': "Sale Total",
    'admin.sale.label_in_plan': "In Payment Plan",
    'admin.sale.label_difference': "Difference",
    'admin.sale.diff_warning': "Payment plan total does not match sale total — adjust installments to clear the difference.",
    'admin.sale.label_payment_method': "Payment Method",
    'admin.sale.method_cash': "Cash",
    'admin.sale.method_card': "Credit Card",
    'admin.sale.method_transfer': "Bank Transfer",
    'admin.sale.label_generate_plan': "Generate Equal Installments",
    'admin.sale.label_installment_count': "Number of Installments",
    'admin.sale.label_first_due': "First Due Date",
    'admin.sale.btn_generate': "GENERATE",
    'admin.sale.btn_clear': "CLEAR",
    'admin.sale.col_amount': "Amount",
    'admin.sale.col_due_date': "Due Date",
    'admin.sale.plan_empty_hint': "Generate a payment plan or save as one-time.",
    'admin.sale.label_extra_lessons': "Extra Lessons To Be Selected Later",
    'admin.sale.label_extra_lessons_count': "Count",
    'admin.sale.label_notes': "Registration Notes",
    'admin.sale.label_sales_staff': "Sales Staff",
    'admin.sale.staff_placeholder': "Select staff...",
    'admin.sale.label_approval_code': "Approval Code",
    'admin.sale.approval_code_placeholder': "Code received from student...",
    'admin.sale.btn_send_sms': "Send SMS",
    'admin.sale.sms_sent': "Sent",
    'admin.sale.error_no_primary': "At least one Main course required",
    'admin.sale.error_no_staff': "Sales staff must be selected",
    'admin.sale.error_diff_not_zero': "Payment plan difference must be zero",
    'admin.sale.btn_confirm': "CONFIRM SALE",
    'admin.sale.confirm_title': "Confirm Sale",
    'admin.sale.confirm_desc': "An enrollment will be created. This cannot be undone.",
    'admin.sale.confirm_btn': "Confirm & Save",
    'admin.sale.success_title': "Sale Recorded!",
    'admin.sale.success_subtitle': "Enrollment completed successfully.",
    'admin.sale.success_invoice': "Invoice No",
    'admin.sale.success_plan': "Payment Plan",
    'admin.sale.btn_student_profile': "Go to Student Profile",
    'admin.sale.btn_back_to_list': "Back to Students",
    'admin.sale.error_meb_extra': "MEB-approved course can only be added as main education",
    'admin.sale.error_already_in_cart': "This course is already in the cart",
    'admin.sale.cert_label': "Certificate Type",
    'admin.sale.exam_date_label': "Exam Date",
    'admin.sale.cert_select_placeholder': "Select...",
    'admin.sale.selected_courses': "Selected Courses",

    // ── CV Wizard (F2) ────────────────────────────────────────────────────────
    'cv.wizard.title': "CV Profile",
    'cv.wizard.subtitle': "Complete your cabin crew candidate profile",
    'cv.step.kisisel': "Personal",
    'cv.step.egitim': "Education",
    'cv.step.is_deneyimi': "Experience",
    'cv.step.dil': "Language",
    'cv.step.motivasyon': "Motivation",
    'cv.step_desc.kisisel': "Basic contact & identity info (required)",
    'cv.step_desc.egitim': "Education background (optional)",
    'cv.step_desc.is_deneyimi': "Work history & competencies (optional)",
    'cv.step_desc.dil': "Language skills & proficiency (optional)",
    'cv.step_desc.motivasyon': "Your aviation motivation (optional)",
    'cv.nav.back': "Back",
    'cv.nav.next': "Next",
    'cv.nav.skip': "Skip for now",
    'cv.nav.submit': "Submit CV",
    'cv.autosave.saving': "Saving...",
    'cv.autosave.saved': "Saved",
    'cv.autosave.error': "Save failed",
    'cv.kvkk.title': "Data Processing Consent (KVKK)",
    'cv.kvkk.read_text': "Read the KVKK Text",
    'cv.kvkk.full_text': "Your personal data entered in this form will be processed by Kariyer Online under Law No. 6698 (KVKK) for the purpose of cabin crew candidate evaluation. Your data will be shared with authorized instructors and will not be transferred to third parties without your consent. You have the right to access, correct, and request deletion of your data. You may request deletion via the KVKK request form at any time.",
    'cv.kvkk.consent_label': "I have read and consent to the processing of my personal data in accordance with KVKK.",
    'cv.kvkk.accept_btn': "I Consent, Continue",
    'cv.kvkk.reject_btn': "I Do Not Consent",
    'cv.field.ad': "First Name",
    'cv.field.soyad': "Last Name",
    'cv.field.dogum_tarihi': "Date of Birth",
    'cv.field.cinsiyet': "Gender",
    'cv.field.boy_cm': "Height (cm)",
    'cv.field.kilo_kg': "Weight (kg)",
    'cv.field.sehir': "City",
    'cv.field.telefon': "Phone",
    'cv.field.eposta': "Email",
    'cv.field.pasaport_var': "I have a valid passport",
    'cv.field.seyahat_engeli': "I have a travel restriction",
    'cv.field.seyahat_engeli_aciklama': "Travel restriction explanation",
    'cv.field.adli_sicil': "I have a criminal record",
    'cv.field.adli_sicil_aciklama': "Criminal record explanation",
    'cv.field.onceki_havayolu_basvurusu': "I have previously applied to an airline",
    'cv.field.mulakate_girdi_mi': "I was invited to an interview",
    'cv.field.elendigi_asama': "Stage eliminated at",
    'cv.field.instagram': "Instagram",
    'cv.field.linkedin': "LinkedIn",
    'cv.field.mezuniyet_durumu': "Education Level",
    'cv.field.okul': "School / University",
    'cv.field.bolum': "Department",
    'cv.field.mezuniyet_yili': "Graduation Year",
    'cv.field.devam_eden_egitim': "Currently enrolled in education",
    'cv.field.yabanci_dil_egitimi': "Foreign language training",
    'cv.field.sertifikalar': "Certificates",
    'cv.field.kabin_memurlugu_egitimi': "Cabin crew training completed",
    'cv.field.ilk_yardim_egitimi': "First aid training completed",
    'cv.field.diksiyon_egitimi': "Diction / public speaking training completed",
    'cv.field.calistimi': "I have work experience",
    'cv.field.son_is_yeri': "Last workplace",
    'cv.field.pozisyon': "Position",
    'cv.field.sure': "Duration",
    'cv.field.musteri_iliskileri_deneyimi': "Customer relations experience",
    'cv.field.satis_deneyimi': "Sales experience",
    'cv.field.yogun_tempo_deneyimi': "High-pace work environment experience",
    'cv.field.vardiya_deneyimi': "Shift work experience",
    'cv.field.ekip_deneyimi': "Team collaboration experience",
    'cv.field.zor_musteri_deneyimi': "Experience with difficult customers",
    'cv.field.ingilizce_seviye': "English proficiency level",
    'cv.field.konusma_pratigi': "Speaking practice",
    'cv.field.ingilizce_mulakat_tecrubesi': "English interview experience",
    'cv.field.diller': "Additional Languages",
    'cv.field.dil_adi': "Language Name",
    'cv.field.dil_seviye': "Proficiency Level",
    'cv.action.dil_ekle': "Add Language",
    'cv.validation.dil_seviye_birlikte': "Both language name and level are required",
    'cv.validation.dil_mukerrer': "This language has already been added",
    'cv.field.neden_kabin_memuru': "Why do you want to be cabin crew?",
    'cv.field.ne_zamandir_ister': "How long have you wanted this career?",
    'cv.field.zorluk_farkindaligi': "Awareness of challenges",
    'cv.field.uzun_ucus_uygunlugu': "Suitable for long-haul flights",
    'cv.field.vardiya_uygunlugu': "Suitable for shift work",
    'cv.field.sehir_degisimi_uygunlugu': "Open to city relocation",
    'cv.field.uniforma_disiplini': "Accepts uniform & appearance standards",
    'cv.field.havayolu_beklentisi': "Expectations from the airline",
    'cv.field.bes_yil_sonra': "Where do you see yourself in 5 years?",
    'cv.placeholder.ad': "e.g. Ayşe",
    'cv.placeholder.soyad': "e.g. Yılmaz",
    'cv.placeholder.sehir': "e.g. Istanbul",
    'cv.placeholder.aciklama': "Brief explanation...",
    'cv.placeholder.aciklama_opsiyonel': "Optional — describe your experience...",
    'cv.placeholder.select': "Select...",
    'cv.placeholder.okul': "e.g. Anadolu University",
    'cv.placeholder.bolum': "e.g. Tourism Management",
    'cv.placeholder.sertifikalar': "e.g. IATA, CPR Certificate",
    'cv.placeholder.son_is_yeri': "e.g. THY Ground Services",
    'cv.placeholder.pozisyon': "e.g. Customer Rep.",
    'cv.placeholder.sure': "e.g. 2 years",
    'cv.placeholder.elendigi_asama': "e.g. Final round",
    'cv.placeholder.neden_kabin_memuru': "Tell us why you chose this career...",
    'cv.placeholder.ne_zamandir_ister': "e.g. Since 2019",
    'cv.placeholder.bes_yil_sonra': "Describe your 5-year plan...",
    'cv.placeholder.yabanci_dil_egitimi': "e.g. IELTS, English language course",
    'cv.hint.comma_separated': "Separate multiple entries with a comma",
    'cv.section.pasaport_seyahat': "Passport & Travel",
    'cv.section.adli_sicil': "Criminal Record",
    'cv.section.onceki_basvuru': "Previous Applications",
    'cv.section.ek_egitimler': "Additional Training",
    'cv.section.deneyim_aciklamalari': "Experience Descriptions",
    'cv.section.uygunluk': "Work Conditions Suitability",
    'cv.option.kadin': "Female",
    'cv.option.erkek': "Male",
    'cv.option.diger': "Other",
    'cv.option.ortaogretim': "High school",
    'cv.option.onlisans': "Associate degree",
    'cv.option.lisans': "Bachelor's degree",
    'cv.option.lisansustu': "Postgraduate",
    'cv.option.baslangic': "Beginner (A1-A2)",
    'cv.option.orta': "Intermediate (B1)",
    'cv.option.iyi': "Good (B2)",
    'cv.option.cok_iyi': "Very Good (C1)",
    'cv.option.akici': "Fluent (C2)",
    'cv.validation.ad_required': "First name is required",
    'cv.validation.soyad_required': "Last name is required",
    'cv.validation.dogum_required': "Date of birth is required",
    'cv.validation.dogum_age': "Candidates must be between 18 and 40 years old",
    'cv.validation.telefon_required': "Phone number is required",
    'cv.validation.telefon_format': "Enter in +905XXXXXXXXX format (10 digits after +90)",
    'cv.validation.eposta_required': "Email is required",
    'cv.validation.eposta_format': "Enter a valid email address",
    'cv.validation.boy_min': "Height must be at least 140 cm",
    'cv.validation.boy_max': "Height cannot exceed 210 cm",
    'cv.validation.kilo_min': "Weight must be at least 40 kg",
    'cv.validation.kilo_max': "Weight cannot exceed 150 kg",
    'cv.validation.yil_min': "Year cannot be before 1980",
    'cv.validation.yil_max': "Year cannot be in the distant future",
    'cv.validation.hakkimda_max': "Professional summary cannot exceed 600 characters",
    'cv.validation.foto_size': "Photo must be smaller than 2 MB",
    'cv.validation.foto_format': "Only JPEG and PNG files are accepted",
    'cv.error.submit_failed': "CV could not be saved. Please try again.",
    'cv.error.foto_upload_failed': "Photo could not be uploaded. Please try again.",
    'cv.error.foto_delete_failed': "Photo could not be deleted. Please try again.",
    'cv.success.title': "CV Submitted!",
    'cv.success.sub': "Your profile has been saved. Your instructor can now review your background before the interview.",
    'cv.success.cta_interview': "Go to Interview Practice",
    'cv.success.cta_dashboard': "Back to Dashboard",

    // ── CV Step 6 — Professional Output ──────────────────────────────────────
    'cv.step.cv_ciktisi': "CV Output",
    'cv.step_desc.cv_ciktisi': "Professional title, summary, work history & photo (optional)",
    'cv.field.unvan': "Professional Title",
    'cv.field.hakkimda': "Professional Summary",
    'cv.field.foto': "Profile Photo",
    'cv.field.is_deneyimleri': "Work Experience Entries",
    'cv.field.is_pozisyon': "Position",
    'cv.field.is_sirket': "Company",
    'cv.field.is_baslangic': "Start Date",
    'cv.field.is_bitis': "End Date",
    'cv.field.is_devam_ediyor': "Currently working here",
    'cv.field.is_maddeler': "Bullet Points",
    'cv.placeholder.unvan': "e.g. Cabin Crew Candidate",
    'cv.placeholder.hakkimda': "A brief professional summary about yourself (max 600 characters)...",
    'cv.placeholder.is_pozisyon': "e.g. Customer Service Representative",
    'cv.placeholder.is_sirket': "e.g. ABC Airlines",
    'cv.placeholder.is_baslangic': "e.g. 06/2021",
    'cv.placeholder.is_bitis': "e.g. 08/2023",
    'cv.placeholder.is_madde': "e.g. Provided excellent customer service to 200+ passengers daily",
    'cv.section.is_deneyimleri': "Work Experience Entries",
    'cv.section.foto': "Profile Photo",
    'cv.section.referans_not': "References",
    'cv.hint.hakkimda_counter': "{count}/600 characters",
    'cv.hint.referans': "References will be provided upon request.",
    'cv.btn.deneyim_ekle': "Add Experience",
    'cv.btn.deneyim_sil': "Remove",
    'cv.btn.madde_ekle': "Add bullet",
    'cv.btn.madde_sil': "Remove",
    'cv.btn.foto_yukle': "Upload Photo",
    'cv.btn.foto_kaldir': "Remove Photo",
    'cv.btn.foto_uploading': "Uploading...",
    'cv.btn.foto_deleting': "Removing...",
    'cv.btn.dil_sil': "Delete language",
    'cv.btn.sil': "Delete",
    'cv.foto.preview_alt': "CV profile photo preview",
    'cv.pdf.download_photo': "Download CV (with photo)",
    'cv.pdf.download_no_photo': "Download ATS Version (no photo)",
    'cv.pdf.downloading': "Downloading...",

    // ── CV Status Screen (F4 — cv-onay) ──────────────────────────────────────
    'cv.status.page_title': "CV Review Status",
    'cv.status.loading': "Loading status...",
    'cv.status.error': "Status could not be loaded",
    'cv.status.error_retry': "Try Again",
    'cv.status.no_cv': "No CV submitted yet",
    'cv.status.no_cv_sub': "Complete the CV wizard to submit your profile for review.",
    'cv.status.no_cv_cta': "Go to CV Wizard",
    'cv.status.review_round': "Review round {round}",
    'cv.status.submitted_at': "Submitted: {date}",

    'cv.status.pending_review.badge': "Awaiting Review",
    'cv.status.pending_review.title': "Awaiting instructor review",
    'cv.status.pending_review.sub': "Your profile has been submitted. An instructor will review it shortly.",

    'cv.status.in_review.badge': "Under Review",
    'cv.status.in_review.title': "Instructor is reviewing",
    'cv.status.in_review.sub': "An instructor has started reviewing your CV. You will be notified when the review is complete.",

    'cv.status.approved.badge': "Approved",
    'cv.status.approved.title': "Your CV is approved!",
    'cv.status.approved.sub': "Congratulations! Your profile has been approved. You can download your approved CV as a PDF.",
    'cv.status.approved.download': "Download PDF",
    'cv.status.approved.downloading': "Downloading...",
    'cv.status.approved.download_error': "PDF could not be downloaded. Please try again.",

    'cv.status.changes_requested.badge': "Changes Requested",
    'cv.status.changes_requested.title': "Changes requested by instructor",
    'cv.status.changes_requested.sub': "The instructor has reviewed your CV and requested some changes. Please review the feedback below and resubmit.",
    'cv.status.changes_requested.message_label': "Instructor feedback:",
    'cv.status.changes_requested.edit_cta': "Edit & Resubmit",

    // ── CV Wizard — Europass 5-adım iskelet (Faz 2) ──────────────────────────
    'cv_wizard.steps.welcome': "Welcome",
    'cv_wizard.steps.select': "Select",
    'cv_wizard.steps.edit': "Edit",
    'cv_wizard.steps.template': "Template",
    'cv_wizard.steps.save': "Save",
    'cv_wizard.welcome.title': "Build Your CV",
    'cv_wizard.welcome.subtitle': "Create your cabin crew candidate profile in Europass format",
    'cv_wizard.welcome.profile_summary': "Your profile has {n} section(s) filled",
    'cv_wizard.welcome.no_name': "Unnamed profile",
    'cv_wizard.welcome.empty_warning': "Your profile has no data yet. Fill in your profile before continuing.",
    'cv_wizard.welcome.go_to_profile': "Go to Profile",
    'cv_wizard.welcome.cta': "Let's Begin",
    'cv_wizard.welcome.kvkk_title': "Data Consent Required (KVKK)",
    'cv_wizard.placeholder.coming_soon': "This step will be available soon",
    'cv_wizard.placeholder.save_coming_soon': "CV save & submit will be available in the next phase",

    // ── CV Wizard — Select step (Faz 3) ──────────────────────────────────────
    'cv_wizard.select.subtitle': "Choose which sections and records to include in your CV",
    'cv_wizard.select.counter': "{n} section(s) · {m} record(s) selected",
    'cv_wizard.select.section.kisisel': "Personal Information",
    'cv_wizard.select.section.kisisel_locked_desc': "Always included in your CV",
    'cv_wizard.select.section.is_deneyimi': "Work Experience",
    'cv_wizard.select.section.egitim': "Education",
    'cv_wizard.select.sertifikalar_note': "Certificates are included with education",
    'cv_wizard.select.section.dil': "Languages",
    'cv_wizard.select.section.hakkimda': "About Me",
    'cv_wizard.select.section.referanslar': "References",
    'cv_wizard.select.referanslar_note': "Excluded by default (KVKK)",
    'cv_wizard.select.empty_section': "No records in this section yet",
    'cv_wizard.select.go_to_profile': "Edit Profile",

    // ── CV Wizard — Edit step (Faz 4) ────────────────────────────────────────
    'cv_wizard.edit.subtitle': "Click any field to edit it inline",
    'cv_wizard.edit.preview_title': "CV Preview",
    'cv_wizard.edit.instructions_title': "Tips",
    'cv_wizard.edit.instructions': "Click a field to edit. Enter saves, Escape cancels. Textarea: Shift+Enter for new line, Escape cancels.",
    'cv_wizard.edit.empty_field': "Not added yet",
    'cv_wizard.edit.unsaved_title': "Unsaved Change",
    'cv_wizard.edit.unsaved_body': "You have an unsaved change. What would you like to do?",
    'cv_wizard.edit.unsaved_back': "Go Back",
    'cv_wizard.edit.unsaved_continue': "Continue Without Saving",
    'cv_wizard.edit.hakkimda_counter': "{count}/600 characters",
    'cv_wizard.edit.section.kisisel': "Personal",
    'cv_wizard.edit.section.is_deneyimi': "Work Experience",
    'cv_wizard.edit.section.egitim': "Education",
    'cv_wizard.edit.section.dil': "Languages",
    'cv_wizard.edit.section.hakkimda': "About",
    'cv_wizard.edit.field.name': "Full Name",
    'cv_wizard.edit.field.unvan': "Job Title",
    'cv_wizard.edit.field.hakkimda': "About Me",
    'cv_wizard.edit.field.okul': "School",
    'cv_wizard.edit.field.bolum': "Department",
    'cv_wizard.edit.field.mezuniyet': "Graduation Year",
    'cv_wizard.edit.field.ingilizce_seviye': "English Level",
    'cv_wizard.edit.required': "This field cannot be empty",
    'cv_wizard.edit.mobile_edit_btn': "Edit This Section",
    'cv_wizard.edit.mobile_sheet_close': "Close",
    'cv_wizard.edit.mobile_sheet_save': "Save",
    'cv_wizard.edit.entry.pozisyon': "Position",
    'cv_wizard.edit.entry.sirket': "Company",
    'cv_wizard.edit.entry.tarih': "Dates",

    // ── CV Wizard Faz 4 — A4 CVCanvas ───────────────────────────────────────
    'cv_wizard.canvas.aria_label': "CV preview area",

    // ── CV Wizard Faz 5 — Canvas Inline Edit ────────────────────────────────
    'cv_wizard.canvas.placeholder_adsoyad': "Full name",
    'cv_wizard.canvas.placeholder_unvan': "Job title",
    'cv_wizard.canvas.placeholder_eposta': "Email",
    'cv_wizard.canvas.placeholder_telefon': "Phone",
    'cv_wizard.canvas.placeholder_sehir': "City",
    'cv_wizard.canvas.placeholder_hakkimda': "Write a short summary about yourself...",
    'cv_wizard.canvas.placeholder_mez_yili': "Year",
    'cv_wizard.canvas.placeholder_dil_adi': "Language name",
    'cv_wizard.canvas.placeholder_dil_seviye': "Level",
    'cv_wizard.canvas.empty_deneyim': "No work experience added yet.",
    'cv_wizard.canvas.empty_dil': "No languages added yet.",
    'cv_wizard.canvas.add_deneyim': "+ Add Work Experience",
    'cv_wizard.canvas.add_dil': "+ Add Language",
    'cv_wizard.canvas.aria_madde': "Bullet point",
    // Faz 6 — toolbar + mobile tabs
    'cv_wizard.canvas.toolbar_label': "Template",
    'cv_wizard.canvas.template_classic': "Classic",
    'cv_wizard.canvas.template_modern': "Modern",
    'cv_wizard.canvas.template_minimal': "Minimal",
    'cv_wizard.canvas.mobile_tabs_label': "CV Actions",
    'cv_wizard.canvas.mobile_preview_tab': "Preview",
    'cv_wizard.canvas.mobile_edit_tab': "Edit",

    // ── CV Wizard Faz 7 — Başvuru Bilgileri Dialog (application_info) ────────
    'cv_wizard.application_info.btn_label': "Application Info",
    'cv_wizard.application_info.dialog_title': "Application Background Info",
    'cv_wizard.application_info.mobile_section_label': "Application Info",
    'cv_wizard.application_info.group_a': "Physical Qualifications",
    'cv_wizard.application_info.group_b': "Application History",
    'cv_wizard.application_info.group_c': "Language Experience",
    'cv_wizard.application_info.group_d': "Additional Training",
    'cv_wizard.application_info.group_e': "Work Experience Detail",
    'cv_wizard.application_info.group_f': "Motivation",
    'cv_wizard.application_info.motivation_note': "These answers won't appear on the CV — your instructor uses them for evaluation",
    'cv_wizard.application_info.save': "Save",
    'cv_wizard.application_info.cancel': "Cancel",
    // ── CV Wizard Faz 7 — Canvas new field placeholders ─────────────────────
    'cv_wizard.canvas.placeholder_dogum_tarihi': "Date of birth",
    'cv_wizard.canvas.placeholder_instagram': "@username",
    'cv_wizard.canvas.placeholder_linkedin': "linkedin.com/in/...",
    'cv_wizard.canvas.placeholder_mez_durumu': "Education level",
    'cv_wizard.canvas.placeholder_yabanci_dil': "Foreign language training",
    'cv_wizard.canvas.placeholder_ing_seviye': "Level",
    'cv_wizard.canvas.placeholder_sertifika': "Certificate",
    'cv_wizard.canvas.add_sertifika': "+ Add Certificate",
    'cv_wizard.canvas.empty_sertifikalar': "No certificates added yet.",
    // ── CV Wizard Faz 7 — Canvas edit field labels ───────────────────────────
    'cv_wizard.edit.field.dogum_tarihi': "Date of Birth",
    'cv_wizard.edit.field.instagram': "Instagram",
    'cv_wizard.edit.field.linkedin': "LinkedIn",
    'cv_wizard.edit.field.mez_durumu': "Education Level",
    'cv_wizard.edit.field.devam_eden_egitim': "In Progress",
    'cv_wizard.edit.field.yabanci_dil_egitimi': "Language Training",
    'cv_wizard.edit.field.sertifikalar': "Certificates",
    'cv_wizard.edit.section_toggle_show': "Show in CV",
    'cv_wizard.edit.section_toggle_hide': "Hide from CV",

    // ── CV Wizard Faz 5-6 — Template & Save ─────────────────────────────────
    'cv_wizard.template.subtitle': "Select a layout for your CV",
    'cv_wizard.template.selected_badge': "Selected",
    'cv_wizard.template.names.classic': "Classic",
    'cv_wizard.template.names.modern': "Modern",
    'cv_wizard.template.names.minimal': "Minimal",
    'cv_wizard.template.names.classic_desc': "Traditional layout, ATS-friendly",
    'cv_wizard.template.names.modern_desc': "Two-column visual sidebar",
    'cv_wizard.template.names.minimal_desc': "Clean & simple, high readability",
    'cv_wizard.save.subtitle': "Review your CV and send it to your instructor",
    'cv_wizard.save.preview_title': "CV Summary",
    'cv_wizard.save.template_label': "Template",
    'cv_wizard.save.selected_sections': "{n} section(s) selected",
    'cv_wizard.save.pdf_photo': "Download CV (With Photo)",
    'cv_wizard.save.pdf_no_photo': "Download ATS Version (No Photo)",
    'cv_wizard.save.pdf_disabled': "PDF available after approval",
    'cv_wizard.save.send_btn': "Send to Instructor",
    'cv_wizard.save.send_modal_title': "Send CV to Instructor",
    'cv_wizard.save.send_modal_body': "Your profile will be submitted for review. Your instructor will evaluate it before your interview.",
    'cv_wizard.save.send_modal_cancel': "Cancel",
    'cv_wizard.save.send_modal_confirm': "Send",
    'cv_wizard.save.sending': "Sending...",

    // ── Instructor CV Reviews (F4 — cv-onay) ─────────────────────────────────
    'instructor.cv.page_title': "CV Review Queue",
    'instructor.cv.page_sub': "Review and process student CV profiles",
    'instructor.cv.filter.all': "All",
    'instructor.cv.filter.pending_review': "Awaiting Review",
    'instructor.cv.filter.in_review': "Under Review",
    'instructor.cv.filter.approved': "Approved",
    'instructor.cv.filter.changes_requested': "Changes Requested",
    'instructor.cv.queue.empty': "No CVs found",
    'instructor.cv.queue.empty_sub': "No entries match the current filter.",
    'instructor.cv.queue.loading': "Loading queue...",
    'instructor.cv.queue.error': "Could not load queue",
    'instructor.cv.list.round': "Round {round}",
    'instructor.cv.list.submitted': "Submitted: {date}",
    'instructor.cv.select_prompt': "Select a CV from the list to review",

    'instructor.cv.panel.original_toggle': "Show original",
    'instructor.cv.panel.merged_toggle': "Show merged",
    'instructor.cv.panel.tabs_label': "CV Sections",
    'instructor.cv.panel.section.kisisel': "Personal Info",
    'instructor.cv.panel.section.egitim': "Education",
    'instructor.cv.panel.section.is_deneyimi': "Work Experience",
    'instructor.cv.panel.section.dil': "Languages",
    'instructor.cv.panel.section.motivasyon': "Motivation",
    'instructor.cv.panel.section.assessment': "Assessment",
    'instructor.cv.panel.note_label': "Internal note (not visible to student)",
    'instructor.cv.panel.note_placeholder': "Add notes for internal use...",
    'instructor.cv.panel.message_label': "Message for student",
    'instructor.cv.panel.message_placeholder': "Describe what changes are needed...",
    'instructor.cv.panel.message_required': "A message to the student is required.",
    'instructor.cv.panel.save_edits': "Save Edits",
    'instructor.cv.panel.saving': "Saving...",
    'instructor.cv.panel.saved': "Saved",
    'instructor.cv.panel.view_toggle_label': "View mode",
    'instructor.cv.panel.view_form': "Form View",
    'instructor.cv.panel.view_preview': "CV Preview",

    'instructor.cv.action.claim': "Claim",
    'instructor.cv.action.claiming': "Claiming...",
    'instructor.cv.action.unclaim': "Release",
    'instructor.cv.action.approve': "Approve",
    'instructor.cv.action.approving': "Approving...",
    'instructor.cv.action.request_changes': "Request Changes",
    'instructor.cv.action.requesting': "Sending...",

    // Pasif eylem gerekçeleri — aktif olmayan Kaydet/Onayla/Değişiklik İste/Bırak
    // butonlarının yanında sürekli gösterilir (bkz. InstructorCVReviews.jsx).
    'instructor.cv.action.locked_reason.pending': "Claim the CV first to take this action.",
    'instructor.cv.action.locked_reason.other_reviewer': "This CV is being reviewed by another instructor.",
    'instructor.cv.action.locked_reason.completed': "This review is already complete.",
    'instructor.cv.action.locked_reason.unavailable': "This action isn't available right now.",

    'instructor.cv.status.pending_review': "Awaiting Review",
    'instructor.cv.status.in_review': "Under Review",
    'instructor.cv.status.approved': "Approved",
    'instructor.cv.status.changes_requested': "Changes Requested",

    'instructor.cv.toast.claimed': "CV claimed successfully.",
    'instructor.cv.toast.unclaimed': "CV released.",
    'instructor.cv.toast.approved': "CV approved successfully.",
    'instructor.cv.toast.changes_sent': "Change request sent to student.",
    'instructor.cv.toast.edits_saved': "Edits saved.",
    'instructor.cv.toast.error': "Action failed. Please try again.",
    'instructor.cv.toast.review_forbidden': "You don't have permission to perform this action on this CV.",
    'instructor.cv.toast.invalid_state': "This action isn't available for the CV's current status.",

    // ── F3 Interview Simulation — per-question countdown ─────────────────────
    'interviewSim.progress.questionLabel': "Question {current} / {total}",
    'interviewSim.progress.timeLeft': "remaining",
    'interviewSim.questionTransition.saved': "Answer recorded.",
    'interviewSim.questionTransition.sectionTitle': "Moving to {section}",
    'interviewSim.questionTransition.remaining': "{count} more questions in this section",
    'interviewSim.allQuestionsAnswered.title': "All questions answered",
    'interviewSim.allQuestionsAnswered.sub': "Feel free to keep talking or finish the recording.",
    'interviewSim.allQuestionsAnswered.cta': "Finish Recording",
    'interviewSim.nextQuestion': "Next Question",
    'interviewSim.previousQuestion': "Previous Question",
    'interviewSim.errors.questionsLoadFailed.title': "Questions failed to load",
    'interviewSim.errors.questionsLoadFailed.sub': "Check your connection and try again.",
    'interviewSim.errors.emptyQuestions.title': "No questions found",
    'interviewSim.errors.emptyQuestions.sub': "No questions could be generated for this session. Please start over.",
    'interviewSim.errors.emptyQuestions.cta': "Go Back",
    'interviewSim.sections.personal_intro': "Introduction",
    'interviewSim.sections.motivation': "Motivation",
    'interviewSim.sections.behavioral': "Behavioral",
    'interviewSim.sections.crisis_management': "Crisis Management",
    'interviewSim.sections.english': "English",
    'interviewSim.questionTransition.lastInSection': "Last question in this section",

    // ── Global timer + auto-submit (feature/mulakat-toplam-sure) ─────────────
    'interviewSim.globalTimer.label': "Interview",
    'interviewSim.globalTimer.a11y': "Interview countdown: {{time}}",
    'interviewSim.timeUp.modalTitle': "Time's Up",
    'interviewSim.timeUp.modalBody': "Your interview was automatically saved and submitted.",
    'interviewSim.timeUp.uploadingBody': "Your video is uploading, please wait…",
    'interviewSim.timeUp.errorTitle': "Submission failed",
    'interviewSim.timeUp.errorBody': "Connection lost — please try again.",
    'interviewSim.timeUp.cta': "OK",
    'interviewSim.timeUp.retryCta': "Try Again",
    'interviewSim.progress.questionProgress': "Question progress",

    // ── F2 CV nav (cabin dashboard card) ─────────────────────────────────────
    'cabin.dashboard.cv_card.title': "CV / Candidate Profile",
    'cabin.dashboard.cv_card.sub': "Complete your profile before the interview",
    'cabin.dashboard.cv_card.cta': "Go to CV",

    // ── Bilgi Sınavı kartı (cabin dashboard) ─────────────────────────────────
    'cabin.dashboard.exam_card.title': "Knowledge Test",
    'cabin.dashboard.exam_card.sub': "Take the cabin crew knowledge test and earn your certificate",
    'cabin.dashboard.exam_card.cta': "Start Exam",

    // ── F4 FIX 2 — Legacy v1 criteria labels (5-kriter, /50) ─────────────────
    'instructor.reviews.criteria.communication': "Communication",
    'instructor.reviews.criteria.professionalism': "Professionalism",
    'instructor.reviews.criteria.content': "Content",
    'instructor.reviews.criteria.confidence': "Confidence",
    'instructor.reviews.criteria.overall_impression': "Overall Impression",

    // ── F5 — Öğrenci Hazırlık Raporu (ReadinessReport) ───────────────────────
    // Sayfa başlığı & yönlendirme
    'readinessReport.pageTitle': "My Readiness Report",
    'readinessReport.pageSubtitle': "Assessment report based on your interview simulation",
    'readinessReport.backToHistory': "Interview History",
    // Empty / loading / error states
    'readinessReport.empty.title': "Report Not Ready Yet",
    'readinessReport.empty.sub': "Your interview is being evaluated by your instructor.",
    'readinessReport.empty.no_scored_title': "No Evaluated Interviews Yet",
    'readinessReport.empty.no_scored_sub': "You need at least one instructor-evaluated interview to view your readiness report. Complete a simulation and wait for your evaluation.",
    'readinessReport.empty.pending_title': "Being Evaluated",
    'readinessReport.empty.pending_sub': "Your instructor is reviewing your interview. Once evaluated, your readiness report will be available here.",
    'readinessReport.loading': "Loading your report...",
    'readinessReport.error.title': "Report Could Not Be Loaded",
    'readinessReport.error.sub': "Check your connection and try again.",
    'readinessReport.error.retry': "Retry",
    // Verdict banner
    'readinessReport.verdict.HAZIR.title': "You Are Ready!",
    'readinessReport.verdict.HAZIR.sub': "You have demonstrated the competencies required for a cabin crew interview.",
    'readinessReport.verdict.KOSULLU.title': "Conditionally Ready",
    'readinessReport.verdict.KOSULLU.sub': "You are close to the target level. Strengthening a few areas will take you there.",
    'readinessReport.verdict.DEGIL.title': "A Few More Steps to Go",
    'readinessReport.verdict.DEGIL.sub': "You have strong areas — focusing on your development priorities will get you interview-ready.",
    // Section headings — uppercase baked-in (CSS uppercase removed for TR locale safety)
    'readinessReport.section.overview': "OVERALL ASSESSMENT",
    'readinessReport.section.ccriBreakdown': "CCRI BREAKDOWN",
    'readinessReport.section.arsBreakdown': "ARS BREAKDOWN",
    'readinessReport.section.strengths': "YOUR STRENGTHS",
    'readinessReport.section.development': "YOUR DEVELOPMENT PRIORITIES",
    'readinessReport.section.cv': "CV ASSESSMENT",
    'readinessReport.section.groups': "CAMERA PERFORMANCE & ENGLISH",
    'readinessReport.section.instructorNote': "INSTRUCTOR NOTE",
    'readinessReport.section.reinterview': "RE-INTERVIEW",
    // CCRI & ARS summary cards
    'readinessReport.ccri.title': "CCRI",
    'readinessReport.ccri.subtitle': "CABIN CREW READINESS INDEX",
    'readinessReport.ars.title': "ARS",
    'readinessReport.ars.subtitle': "APPLICANT RATING SCORE",
    'readinessReport.score.outOf100': "out of 100",
    // Tier labels
    'readinessReport.tier.GUCLU': "Strong",
    'readinessReport.tier.ORTA': "Adequate",
    'readinessReport.tier.GELISTIRILECEK': "Needs Work",
    // Score tier labels — 3-band pct rule (shared with mobile, namespace: interview.scoreTier.*)
    'interview.scoreTier.high.label': "Excellent",
    'interview.scoreTier.mid.label': "Needs Improvement",
    'interview.scoreTier.low.label': "Insufficient",
    'interview.scoreTier.high.areaLabel': "Strong",
    'interview.scoreTier.mid.areaLabel': "Needs Improvement",
    'interview.scoreTier.low.areaLabel': "Insufficient",
    // Band a11y (screen reader)
    'readinessReport.band.a11y.ccri': "CCRI score: {{score}} out of 100. Band: {{band}}.",
    'readinessReport.band.a11y.ars': "ARS score: {{score}} out of 100. Band: {{band}}.",
    // Risk / development area severity
    'readinessReport.severity.KRITIK': "Priority",
    'readinessReport.severity.AGIR': "Important",
    'readinessReport.severity.DIGER': "Note",
    // Strengths / development area derived items — uppercase baked-in (TR locale safety)
    'readinessReport.strengths.derived.heading': "STRONGEST AREAS",
    'readinessReport.development.derived.heading': "FOCUS AREAS",
    'readinessReport.strengths.instructor.heading': "INSTRUCTOR OBSERVATION",
    'readinessReport.development.instructor.heading': "INSTRUCTOR RECOMMENDATION",
    // CV assessment labels
    'readinessReport.cv.approved': "CV Approved",
    'readinessReport.cv.pending': "CV Pending Review",
    'readinessReport.cv.notSubmitted': "CV Not Submitted",
    'readinessReport.cv.status': "Status",
    'readinessReport.cv.careerScore': "Career Score",
    // KP / IN group — uppercase baked-in (TR locale safety)
    'readinessReport.group.kp.title': "CAMERA PERFORMANCE GROUP",
    'readinessReport.group.in.title': "ENGLISH GROUP",
    'readinessReport.group.avgPct': "Group Average",
    'readinessReport.group.notApplicable': "Not assessed in this interview",
    // Instructor note
    'readinessReport.instructorNote.scoredBy': "Evaluated by {{name}}",
    'readinessReport.instructorNote.scoredAt': "on {{date}}",
    // Re-interview
    'readinessReport.reinterview.title': "Re-Interview Suggested",
    'readinessReport.reinterview.sub': "Your instructor has suggested you take another simulation.",
    // PDF button
    'readinessReport.pdf.download': "Download PDF Report",
    'readinessReport.pdf.downloading': "Downloading...",
    'readinessReport.pdf.error': "PDF could not be generated. Please try again.",
    'readinessReport.pdf.error_auth': "Session expired. Please log in again.",
    'readinessReport.pdf.error_server': "Server error. Please try again later.",
    // Dashboard entry point
    'readinessReport.entry.title': "Your Readiness Report",
    'readinessReport.entry.available': "View your assessment",
    'readinessReport.entry.pending': "Being evaluated...",

    // ── F2/F3 — Session selector + compare ───────────────────────────────────
    'readinessReport.sessionSelector.label': "Select Session",
    'readinessReport.sessionSelector.latest': "Latest session",
    'readinessReport.compare.title': "Progress Comparison",
    'readinessReport.compare.subtitle': "Previous vs latest interview",
    'readinessReport.compare.ccriLabel': "CCRI Change",
    'readinessReport.compare.arsLabel': "ARS Change",
    'readinessReport.compare.verdictChange': "Status Change",
    'readinessReport.compare.prev': "Previous",
    'readinessReport.compare.latest': "Latest",
    'readinessReport.compare.delta.up': "up by {{n}}",
    'readinessReport.compare.delta.down': "down by {{n}}",
    'readinessReport.compare.delta.same': "No change",
    'readinessReport.compare.empty.single.title': "Complete a second interview to compare",
    'readinessReport.compare.empty.single.sub': "Your first evaluation is ready. Compare your progress after your second interview.",
    'readinessReport.compare.empty.single.cta': "Start Interview",
    'readinessReport.compare.entryBanner.title': "Progress Comparison",
    'readinessReport.compare.entryBanner.sub': "Compare your previous and latest interview results",
    'readinessReport.compare.entryBanner.disabled': "Available after your second scored interview",
    'readinessReport.compare.history.sessionLabel': "Session",
    'readinessReport.compare.regression.major': "There is a significant drop in this area — we recommend consulting your instructor.",
    'readinessReport.compare.regression.minor': "This area fell slightly behind — keep focusing on it.",
    'readinessReport.compare.fetchError': "One or both reports could not be loaded. Please try again.",
    'readinessReport.studentPanelEyebrow': "STUDENT PANEL",
    'readinessReport.empty.not_v3_schema_title': "Session Format Not Supported",
    'readinessReport.empty.not_v3_schema_sub': "This session uses an older format. Complete a new interview to view your updated readiness report.",

    // ===== Landing v2 (aviation navy/gold redesign) =====
    'landing2.common.error': "Something went wrong, please try again.",
    'landing2.common.retry': "Retry",
    'landing2.common.lessonsSuffix': "lessons",
    'landing2.common.studentsSuffix': "students",
    'landing2.common.academyLabel': "Kariyer Online Academy",

    'landing2.hero.emptyTitle': "Courses coming soon here",

    // ---- Hero slider v2 (3 colored slides, edX/Harvard-style) ----
    'landing2.hero.slide1.eyebrow': "Turkey's Aviation Academy",
    'landing2.hero.slide1.titleLine1': "Lift your",
    'landing2.hero.slide1.titleHighlight': "career skyward.",
    'landing2.hero.slide1.body': "Train with expert instructors, earn a MoNE-approved certificate, and step confidently into the industry with AI-powered interview practice.",
    'landing2.hero.slide1.cta': "Explore Programs",

    'landing2.hero.slide2.eyebrow': "AI-Powered Interview Prep",
    'landing2.hero.slide2.titleLine1': "Walk into your",
    'landing2.hero.slide2.titleHighlight': "interview ready.",
    'landing2.hero.slide2.body': "Practice real interview scenarios with an AI-powered assistant, get instant feedback, and prepare for your cabin crew interview with real confidence.",
    'landing2.hero.slide2.cta': "Start Practicing",
    'landing2.hero.slide2.note': "Your first session is free.",

    'landing2.hero.slide3.eyebrow': "New & Popular Lessons",
    'landing2.hero.slide3.titleLine1': "This season's lessons",
    'landing2.hero.slide3.titleHighlight': "are here.",
    'landing2.hero.slide3.body': "Browse the newly added lessons across aviation, language, and career tracks, pick the program that fits you best, and start learning today.",
    'landing2.hero.slide3.cta': "Browse Lessons",

    // ---- Küratörlü hero kartları (landing-v2-duzeltmeler madde 4/6) ----
    'landing2.hero.curated.groundHostess.title': "Ground Hostess Training",
    'landing2.hero.curated.cabinSim.title': "Mock-up Cabin Simulation Training",
    'landing2.hero.curated.aiLiteracy.title': "AI Literacy",
    'landing2.hero.curated.exploreFallback.title': "Explore Programs",

    'landing2.hero.nav.prevSlide': "Previous slide",
    'landing2.hero.nav.nextSlide': "Next slide",
    'landing2.hero.nav.goToSlideLabel': "Go to slide",
    'landing2.hero.nav.carouselRoleDesc': "carousel",
    'landing2.hero.nav.slideRoleDesc': "slide",

    'landing2.marquee.item1': "MONE-APPROVED PRIVATE EDUCATION INSTITUTION",
    'landing2.marquee.item2': "AI-POWERED INTERVIEW PRACTICE",
    'landing2.marquee.item3': "INDUSTRY EXPERT INSTRUCTORS",
    'landing2.marquee.item4': "REAL INTERVIEW SIMULATIONS",
    'landing2.marquee.item5': "MONE-APPROVED CERTIFICATE",
    'landing2.marquee.item6': "CABIN CREW TRAINING PROGRAM",

    'landing2.trending.titleStart': "Trending on",
    'landing2.trending.titleHighlight': "Kariyer Online",
    'landing2.trending.tab.aviation': "Aviation",
    'landing2.trending.tab.languages': "Languages",
    'landing2.trending.tab.career': "Career",
    'landing2.trending.tab.health': "Health",
    'landing2.trending.tab.tech': "Technology",
    'landing2.trending.tab.general': "Program",
    'landing2.trending.tab.all': "All Programs",
    'landing2.trending.levelAll': "All",
    'landing2.trending.emptyTitle': "New content is coming soon to this category",
    'landing2.trending.featureBadge': "Feature",
    'landing2.trending.featureInterviewTitle': "Interview Simulation",
    'landing2.trending.featureInterviewDesc': "Test yourself with AI-powered interview practice",
    'landing2.trending.featureCvTitle': "CV & Career Prep",
    'landing2.trending.featureCvDesc': "Build an aviation-ready CV and prepare to apply",

    'landing2.popular.title': "Popular right now",
    'landing2.popular.viewAll': "View all",
    'landing2.popular.empty': "No courses to show yet",
    'landing2.popular.curatedSubtitle': "Explore program",

    'landing2.curriculum.eyebrow': "Curriculum",
    'landing2.curriculum.title': "Training content",
    'landing2.curriculum.subtitle': "The real modules of our Cabin Crew program — covering everything from aviation terminology to interview practice.",
    'landing2.curriculum.mod1.bullet1': "Overview of the aviation industry and airline operations",
    'landing2.curriculum.mod1.bullet2': "Get to know career opportunities in the industry",
    'landing2.curriculum.mod2.bullet1': "Learn airline recruitment processes and expectations",
    'landing2.curriculum.mod2.bullet2': "Prepare for interview questions and scenarios",
    'landing2.curriculum.mod3.bullet1': "Learn in-cabin duties and responsibilities",
    'landing2.curriculum.mod3.bullet2': "Understand flight operation procedures",
    'landing2.curriculum.mod4.bullet1': "Learn passenger satisfaction and service standards",
    'landing2.curriculum.mod4.bullet2': "Develop professional communication techniques",
    'landing2.curriculum.mod5.bullet1': "Build decision-making skills for crisis moments",
    'landing2.curriculum.mod5.bullet2': "Learn crew cooperation and emergency management",
    'landing2.curriculum.card6.title': "CV, Interview & Career Prep",
    'landing2.curriculum.card6.bullet1': "Aviation-ready CV builder wizard",
    'landing2.curriculum.card6.bullet2': "AI-powered interview simulation",
    'landing2.curriculum.card6.bullet3': "Instant feedback and progress tracking",

    'landing2.formats.title': "Choose the format that fits you",
    'landing2.formats.subtitle': "Chart your path based on how you want to learn.",
    'landing2.formats.card1.title': "Individual Lessons",
    'landing2.formats.card1.desc': "Learn any topic, anytime you want.",
    'landing2.formats.card1.meta1': "Learn at your own pace",
    'landing2.formats.card1.meta2': "Earn a certificate",
    'landing2.formats.card1.meta3': "Quick access",
    'landing2.formats.card1.cta': "Explore Lessons",
    'landing2.formats.card2.title': "Certificate Program",
    'landing2.formats.card2.desc': "Combine lessons and complete the Cabin Crew program end-to-end.",
    'landing2.formats.card2.meta1': "MoNE-approved certificate",
    'landing2.formats.card2.meta2': "105 video lessons",
    'landing2.formats.card2.meta3': "In-depth mastery",
    'landing2.formats.card2.cta': "View the Program",
    'landing2.formats.card3.title': "Interview Simulation",
    'landing2.formats.card3.desc': "Practice real interviews with AI.",
    'landing2.formats.card3.meta1': "Unlimited practice",
    'landing2.formats.card3.meta2': "Instant feedback",
    'landing2.formats.card3.meta3': "Build confidence",
    'landing2.formats.card3.cta': "Try the Simulation",
    'landing2.formats.card4.title': "CV & Career Prep",
    'landing2.formats.card4.desc': "Build an aviation-ready CV and get ready to apply.",
    'landing2.formats.card4.meta1': "Step-by-step CV wizard",
    'landing2.formats.card4.meta2': "A profile that stands out to HR",
    'landing2.formats.card4.meta3': "Application strategy",
    'landing2.formats.card4.cta': "Start Your CV",

    'landing2.roadmap.eyebrow': "Step by Step",
    'landing2.roadmap.title': "Your journey from sign-up to career",
    'landing2.roadmap.subtitle': "From your first lesson to the interview, we're with you at every step.",
    'landing2.roadmap.step1.title': "Start Learning",
    'landing2.roadmap.step1.desc': "Log in to the program that fits you and begin your learning journey.",
    'landing2.roadmap.step2.title': "Watch Video Lessons",
    'landing2.roadmap.step2.desc': "Complete lessons prepared by expert instructors at your own pace.",
    'landing2.roadmap.step3.title': "Track Your Progress",
    'landing2.roadmap.step3.desc': "Progress module by module on your roadmap, earning badges and points.",
    'landing2.roadmap.step4.title': "Practice Interviews",
    'landing2.roadmap.step4.desc': "Develop your interview skills with AI-powered simulation.",
    'landing2.roadmap.step5.title': "Get Your Certificate",
    'landing2.roadmap.step5.desc': "Earn your MoNE-approved, CPD-registered certificate at the end of the program.",
    'landing2.roadmap.step6.title': "Submit Your Applications",
    'landing2.roadmap.step6.desc': "Strengthen your CV, apply to job postings, and attend interviews.",
    'landing2.roadmap.step7.title': "Launch Your Career",
    'landing2.roadmap.step7.desc': "Feel ready to start your career with the skills and confidence you've gained.",

    'landing2.testimonials.title': "Why students love it",
    'landing2.testimonials.t1.role': "Cabin Crew Candidate",
    'landing2.testimonials.t1.quote': "Thanks to the interview simulation, I wasn't nervous at all in my real interview. I went in prepared.",
    'landing2.testimonials.t2.role': "Career Changer",
    'landing2.testimonials.t2.quote': "I came from a different industry and thought I was too late. The step-by-step roadmap made everything clear.",
    'landing2.testimonials.t3.role': "Aviation English Student",
    'landing2.testimonials.t3.quote': "I thought my English was a barrier. Lessons that progressed in small steps put me at ease.",
    'landing2.testimonials.t4.role': "Recent Graduate",
    'landing2.testimonials.t4.quote': "Even after finishing the course, I can go back and rewatch the lessons — that's great.",

    'landing2.faq.title': "Frequently asked questions",
    'landing2.faq.q1': "What is Kariyer Online Aviation Academy?",
    'landing2.faq.a1': "We are an official private education institution based in Ankara Çankaya, affiliated with the Ministry of National Education. We provide professional training in aviation and career development, with a focus on cabin crew.",
    'landing2.faq.q2': "My height/weight is borderline — am I eligible?",
    'landing2.faq.a2': "Height, reach and physical criteria vary by airline. Our training content explains these criteria in detail and which airlines prioritize what.",
    'landing2.faq.q3': "Is my English level sufficient?",
    'landing2.faq.a3': "English expectations vary by airline. Our English & Communication module starts from basic patterns — you may need less English than you think to get started.",
    'landing2.faq.q4': "Which airline is right for me?",
    'landing2.faq.a4': "Every airline has its own criteria. Our Turkish Airlines and World Airlines modules teach you to evaluate your profile using up-to-date industry knowledge.",
    'landing2.faq.q5': "How does the interview simulation work?",
    'landing2.faq.a5': "You practice with real interview questions through AI-powered simulation, get instant feedback, and work on body language and stress management.",
    'landing2.faq.q6': "Do I keep access after finishing the program?",
    'landing2.faq.a6': "You keep access to the courses you enrolled in through the platform, and you can rewatch lessons anytime.",

    'landing2.whyUs.eyebrow': "Why they choose us",
    'landing2.whyUs.title': "Feel the difference",
    'landing2.whyUs.statBadge': "MoNE-Approved Private Education Institution",
    'landing2.whyUs.statNote': "An official private education institution based in Ankara Çankaya.",
    'landing2.whyUs.card1.title': "MoNE-Approved Certificate",
    'landing2.whyUs.card1.desc': "When you complete your training, you receive an official, CPD-registered certificate.",
    'landing2.whyUs.card2.title': "Up-to-date Industry Knowledge",
    'landing2.whyUs.card2.desc': "Content aligned with airlines' current criteria.",
    'landing2.whyUs.card3.title': "Real Interview Simulations",
    'landing2.whyUs.card3.desc': "Turn theory into practice and overcome your nerves.",
    'landing2.whyUs.card4.title': "AI-Powered Interview Practice",
    'landing2.whyUs.card4.desc': "Improve yourself with voice practice and instant feedback.",

    'landing2.finalCta.title': "The career of your dreams is one click away.",
    'landing2.finalCta.subtitle': "Take the first step today.",
    'landing2.finalCta.ctaPrimary': "Start for Free",
    'landing2.finalCta.ctaSecondary': "Log In",
    'landing2.finalCta.statsLessons': "Lessons",

    'landing2.footer.interviewSim': "Interview Simulation",

    // DÜZELTME (reviewer MERGE BLOKERİ): "(2 Levels)" ibaresi kaldırıldı — DB'de
    // bu kategoride gerçek bir "2 seviye" yapısı yok, var olmayan ürün vaadiydi.
    'landing2.trending.langFrench.title': "French",
    'landing2.trending.langArabic.title': "Arabic",

    'landing2.curriculum.fallback.mod1.title': "Core Cabin Crew Fundamentals",
    'landing2.curriculum.fallback.mod1.bullet1': "Aviation terminology and operational logic",
    'landing2.curriculum.fallback.mod1.bullet2': "In-cabin duties and responsibilities",
    'landing2.curriculum.fallback.mod1.bullet3': "Passenger service standards",
    'landing2.curriculum.fallback.mod1.bullet4': "Basic emergency procedures",
    'landing2.curriculum.fallback.mod2.title': "Cabin Personality & Interview Prep",
    'landing2.curriculum.fallback.mod2.bullet1': "Cabin personality fit logic and evaluation criteria",
    'landing2.curriculum.fallback.mod2.bullet2': "Interview simulations (HR Q&A)",
    'landing2.curriculum.fallback.mod2.bullet3': "Diction, body language and first-impression management",
    'landing2.curriculum.fallback.mod2.bullet4': "Effective communication techniques under stress",
    'landing2.curriculum.fallback.mod3.title': "Cabin Communication in English",
    'landing2.curriculum.fallback.mod3.bullet1': "Core aviation English phrases",
    'landing2.curriculum.fallback.mod3.bullet2': "Passenger welcome and service dialogues",
    'landing2.curriculum.fallback.mod3.bullet3': "Emergency communication phrases",
    'landing2.curriculum.fallback.mod3.bullet4': "Fluent, clear speaking practice",
    'landing2.curriculum.fallback.mod4.title': "Crisis & Stress Management",
    'landing2.curriculum.fallback.mod4.bullet1': "Sound decision-making under pressure",
    'landing2.curriculum.fallback.mod4.bullet2': "Difficult passenger management techniques",
    'landing2.curriculum.fallback.mod4.bullet3': "In-flight crisis scenarios",
    'landing2.curriculum.fallback.mod4.bullet4': "Building calm and professional reflexes",
    'landing2.curriculum.fallback.mod5.title': "Service & Passenger Experience",
    'landing2.curriculum.fallback.mod5.bullet1': "The \"experience, not just service\" approach",
    'landing2.curriculum.fallback.mod5.bullet2': "Passenger satisfaction psychology",
    'landing2.curriculum.fallback.mod5.bullet3': "In-cabin communication style and tone",
    'landing2.curriculum.fallback.mod5.bullet4': "Brand representation awareness",

    // Documents — upload redirect (Kariyer Paneli)
    'documents.redirect.title': "Document upload has moved",
    'documents.redirect.body': "You can no longer upload documents here — please use Kariyer Paneli instead.",
    'documents.redirect.cta': "Open Kariyer Paneli",
    'documents.redirect.ariaLabel': "Open Kariyer Paneli (opens in new tab)",
    'documents.redirect.note': "Documents you uploaded before are still safe.",
    'documents.redirect.thirdParty': "Kariyer Paneli is an independent third-party service, separate from Academy Pro. Documents you upload there are processed under that platform's own privacy policy.",

    // Admin — Dijital Rozet, KVKK deletion queue summary. Visible even when the
    // integration is disabled (DIJITAL_ROZET_ENABLED=false), because that's
    // exactly when the queue can grow unnoticed — a real KVKK compliance risk,
    // not just a nice-to-have counter.
    'admin.dijitalRozet.silmeKuyrugu.title': "KVKK Deletion Queue",
    'admin.dijitalRozet.silmeKuyrugu.error': "Couldn't load the deletion queue summary.",
    'admin.dijitalRozet.silmeKuyrugu.retry': "Try again",
    'admin.dijitalRozet.silmeKuyrugu.emptyTitle': "Queue is clear",
    'admin.dijitalRozet.silmeKuyrugu.emptyDescription': "No pending KVKK deletion requests.",
    'admin.dijitalRozet.silmeKuyrugu.pendingBadge': "{{count}} pending",
    'admin.dijitalRozet.silmeKuyrugu.oldestAgeBadge': "oldest: {{days}} days",
    'admin.dijitalRozet.silmeKuyrugu.failedBadge': "{{count}} failed",
    'admin.dijitalRozet.silmeKuyrugu.thresholdWarning': "The oldest request has been waiting {{days}} days, past the {{threshold}}-day limit. Under KVKK (Türkiye's data protection law), deletion requests must be resolved within 30 days — this limit is currently exceeded because the Digital Badge worker is disabled.",
    'admin.dijitalRozet.silmeKuyrugu.pendingNote': "These records stay on the Ministry's system until the Digital Badge worker runs (currently disabled).",
    'admin.dijitalRozet.silmeKuyrugu.failedWarning': "{{count}} record(s) failed permanently and need manual review.",

    // Muhasebe — AccountingTab (Faz 3.8, plan §11). Ekran bilesenleri henuz
    // yazilmadi (Faz 3.6, sonraki tur); bu anahtarlar altyapi olarak eklendi.
    'accounting.tabs.summary': "Summary",
    'accounting.tabs.debt_ledger': "Debt & Collection",
    'accounting.tabs.promissory_notes': "Promissory Notes",
    'accounting.tabs.documents': "Documents",

    'accounting.summary.mixed_down_payment': "Mixed/Down Payment",
    'accounting.summary.empty_title': "No payment history",
    'accounting.summary.empty_description': "No installment or payment records for this student.",
    'accounting.summary.installments_heading': "Installments",
    'accounting.summary.due_label': "Due",
    'accounting.summary.installment_unit': "installment",
    'accounting.summary.payment_history_heading': "Payment History",
    'accounting.payment_status.completed': "Completed",
    'accounting.payment_status.pending': "Pending",
    'accounting.payment_status.failed': "Failed",
    'accounting.payment_status.refunded': "Refunded",
    'accounting.payment_status.cancelled': "Cancelled",

    'accounting.status.pending': "Pending",
    'accounting.status.due': "Due Today",
    'accounting.status.overdue': "Overdue",
    'accounting.status.overdue_warn': "Final Notice",
    'accounting.status.interest': "Accruing Interest",
    'accounting.status.legal': "Legal Action",
    'accounting.status.paid': "Paid",
    'accounting.status.cancelled': "Cancelled",
    'accounting.status.split': "Split",
    'accounting.status.merged': "Merged",
    'accounting.status.collected': "Collected",

    'accounting.columns.course': "Course",
    'accounting.columns.debt_date': "Debt Date",
    'accounting.columns.debt_amount': "Debt Amount",
    'accounting.columns.remaining': "Remaining",
    'accounting.columns.status': "Status",
    'accounting.columns.action': "Action",
    'accounting.columns.payment_date': "Payment Date",
    'accounting.columns.payment_amount': "Payment Amount",
    'accounting.columns.promissory_note_no': "Promissory Note No",
    'accounting.columns.recorded_by': "Recorded By",
    'accounting.columns.parasut_status': "Paraşüt Invoice",
    'accounting.columns.net_settled_amount': "Net Settled Amount",
    'accounting.columns.vat_amount': "VAT Amount",
    'accounting.columns.amount_excl_vat': "Amount Excl. VAT",
    // FAZ 1-B (Seçenek C, 2026-08-24) — collection_rows[].interest_amount_row,
    // installment tahsilatlarında dolu. columnDefs.js "optional" (Sütun Seç).
    'accounting.columns.interest_amount': "Interest Amount",
    'accounting.columns.bank': "Bank",
    'accounting.columns.notes': "Notes",
    'accounting.columns.kind': "Payment Type",

    'accounting.column_selector.label': "Select Columns",
    'accounting.details_popover.label': "Details",

    'accounting.actions.collect': "Collect Payment",
    'accounting.actions.reverse_payment': "Reverse Payment",
    'accounting.actions.issue_invoice': "Issue Invoice",
    'accounting.actions.print_notice': "Print Notice",
    'accounting.actions.print_statement': "Print Statement",

    'accounting.notes_tabs.split_merge': "Split/Merge Note",
    'accounting.notes_tabs.add': "Add Note",
    'accounting.notes_tabs.cancel': "Cancel Note",
    'accounting.notes_tabs.legal_ops': "Legal Operations",
    'accounting.notes_tabs.edit': "Edit Note",

    'accounting.fields.net_settled_amount.label': "Net Settled Amount",
    'accounting.fields.net_settled_amount.help': "The net amount that reaches our account after bank/POS commission is deducted. Correct manually if it differs.",
    'accounting.fields.net_settled_amount.tooltip': "Example: if the student paid ₺1,000 by credit card and the POS commission was ₺25, the net settled amount is ₺975.",

    'accounting.reverse_payment.title': "Reverse Payment",
    'accounting.reverse_payment.warning': "This action cannot be undone — a new reversing entry is created, the existing record is not deleted.",
    'accounting.reverse_payment.reason_label': "Reason",
    'accounting.reverse_payment.reason_placeholder': "Why are you reversing this transaction?",
    'accounting.reverse_payment.reason_min_error': "Reason must be at least 10 characters.",
    'accounting.reverse_payment.confirm_button': "Reverse",
    'accounting.reverse_payment.second_confirm_title': "Are you sure?",
    'accounting.reverse_payment.second_confirm_desc': "This action cannot be undone. Are you sure you want to continue?",
    'accounting.reverse_payment.payment_summary': "{amount} · {date} collection",
    'accounting.reverse_payment.reason_counter': "{count}/10 characters",
    'accounting.reverse_payment.continue_button': "Continue",
    'accounting.reverse_payment.submitting': "Submitting…",
    'accounting.reverse_payment.request_success': "Reversal request created — it will complete once another admin approves it.",
    'accounting.reverse_payment.pending_badge': "Pending Approval",
    'accounting.reverse_payment.pending_list_label': "Pending Approval ({count})",
    'accounting.reverse_payment.pending_list_empty': "No pending reversal requests.",
    'accounting.reverse_payment.reversed_label': "Reversed",
    'accounting.reverse_payment.approve_button': "Approve",
    'accounting.reverse_payment.reject_button': "Reject",
    'accounting.reverse_payment.approve_success': "Reversal request approved — the payment has been reversed.",
    'accounting.reverse_payment.reject_success': "Reversal request rejected.",
    'accounting.reverse_payment.note_linked_warning': "This collection closed a linked promissory note. Reversing it will reopen the installment and return the note to active status.",
    'accounting.reverse_payment.direct_note_warning': "This is the direct collection of a promissory note. Reversing it will return the note to active status.",
    'accounting.reverse_payment.moka_warning': "This collection was made through Moka. Reversing it only corrects the record — the payment in Moka is not refunded; process the refund from the Moka panel.",
    'accounting.reverse_payment.parasut_invoice_warning': "A Paraşüt sales invoice has already been issued for this collection. Reversing it will NOT automatically cancel or correct that invoice — a manual credit note may be required in Paraşüt.",

    // Accounting FAZ 4 (round 2, 2026-09-29) — Paraşüt invoice status badge
    'accounting.parasut_status.pending': "Waiting to be sent",
    'accounting.parasut_status.in_progress': "Sending",
    'accounting.parasut_status.awaiting_job': "Paraşüt is processing",
    'accounting.parasut_status.done': "Completed",
    'accounting.parasut_status.failed': "Failed",
    'accounting.parasut_status.missing_tax_number': "Missing student's national ID",
    'accounting.parasut_status.missing_product_mapping': "Missing product mapping",
    'accounting.parasut_status.missing_account_mapping': "Missing account mapping",
    'accounting.parasut_status.blocked_waiting': "Waiting on a previous step",
    'accounting.parasut_status.needs_verification': "Needs review",
    'accounting.parasut_status.unknown': "Status unclear",
    'accounting.parasut_status.cancelled': "Cancelled",
    'accounting.parasut_status.refunded_badge': "Marked refunded",
    'accounting.parasut_status.failed_hint': "The draft invoice could not be created.",
    'accounting.parasut_status.go_to_outbox': "Review in invoice queue",
    'accounting.parasut_status.retry_note_label': "Note (required)",
    'accounting.parasut_status.retry_note_required': "A note is required to retry.",
    'accounting.parasut_status.retry_button': "Resend",
    'accounting.parasut_status.retry_pending': "Resending…",
    'accounting.parasut_status.retry_success': "Resent — status will update shortly.",
    'accounting.parasut_status.manual_credit_note_warning': "This collection was reversed/refunded but the Paraşüt invoice was not automatically cancelled — you need to issue a manual credit note in Paraşüt.",
    'accounting.parasut_status.mark_refunded_note_label': "Note (required)",
    'accounting.parasut_status.mark_refunded_note_required': "A note is required to mark this as refunded.",
    'accounting.parasut_status.mark_refunded_button': "Mark as refunded",
    'accounting.parasut_status.mark_refunded_pending': "Marking…",
    'accounting.parasut_status.mark_refunded_success': "Marked as refunded.",
    'accounting.parasut_status.missing_tc_top_warning': "This student is missing a T.C. ID number — a Paraşüt contact record and automatic invoice cannot be created.",
    'accounting.parasut_status.missing_tc_goto_profile': "Go to Identity tab",

    'accounting.errors.odeme_zaten_geri_alindi': "This payment has already been reversed.",
    'accounting.errors.ters_kayit_geri_alinamaz': "A reversing entry cannot itself be reversed.",
    'accounting.errors.ters_kayit_gerekce_zorunlu': "A reason is required.",
    'accounting.errors.maker_checker_ihlali': "You created this request; it must be approved by a different admin.",

    'accounting.empty.title': "No records",
    'accounting.empty.description': "No accounting records found for this student.",
    'accounting.error.title': "Something went wrong",
    'accounting.error.description': "Could not load accounting data. Please try again.",
    'accounting.error.retry': "Try again",

    // Muhasebe FAZ 3.6 — DebtLedgerTable / CollectionForm (AccountingTab)
    'accounting.methods.cash': "Cash",
    'accounting.methods.bank_transfer': "Bank Transfer",
    'accounting.methods.postal': "Postal/EFT",
    'accounting.methods.credit_card': "Credit Card",

    'accounting.kind.manual': "Manual Collection",
    'accounting.kind.down_payment': "Down Payment",
    'accounting.kind.sale': "Cash Sale",
    'accounting.kind.installment': "Installment",
    'accounting.kind.promissory': "Promissory Note",
    'accounting.kind.refund': "Refund",
    'accounting.kind.reversal': "Reversing Entry",

    'accounting.collect_form.title': "Collect Payment",
    'accounting.collect_form.subtitle': "Record a manual collection or down payment for this student.",
    'accounting.collect_form.amount_label': "Amount",
    'accounting.collect_form.bank_label': "Bank",
    'accounting.collect_form.bank_placeholder': "Select bank...",
    'accounting.collect_form.bank_required_hint': "Required for every payment method.",
    'accounting.collect_form.method_label': "Payment Method",
    'accounting.collect_form.kind_label': "Collection Type",
    'accounting.collect_form.course_label': "Course",
    'accounting.collect_form.course_general': "General",
    'accounting.collect_form.muhasebe_tipi_label': "Accounting Type",
    'accounting.collect_form.muhasebe_tipi_none': "None",
    'accounting.collect_form.vat_override_label': "VAT Amount (manual override)",
    'accounting.collect_form.vat_override_help': "Optional. Cannot exceed the payment amount.",
    'accounting.collect_form.description_label': "Note",
    'accounting.collect_form.description_placeholder': "Payment note...",
    'accounting.collect_form.submit': "Save Collection",
    'accounting.collect_form.submitting': "Saving…",
    'accounting.collect_form.success': "Collection recorded",
    'accounting.collect_form.errors.amount_invalid': "Enter an amount greater than zero.",
    'accounting.collect_form.errors.bank_required': "Bank selection is required.",
    'accounting.collect_form.errors.vat_exceeds_amount': "VAT amount cannot exceed the payment amount.",
    // F1-B fix — client-side mirror of the backend's `tahsilat_tutari_kalani_asiyor`
    // check, when the row-level "Collect" was opened from a specific invoice
    // (row_type="invoice") debt row. Amount is pre-filled with the invoice's
    // remaining balance but stays editable; this prevents a round trip to the
    // server just to find out the amount is too large.
    'accounting.collect_form.errors.amount_exceeds_invoice_remaining': "The amount cannot exceed the debt record's remaining balance.",
    'accounting.collect_form.invoice_amount_hint': "Pre-filled with the debt record's remaining balance ({remaining}). Editable, but cannot exceed it.",

    // F1 fix (fix/tahsilat-taksit-baglama) — installment mode of CollectionForm
    // (row-level "Collect" on an installment row, POST /admin/installments/
    // {id}/collect). Amount is fixed to the installment's remaining balance
    // and shown read-only — partial collection is not supported by the API.
    'accounting.collect_form.installment_title': "Collect Installment",
    'accounting.collect_form.installment_subtitle': "The full installment amount will be collected.",
    'accounting.collect_form.installment_amount_readonly_hint': "The full installment amount is collected; partial collection is not supported.",

    // Backend error_code -> friendly message (add-payment / ledger). Fallback:
    // if a code isn't listed here, getApiErrorMessage() uses the backend's own
    // message_tr/message_en directly (see accountingErrorMessage helper).
    'accounting.errors.manuel_odeme_tutari_gecersiz': "Enter a valid amount.",
    'accounting.errors.manuel_odeme_banka_zorunlu': "A bank must be selected for manual collection/down payment.",
    'accounting.errors.banka_bulunamadi': "Selected bank not found or inactive.",
    'accounting.errors.muhasebe_tipi_gecersiz': "Invalid accounting type.",
    'accounting.errors.manuel_odeme_kdv_tutari_gecersiz': "Invalid VAT amount.",
    'accounting.errors.manuel_odeme_istek_govdesi_gecersiz': "Invalid request body.",
    'accounting.errors.ogrenci_bulunamadi': "Student not found.",

    // F1-B fix (fix/tahsilat-taksit-baglama, reviewer High finding) —
    // manual collection on a taksitsiz (row_type="invoice") open debt row
    // now targets an explicit invoice_id instead of silently picking the
    // oldest open invoice.
    'accounting.errors.fatura_secimi_gerekli': "This student has more than one open invoice. Select the invoice to collect.",
    'accounting.errors.tahsilat_tutari_kalani_asiyor': "The collection amount cannot exceed the invoice's remaining balance.",
    'accounting.errors.manuel_odeme_fatura_bulunamadi': "The specified invoice does not belong to this student or was not found.",
    'accounting.errors.manuel_odeme_fatura_acik_degil': "The specified invoice is not open (collectible).",

    // FAZ 1 satış sadeleştirme (2026-09-25) — 5 satış formu ortak metinleri.
    // Bank/account seçimi + kart-vadeli bilgi notu + taksit önizleme
    // (components/sales/InstallmentPreview.jsx). Backend error_code'ları
    // (satis_pos_hesabi_gecersiz, satis_tahsilat_banka_zorunlu, banka_bulunamadi,
    // tutar_gecersiz, taksit_sayisi_gecersiz, pesinat_negatif,
    // pesinat_toplami_asiyor, gecersiz_ilk_taksit_tarihi,
    // ilk_taksit_tarihi_gecmis, ilk_taksit_tarihi_cok_ileri) burada AYRI
    // anahtar olarak listelenmez — backend her hatada message_tr/message_en
    // döner ve getApiErrorMessage() bunu doğrudan kullanır (bkz. yukarıdaki
    // "Fallback" notu, satır 5193-5195); bu form'lar zaten hep bu deseni
    // kullanıyor (toast.error(getApiErrorMessage(err, lang))).
    'sales.bank_label': "Collection Account",
    'sales.bank_placeholder': "Select cash/bank account...",
    'sales.bank_required_hint': "Required for cash payment collected at sale time (POS accounts are not listed — card payments are collected later via the Moka payment link).",
    'sales.bank_none_available': "No active cash/bank account found. An admin must add one first.",
    'sales.card_deferred_notice': "Card payment is collected later via a payment link, not at sale time.",
    'sales.installmentPreview.title': "Installment Schedule (preview)",
    'sales.installmentPreview.loading': "Calculating…",
    'sales.installmentPreview.col_no': "Installment",
    'sales.installmentPreview.col_due_date': "Due Date",
    'sales.installmentPreview.col_amount': "Amount",
    'sales.installmentPreview.total': "Total",
    // FAZ 1 düzeltme turu — madde F2 (2026-09-25): düzenlenebilir taksit planı.
    'sales.installmentPreview.reset_btn': "Split equally",
    'sales.installmentPreview.diff_label': "Remaining difference",
    'sales.installmentPreview.diff_warning': "The installment amounts do not add up to the sale total. Clear the difference before saving.",
    // Madde 6 — kayıtlı öğrenci aynı kursu tekrar seçerse anlık uyarı.
    'sales.course_already_enrolled': "This student is already enrolled in this course.",

    // FAZ 1 karma ödeme turu (2026-09-25) — components/sales/PaymentPlanFields.jsx.
    'sales.paymentPlan.down_payment_title': "Down Payment",
    'sales.paymentPlan.down_payment_amount_label': "Down Payment Amount (optional)",
    'sales.paymentPlan.down_payment_method_label': "Down Payment Method",
    'sales.paymentPlan.method_cash': "Cash",
    'sales.paymentPlan.method_bank_transfer': "Bank Transfer",
    'sales.paymentPlan.down_payment_date_label': "Down Payment Date",
    'sales.paymentPlan.down_payment_danisman_note': "The admin enters this in Accounting once the down payment is actually collected from the student.",
    'sales.paymentPlan.remaining_title': "Remaining Amount",
    'sales.paymentPlan.remaining_amount_label': "Remaining Amount",
    'sales.paymentPlan.remaining_method_label': "Remaining Payment Method",
    'sales.paymentPlan.remaining_method_bank_transfer': "Bank Transfer Installments",
    'sales.paymentPlan.remaining_method_credit_card': "Credit Card (Moka)",
    'sales.paymentPlan.installments_count_label': "Number of Installments",
    'sales.paymentPlan.first_due_date_label': "First Due Date",
    'sales.paymentPlan.card_due_date_label': "Charge Date",
    'sales.paymentPlan.card_notice': "The remaining amount is sent to Moka as a single charge; the student chooses installments on Moka's own payment page.",
    'sales.paymentPlan.full_down_payment_notice': "Down payment equals the total amount — the remaining section is hidden.",

    // FAZ 1 karma ödeme turu (2026-09-25, madde 3) — danışman formu artık
    // ücretli katalogdan (GET /admin/certificates/types) okur.
    'sales.certificate_type_label': "Certificate Type",
    'sales.certificate_fee_included_label': "Price includes certificate fee",
    'sales.certificate_fee_included_hint': "When checked, the course price already covers the certificate exam fee, so it is not added to the total. When unchecked, the fee is added.",

    'accounting.debt_ledger.total_debt': "Total Debt",
    'accounting.debt_ledger.total_collected': "Total Collected",
    'accounting.debt_ledger.balance': "Remaining Balance",
    // B2 fix
    'accounting.debt_ledger.collect_blocked_terminal': "This debt row is cancelled or already paid; it cannot be collected.",

    // F1 fix (fix/tahsilat-taksit-baglama) — the general "Collect" button (not
    // tied to a specific row) used to silently attach the payment to the
    // oldest open invoice. If the student has open installment rows, it now
    // asks which one to collect first instead of guessing.
    'accounting.debt_ledger.pick_installment_title': "Select an installment",
    'accounting.debt_ledger.pick_installment_hint': "This student has open installments. Select the one to collect.",
    'accounting.debt_ledger.pick_installment_label': "Installment {no}/{total}",

    // F1-B fix (fix/tahsilat-taksit-baglama) — the general "Collect" button's
    // picker now also lists taksitsiz (row_type="invoice") open debt rows,
    // not just installments. Picking one opens the form pre-bound to that
    // invoice_id instead of letting the backend guess the oldest open invoice.
    'accounting.debt_ledger.pick_debt_row_title': "Select a debt row",
    'accounting.debt_ledger.pick_debt_row_hint': "This student has open debt rows. Select the one to collect.",
    'accounting.debt_ledger.pick_invoice_label': "Debt Record",

    // FAZ 1-B (Seçenek B/C, 2026-08-24) — negative balance (student is a
    // creditor after a refund) and interest collected shown separately from
    // the totals. Shared between DebtLedgerSubTab.jsx and
    // AccountingSummarySubTab.jsx so both screens read the same label.
    'accounting.debt_ledger.balance_creditor': "Refund Owed to Student",
    'accounting.debt_ledger.total_interest': "Interest Collected",
    'accounting.debt_ledger.interest_hint': "Not included in Total Collected — actual cash received equals Collected + Interest.",

    // "Özet" alt sekmesi (AccountingSummarySubTab) — invoice_billed/collected/outstanding kartlari.
    // debt_ledger.* ile ayni yazi degil cunku veri kaynagi farkli (fatura ozeti, ledger degil).
    'accounting.summary.total_billed': "Total Billed",

    'accounting.promissory_notes.empty_title': "No promissory notes",
    'accounting.promissory_notes.empty_description': "No promissory notes recorded for this student.",
    'accounting.promissory_notes.status_active': "Active",
    'accounting.promissory_notes.no_number': "No note number",

    'accounting.docs.empty_title': "Documents coming soon",
    'accounting.docs.empty_description': "Invoice, notice and statement PDF history will appear here in a future update.",

    // ── Muhasebe FAZ 4.8 — senet yaşam döngüsü (5 iç sekme) ───────────────────
    'accounting.status.collecting': "Collection in progress",
    'accounting.status.splitting': "Split in progress",
    'accounting.status.merging': "Merge in progress",

    'accounting.notes.select_placeholder': "Select a note...",
    'accounting.notes.note_required': "Select a promissory note.",
    'accounting.notes.submitting': "Saving…",
    'accounting.notes.irreversible': "This action cannot be undone.",
    'accounting.notes.confirm_continue': "Yes, continue",
    'accounting.notes.reconcile_title': "Operation left unfinished",
    'accounting.notes.reconcile_desc': "The note was left in a temporary state and accounting reconciliation is required. Actions are disabled until it is resolved.",
    'accounting.notes.amount_label': "Amount",
    'accounting.notes.amount_invalid': "Enter an amount greater than zero.",
    'accounting.notes.due_label': "Due Date",
    'accounting.notes.due_invalid': "Due date must be in YYYY-MM-DD format.",
    'accounting.notes.number_label': "Note Number",
    'accounting.notes.number_hint': "Optional. Left blank, the system assigns one.",
    'accounting.notes.course_label': "Course",
    'accounting.notes.bank_label': "Bank",
    'accounting.notes.bank_hint': "Optional here, but required when the note is collected.",
    'accounting.notes.bank_none': "None",
    'accounting.notes.banks_error': "Bank list could not be loaded. You can still save; a bank is required at collection time.",
    'accounting.notes.description_label': "Note",
    'accounting.notes.description_placeholder': "Optional note...",

    'accounting.notes.split.mode': "Split",
    'accounting.notes.split.title': "Split Promissory Note",
    'accounting.notes.split.description': "Splits one active note (and its linked installment) into 2–60 new notes.",
    'accounting.notes.split.note_label': "Note to split",
    'accounting.notes.split.no_active_notes': "There is no active note that can be split.",
    'accounting.notes.split.count_label': "Number of parts",
    'accounting.notes.split.count_hint': "Must be between 2 and 60.",
    'accounting.notes.split.count_error': "Enter a part count between 2 and 60.",
    'accounting.notes.split.manual_label': "I will enter the amounts manually",
    'accounting.notes.split.equal_hint': "Amounts are divided equally; the entire rounding remainder is added to the LAST part.",
    'accounting.notes.split.preview_title': "Part preview",
    'accounting.notes.split.part': "Part",
    'accounting.notes.split.due_label': "Due date",
    'accounting.notes.split.amount_label': "Amount",
    'accounting.notes.split.total_label': "Total",
    'accounting.notes.split.due_required': "A due date is required for every part.",
    'accounting.notes.split.amount_required': "Enter an amount for every part (or switch back to equal split).",
    'accounting.notes.split.total_mismatch': "The sum of the parts does not equal the note amount.",
    'accounting.notes.split.submit': "Split Note",
    'accounting.notes.split.confirm_title': "Split this note?",
    'accounting.notes.split.confirm_description': "The note and its linked installment will be replaced by the new parts.",
    'accounting.notes.split.success': "Note split into parts.",

    'accounting.notes.merge.mode': "Merge",
    'accounting.notes.merge.title': "Merge Promissory Notes",
    'accounting.notes.merge.description': "Consolidates several active notes on the same invoice into a single note.",
    'accounting.notes.merge.select_label': "Notes to merge",
    'accounting.notes.merge.select_hint': "Only active notes belonging to the same invoice can be merged; the list narrows after the first selection.",
    'accounting.notes.merge.no_eligible': "There is no active note that can be merged.",
    'accounting.notes.merge.min_error': "Select at least 2 notes.",
    'accounting.notes.merge.total_label': "Total to be merged",
    'accounting.notes.merge.due_label': "New due date",
    'accounting.notes.merge.due_hint': "Pre-filled with the latest due date of the selected notes — change it if needed. The system never picks a date on its own.",
    'accounting.notes.merge.due_required': "A new due date is required.",
    'accounting.notes.merge.due_invalid': "Due date must be in YYYY-MM-DD format.",
    'accounting.notes.merge.past_warning_title': "Due date in the past",
    'accounting.notes.merge.past_warning': "You picked a past date: the merged note will count as overdue the moment it is created. You can still continue.",
    'accounting.notes.merge.submit': "Merge Notes",
    'accounting.notes.merge.confirm_title': "Merge these notes?",
    'accounting.notes.merge.confirm_description': "The selected notes and their installments will be consolidated into a single note.",
    'accounting.notes.merge.success': "Notes merged.",

    'accounting.notes.add.title': "Add Promissory Note",
    'accounting.notes.add.description': "Records a manual promissory note against one of the student's debt records.",
    'accounting.notes.add.no_invoice': "This student has no debt record yet. A promissory note must be linked to a debt record, so it cannot be created.",
    'accounting.notes.add.invoice_label': "Debt Record",
    'accounting.notes.add.invoice_hint': "Required: the collection of the note is applied to this debt record.",
    'accounting.notes.add.invoice_placeholder': "Select debt record...",
    'accounting.notes.add.invoice_required': "Debt record selection is required.",
    'accounting.notes.add.submit': "Save Note",
    'accounting.notes.add.success': "Promissory note created.",
    'accounting.notes.add.installment_word': "Installment",
    'accounting.notes.add.installment_label': "Installment link (optional)",
    'accounting.notes.add.installment_hint': "The debt lives on the installment; the note is its legal representation. Only open installments of the selected debt record that are not already represented by another note are listed.",
    'accounting.notes.add.installment_placeholder': "Do not link to an installment",
    'accounting.notes.add.installment_pick_invoice': "Select a debt record first: installments are listed per debt record.",
    'accounting.notes.add.installment_none_eligible': "There is no linkable installment on this invoice. Closed (paid/cancelled) installments and installments already represented by an active note are not listed.",
    'accounting.notes.add.installment_interest_title': "Installment(s) hidden because of accrued interest",
    'accounting.notes.add.installment_interest_desc': "installment(s) on this invoice have accrued late interest, so they are not listed. The note amount would equal the principal only and the interest would be silently lost; reconcile the interest first.",
    'accounting.notes.add.unlinked_title': "This note will be unlinked",
    'accounting.notes.add.unlinked_desc': "This note will not be linked to any installment; the debt cannot be tracked through an installment.",
    'accounting.notes.add.linked_title': "Linked to the installment",
    'accounting.notes.add.linked_desc': "The amount and the due date were filled in from the installment. The note represents this installment; when it is collected, the installment is closed.",
    'accounting.notes.add.amount_locked_hint': "Locked because an installment is selected: the note amount must equal the installment amount. Clear the installment selection to enter an amount by hand.",
    'accounting.notes.add.due_locked_hint': "Taken from the installment and locked: the note due date must match the installment due date exactly.",

    'accounting.notes.cancel.title': "Cancel Promissory Note",
    'accounting.notes.cancel.description': "Cancels an active note with a mandatory reason. The note is not deleted.",
    'accounting.notes.cancel.effect_title': "What cancelling does — and does not do",
    'accounting.notes.cancel.effect_desc': "The debt is NOT cleared: the linked installment stays open and collectable. Only the note is voided, and the interest exemption it granted to the installment is lifted, so the installment becomes subject to normal late interest.",
    'accounting.notes.cancel.result_title': "Note cancelled",
    'accounting.notes.cancel.result_exemption_lifted': "The interest exemption on the linked installment has been lifted; the installment stays open and is now subject to normal late interest.",
    'accounting.notes.cancel.result_no_installment': "The note was cancelled. No linked installment was found, so no interest exemption had to be lifted.",
    'accounting.notes.cancel.note_label': "Note to cancel",
    'accounting.notes.cancel.no_active_notes': "There is no active note that can be cancelled.",
    'accounting.notes.cancel.reason_label': "Reason",
    'accounting.notes.cancel.reason_hint': "Stored in the audit trail; write why the note is being voided.",
    'accounting.notes.cancel.reason_placeholder': "Why is this note being cancelled?",
    'accounting.notes.cancel.reason_required': "A reason is required to cancel a note.",
    'accounting.notes.cancel.submit': "Cancel Note",
    'accounting.notes.cancel.confirm_title': "Cancel this note?",
    'accounting.notes.cancel.confirm_description': "The note will be voided with your reason; the installment debt stays open.",
    'accounting.notes.cancel.success': "Note cancelled.",

    'accounting.notes.legal.title': "Legal Operations",
    'accounting.notes.legal.description': "Transfers the selected notes to a legal case (enforcement file).",
    'accounting.notes.legal.select_label': "Notes to transfer",
    'accounting.notes.legal.select_hint': "Only active notes can be transferred; they all belong to this student.",
    'accounting.notes.legal.no_active_notes': "There is no active note that can be transferred.",
    'accounting.notes.legal.min_error': "Select at least one note.",
    'accounting.notes.legal.total_label': "Total to be transferred",
    'accounting.notes.legal.total_hint': "Shown for information only — the debt amount is calculated by the server from the selected notes and cannot be entered manually.",
    'accounting.notes.legal.lawyers_error': "Lawyer list could not be loaded. You can continue without assigning a lawyer.",
    'accounting.notes.legal.lawyer_label': "Lawyer",
    'accounting.notes.legal.lawyer_hint': "Optional; can be assigned later.",
    'accounting.notes.legal.lawyer_none': "Not assigned",
    'accounting.notes.legal.interest_label': "Interest amount",
    'accounting.notes.legal.fees_label': "Legal fees",
    'accounting.notes.legal.amount_invalid': "Enter zero or a positive amount.",
    'accounting.notes.legal.notes_label': "Case note",
    'accounting.notes.legal.notes_placeholder': "Opening note for the case file...",
    'accounting.notes.legal.submit': "Transfer to Legal",
    'accounting.notes.legal.confirm_title': "Transfer notes to legal?",
    'accounting.notes.legal.confirm_description': "A legal case will be opened and the selected notes will move to the legal state.",
    'accounting.notes.legal.success': "Notes transferred to legal.",

    'accounting.notes.edit.title': "Edit Promissory Note",
    'accounting.notes.edit.description': "Edits an existing note or marks it as collected.",
    'accounting.notes.edit.note_label': "Note",
    'accounting.notes.edit.locked_title': "This note is no longer active",
    'accounting.notes.edit.locked_desc': "Amount and due date are locked because the note is part of the accounting chain. Only the note text can be edited.",
    'accounting.notes.edit.field_locked_hint': "Locked — editable only while the note is active.",
    'accounting.notes.edit.installment_locked_hint': "Locked — this note represents an installment; changing the amount would change the debt.",
    'accounting.notes.edit.collect_locked_hint': "Locked while collecting — the collected amount is the recorded amount of the note.",
    'accounting.notes.edit.installment_locked_title': "Amount/due date locked",
    'accounting.notes.edit.installment_locked_desc': "This note is the legal representation of an installment. Changing the amount or the due date means changing the debt itself, so it cannot be done here: use split or merge, which update the note and the installment together.",
    'accounting.notes.edit.delete_result_title': "Result of the deletion",
    'accounting.notes.edit.delete_result_exemption_lifted': "Promissory note deleted (record retained). The interest exemption on the linked installment has been lifted; the installment stays open and is now subject to normal late interest.",
    'accounting.notes.edit.delete_result_no_installment': "Promissory note deleted (record retained). No linked installment was found, so no interest exemption had to be lifted.",
    'accounting.notes.edit.collect_label': "Mark as collected",
    'accounting.notes.edit.collect_warning': "Collecting records a payment and applies it to the invoice. It cannot be undone from this screen.",
    'accounting.notes.edit.bank_required': "A bank must be selected to collect the note.",
    'accounting.notes.edit.paid_date_label': "Payment date",
    'accounting.notes.edit.collect_confirm_title': "Mark this note as collected?",
    'accounting.notes.edit.collect_confirm_description': "A payment will be recorded and applied to the invoice.",
    'accounting.notes.edit.delete': "Delete",
    'accounting.notes.edit.delete_confirm_title': "Delete this note?",
    'accounting.notes.edit.delete_confirm_description': "Deleting leaves no reason in the record — prefer cancelling with a reason.",
    'accounting.notes.edit.delete_success': "Note deleted.",
    'accounting.notes.edit.cancel_note': "Cancel Note",
    'accounting.notes.edit.cancel_unavailable_hint': "Only an active note can be cancelled; this one is already part of the accounting chain.",
    'accounting.notes.edit.submit': "Save",
    'accounting.notes.edit.success': "Note updated.",

    // FAZ 4 backend error_code -> mesaj (getApiErrorMessage fallback'i yerine)
    'accounting.errors.senet_bulunamadi': "Promissory note not found.",
    'accounting.errors.senet_durum_gecisi_gecersiz': "This operation is not allowed in the note's current state.",
    'accounting.errors.senet_alani_kilitli': "Amount and due date cannot be changed once the note is no longer active.",
    'accounting.errors.senet_tutar_gecersiz': "Invalid note amount.",
    'accounting.errors.senet_invoice_id_zorunlu': "An invoice must be selected for the note.",
    'accounting.errors.senet_invoice_bulunamadi': "Selected invoice not found or deleted.",
    'accounting.errors.senet_invoice_kullanici_uyusmuyor': "The invoice belongs to a different student.",
    'accounting.errors.senet_tahsilat_banka_zorunlu': "A bank must be selected to collect the note.",
    'accounting.errors.senet_silinemez': "A note in this state cannot be deleted; cancel it with a reason instead.",
    'accounting.errors.senet_iptal_gerekce_zorunlu': "A reason is required to cancel a note.",
    'accounting.errors.senet_parcalama_adet_gecersiz': "Split requires between 2 and 60 parts.",
    'accounting.errors.senet_parcalama_parca_gecersiz': "One of the parts is invalid.",
    'accounting.errors.senet_parcalama_vade_zorunlu': "A due date is required for every part.",
    'accounting.errors.senet_parcalama_tutar_eksik': "Amounts must be provided for all parts or for none.",
    'accounting.errors.senet_parcalama_toplam_uyusmuyor': "The sum of the parts does not equal the note amount.",
    'accounting.errors.senet_taksidi_bulunamadi': "The installment linked to the note was not found.",
    'accounting.errors.senet_taksit_durumu_uygun_degil': "The linked installment is not in a suitable state for this operation.",
    'accounting.errors.senet_taksit_tutari_uyusmuyor': "The note amount and the linked installment amount do not match.",
    'accounting.errors.senet_taksit_kapatilamadi': "The operation was left unfinished and accounting reconciliation is required.",
    'accounting.errors.senet_taksit_baglantisi_tutarsiz': "The note/installment links are inconsistent. Select notes that are either all linked to one installment, or all unlinked.",
    'accounting.errors.senet_taksit_faturasi_uyusmuyor': "The linked installments belong to different invoices.",
    'accounting.errors.senet_installment_id_gecersiz': "The installment link is invalid.",
    'accounting.errors.senet_taksit_kullanici_uyusmuyor': "The installment belongs to a different student; the note cannot be linked to it.",
    'accounting.errors.senet_taksit_faiz_tahakkuk_etmis': "The installment has accrued late interest; reconcile the interest before linking a note to it.",
    'accounting.errors.senet_taksidi_zaten_bagli': "This installment is already represented by an active note; a second note cannot be created for the same installment.",
    'accounting.errors.senet_birlestirme_adet_gecersiz': "Merge requires between 2 and 60 notes.",
    'accounting.errors.senet_birlestirme_tekrarli_id': "The same note cannot be submitted more than once.",
    'accounting.errors.senet_birlestirme_durum_gecersiz': "Only active notes can be merged.",
    'accounting.errors.senet_birlestirme_fatura_uyusmuyor': "Only notes belonging to the same invoice can be merged.",
    'accounting.errors.senet_birlestirme_kullanici_uyusmuyor': "Only notes belonging to the same student can be merged.",
    'accounting.errors.senet_birlestirme_vade_zorunlu': "A new due date is required for the merged note.",
    'accounting.errors.senet_birlestirme_vade_gecersiz': "Due date must be in YYYY-MM-DD format.",
    'accounting.errors.senet_icra_id_zorunlu': "At least one note is required for legal transfer.",
    'accounting.errors.senet_icra_tekrarli_id': "The same note cannot be submitted more than once.",
    'accounting.errors.senet_icra_kullanici_uyusmuyor': "All notes must belong to the same student.",
    'accounting.errors.legal_case_validation_error': "The legal case could not be created with the given values.",
    // FAZ 4 PAKET I/K — sikilastirilan sozlesmenin yeni/degisen kodlari
    'accounting.errors.senet_taksite_bagli_alan_duzenlenemez': "This note represents an installment; its amount and due date cannot be edited here. Use split or merge so the debt and the note change together.",
    'accounting.errors.senet_tahsilatinda_alan_duzenlenemez': "Amount, due date, invoice or installment cannot be sent together with a collection request. Correct the field first, then collect.",
    'accounting.errors.senet_tahsilati_devam_ediyor': "A collection is already running for this note, or a previous attempt was left unfinished; a second collection cannot start. Reconcile the accounting first.",
    'accounting.errors.taksit_senedi_tahsil_ediliyor': "The note linked to this installment is currently being collected; wait for that collection to finish.",
    'accounting.errors.taksit_senedi_durumu_degisti': "The note linked to this installment was changed by another operation in the meantime; no collection was made. Refresh and try again.",
    'accounting.errors.taksit_senedi_icrada': "The note linked to this installment has been transferred to enforcement; the collection must be made through the enforcement file.",
    'accounting.errors.taksit_senedi_zaten_tahsil_edildi': "This debt has already been collected through the promissory note; collecting the installment as well would record the same money twice.",
    'accounting.errors.taksit_tahsilat_banka_zorunlu': "A bank/cash account must be selected to collect an installment.",
    'accounting.errors.taksit_tahsilat_tutari_eslesmiyor': "The collected amount must equal the installment amount (interest included). Partial collection is not supported.",
    // F1 fix (fix/tahsilat-taksit-baglama) — row-level "Collect" button on an
    // installment row now calls POST /admin/installments/{id}/collect directly
    // (CollectionForm.jsx installment mode) instead of add-payment.
    'accounting.errors.taksit_tahsilat_tutari_gecersiz': "Enter a valid amount.",
    'accounting.errors.taksit_odeme_yontemi_gecersiz': "Invalid payment method.",
    'accounting.errors.taksit_net_tutari_gecersiz': "Invalid net settled amount.",
    'accounting.errors.taksit_kdv_tutari_gecersiz': "Invalid VAT amount.",
    'accounting.errors.taksit_durumu_tahsilata_kapali': "This installment cannot be collected in its current state.",
    'accounting.errors.senet_vade_gecersiz': "Due date is required and must be in YYYY-MM-DD format.",
    'accounting.errors.senet_alan_tipi_gecersiz': "One of the submitted fields has an invalid type.",
    'accounting.errors.senet_parcalama_vade_gecersiz': "The due date of one of the parts is invalid; use YYYY-MM-DD format.",
    'accounting.errors.senet_parcalama_tutar_cok_kucuk': "The note amount is too small to be split into this many parts.",
    'accounting.errors.senet_birlestirme_id_gecersiz': "The note ids submitted for merging are invalid.",
    'accounting.errors.senet_icra_id_gecersiz': "The note ids submitted for enforcement transfer are invalid.",
    'accounting.errors.senet_taksit_vadesi_uyusmuyor': "The note due date must be exactly the same as the due date of the linked installment.",
    'accounting.errors.senet_disi_taksit_karisimi': "Installments that are not represented by the selected notes cannot be added to the same file; open a separate file for them.",
    'accounting.errors.senet_sahiplik_uyusmuyor': "The notes do not belong to this student.",
    'accounting.errors.missing_debt_reference': "At least one installment or promissory note must be selected to open an enforcement file.",
    'accounting.errors.invalid_id_list': "The submitted id list is invalid. Refresh the list and select again.",
    'accounting.errors.unknown_with_code': "The operation could not be completed ({code}). Please try again; if it persists, share this code with support.",

    // === FAZ5 (brüt/net) i18n — başlangıç ===
    'accounting.collect_form.net_settled_amount_help': "The amount you entered is the gross amount the student paid. After the POS fee, the amount that reaches the bank may be lower; leave this blank to use the gross amount.",
    'accounting.collect_form.net_settled_amount_help_emphasis': "Commission is still calculated on the gross amount.",
    'accounting.collect_form.errors.net_settled_amount_invalid': "Enter a valid amount (cannot be negative).",
    'accounting.collect_form.errors.net_settled_amount_exceeds_amount': "The net settled amount cannot exceed the amount entered (gross).",
    // === FAZ5 (brüt/net) i18n — bitiş ===

    // === POS aktarılan tutar zorunluluğu i18n — başlangıç ===
    'accounting.collect_form.net_settled_amount_required_label': "Transferred Amount (required for POS)",
    'accounting.collect_form.net_settled_amount_optional_hint': "Leave blank to use the paid amount.",
    'accounting.collect_form.errors.net_settled_amount_required_pos': "Transferred amount is required for POS collections.",
    'accounting.collect_form.errors.net_settled_amount_zero': "Transferred amount must be greater than zero.",
    'accounting.collect_form.commission_label': "Commission",
    // === POS aktarılan tutar zorunluluğu i18n — bitiş ===
    // === BELGELER i18n — başlangıç ===
    'accounting.docs.invoice_pdf.label': "Download Sale Record PDF",
    'accounting.docs.invoice_pdf.aria_label': "Download sale record PDF — {course}",
    'accounting.docs.invoice_pdf.loading': "Preparing…",
    'accounting.docs.invoice_pdf.success': "Sale record PDF downloaded.",
    'accounting.docs.invoice_pdf.error': "Couldn't download the sale record PDF. Please try again.",
    'accounting.docs.invoice_pdf.disabled_tooltip': "No sale record is linked to this row.",
    'accounting.docs.statement_pdf.label': "Download Statement PDF",
    'accounting.docs.statement_pdf.aria_label': "Download account statement PDF",
    'accounting.docs.statement_pdf.loading': "Preparing…",
    'accounting.docs.statement_pdf.success': "Statement PDF downloaded.",
    'accounting.docs.statement_pdf.error': "Couldn't download the statement PDF. Please try again.",
    'accounting.docs.notice_pdf.label': "Download Notice PDF",
    'accounting.docs.notice_pdf.aria_label': "Download notice letter PDF",
    'accounting.docs.notice_pdf.loading': "Checking…",
    'accounting.docs.notice_pdf.success': "Notice PDF downloaded.",
    'accounting.docs.notice_pdf.error': "Couldn't download the notice PDF. Please try again.",
    'accounting.docs.notice_pdf.no_case_title': "No legal case on file",
    'accounting.docs.notice_pdf.no_case_desc': "A notice can only be generated from a legal case. Open a legal case for this student first.",
    'accounting.docs.notice_pdf.no_case_link': "Go to Legal & Lawyers",
    'accounting.docs.notice_pdf.picker_title': "Which legal case?",
    'accounting.docs.notice_pdf.picker_hint': "This student has more than one legal case — choose one.",
    // === BELGELER i18n — bitiş ===

  },
  tr: {
    // Nav
    home: "Ana Sayfa",
    courses: "Kurslar",
    dashboard: "Panel",
    profile: "Profil",
    login: "Giriş Yap",
    register: "Kayıt Ol",
    logout: "Çıkış Yap",
    search: "Kurs ara...",

    // Profil — Bildirim Tercihleri (KVKK / iletişim izni)
    'profile.notifPrefs.tabLabel': "Bildirimler",
    'profile.notifPrefs.title': "Bildirim Tercihleri",
    'profile.notifPrefs.subtitle': "Sana nasıl ulaşabileceğimizi seç. Eğitim sürecinle ilgili hizmet mesajları varsayılan olarak açıktır; tanıtım amaçlı mesajlar yalnızca açık onayınla gönderilir.",
    'profile.notifPrefs.serviceTitle': "Hizmet Bildirimleri",
    'profile.notifPrefs.serviceDesc': "Ders hatırlatmaları, mülakat sonuçları, ödeme ve hesap bilgilendirmeleri. İstersen bunları kapatabilirsin.",
    'profile.notifPrefs.serviceEmailLabel': "E-posta ile hizmet bildirimleri",
    'profile.notifPrefs.serviceWhatsappLabel': "WhatsApp ile hizmet bildirimleri",
    'profile.notifPrefs.commercialTitle': "Tanıtım Amaçlı İletiler",
    'profile.notifPrefs.commercialDesc': "Kampanya, indirim ve yeni kurs duyuruları. Bunlar varsayılan olarak kapalıdır ve yalnızca açık onayınla gönderilir.",
    'profile.notifPrefs.commercialEmailLabel': "E-posta ile tanıtım iletileri",
    'profile.notifPrefs.commercialWhatsappLabel': "WhatsApp ile tanıtım iletileri",
    'profile.notifPrefs.consentNote': "Bu onayı istediğin zaman geri çekebilirsin — kurslara erişimini veya destek almanı etkilemez.",
    'profile.notifPrefs.inAppNote': "Uygulama içi bildirimler (zil ikonunda gördüğün ders hatırlatmaları gibi) her zaman açıktır ve kapatılamaz — bunlar hizmetin ayrılmaz bir parçasıdır.",
    'profile.notifPrefs.updatedAt': "Son güncelleme: {{date}}",
    'profile.notifPrefs.loadError': "Bildirim tercihlerin yüklenemedi.",
    'profile.notifPrefs.retry': "Tekrar dene",
    'profile.notifPrefs.saveSuccess': "Tercih kaydedildi",
    'profile.notifPrefs.saveError': "Tercihin kaydedilemedi. Lütfen tekrar dene.",

    // Profil — "Gizlilik" sekmesi (Dijital Rozet rızasını barındırır; ileride
    // başka gizlilik ayarları da bu sekmeye eklenebilir, o yüzden genel isim).
    'profile.privacyTab.label': "Gizlilik",

    // Profil — Dijital Rozet rızası, Gizlilik sekmesi.
    // TASLAK METIN — KVKK hukuki incelemesi bekliyor. Prod'a cikmadan once
    // hukuk onayi ZORUNLU. Rıza kullanıcı seviyesinde tutulur
    // (users.dr_share_consent), sertifika bazında DEĞİL — tek bir toggle
    // öğrencinin tüm sertifikalarını kapsar (bkz. GET/PUT
    // /users/me/dijital-rozet-consent).
    'profile.drConsent.title': "Dijital Rozet Paylaşımı",
    'profile.drConsent.subtitle': "Sertifikalarını T.C. Sanayi ve Teknoloji Bakanlığı'nın resmi Dijital Rozet sistemiyle doğrulanabilir hale getir.",
    'profile.drConsent.description': "Bu ayarı açtığında, tamamladığın kurslara ait ad, e-posta ve eğitim bilgilerin T.C. Sanayi ve Teknoloji Bakanlığı'nın Dijital Rozet platformuna aktarılır. Bakanlık bu bilgilerle sana devlet onaylı, doğrulanabilir bir dijital rozet ve doğrulama bağlantısı üretir. Bu bağlantıyı LinkedIn'de veya iş başvurularında paylaşabilirsin. Onayını istediğin zaman geri çekebilirsin.",
    'profile.drConsent.dataItem.name': "Ad Soyad",
    'profile.drConsent.dataItem.email': "E-posta adresi",
    'profile.drConsent.dataItem.courses': "Tamamladığın kurs ve sertifika bilgileri",
    'profile.drConsent.toggleLabel': "Bilgilerimi Dijital Rozet için Bakanlığa aktar",
    'profile.drConsent.note': "Bu onayı istediğin zaman geri çekebilirsin. Geri çekmek, gelecekteki sertifikalarının Bakanlığa gönderilmesini durdurur; kurslara erişimini etkilemez.",
    'profile.drConsent.withdrawTitle': "Onayını geri çekmek istiyor musun?",
    'profile.drConsent.withdrawBody': "Bunu kapatırsan gelecekteki sertifikaların Bakanlığa gönderilmez. Daha önce yayınlanmış rozetler Bakanlık sisteminde kalmaya devam eder — bunların kaldırılması bu ayardan bağımsız, ayrı bir taleple yapılır.",
    'profile.drConsent.withdrawConfirm': "Onayı geri çek",
    'profile.drConsent.withdrawCancel': "Vazgeç",
    'profile.drConsent.loadError': "Dijital Rozet paylaşım tercihin yüklenemedi.",
    'profile.drConsent.retry': "Tekrar dene",
    'profile.drConsent.saveSuccessOn': "Dijital Rozet paylaşımı açıldı",
    'profile.drConsent.saveSuccessOff': "Dijital Rozet paylaşımı kapatıldı",
    'profile.drConsent.saveError': "Tercihin kaydedilemedi. Lütfen tekrar dene.",
    'profile.drConsent.updatedAt': "Son güncelleme: {{date}}",
    // TASLAK METIN — KVKK hukuki incelemesi bekliyor. Prod'a cikmadan once hukuk onayi ZORUNLU.
    // Hesap silme (DELETE /users/me) sonrasinda da Bakanliga aktarilmis rozet kaydinin ayri bir
    // surec oldugunu acikca belirtir — "otomatik silinir" gibi yanlis bir vaat ICERMEZ.
    // DUZELTME (2026-09-01): EN taraftaki ayni anahtarin ustundeki yorumdaki gerekce gecerlidir
    // — eski TR metin de "yalnızca talep etmen hâlinde başlatılır" diyordu, bu YANLISTI.
    // profile.deleteAccount.bulletMinistry ile hizalandi.
    'profile.drConsent.accountDeletionTitle': "Hesabını silersen rozetine ne olur?",
    'profile.drConsent.accountDeletionBody': "Sertifikaların T.C. Sanayi ve Teknoloji Bakanlığı'nın Dijital Rozet platformuna aktarılmışsa, hesabını sildiğinde bu kayıtlar için otomatik olarak bir geri çekme talebi oluşturulur; ancak bu talep henüz işletilmediği için kayıtlar Bakanlık sisteminde kalmaya devam eder.",

    // Sınav — final sınavı başlamadan önce (intro fazında, POST
    // .../final-exam/start ÇAĞRILMADAN önce) gösterilen opsiyonel Dijital
    // Rozet rıza adımı. Hukuki metin yukarıdaki profile.drConsent.*'tan
    // aynen kullanılır; sadece bu 5 anahtar yenidir.
    'exam.drConsent.title': "Sertifikan Dijital Rozet'e dönüşsün mü?",
    'exam.drConsent.intro': "Bu tercih opsiyoneldir, sınavını etkilemez. Geçersen sertifikan her durumda hesabına tanımlanır.",
    'exam.drConsent.declineButton': "Şimdi değil",
    'exam.drConsent.confirmButton': "Onaylıyorum",
    'exam.drConsent.declineHint': "Bunu istediğin zaman profilinden açabilirsin — sertifikanı etkilemez.",

    // Profil — "Hesabını Sil" bölümü, Gizlilik sekmesi (DijitalRozetConsentSection'ın
    // altında render edilir). Backend davranışı (routes/auth.py DELETE /users/me
    // + services/account_deactivation.py): hesap ANINDA pasife alınır
    // (account_status="deactivated") ve kişisel veriler 72 saat içinde GERÇEKTEN
    // silinir. 30 günlük geri alma penceresi yok.
    // BEŞ istisna — biri bile yazılmazsa EKSİK İFŞA olur, o yüzden tek tek sayılır:
    //   1. Ödeme / taksit / senet / fatura VE icra kayıtları yasal saklama süresi
    //      boyunca saklanır VE üzerlerindeki ad, adres, TCKN/VKN maskelenmez
    //      (VUK m. 253 ve 256, TTK m. 82, TBK m. 146).
    //   2. Onaylanan mesafeli satış sözleşmesinin kopyası, içindeki kimlik
    //      bilgileri ve onay anının IP adresiyle birlikte saklanır.
    //   3. Sertifika kayıtları SÜRESİZ saklanır; /verify/{kod} giriş gerektirmez,
    //      yani bağlantıya sahip herkes sertifika sahibinin adını görmeye devam eder.
    //   4. Bakanlıktaki Dijital Rozet kopyası BUGÜN geri çekilmiyor: silme yalnızca
    //      geri çekme talebi kuyruğa alır, akış henüz devrede değil.
    //   5. Öğrenci olunmadan önceki lead kayıtları (ad soyad, telefon, danışman
    //      görüşme notları) hesap silme kapsamının dışındadır.
    // Copy kuralı: silme gerçek, o yüzden söylenir — ama 72 saat GECİKMELİ
    // olduğu için GEÇMİŞ ZAMAN KİPİ ("silindi") kullanılmaz; "silinir /
    // silinecek ... en geç 3 gün içinde" denir.
    'profile.deleteAccount.title': "Hesabını Sil",
    'profile.deleteAccount.subtitle': "Hesabın anında kapanır, kişisel verilerin silinir ve bir daha giriş yapamazsın. Bu işlemi kendin geri alamazsın.",
    'profile.deleteAccount.bulletNoLogin': "Erişimin hemen sona erer: onayladığın an oturumun kapanır; e-posta ve şifreyle de Google ile de bir daha giriş yapamazsın.",
    'profile.deleteAccount.bulletDeleted': `Kişisel verilerin silinir: profilin, notların, favorilerin, quiz oturumların, sınav denemelerin, ses kayıtların, mülakat transkriptlerin, bildirimlerin ve sağlık ile adli sicil beyanların en geç ${ACCOUNT_DELETION_GRACE_DAYS} gün içinde silinir. Aşağıdaki maddelerde sayılan kayıtlar ise silinmez.`,
    'profile.deleteAccount.bulletRetained': "Ödeme ve icra kayıtların silinmez: ödeme, taksit, senet, fatura ve icra kayıtların, üzerlerindeki ad, adres ve TCKN/VKN bilgisiyle birlikte yasal saklama süresi boyunca saklanır. Vergi Usul Kanunu ve Türk Ticaret Kanunu bunu zorunlu kılıyor: faturanın kime kesildiği ve borç–alacak ilişkisi ispatlanabilir kalmalı.",
    'profile.deleteAccount.bulletContract': "Sözleşme kaydın saklanır: kayıt sırasında onayladığın mesafeli satış sözleşmesinin kopyası, içindeki ad soyad, e-posta, telefon ve adres bilgisiyle ve onayı verdiğin andaki IP adresiyle birlikte saklanır.",
    'profile.deleteAccount.bulletCertificates': "Sertifika kayıtların süresiz saklanır: hesabın silindikten sonra da sertifikan geçerli kalır. Doğrulama bağlantısını açan herkes adını, kurs adını ve veriliş tarihini görmeye devam eder; bu kayıt için bir süre sınırı yok.",
    'profile.deleteAccount.bulletMinistry': "Bakanlıktaki kopya bugün geri çekilmiyor: sertifikan Sanayi ve Teknoloji Bakanlığı'nın Dijital Rozet platformuna aktarıldıysa adın ve e-posta adresin orada da kayıtlı. Hesabını sildiğinde geri çekme talebi oluşturuyoruz, ama bu talep henüz işletilmiyor.",
    'profile.deleteAccount.bulletLeads': "Öğrenci olmadan önceki iletişim kaydın kalır: bize ilk kez bilgi almak için ulaştığında bıraktığın ad soyad ve telefon bilgisi, danışman görüşme notlarınla birlikte satış kayıtlarımızda saklanmaya devam eder.",
    'profile.deleteAccount.cta': "Hesabımı Sil",
    'profile.deleteAccount.dialogA.title': "Hesabını silmek istediğine emin misin?",
    'profile.deleteAccount.dialogA.body': `Onayladığın an hesabın kapanır, oturumun sona erer ve kişisel verilerin en geç ${ACCOUNT_DELETION_GRACE_DAYS} gün içinde silinir. Bir daha giriş yapamazsın — ne e-posta ve şifreyle ne de Google ile. Ödeme, icra ve sözleşme kayıtların yasa gereği saklanır; sertifikan ise adınla birlikte doğrulanabilir kalır.`,
    'profile.deleteAccount.dialogA.cancel': "Vazgeç",
    'profile.deleteAccount.dialogA.continue': "Devam Et",
    'profile.deleteAccount.dialogB.title': "Son adım — kararını onayla",
    'profile.deleteAccount.dialogB.body': `Onayladığın an hesabın kapanır, kişisel verilerin en geç ${ACCOUNT_DELETION_GRACE_DAYS} gün içinde silinir ve bunu kendin geri alamazsın. Ödeme, icra ve sözleşme kayıtların Vergi Usul Kanunu ile Türk Ticaret Kanunu gereği saklanmaya devam eder. Sertifika kayıtların da süresiz saklanır; adın doğrulama sayfasında görünmeye devam eder.`,
    'profile.deleteAccount.dialogB.checkboxLabel': "Hesabımın kapanacağını, kişisel verilerimin silineceğini, ödeme ve sözleşme kayıtlarımın yasa gereği saklanacağını ve adımın sertifika doğrulama sayfasında görünmeye devam edeceğini anlıyorum.",
    'profile.deleteAccount.dialogB.cancel': "Vazgeç",
    'profile.deleteAccount.dialogB.confirm': "Evet, Hesabımı Sil",
    'profile.deleteAccount.dialogB.confirming': "Onaylanıyor…",
    'profile.deleteAccount.success.title': "Hesabın kapatıldı",
    'profile.deleteAccount.success.body': `Hesabın kapandı ve kişisel verilerin en geç ${ACCOUNT_DELETION_GRACE_DAYS} gün içinde silinecek. Bu hesaba artık giriş yapamazsın. Ödeme, icra, sözleşme ve sertifika kayıtların saklanmaya devam ediyor. Yolun açık olsun.`,
    'profile.deleteAccount.success.signOutNote': "Şimdi oturumun kapatılacak.",
    'profile.deleteAccount.success.backHome': "Anasayfaya Dön",
    'profile.deleteAccount.error': "İşlemi tamamlayamadık. Bağlantını kontrol edip tekrar dene.",

    // Public "Hesap Silme" sayfası — /account-deletion, giriş gerektirmez. Google
    // Play Data Safety formundaki "hesap silme URL'i" ve Apple App Store'un
    // uygulama-içi-başlatılabilir silme referansı olarak kullanılır. Yukarıdaki
    // profile.deleteAccount.* ile AYNI gerçekleri anlatır, ama giriş yapmamış
    // (veya App/Play incelemecisi olabilecek) bir okuyucu için yazılmıştır.
    //
    // ⚠️ bu sayfayı MAĞAZA İNCELEMECİSİ okuyor; backend davranışıyla birebir
    // örtüşmeli — ne fazlası ne eksiği. Öğrenci akışı hesabı ANINDA pasife alır
    // ve kişisel veriler 72 saat içinde gerçekten silinir. Saklanan beş kategori
    // (maskelenmeyen kimlik bilgisiyle mali + icra kayıtları; onay IP'siyle
    // birlikte sözleşme kopyası; SÜRESİZ saklanan ve adla birlikte herkese açık
    // doğrulanabilen sertifika kayıtları; bugün geri çekilmeyen bakanlık Dijital
    // Rozet kopyası; öğrenci öncesi lead kayıtları) tek tek adıyla yazılır — biri
    // bile yazılmazsa eksik ifşa olur. Silme 72
    // saat GECİKMELİ olduğu için geçmiş zaman kipi kullanılmaz; "en geç 3 gün
    // içinde silinir" denir.
    'accountDeletion.meta.title': "Hesabını Sil — Kariyer Online",
    'accountDeletion.header.title': "Hesap Silme",
    'accountDeletion.header.subtitle': "Kariyer Online (Academy Pro) hesabını nasıl silersin ve sildikten sonra verilerine ne olur.",
    'accountDeletion.intro': `Bu sayfa, giriş yapabilsen de yapamasan da hesabını nasıl sileceğini anlatır. Hesabını sildiğinde erişimin anında sona erer ve kişisel verilerin en geç ${ACCOUNT_DELETION_GRACE_DAYS} gün içinde silinir. Ödeme, icra, sözleşme ve sertifika kayıtların ise saklanmaya devam eder; aşağıda neyin silindiğini, neyin kaldığını ve nedenini tek tek yazdık.`,
    'accountDeletion.pathA.title': "Giriş yapabiliyorsan",
    'accountDeletion.pathA.step1': "kariyeronline.tr üzerinden hesabına giriş yap.",
    'accountDeletion.pathA.step2': "Profilini aç, Gizlilik sekmesine git.",
    'accountDeletion.pathA.step3': "\"Hesabını Sil\" bölümüne gel ve butona dokun.",
    'accountDeletion.pathA.step4': "İki kez onayla — önce sonuçları anladığını onaylarsın, sonra bir kutucuğu işaretleyerek son kararını verirsin. İkisi de tamamlanmadan hiçbir şey olmaz.",
    'accountDeletion.pathA.step5': `Onayladığın an hesabın kapanır, oturumun sona erer ve kişisel verilerin en geç ${ACCOUNT_DELETION_GRACE_DAYS} gün içinde silinir. Bir daha giriş yapamazsın — ne e-posta ve şifreyle ne de Google ile.`,
    'accountDeletion.pathA.mobileNote': "Mobil uygulamadan silmek istersen: Profil → Hesap Ayarları → Hesabı sil. Onay adımları ve sonuç aynıdır.",
    'accountDeletion.pathA.cta': "Profiline Git",
    'accountDeletion.pathB.title': "Giriş yapamıyorsan",
    'accountDeletion.pathB.body': "Giriş yapamasan bile KVKK madde 11 kapsamında bize e-posta göndererek silme talebinde bulunabilirsin. Kimliğini doğrulayabilmemiz için ad soyadını ve hesabının kayıtlı olduğu e-posta veya telefon numarasını ekle. Talebini uyguladığımızda sonuç aynıdır: hesabın kapanır, kişisel verilerin silinir.",
    'accountDeletion.pathB.emailLabel': "E-posta adresimiz:",
    'accountDeletion.pathB.sla': "Bu tür talepleri KVKK madde 13/2 uyarınca en geç 30 gün içinde sonuçlandırıyoruz.",
    'accountDeletion.data.title': "Hesabına ve verilerine ne olur",
    'accountDeletion.data.accessTitle': "Erişimin sona erer",
    'accountDeletion.data.accessBody': "Hesabın anında kapanır ve oturumun sona erer. Ne e-posta ve şifreyle ne de Google ile bir daha giriş yapabilirsin; silinen hesabı kendi başına yeniden açamazsın.",
    'accountDeletion.data.deletedTitle': "Silinen kişisel verilerin",
    'accountDeletion.data.deletedBody': `Profilin, notların, favorilerin, quiz oturumların, sınav denemelerin, ses kayıtların, mülakat transkriptlerin, bildirimlerin ve sağlık ile adli sicil beyanların en geç ${ACCOUNT_DELETION_GRACE_DAYS} gün içinde silinir. Burada sayılanlar geri getirilemez; silinmeyen kayıtlar için diğer başlıklara bak.`,
    'accountDeletion.data.retainedTitle': "Yasa gereği saklanan kayıtlar",
    'accountDeletion.data.retainedBody': "Ödeme, taksit, senet, fatura ve icra kayıtların silinmez. Bu belgelerdeki ad, adres ve TCKN/VKN bilgisi de maskelenmez — faturanın kime kesildiğinin ve borç–alacak ilişkisinin belli kalması gerekiyor. Bu bizim tercihimiz değil: Vergi Usul Kanunu (m. 253 ve 256), Türk Ticaret Kanunu (m. 82) ve Türk Borçlar Kanunu (m. 146) bu kayıtların saklanmasını zorunlu kılıyor.",
    'accountDeletion.data.contractsTitle': "Sözleşme kaydın saklanır",
    'accountDeletion.data.contractsBody': "Kayıt sırasında onayladığın mesafeli satış sözleşmesinin kopyası saklanır. Bu kopya, sözleşme metninde geçen ad soyadını, e-posta adresini, telefonunu ve adresini taşır; ayrıca onayı verdiğin andaki IP adresi, onayın kimden geldiğinin kanıtı olarak kayıtta kalır. Dayanak, Türk Borçlar Kanunu ve Türk Ticaret Kanunu'nun sözleşmenin ispatına ilişkin hükümleridir.",
    'accountDeletion.data.certificatesTitle': "Sertifikan ve adın doğrulanabilir kalır",
    'accountDeletion.data.certificatesBody': "Sertifika kayıtların süresiz saklanır. Hesabını sildikten sonra da doğrulama bağlantısını açan herkes — bağlantıyı daha önce kimin paylaştığından bağımsız olarak — adını, kurs adını ve veriliş tarihini görür. Bu doğrulama sayfası giriş gerektirmez ve kayıt için bir süre sınırı bulunmuyor.",
    'accountDeletion.data.ministryTitle': "Bakanlıktaki kopya bugün geri çekilmiyor",
    'accountDeletion.data.ministryBody': "Sertifikan Sanayi ve Teknoloji Bakanlığı'nın Dijital Rozet platformuna aktarıldıysa, adın ve e-posta adresin bakanlığın kendi kaydında da bulunur. Hesabını sildiğinde bu kaydın geri çekilmesi için talep oluşturuyoruz; ancak geri çekme akışı henüz devrede değil, dolayısıyla bugün itibarıyla bakanlıktaki kopya yerinde kalıyor. Akış devreye girdiğinde bekleyen talepler işlenecek.",
    'accountDeletion.data.leadsTitle': "Öğrenci olmadan önceki iletişim kaydın",
    'accountDeletion.data.leadsBody': "Bize ilk kez bilgi almak için ulaştığında bıraktığın ad soyad ve telefon bilgisi, danışman görüşme notlarınla birlikte satış kayıtlarımızda saklanmaya devam eder. Bu kayıt hesap silme kapsamının dışındadır; silinmesini istersen KVKK madde 11 kapsamında aşağıdaki e-posta adresinden talep edebilirsin.",
    'accountDeletion.footer.entity': "Kariyer Online Eğitim Hizmetleri",
    'accountDeletion.footer.backHome': "Anasayfaya Dön",
    'accountDeletion.footer.backLogin': "Giriş Yap",

    // Payment Result — PUBLIC /odeme-sonuc, Moka callback iniş sayfası. Not:
    // "tamamlandi" bilerek kesin dil KULLANMAZ — callback yalnızca doğrulamanın
    // TETİKLENDİĞİ anlamına gelir, tahsilatın kesin yazıldığı anlamına gelmez.
    'paymentResult.meta.title': "Ödeme Sonucu — Kariyer Online",
    'paymentResult.completed.title': "Ödemeniz alındı",
    'paymentResult.completed.body': "Ödemeniz alındı, kaydınız kısa süre içinde güncellenecek. Herhangi bir sorunuz olursa danışmanınızla iletişime geçebilirsiniz.",
    'paymentResult.unknown.title': "Ödeme bulunamadı",
    'paymentResult.unknown.body': "Bu ödeme bağlantısını tanıyamadık. Lütfen danışmanınızla iletişime geçin.",
    'paymentResult.dogrulanamadi.title': "Doğrulama tamamlanamadı",
    'paymentResult.dogrulanamadi.body': "Ödeme bilgisi otomatik olarak doğrulanamadı. Lütfen danışmanınızla iletişime geçin — ödemeniz elle kontrol edilecektir.",
    'paymentResult.kapali.title': "Ödeme sistemi şu anda kullanılamıyor",
    'paymentResult.kapali.body': "Bu ödeme bağlantısı şu anda aktif değil. Lütfen danışmanınızla iletişime geçin.",
    'paymentResult.basarisiz.title': "Ödeme başarısız",
    'paymentResult.basarisiz.body': "Ödeme tamamlanamadı, kartınızdan herhangi bir tutar çekilmedi. Aynı ödeme linkini kullanarak tekrar deneyebilir ya da danışmanınızla iletişime geçebilirsiniz.",
    'paymentResult.fallback.title': "Ödeme durumu",
    'paymentResult.fallback.body': "İşleminiz alındı. Herhangi bir sorunuz olursa danışmanınızla iletişime geçin.",
    'paymentResult.backHome': "Ana Sayfaya Dön",
    'paymentResult.backLogin': "Giriş Yap",

    // Moka United — Admin ödeme linki dialog'u (AdminAccounting.jsx taksit
    // satırı) + eşleştirme kuyruğu (AdminMokaQueue.jsx)
    'admin.moka_row_button': "Ödeme Linki",
    'admin.moka_row_button_disabled_hint': "Moka ödeme linki entegrasyonu kapalı",
    'admin.moka_link_dialog_title': "Moka Ödeme Linki",
    'admin.moka_disabled_banner': "Moka ödeme linki entegrasyonu şu anda kapalı.",
    'admin.moka_loading': "Yükleniyor…",
    'admin.moka_voice_note_label': "Not (opsiyonel)",
    'admin.moka_voice_note_placeholder': "Görüşme notu…",
    'admin.moka_bank_label': "POS Hesabı",
    'admin.moka_bank_placeholder': "POS hesabı seçin",
    'admin.moka_bank_required': "POS hesabı seçilmelidir.",
    'admin.moka_no_pos_banks': "Aktif POS hesabı bulunamadı. Önce Muhasebe > Bankalar sekmesinden bir POS hesabı ekleyin.",
    'admin.moka_generate_link': "Link Üret",
    'admin.moka_generating': "Üretiliyor…",
    'admin.moka_active_link_title': "Aktif Ödeme Linki",
    'admin.moka_link_created_banner': "Link üretildi ve öğrenciye gönderildi.",
    'admin.moka_link_url_label': "Ödeme sayfası",
    'admin.moka_copy': "Kopyala",
    'admin.moka_voice_approval_by': "Onaylayan",
    'admin.moka_delivery_title': "Gönderim Durumu",
    'admin.moka_channel_sms': "SMS",
    'admin.moka_channel_email': "E-posta",
    'admin.moka_delivery_status_sent': "Gönderildi",
    'admin.moka_delivery_status_error': "Hata",
    'admin.moka_delivery_status_not_sent': "Gönderilmedi",
    'admin.moka_delivery_status_skipped': "Atlandı (iletişim bilgisi yok)",
    'admin.moka_resend': "Yeniden Gönder",
    'admin.moka_resending': "Gönderiliyor…",
    'admin.moka_resend_success': "Link yeniden gönderildi.",
    'admin.moka_cancel_link': "Linki İptal Et",
    'admin.moka_cancel_confirm_title': "Ödeme linki iptal edilsin mi?",
    'admin.moka_cancel_confirm_desc': "Bu link artık kullanılamayacak. Öğrenci linke tıklarsa ödeme yapamaz.",
    'admin.moka_cancel_success': "Link iptal edildi.",
    'admin.moka_generate_new_link': "Yeni link üret (eskisini geçersiz kıl)",
    'admin.moka_force_confirm_title': "Mevcut link geçersiz kılınsın mı?",
    'admin.moka_force_confirm_desc': "Aktif link iptal edilip yerine yeni bir link üretilecek. Öğrenciye eski linki kullanmaması gerektiğini bildirin.",
    'admin.moka_back_to_active': "Aktif linke dön",
    'admin.moka_conflict_title': "Az önce başka biri link üretti",
    'admin.moka_conflict_desc': "Bu taksit için biraz önce başka bir yönetici ödeme linki üretti. Yeni bir link üretmeden önce aşağıdaki özeti inceleyin.",
    'admin.moka_conflict_created_by': "Üreten",
    'admin.nav.mokaQueue': "Moka Eşleştirme",
    'admin.moka_queue_title': "Moka Eşleştirme Kuyruğu",
    'admin.moka_queue_subtitle': "Otomatik eşleşmeyen Moka tahsilatlarını onaylayın veya reddedin.",
    'admin.moka_queue_filter_label': "Durum",
    'admin.moka_queue_filter_pending': "Bekleyen",
    'admin.moka_queue_filter_queued': "Kuyrukta",
    'admin.moka_queue_filter_unmatched': "Link Bulunamadı",
    'admin.moka_queue_filter_approved': "Onaylandı",
    'admin.moka_queue_filter_rejected': "Reddedildi",
    'admin.moka_queue_filter_auto': "Otomatik Tahsil Edildi",
    'admin.moka_queue_filter_ignored': "Başarısız",
    'admin.moka_queue_empty_title': "Kuyrukta kayıt yok",
    'admin.moka_queue_empty_desc': "İncelemeyi bekleyen Moka tahsilatı bulunmuyor.",
    'admin.moka_queue_error_title': "Kuyruk yüklenemedi",
    'admin.moka_queue_error_desc': "Moka eşleştirme kuyruğu yüklenirken bir sorun oluştu. Lütfen tekrar deneyin.",
    'admin.moka_queue_error_retry': "Tekrar Dene",
    'admin.moka_queue_candidates_title': "Aday Taksitler",
    'admin.moka_queue_no_candidates': "Otomatik aday bulunamadı — taksit ID'sini elle girin.",
    'admin.moka_queue_manual_installment_placeholder': "installment_id…",
    'admin.moka_queue_compare_title': "Karşılaştırma",
    'admin.moka_queue_compare_amount_payment': "Ödenen Tutar",
    'admin.moka_queue_compare_amount_installment': "Taksit Tutarı",
    'admin.moka_queue_compare_name_payer': "Kart Sahibi",
    'admin.moka_queue_compare_name_student': "Öğrenci",
    'admin.moka_queue_compare_date': "Ödeme Tarihi",
    'admin.moka_queue_compare_due': "Vade Tarihi",
    'admin.moka_queue_approve_bank_label': "Tahsilat POS Hesabı",
    'admin.moka_queue_approve_button': "Onayla ve Tahsil Et",
    'admin.moka_queue_approve_confirm_title': "Bu tahsilatı onaylıyor musunuz?",
    'admin.moka_queue_approve_confirm_desc': "Onayladığınızda taksit tahsil edilmiş sayılacak ve geri alınamayacaktır. Onaylamadan önce tutar, isim ve tarih eşleşmesini kontrol ettiğinizden emin olun.",
    'admin.moka_queue_approve_success': "Tahsilat onaylandı.",
    'admin.moka_queue_reject_button': "Reddet",
    'admin.moka_queue_reject_note_label': "Red notu (opsiyonel)",
    'admin.moka_queue_reject_confirm_title': "Bu kaydı reddetmek istediğinize emin misiniz?",
    'admin.moka_queue_reject_success': "Kayıt reddedildi.",
    'admin.moka_queue_select_bank_required': "POS hesabı seçilmelidir.",
    'admin.moka_queue_select_installment_required': "Taksit ID'si girilmelidir.",
    'admin.moka_queue_cancel_selection': "Vazgeç",
    'admin.moka_queue_status_queued': "Kuyrukta",
    'admin.moka_queue_status_unmatched_link_not_found': "Link Bulunamadı",
    'admin.moka_queue_status_approved': "Onaylandı",
    'admin.moka_queue_status_rejected': "Reddedildi",
    'admin.moka_queue_status_auto_collected': "Otomatik Tahsil Edildi",
    'admin.moka_queue_status_ignored_not_successful': "Başarısız",
    'admin.moka_queue_status_invalid_no_dealer_payment_id': "Geçersiz Kayıt",
    'admin.moka_queue_status_pending': "Beklemede",

    // Bank matching pool (Eşleşme Havuzu)
    'admin.nav.bankMatchingPool': "Banka Eşleştirme",
    'admin.bank_match_title': "Banka Hareketi Eşleşme Havuzu",
    'admin.bank_match_subtitle': "Gelen havaleleri öğrencinin bekleyen ödemesiyle eşleştirin.",
    'admin.bank_match_auto_button': "Otomatik Eşleştir",
    'admin.bank_match_auto_confirm_title': "Otomatik eşleştirme motoru şimdi çalıştırılsın mı?",
    'admin.bank_match_auto_confirm_desc': "Referans + tutar tam eşleşen (%100 güven) hareketler otomatik onaylanır. Daha düşük güvenli eşleşmeler öneri olarak eklenir ve elle onay bekler.",
    'admin.bank_match_auto_success': "Otomatik eşleştirme tamamlandı",
    'admin.bank_match_auto_approved': "Otomatik onaylanan",
    'admin.bank_match_auto_suggestions': "Eklenen öneri",
    'admin.bank_match_filter_bank': "Banka",
    'admin.bank_match_filter_direction': "Yön",
    'admin.bank_match_filter_date_from': "Başlangıç",
    'admin.bank_match_filter_date_to': "Bitiş",
    'admin.bank_match_filter_all': "Tümü",
    'admin.bank_match_direction_in': "Gelen",
    'admin.bank_match_direction_out': "Giden",
    'admin.bank_match_empty_title': "Eşleşme havuzu boş",
    'admin.bank_match_empty_desc': "Şu anda eşleşmemiş banka hareketi yok.",
    'admin.bank_match_error_title': "Eşleşme havuzu yüklenemedi",
    'admin.bank_match_error_desc': "Banka hareketi havuzu yüklenirken bir sorun oluştu. Lütfen tekrar deneyin.",
    'admin.bank_match_error_retry': "Tekrar Dene",
    'admin.bank_match_suggestion_title': "Sistem Önerisi",
    'admin.bank_match_suggest_button': "Önerileni Eşleştir",
    'admin.bank_match_no_suggestion': "Henüz öneri yok — \"Otomatik Eşleştir\"i çalıştırın veya elle seçin.",
    'admin.bank_match_ref_recognized_no_payment': "Referans tanındı ama eşleşen bekleyen bir ödeme bulunamadı:",
    'admin.bank_match_suggest_confirm_title': "Bu eşleştirmeyi onaylıyor musunuz?",
    'admin.bank_match_suggest_confirm_desc': "Hareket eşleşmiş olarak işaretlenecek ve öğrencinin ödemesi tamamlanacaktır. Onaylamadan önce yukarıdaki gönderen adı/açıklamayı önerilen referansla karşılaştırın.",
    'admin.bank_match_method_reference': "Referans + tutar",
    'admin.bank_match_method_reference_mismatch': "Referans eşleşti, tutar farklı",
    'admin.bank_match_method_name_amount': "Ad + tutar",
    'admin.bank_match_method_amount_only': "Sadece tutar",
    'admin.bank_match_manual_button': "Elle Seç",
    'admin.bank_match_student_search_label': "Öğrenci Ara",
    'admin.bank_match_student_search_placeholder': "İsim veya e-posta…",
    'admin.bank_match_searching': "Aranıyor…",
    'admin.bank_match_no_student_found': "Öğrenci bulunamadı.",
    'admin.bank_match_open_student_detail': "Öğrenci detayını aç",
    'admin.bank_match_pending_payments_loading': "Bekleyen kayıtlar yükleniyor…",
    'admin.bank_match_pending_payments_label': "Bekleyen kayıtlar",
    'admin.bank_match_pending_payments_error': "Bu öğrencinin bekleyen kayıtları yüklenemedi.",
    'admin.bank_match_pending_payments_empty': "Bu öğrencinin bekleyen ödemesi veya taksiti yok.",
    'admin.bank_match_pending_payment_required': "Eşleştirmek için bir kayıt seçin.",
    'admin.bank_match_manual_match_button': "Eşleştir",
    'admin.bank_match_manual_confirm_title': "Bu elle eşleştirmeyi onaylıyor musunuz?",
    'admin.bank_match_manual_confirm_desc': "Hareket eşleşmiş olarak işaretlenecek ve seçilen kayıt tamamlanacaktır. Bu ekrandan geri alınamaz.",
    'admin.bank_match_cancel_selection': "Vazgeç",
    'admin.bank_match_success': "Hareket eşleştirildi.",
    'admin.bank_match_amount_mismatch_warning': "Eşleştirildi ancak hareket tutarı ile ödeme tutarı uyuşmuyor — lütfen kontrol edin.",
    'admin.bank_match_name_match_info': "Gönderen adı ile eşleşiyor: {student}",
    'admin.bank_match_name_match_no_amount_match': "Tutar hiçbir açık kalemle tutmuyor.",
    'admin.bank_match_name_match_match_button': "Bu kalemle eşleştir",
    'admin.bank_match_name_conflict_badge': "Uyuşmazlık",
    'admin.bank_match_name_conflict_warning': "Gönderen adı, sistem önerisinden FARKLI bir öğrenciyle eşleşiyor. Onaylamadan önce dikkatlice kontrol edin.",
    'admin.bank_match_name_conflict_ack': "Uyuşmazlığın farkındayım, yine de eşleştir.",
    'admin.bank_match_name_conflict_ack_required': "Devam etmeden önce bu kutuyu işaretlemelisiniz.",
    'admin.bank_match_ignore_button': "Yoksay",
    'admin.bank_match_ignore_confirm_title': "Bu hareket yoksayılsın mı?",
    'admin.bank_match_ignore_confirm_desc': "Hareket yumuşak silinecek ve havuzdan kaldırılacaktır. Gerekirse veritabanından geri getirilebilir ama bu ekrandan değil.",
    'admin.bank_match_ignore_success': "Hareket yoksayıldı.",
    'admin.bank_match_tab_pool': "Havuz",
    'admin.bank_match_tab_matched': "Eşleşenler",
    'admin.bank_matched_error_title': "Eşleşen hareketler yüklenemedi",
    'admin.bank_matched_error_desc': "Eşleşen hareketler listesi yüklenirken bir sorun oluştu. Lütfen tekrar deneyin.",
    'admin.bank_matched_empty_title': "Eşleşen hareket yok",
    'admin.bank_matched_empty_desc': "Bu filtrede henüz eşleşmiş banka hareketi yok.",
    'admin.bank_matched_matched_at': "Eşleşme tarihi",

    // Landing
    hero_title: "Sınırsız Öğren",
    hero_subtitle: "Yapay zeka destekli quizlerle interaktif kurslar. Kendi hızında öğren, ilerlemeni takip et ve ödüller kazan.",
    get_started: "Başla",
    explore_courses: "Kursları Keşfet",
    featured_courses: "Öne Çıkan Kurslar",
    all_categories: "Tüm Kategoriler",
    view_all: "Tümünü Gör",
    students: "öğrenci",
    lessons: "ders",

    // Course
    enroll_now: "Hemen Kaydol",
    buy_now: "Satın Al",
    coming_soon: "Yakında",
    start_learning: "Öğrenmeye Başla",
    continue_learning: "Devam Et",
    curriculum: "Müfredat",
    reviews: "Yorumlar",
    about: "Hakkında",
    instructor: "Eğitmen",
    level: "Seviye",
    beginner: "Başlangıç",
    intermediate: "Orta",
    advanced: "İleri",
    interactive_course: "İnteraktif",
    
    // Dashboard
    my_courses: "Kurslarım",
    your_progress: "İlerlemen",
    streak: "Günlük Seri",
    total_xp: "Toplam XP",
    courses_enrolled: "Kayıtlı Kurs",
    completion: "Tamamlama",
    no_courses: "Henüz bir kursa kayıt olmadınız.",
    browse_courses: "Kurslara Göz At",

    // Dashboard v2 — Faz 4a
    'dashboard.welcomeTitle': "Hoş geldin, {{name}}!",
    'dashboard.defaultName': "Öğrenci",
    'dashboard.streakMessage': "{{days}} gün üst üste devamlısın — böyle devam!",
    'dashboard.continuePrograms': "Öğrenmeye Devam Et",
    'dashboard.recentActivity': "Son Aktiviteler",
    'dashboard.quickAccess': "Hızlı Erişim",
    'dashboard.programs.freeAccess': "Hediye Erişim",
    'dashboard.programs.moduleProgress': "İlerleme",
    'dashboard.programs.progressAriaLabel': "{{title}} — %{{pct}} tamamlandı",
    'dashboard.programs.continue': "Devam Et",
    'dashboard.programs.emptyTitle': "Henüz kurs yok",
    'dashboard.programs.emptySub': "Hedeflerinize uygun kursları keşfedin ve öğrenmeye başlayın.",
    'dashboard.activity.emptyTitle': "Henüz aktivite yok",
    'dashboard.activity.emptySub': "Ders tamamladıkça aktiviteleriniz burada görünür.",
    'dashboard.activity.emptyCta': "Kurslara Göz At",
    'dashboard.activity.errorTitle': "Aktiviteler yüklenemedi",
    'dashboard.milestones.heading': "İlerleme Yolculuğun",
    'dashboard.milestones.xpRemaining': "Sonraki seviye için {{xp}} XP kaldı",
    'dashboard.milestones.ariaLabel': "Genel ilerleme: %{{pct}}",
    'dashboard.milestones.level1': "Başlangıç",
    'dashboard.milestones.level2': "Öğrenci",
    'dashboard.milestones.level3': "Pratikçi",
    'dashboard.milestones.level4': "Uzman",
    'dashboard.milestones.level5': "Kabin Memuru",
    'dashboard.error.title': "Bağlantı kesildi",
    'dashboard.error.sub': "Paneliniz yüklenemedi. Tekrar dene.",
    'dashboard.docRejected': "{{count}} belgeniz reddedildi — düzeltmek için tıklayın",
    'dashboard.docRejected.fix': "Düzeltmek için tıkla",
    'dashboard.duo.greeting': "Tekrar hoş geldin, {{name}}!",
    'dashboard.duo.streakLabel': "Gün serisi",
    'dashboard.duo.xpLabel': "Toplam XP",
    'dashboard.duo.continueLearning': "Kaldığın yerden devam et",
    'dashboard.duo.dailyGoalLabel': "Günlük hedef",
    'dashboard.duo.dailyGoalUnit': "dak",
    'dashboard.duo.continueBtn': "Devam Et",
    'dashboard.duo.lessonLabel': "Ders",
    'dashboard.duo.myCourse': "Kursum",
    'dashboard.duo.lessonProgress': "{{total}} dersten {{completed}} tanesini tamamladın, %{{pct}}",
    'dashboard.duo.allLessons': "Tüm dersleri gör",
    'dashboard.duo.thisWeek': "Bu Hafta",
    'dashboard.duo.studied': "Çalışıldı",
    'dashboard.duo.notStudied': "Dinlenme günü",
    'dashboard.duo.achievements': "Rozetler",
    'dashboard.duo.empty.title': "Henüz kayıtlı kursun yok",
    'dashboard.duo.empty.sub': "Kabin Memurluğu kursunu keşfet ve hemen başla",
    'dashboard.duo.empty.cta': "Kurslara Bak",
    'dashboard.duo.noEnrollment.title': "Henüz bir kursa kayıt olmadın",
    'dashboard.duo.noEnrollment.cta': "Kurslara göz at",
    'dashboard.duo.lessonNow': "Şimdi",
    'dashboard.duo.lessonCompleted': "Tamamlandı",
    'dashboard.duo.lessonLocked': "Kilitli",
    'dashboard.duo.streakUnit': "gün",
    'dashboard.duo.xpUnit': "XP",
    'dashboard.duo.badgeUnit': "rozet",
    'dashboard.duo.badgesEarnedCount': "{{count}} kazanıldı",
    'dashboard.duo.interviewPromo.badge': "Yeni",
    'dashboard.duo.interviewPromo.title': "Mülakat Simülasyonu",
    'dashboard.duo.interviewPromo.desc': "Gerçek kabin memuru mülakatına yapay zekâ ile hazırlan.",
    'dashboard.duo.interviewPromo.cta': "Simülasyona Git",
    'dashboard.quickAccess.certificates': "Sertifikalar",
    'dashboard.quickAccess.profile': "Profil",
    'dashboard.quickAccess.documents': "Belgeler",
    'dashboard.quickAccess.interview': "Mülakat Pratiği",
    'dashboard.quickAccess.exams': "Sınavlarım",
    'dashboard.quickAccess.courses': "Kurslar",
    'dashboard.quickAccess.progress': "İlerlemem",
    'gamificationBar.ariaLabel': "İstatistikleriniz: XP, seri ve seviye",
    'gamificationBar.streakAriaLabel': "{{days}} günlük seri",
    'courseType.aviation': "Havacılık",
    'courseType.cabin': "Kabin Ekibi",
    'courseType.pax': "Yolcu İletişimi",
    'courseType.general': "Genel",
    'common.retry': "Tekrar Dene",
    'common.cancel': "İptal",
    'common.save': "Kaydet",
    'catalog.filterAll': "Tümü",
    'catalog.sort.newest': "En yeni",
    'catalog.sort.popular': "En popüler",
    'catalog.sort.rating': "En yüksek puan",
    'catalog.sort.priceLow': "Fiyat: ucuzdan pahalıya",
    'catalog.sort.priceHigh': "Fiyat: pahalıdan ucuza",
    'catalog.levelFilter.all': "Tüm seviyeler",
    'catalog.resultCount': "{{count}} kurs",
    'catalog.shownCount': "{{total}} kurstan {{shown}} tanesi gösteriliyor",
    'catalog.loadMore': "Daha fazla göster",
    'catalog.loadMore.loading': "Yükleniyor...",
    'catalog.loadMore.error': "Daha fazla kurs yüklenemedi. Lütfen tekrar dene.",
    'catalog.empty.title': "Aramanla eşleşen kurs yok",
    'catalog.empty.sub': "Farklı anahtar kelimeler dene veya filtreni kaldır.",
    'catalog.empty.clearFilters': "Filtreleri temizle",
    'catalog.error.title': "Kurslar yüklenemedi",
    'catalog.error.sub': "Bağlantını kontrol et ve tekrar dene.",
    'catalog.loading.ariaLabel': "Kurslar yükleniyor",
    'catalog.search.placeholder': "Kurs ara...",
    'catalog.heading': "Kurslar",
    'catalog.subheading': "Kariyerini şekillendir, adımını at",
    'catalog.card.by': "eğitmen",

    // ── Programs + ProgramLanding + Certificates — Faz 2.2bc ────────────────
    'programs.pageTitle': "Eğitim Programları",
    'programs.pageTitle.sub': "Hangi alanda kendini geliştirmek istiyorsun? Programı seç, sana özel kurslarla yola koyul.",
    'programs.loading.ariaLabel': "Programlar yükleniyor",
    'programs.error.title': "Programlar yüklenemedi",
    'programs.error.sub': "Bağlantını kontrol et ve tekrar dene.",
    'programs.empty.title': "Henüz aktif program yok",
    'programs.empty.sub': "Programlar yayınlandığında burada görünür.",
    'programs.empty.cta': "Kurslara Göz At",
    'programs.card.cta': "Programa Git",
    'programLanding.loading': "Yükleniyor...",
    'programLanding.notFound': "Program bulunamadı.",
    'programLanding.allPrograms': "Tüm Programlar",
    'programLanding.backAriaLabel': "Tüm programlara dön",
    'programLanding.coursesHeading': "Bu Programa Ait Kurslar",
    'programLanding.coursesAll': "Tüm Kursları Gör",
    'programLanding.coursesAll.ariaLabel': "{{name}} programındaki tüm kursları gör",
    'programLanding.coursesLoading': "Kurslar yükleniyor",
    'programLanding.lessonsCount': "{{count}} ders",
    'programLanding.courses.emptyTitle': "Henüz yayınlanmış kurs yok",
    'programLanding.courses.emptySub': "Kurslar eklenince burada görünür.",
    'programLanding.courses.errorTitle': "Kurslar yüklenemedi",
    'programLanding.feature.video': "Video",
    'programLanding.feature.pdf': "PDF",
    'programLanding.feature.text_note': "Notlar",
    'programLanding.feature.quiz': "Quiz",
    'programLanding.feature.podcast': "Podcast",
    'programLanding.feature.certificate': "Sertifika",
    'programLanding.feature.cabin_games': "İnteraktif Oyunlar",
    'programLanding.feature.interview_practice': "Mülakat Provası",
    'programLanding.feature.level_system': "Seviye Sistemi",
    'programLanding.feature.english_interview': "İngilizce Mülakat",
    'certificates.pageTitle': "Sertifikalarım",
    'certificates.loading.ariaLabel': "Sertifikalar yükleniyor",
    'certificates.error.title': "Sertifikalar yüklenemedi",
    'certificates.error.sub': "Bağlantını kontrol et ve tekrar dene.",
    'certificates.empty.title': "Henüz sertifikan yok",
    'certificates.empty.sub': "İlk sertifikanı kazanmak için bir kursu tamamla.",
    'certificates.empty.cta': "Kurslara Göz At",
    'certificates.card.completionTitle': "Tamamlama Sertifikası",
    'certificates.card.issuedLabel': "Verildiği tarih",
    'certificates.card.verificationLabel': "Doğrulama",
    'certificates.view.ariaLabel': "PDF sertifikayı görüntüle",
    'certificates.download.ariaLabel': "PDF sertifikayı indir",
    'certificates.error.downloadFailed': "Sertifika açılamadı. Lütfen tekrar dene.",
    'certificates.error.auth': "Oturum süresi doldu. Lütfen tekrar giriş yapın.",
    'certificates.share.ariaLabel': "Sertifikayı paylaş",
    'certificates.share.copied': "Kopyalandı!",
    'certificates.share.text': "Kariyer Online'da \"{{title}}\" kursunu tamamladım! Doğrulama: {{code}}",
    'certificates.status.revoked': "İptal Edildi",
    'certificates.status.verified': "Doğrulanmış",

    // ── Dijital Rozet (Sanayi ve Teknoloji Bakanlığı dijital rozet sistemi) ───
    // Yalnızca cert.dr_status doluysa gösterilir (kurs dijital rozet
    // programına dahilse). Teknik durum adları kullanıcıya ASLA gösterilmez —
    // yalnızca bu insan-dili metinler (ham değer için data-testid'e bak).
    'certificates.dr.pending.title': "Rozet hazırlanıyor",
    'certificates.dr.pending.body': "Bu sertifikanın dijital rozeti için hazırlık süreci başladı. Kısa süre içinde güncellenecek.",
    'certificates.dr.publishing.title': "Rozet yayınlanıyor",
    'certificates.dr.publishing.body': "Rozetin şu anda Bakanlık sistemine gönderiliyor. Bu işlem birkaç dakika sürebilir.",
    'certificates.dr.sent.title': "Rozetin hazır",
    'certificates.dr.sent.body': "Bu sertifika artık T.C. Sanayi ve Teknoloji Bakanlığı'nın Dijital Rozet sisteminde doğrulanabilir.",
    'certificates.dr.sent.viewBadge': "Rozeti Görüntüle",
    'certificates.dr.sent.shareLinkedin': "LinkedIn'de Paylaş",
    'certificates.dr.failed.title': "Rozet şu anda yayınlanamadı",
    'certificates.dr.failed.body': "Bu senin hatan değil — teknik bir sorun oluştu, ekibimiz bakıyor. Sertifikan geçerliliğini koruyor.",
    'certificates.dr.skipped.title': "Rozet şu anda kullanılamıyor",
    'certificates.dr.skipped.body': "Bu kurs için dijital rozet oluşturma şu anda uygun değil. Sertifikan geçerliliğini koruyor.",
    'certificates.dr.awaitingConsent.title': "Rozetin onayını bekliyor",
    'certificates.dr.awaitingConsent.body': "Dijital rozetini yayınlayabilmemiz için Gizlilik ayarlarından onay vermen gerekiyor.",
    'certificates.dr.awaitingConsent.cta': "Onay ver",
    'certificates.dr.revoked.title': "Rozet geri çekildi",
    'certificates.dr.revoked.body': "Bu sertifikaya ait dijital rozet Bakanlık sisteminden geri çekildi.",
    'certificates.dr.linkedin.error': "LinkedIn paylaşımı başlatılamadı. Lütfen tekrar dene.",
    'certificates.dr.linkedin.revokedSrOnly': "Bu sertifika iptal edildiği için paylaşılamaz.",
    'certificates.dr.badgeImageAlt': "Dijital rozet görseli",

    // ── Sertifika Doğrulama Sayfası (/verify/:code) ───────────────────────────
    'certVerify.subtitle': "Sertifika Doğrulama Sistemi",
    'certVerify.error.fetch': "Sertifika doğrulanamadı",
    'certVerify.error.notFoundTitle': "Sertifika Bulunamadı",
    'certVerify.error.notFoundSub': "Bu kod ile kayıtlı bir sertifika bulunamadı.",
    'certVerify.valid': "Geçerli Sertifika",
    'certVerify.revoked': "İptal Edilmiş Sertifika",
    'certVerify.invalid': "Geçersiz Sertifika",
    'certVerify.student': "Öğrenci",
    'certVerify.course': "Kurs",
    'certVerify.issuedAt': "Veriliş Tarihi",
    'certVerify.verificationCode': "Doğrulama Kodu",
    'certVerify.revokedAt': "İptal Tarihi",
    'certVerify.revokeReason': "İptal Sebebi:",
    'certVerify.footer.valid': "Bu sertifika geçerlidir. Sorularınız için Kariyer Online ekibine başvurunuz.",
    'certVerify.footer.invalid': "Bu sertifika geçersizdir. Sorularınız için Kariyer Online ekibine başvurunuz.",

    'courseDetail.reviewForm.title': "Yorum yaz",
    'courseDetail.reviewForm.placeholder': "Bu kurs hakkında düşüncelerini paylaş...",
    'courseDetail.reviewForm.submitted': "Yorumun gönderildi — teşekkürler!",
    'courseDetail.continueWhereLeft': "Kaldığın yerden devam et",
    'courseDetail.finalExam.title': "Final sınavı hazır",
    'courseDetail.finalExam.sub': "Sertifika almak için sınava gir.",
    'courseDetail.finalExam.cta': "Sınava gir",
    'courseDetail.curriculum.assessment.title': "Değerlendirme",
    'courseDetail.curriculum.assessment.unlockedSub': "Hazır — sertifika almak için sınava gir.",
    'courseDetail.curriculum.assessment.lockedSub': "Tüm dersler tamamlanınca açılır",
    'courseDetail.curriculum.assessment.lockedBadge': "Kilitli",
    'courseDetail.curriculum.assessment.cta': "Sınava git",
    'courseDetail.levelMismatch.title': "Seviye gereksinimi",
    'courseDetail.levelMismatch.body': "Bu kurs {{required}} seviyesi gerektiriyor. Mevcut seviyeniz: {{current}}.",
    'courseDetail.lessonType.interactive': "İnteraktif ders",
    'courseDetail.lessonType.video': "Video ders",
    'courseDetail.lessonType.pdf': "PDF",
    'courseDetail.lessonType.text': "Metin ders",
    'courseDetail.lessonType.quiz': "Quiz",
    'courseDetail.lessonType.exam': "Sınav",
    'courseDetail.lessonType.flashcard': "Kart",
    'courseDetail.lessonType.default': "Ders",
    'courseDetail.lesson.locked': "Kilidi açmak için iletişime geçin",
    'courseDetail.contactToEnroll.title': "Kayıt olmak ister misin?",
    'courseDetail.contactToEnroll.sub': "Detaylar için ekibimizle WhatsApp'tan konuş.",
    'courseDetail.contactToEnroll.cta': "WhatsApp'tan Bilgi Al",
    'courseDetail.contactToEnroll.ctaPrimaryGuest': "Giriş Yap",
    'whatsapp.message.courseInquiry': "Merhaba, websitenizden {{courseTitle}} kursunuzu gördüm, bilgi almak istiyorum.",
    'whatsapp.message.generic': "Merhaba, kurslarınız hakkında bilgi almak istiyorum.",
    'whatsapp.cta.short': "WhatsApp",
    'whatsapp.cta.askOnWhatsapp': "WhatsApp'tan Bilgi Al",
    'whatsapp.floating.ariaLabel': "WhatsApp'tan bize sor (yeni sekmede açılır)",
    'whatsapp.opensExternal': "WhatsApp'ta açılır",
    'courseDetail.curriculum.empty': "Dersler yakında eklenecek",
    'courseDetail.reviews.empty': "Henüz yorum yok — ilk yorumu sen yaz!",
    'courseDetail.notFound': "Bu kursu bulamadık",
    'courseDetail.enrollSuccess': "Kayıt oldun — öğrenmeye başla!",
    'courseDetail.progress.ariaLabel': "Kurs ilerlemesi: %{{pct}}",
    'courseDetail.fav.add': "Favorilere ekle",
    'courseDetail.fav.remove': "Favorilerden çıkar",
    'nav.cabinMemurlugu': "Kabin Memurluğu",
    'nav.courses': "Kurslar",
    'nav.myCourses': "Kurslarım",
    'nav.allCourses': "Tüm Kurslar",
    'nav.homeLink': "Ana sayfa",
    'nav.openMenu': "Menüyü aç",
    'nav.closeMenu': "Menüyü kapat",
    'nav.mobileMenuLabel': "Mobil menü",
    'nav.langToggleLabel': "Dil değiştir",
    'nav.mainNav': "Ana navigasyon",
    'myCourses.title': "Kurslarım",
    'myCourses.empty.title': "Henüz kayıtlı kursun yok",
    'myCourses.empty.sub': "Kurslara göz at ve öğrenmeye başla.",
    'myCourses.empty.cta': "Kurslara Göz At",
    'myCourses.progress': "%{{pct}} tamamlandı",
    'myCourses.lessons': "{{completed}} / {{total}} ders",
    'myCourses.continue': "Devam Et",
    'myCourses.start': "Eğitime Başla",

    // Quiz
    check_answer: "Cevabı Kontrol Et",
    next_question: "Sonraki Soru",
    correct: "Doğru!",
    incorrect: "Yanlış!",
    quiz_complete: "Quiz Tamamlandı!",
    xp_earned: "Kazanılan XP",

    // Auth
    email: "E-posta",
    password: "Şifre",
    name: "Ad Soyad",
    login_with_google: "Google ile Devam Et",
    or: "veya",
    no_account: "Hesabınız yok mu?",
    have_account: "Zaten hesabınız var mı?",

    // Payment
    checkout: "Ödeme",
    payment_success: "Ödeme Başarılı!",
    total: "Toplam",

    // Stats
    total_courses: "Kurs",
    total_students: "Öğrenci",
    satisfaction: "Memnuniyet",

    // General
    free: "Ücretsiz",
    popular: "Popüler",
    new_badge: "Yeni",
    loading: "Yükleniyor...",

    // Admin primitives
    'admin.select_all': "Tümünü seç",
    'admin.select_row': "Satırı seç",
    'admin.empty_table_title': "Kayıt bulunamadı",
    'admin.empty_table_desc': "Filtrelerinizi veya arama sorgunuzu ayarlayın.",
    'admin.pagination_info': "{total} kaydın {start}–{end}",
    'admin.prev_page': "Önceki sayfa",
    'admin.next_page': "Sonraki sayfa",
    'admin.pagination_label': "Sayfalama",
    'admin.pagination_empty': "Veri yok",
    'admin.search_placeholder': "Ara...",
    'admin.clear_search': "Aramayı temizle",
    'admin.clear_filters': "Filtreleri temizle",
    'admin.filter_all': "Tümü",
    'admin.active_filters': "Aktif filtreler",
    'admin.btn_cancel': "İptal",
    'admin.btn_confirm': "Onayla",
    'admin.btn_loading': "Yükleniyor...",
    'admin.btn_delete': "Sil",
    'admin.btn_edit': "Düzenle",
    'admin.btn_add': "Ekle",
    'admin.btn_save': "Kaydet",
    'admin.btn_create': "Oluştur",
    'admin.btn_close': "Kapat",
    'admin.action_menu_label': "Satır işlemleri",
    'admin.file_too_large': "Dosya {max}MB sınırını aşıyor.",
    'admin.file_uploaded': "Yüklendi",
    'admin.file_remove': "Dosyayı kaldır",
    'admin.file_drop_zone': "Dosya bırakma alanı",
    'admin.file_drag_title': "Sürükle bırak veya gözat",
    'admin.file_accepted': "Kabul edilen: {types}",
    'admin.file_any_type': "Tüm dosya türleri kabul edilir",
    'admin.file_max_size': "Maks {max} MB",
    'admin.file_browse': "Gözat",
    'admin.file_preview': "Dosya önizleme",
    'admin.select_placeholder': "Seç...",
    'admin.rich_select_search': "Ara...",
    'admin.rich_select_no_results': "Seçenek bulunamadı.",
    'admin.date_today': "Bugün",
    'admin.date_last7': "Son 7 gün",
    'admin.date_last30': "Son 30 gün",
    'admin.date_this_month': "Bu ay",
    'admin.date_last_month': "Geçen ay",
    'admin.date_placeholder': "Tarih aralığı seç",
    'admin.date_range_label': "Tarih aralığı",
    'admin.date_clear': "Tarihi temizle",

    // ── AdminCourseManager v2 ─────────────────────────────────────────────────
    'admin.courseManager.title': "Kurs Yöneticisi",
    'admin.courseManager.subtitle': "Kurs, modül ve dersleri oluşturun ve yönetin",
    'admin.courseManager.breadcrumb_admin': "Admin",
    'admin.courseManager.breadcrumb_courses': "Kurslar",
    'admin.courseManager.btn_new_course': "Yeni Kurs",
    'admin.courseManager.filter_search_placeholder': "Başlığa göre ara...",
    'admin.courseManager.filter_category': "Kategori",
    'admin.courseManager.filter_level': "Seviye",
    'admin.courseManager.filter_interactive': "İnteraktif",
    'admin.courseManager.filter_interactive_yes': "İnteraktif",
    'admin.courseManager.filter_interactive_no': "Standart",
    'admin.courseManager.col_course': "Kurs",
    'admin.courseManager.col_category': "Kategori",
    'admin.courseManager.col_level': "Seviye",
    'admin.courseManager.col_lessons': "Ders",
    'admin.courseManager.col_enrolled': "Kayıtlı",
    'admin.courseManager.col_price': "Fiyat",
    'admin.courseManager.col_actions': "İşlemler",
    'admin.courseManager.action_edit': "Düzenle",
    'admin.courseManager.action_modules': "Modüller",
    'admin.courseManager.action_delete': "Sil",
    'admin.courseManager.delete_title': "Kursu Sil",
    'admin.courseManager.delete_desc': "Bu işlem geri alınamaz. Tüm dersler de silinecektir.",
    'admin.courseManager.empty_title': "Henüz kurs yok",
    'admin.courseManager.empty_desc': "Başlamak için ilk kursu oluşturun.",
    'admin.courseManager.draft_badge': "TASLAK",
    // Dialog — genel
    'admin.courseManager.dialog_new_title': "Yeni Kurs",
    'admin.courseManager.dialog_edit_title': "Kursu Düzenle",
    'admin.courseManager.tab_general': "Genel",
    'admin.courseManager.tab_modules': "Modüller",
    'admin.courseManager.tab_lessons': "Dersler",
    'admin.courseManager.tab_quiz': "Quiz",
    'admin.courseManager.tab_certificate': "Sertifika",
    // Genel tab
    'admin.courseManager.section_thumbnail': "Kurs Görseli",
    'admin.courseManager.section_basic': "Kurs Bilgileri",
    'admin.courseManager.section_settings': "Ayarlar",
    'admin.courseManager.field_title_en': "Başlık (EN)",
    'admin.courseManager.field_title_tr': "Başlık (TR)",
    'admin.courseManager.field_desc_en': "Açıklama (EN)",
    'admin.courseManager.field_desc_tr': "Açıklama (TR)",
    'admin.courseManager.field_category': "Kategori",
    'admin.courseManager.field_subcategory': "Alt Kategori",
    'admin.courseManager.field_level': "Seviye",
    'admin.courseManager.field_price': "Fiyat",
    'admin.courseManager.field_currency': "Para Birimi",
    'admin.courseManager.field_language': "Dil",
    'admin.courseManager.field_interactive': "İnteraktif Kurs",
    'admin.courseManager.field_vimeo_intro': "Vimeo Tanıtım URL",
    'admin.courseManager.field_level_required': "Gereken Seviye (Kabin Memurları için)",
    'admin.courseManager.btn_upload_thumb': "Görsel Yükle",
    'admin.courseManager.btn_save': "Kaydet",
    'admin.courseManager.btn_saving': "Kaydediliyor...",
    'admin.courseManager.save_success_create': "Kurs oluşturuldu",
    'admin.courseManager.save_success_update': "Kurs güncellendi",
    'admin.courseManager.validation_title_required': "Kurs başlığı zorunludur",
    // Modüller tab
    'admin.courseManager.section_modules': "Modüller",
    'admin.courseManager.section_modules_desc': "Sıralama için sürükle. Modülü genişletmek için tıkla ve içerideki dersleri yönet.",
    'admin.courseManager.btn_add_module': "Modül Ekle",
    'admin.courseManager.module_title_en': "Modül Başlığı (EN)",
    'admin.courseManager.module_title_tr': "Modül Başlığı (TR)",
    'admin.courseManager.module_description': "Açıklama",
    'admin.courseManager.module_order': "Sıra",
    'admin.courseManager.module_edit': "Modülü Düzenle",
    'admin.courseManager.module_delete': "Modülü Sil",
    'admin.courseManager.module_delete_title': "Modülü Sil",
    'admin.courseManager.module_delete_desc': "Modül silinecek. İçindeki dersler sahipsiz kalabilir.",
    'admin.courseManager.module_save': "Modülü Kaydet",
    'admin.courseManager.module_cancel': "İptal",
    'admin.courseManager.module_empty': "Henüz modül yok",
    'admin.courseManager.module_empty_desc': "Dersleri düzenlemek için ilk modülü ekleyin.",
    'admin.courseManager.drag_hint': "Sıralama için sürükle",
    // Dersler tab
    'admin.courseManager.section_lessons': "Dersler",
    'admin.courseManager.btn_add_lesson': "Ders Ekle",
    'admin.courseManager.lesson_title_en': "Ders Başlığı (EN)",
    'admin.courseManager.lesson_title_tr': "Ders Başlığı (TR)",
    'admin.courseManager.lesson_desc_en': "Açıklama (EN)",
    'admin.courseManager.lesson_desc_tr': "Açıklama (TR)",
    'admin.courseManager.lesson_type': "Tür",
    'admin.courseManager.lesson_module': "Modül",
    'admin.courseManager.lesson_order': "Sıra",
    'admin.courseManager.lesson_vimeo': "Vimeo URL",
    'admin.courseManager.lesson_vimeo_or': "veya",
    'admin.lessons.vimeoPicker.button': "Vimeo Kütüphanesinden Seç",
    'admin.lessons.vimeoPicker.title': "Vimeo Kütüphanesi",
    'admin.lessons.vimeoPicker.loading': "Klasörler yükleniyor...",
    'admin.lessons.vimeoPicker.loadingVideos': "Videolar yükleniyor...",
    'admin.lessons.vimeoPicker.empty': "Bu klasörde video yok",
    'admin.lessons.vimeoPicker.emptyFolders': "Klasör bulunamadı",
    'admin.lessons.vimeoPicker.search': "Tüm videolarda ara...",
    'admin.lessons.vimeoPicker.backToFolders': "Klasörler",
    'admin.lessons.vimeoPicker.selected': "Seçili",
    'admin.lessons.vimeoPicker.confirm': "Bu Videoyu Kullan",
    'admin.courseManager.lesson_content_en': "İçerik (EN)",
    'admin.courseManager.lesson_content_tr': "İçerik (TR)",
    'admin.courseManager.lesson_duration': "Süre (dk)",
    'admin.courseManager.lesson_watch_percent': "Min İzleme %",
    'admin.courseManager.lesson_passing_score': "Geçme Puanı",
    'admin.courseManager.lesson_edit': "Dersi Düzenle",
    'admin.courseManager.lesson_delete': "Dersi Sil",
    'admin.courseManager.lesson_delete_title': "Dersi Sil",
    'admin.courseManager.lesson_delete_desc': "Bu ders kalıcı olarak silinecektir.",
    'admin.courseManager.lesson_save': "Dersi Kaydet",
    'admin.courseManager.lesson_validation_title': "Ders başlığı zorunludur",
    'admin.courseManager.lesson_validation_course': "Önce bir kurs seçin",
    'admin.courseManager.lesson_empty': "Henüz ders yok",
    'admin.courseManager.lesson_empty_desc': "Kurs içeriğinizi oluşturmak için ders ekleyin.",
    // Quiz tab
    'admin.courseManager.section_quiz': "Quiz Soruları",
    'admin.courseManager.section_quiz_desc': "İnteraktif dersler için quiz sorularını tanımlayın.",
    'admin.courseManager.quiz_question': "Soru",
    'admin.courseManager.quiz_options': "Seçenekler (her satıra bir seçenek)",
    'admin.courseManager.quiz_correct': "Doğru Cevap",
    'admin.courseManager.quiz_add': "Soru Ekle",
    'admin.courseManager.quiz_remove': "Kaldır",
    'admin.courseManager.quiz_empty': "Quiz sorusu yok",
    'admin.courseManager.quiz_select_lesson': "Quiz yapılandırmak için interaktif bir ders seçin.",
    // Sertifika tab
    'admin.courseManager.section_cert': "Sertifika Bilgisi",
    'admin.courseManager.cert_title': "Sertifika Başlığı",
    'admin.courseManager.cert_subtitle': "Alt Başlık",

    // ── Ders İçerik Editörleri — Faz 4e ─────────────────────────────────────
    // Ortak
    'lessonEditor.common.contentSection': "İçerik",
    'lessonEditor.common.emptyList': "Henüz öğe yok. Aşağıdaki butona tıkla.",
    'lessonEditor.common.add': "Ekle",
    'lessonEditor.common.remove': "Kaldır",
    'lessonEditor.common.moveUp': "Yukarı taşı",
    'lessonEditor.common.moveDown': "Aşağı taşı",
    'lessonEditor.common.preview': "Önizleme",
    'lessonEditor.common.save': "Kaydet",
    'lessonEditor.common.cancel': "İptal",
    // Flashcard
    'lessonEditor.flashcard.front': "Ön yüz (terim)",
    'lessonEditor.flashcard.frontPlaceholder': "örnek: Gate",
    'lessonEditor.flashcard.back': "Arka yüz (çeviri/tanım)",
    'lessonEditor.flashcard.backPlaceholder': "örnek: Kapı",
    'lessonEditor.flashcard.example': "Örnek cümle",
    'lessonEditor.flashcard.examplePlaceholder': "örnek: Please proceed to gate 12.",
    'lessonEditor.flashcard.addCard': "Kart Ekle",
    'lessonEditor.flashcard.removeCard': "Kartı kaldır",
    // Eşleştirme
    'lessonEditor.matching.left': "Sol öğe",
    'lessonEditor.matching.leftPlaceholder': "örnek: Ekmek",
    'lessonEditor.matching.right': "Sağ öğe (eşleşme)",
    'lessonEditor.matching.rightPlaceholder': "örnek: Bread",
    'lessonEditor.matching.addPair': "Çift Ekle",
    'lessonEditor.matching.removePair': "Çifti kaldır",
    'lessonEditor.matching.mode': "Oyun modu",
    'lessonEditor.matching.mode_two_column': "İki Sütun",
    'lessonEditor.matching.mode_memory': "Hafıza Kartı",
    // Sürükleme
    'lessonEditor.dragDrop.sentence': "Doğru cümle",
    'lessonEditor.dragDrop.sentenceHint': "kelimeler boşlukla ayrılmış",
    'lessonEditor.dragDrop.sentencePlaceholder': "örnek: the cat is on the mat",
    'lessonEditor.dragDrop.hint': "İpucu (opsiyonel)",
    'lessonEditor.dragDrop.hintPlaceholder': "örnek: cat ve mat kafiyeli",
    'lessonEditor.dragDrop.addSentence': "Cümle Ekle",
    'lessonEditor.dragDrop.removeSentence': "Cümleyi kaldır",
    'lessonEditor.dragDrop.scrambledPreview': "Karışık önizleme",
    'lessonEditor.dragDrop.rescramble': "Yeniden karıştır",
    // Diyalog
    'lessonEditor.dialogue.scenario': "Senaryo açıklaması",
    'lessonEditor.dialogue.scenarioPlaceholder': "örnek: Uçuş sırasında bir yolcu su istiyor.",
    'lessonEditor.dialogue.speaker': "Konuşanın adı",
    'lessonEditor.dialogue.speakerPlaceholder': "örnek: Yolcu",
    'lessonEditor.dialogue.text': "Diyalog metni",
    'lessonEditor.dialogue.textPlaceholder': "Bu kişi ne diyor?",
    'lessonEditor.dialogue.textUserPlaceholder': "Boş bırakın — kullanıcı aşağıdan seçer",
    'lessonEditor.dialogue.isUser': "Kullanıcının sırası",
    'lessonEditor.dialogue.isUserYes': "Sen",
    'lessonEditor.dialogue.isUserNo': "Diğer",
    'lessonEditor.dialogue.options': "Cevap seçenekleri",
    'lessonEditor.dialogue.addOption': "Seçenek Ekle",
    'lessonEditor.dialogue.removeOption': "Seçeneği kaldır",
    'lessonEditor.dialogue.isCorrect': "Doğru cevap",
    'lessonEditor.dialogue.isCorrectYes': "Doğru",
    'lessonEditor.dialogue.isCorrectNo': "Yanlış",
    'lessonEditor.dialogue.optionTextPlaceholder': "Seçenek metni...",
    'lessonEditor.dialogue.feedbackPlaceholder': "Bu seçenek için geri bildirim...",
    'lessonEditor.dialogue.noOptions': "Henüz seçenek yok.",
    'lessonEditor.dialogue.addStep': "Diyalog Adımı Ekle",
    'lessonEditor.dialogue.removeStep': "Adımı kaldır",
    // Boşluk Doldurma
    'lessonEditor.fillInBlank.sentence': "Boşluklu cümle",
    'lessonEditor.fillInBlank.sentenceHint': "boşluk için ___ kullan",
    'lessonEditor.fillInBlank.sentencePlaceholder': "örnek: I ___ to the airport.",
    'lessonEditor.fillInBlank.correct': "Doğru cevap",
    'lessonEditor.fillInBlank.correctPlaceholder': "örnek: went",
    'lessonEditor.fillInBlank.mode': "Giriş modu",
    'lessonEditor.fillInBlank.mode_word_pool': "Kelime Havuzu",
    'lessonEditor.fillInBlank.mode_text_input': "Metin Girişi",
    'lessonEditor.fillInBlank.alternatives': "Alternatif kelimeler (yanıltmaca)",
    'lessonEditor.fillInBlank.noAlternatives': "Alternatif yok — yanıltmaca ekle.",
    'lessonEditor.fillInBlank.addQuestion': "Soru Ekle",
    'lessonEditor.fillInBlank.removeQuestion': "Soruyu kaldır",
    // Kaydırma
    'lessonEditor.swipe.word': "Kelime / Terim",
    'lessonEditor.swipe.wordPlaceholder': "örnek: Boarding",
    'lessonEditor.swipe.translation': "Çeviri",
    'lessonEditor.swipe.translationPlaceholder': "örnek: Biniş",
    'lessonEditor.swipe.example': "Örnek cümle",
    'lessonEditor.swipe.examplePlaceholder': "örnek: Boarding starts at 7 AM.",
    'lessonEditor.swipe.addItem': "Kelime Ekle",
    'lessonEditor.swipe.removeItem': "Öğeyi kaldır",
    // Zaman Yarışı
    'lessonEditor.timeRace.duration': "Süre (saniye)",
    'lessonEditor.timeRace.question': "Soru",
    'lessonEditor.timeRace.questionPlaceholder': "örnek: Bir yolcuyu nasıl karşılarsınız?",
    'lessonEditor.timeRace.options': "Cevap seçenekleri",
    'lessonEditor.timeRace.option': "Seçenek",
    'lessonEditor.timeRace.correctIndex': "Doğru cevap",
    'lessonEditor.timeRace.correctIndexHint': "Doğru cevap için yanındaki radyo düğmesine tıklayın.",
    'lessonEditor.timeRace.addQuestion': "Soru Ekle",
    'lessonEditor.timeRace.removeQuestion': "Soruyu kaldır",

    // ── AdminStudentHub (Öğrenci 360) ────────────────────────────────────────
    'admin.studentHub.title': "Öğrenciler",
    'admin.studentHub.subtitle': "360 derece öğrenci yönetimi",
    'admin.studentHub.breadcrumb_admin': "Admin",
    'admin.studentHub.breadcrumb_students': "Öğrenciler",
    'admin.studentHub.btn_new_user': "Yeni Kullanıcı",
    'admin.studentHub.btn_bulk_enroll': "Toplu Kayıt",
    'admin.studentHub.filter_search': "Ad veya e-posta ile ara...",
    'admin.studentHub.filter_role': "Rol",
    'admin.studentHub.filter_level': "Seviye",
    'admin.studentHub.filter_pending': "Silme Bekleyen",
    'admin.studentHub.col_user': "Kullanıcı",
    'admin.studentHub.col_role': "Rol",
    'admin.studentHub.col_activity': "Son Aktivite",
    'admin.studentHub.select_user': "Detaylarını görmek için bir kullanıcı seçin",
    'admin.studentHub.tab_profile': "Profil",
    'admin.studentHub.tab_enrollments': "Kayıtlar",
    'admin.studentHub.tab_certificates': "Sertifikalar",
    'admin.studentHub.tab_activity': "Aktivite",
    'admin.studentHub.tab_payments': "Ödemeler",
    'admin.studentHub.tab_kvkk': "KVKK",
    'admin.studentHub.tab_security': "Güvenlik",
    'admin.studentHub.action_message': "Mesaj Gönder",
    'admin.studentHub.action_reset_pw': "Şifre Sıfırla",
    'admin.studentHub.action_freeze': "Hesabı Dondur",
    'admin.studentHub.action_unfreeze': "Hesabı Aktifle",
    'admin.studentHub.action_cancel_deletion': "Silmeyi İptal Et",
    'admin.studentHub.action_delete': "Hesabı Sil",
    'admin.studentHub.action_ban': "Kullanıcıyı Engelle",
    'admin.studentHub.action_unban': "Engeli Kaldır",
    'admin.studentHub.action_deactivate': "Pasife Al",
    'admin.studentHub.action_reactivate': "Aktife Al",
    'admin.studentHub.profile_name': "Ad Soyad",
    'admin.studentHub.profile_email': "E-posta",
    'admin.studentHub.profile_phone': "Telefon",
    'admin.studentHub.profile_cabin_level': "Kabin Ekibi Seviyesi",
    'admin.studentHub.profile_address': "Adres",
    'admin.studentHub.profile_city': "Şehir",
    'admin.studentHub.profile_district': "İlçe",
    'admin.studentHub.profile_postal_code': "Posta Kodu",
    'admin.studentHub.profile_tc_no': "TC Kimlik No",
    'admin.studentHub.profile_birth_date': "Doğum Tarihi",
    'admin.studentHub.profile_emergency_contact': "Acil İletişim",
    'admin.studentHub.profile_emergency_phone': "Acil Telefon",
    'admin.studentHub.profile_notes': "Notlar",
    'admin.studentHub.profile_save': "Profili Kaydet",
    'admin.studentHub.profile_saving': "Kaydediliyor...",
    'admin.studentHub.profile_saved': "Profil güncellendi",
    'admin.studentHub.enroll_col_course': "Kurs",
    'admin.studentHub.enroll_col_progress': "İlerleme",
    'admin.studentHub.enroll_col_status': "Durum",
    'admin.studentHub.enroll_action_delete': "Kaydı Sil",
    'admin.studentHub.enroll_action_complete': "Tamamlandı Yap",
    'admin.studentHub.enroll_delete_title': "Kaydı Sil",
    'admin.studentHub.enroll_delete_desc': "Öğrenci bu kursun kaydından çıkarılacak.",
    'admin.studentHub.enroll_complete_title': "Tamamlandı Olarak İşaretle",
    'admin.studentHub.enroll_complete_desc': "Bu kayıt manuel olarak tamamlandı olarak işaretlenecek.",
    'admin.studentHub.btn_assign_course': "Ders Ata",
    'admin.studentHub.assign_course_title': "Yeni Ders Ata",
    'admin.studentHub.assign_course_placeholder': "Kurs seç...",
    'admin.studentHub.assign_course_label': "Kurs",
    'admin.studentHub.assign_course_success': "Kurs başarıyla atandı.",
    'admin.studentHub.assign_course_already': "Öğrenci bu kursa zaten kayıtlı.",
    'admin.studentHub.assign_course_payment_method': "Ödeme yöntemi",
    'admin.studentHub.assign_course_method_cash': "Peşin",
    'admin.studentHub.assign_course_method_installments': "Taksitli",
    'admin.studentHub.assign_course_method_free': "Ücretsiz",
    // FAZ 1 karma ödeme turu (2026-09-25) — eski Nakit/Taksitli ikilisi
    // "paid" (Peşinat + Kalan, bkz. components/sales/PaymentPlanFields.jsx)
    // olarak birleşti.
    'admin.studentHub.assign_course_method_paid': "Ücretli",
    'admin.studentHub.assign_course_discount': "İndirim (%)",
    'admin.studentHub.assign_course_installments_count': "Taksit sayısı",
    'admin.studentHub.assign_course_down_payment': "Peşinat",
    'admin.studentHub.assign_course_price_override': "Manuel fiyat (opsiyonel)",
    'admin.studentHub.assign_course_notes': "Notlar (opsiyonel)",
    'admin.studentHub.cert_col_course': "Kurs",
    'admin.studentHub.cert_col_issued': "Veriliş Tarihi",
    'admin.studentHub.cert_col_verify': "Doğrulama URL",
    'admin.studentHub.btn_issue_certificate': "Sertifika Oluştur",
    'admin.studentHub.issue_certificate_title': "Sertifika Oluştur",
    'admin.studentHub.issue_certificate_desc': "Bu öğrenci için elle sertifika oluşturun. Kursu seçin — sertifika yalnızca kayıt tamamlanmışsa (ve gerekiyorsa sınav geçilmişse) üretilir.",
    'admin.studentHub.issue_certificate_label': "Kurs",
    'admin.studentHub.issue_certificate_placeholder': "Kurs seç...",
    'admin.studentHub.issue_certificate_already_suffix': "sertifika mevcut",
    'admin.studentHub.issue_certificate_empty': "Bu öğrencinin kaydı yok.",
    'admin.studentHub.issue_certificate_confirm': "Sertifika Oluştur",
    'admin.studentHub.issue_certificate_success': "Sertifika başarıyla oluşturuldu.",
    'admin.studentHub.issue_certificate_already_exists': "Bu kurs için sertifika zaten mevcut — mevcut kayıt döndürüldü.",
    'admin.studentHub.activity_empty': "Aktivite kaydı yok",
    'admin.studentHub.action.register': "Kayıt oldu",
    'admin.studentHub.action.login': "Giriş yaptı",
    'admin.studentHub.action.logout': "Çıkış yaptı",
    'admin.studentHub.action.password_changed': "Şifre değiştirildi",
    'admin.studentHub.action.password_reset_requested': "Şifre sıfırlama istendi",
    'admin.studentHub.action.password_reset_completed': "Şifre sıfırlama tamamlandı",
    'admin.studentHub.action.password_reset_by_admin': "Şifre admin tarafından sıfırlandı",
    'admin.studentHub.action.account_deletion_requested': "Hesap silme talebi oluşturuldu",
    'admin.studentHub.action.account_deletion_cancelled': "Hesap silme iptal edildi",
    'admin.studentHub.action.data_exported': "Veri dışa aktarıldı",
    'admin.studentHub.action.role_changed': "Rol değiştirildi",
    'admin.studentHub.action.student_registered': "Öğrenci kaydedildi",
    'admin.studentHub.action.onay_kodu_dogrulandi': "Onay kodu doğrulandı",
    'admin.studentHub.action.courses_enrolled': "Kurslara kaydedildi",
    'admin.studentHub.action.courses_assigned_free': "Ücretsiz kurs atandı",
    'admin.studentHub.action.ek_ders_hakki_set': "Ek ders hakkı tanımlandı",
    'admin.studentHub.action.ek_ders_hakki_kullanildi': "Ek ders hakkı kullanıldı",
    'admin.studentHub.action.status_changed': "Durum değiştirildi",
    'admin.studentHub.action.manual_payment_added': "Manuel ödeme eklendi",
    'admin.studentHub.action.enrollment_updated': "Kayıt güncellendi",
    'admin.studentHub.action.enrollment_cancelled': "Kayıt iptal edildi",
    'admin.studentHub.action.meb_doc_set': "MEB belgesi tanımlandı",
    'admin.studentHub.action.students_export': "Öğrenciler dışa aktarıldı",
    'admin.studentHub.action.student_note_added': "Öğrenci notu eklendi",
    'admin.studentHub.action.lesson_updated': "Ders güncellendi",
    'admin.studentHub.action.lesson_deleted': "Ders silindi",
    'admin.studentHub.action.lesson_completed': "Ders tamamlandı",
    'admin.studentHub.action.cabin_lesson_complete': "Kabin dersi tamamlandı",
    'admin.studentHub.action.cabin_progress.reset': "Kabin ilerlemesi sıfırlandı",
    'admin.studentHub.action.profile_updated': "Profil güncellendi",
    'admin.studentHub.action.certificate_shared': "Sertifika paylaşıldı",
    'admin.studentHub.action.course_created': "Kurs oluşturuldu",
    'admin.studentHub.action.contract_signed': "Sözleşme onaylandı",
    'admin.studentHub.action.teyit_guncellendi': "Teyit güncellendi",
    'admin.studentHub.action.ogrenci_info_sms_gonderildi': "Bilgi SMS gönderildi",
    'admin.studentHub.action.notification_sent': "Bildirim gönderildi",
    'admin.studentHub.action.reminders_sent': "Hatırlatma gönderildi",
    'admin.studentHub.action.bug_report_create': "Hata bildirimi oluşturuldu",
    'admin.studentHub.action.bug_report_update': "Hata bildirimi güncellendi",
    'admin.studentHub.action.definition_created': "Tanım oluşturuldu",
    'admin.studentHub.action.definition_updated': "Tanım güncellendi",
    'admin.studentHub.action.definition_deleted': "Tanım silindi",
    'admin.studentHub.action.exam_group_created': "Sınav grubu oluşturuldu",
    'admin.studentHub.action.exam_group_deleted': "Sınav grubu silindi",
    'admin.studentHub.action.exam_group_members_added': "Sınav grubuna üye eklendi",
    'admin.studentHub.action.forum_thread_created': "Forum konusu açıldı",
    'admin.studentHub.action.forum_thread_deleted': "Forum konusu silindi",
    'admin.studentHub.action.forum_post_created': "Forum cevabı yazıldı",
    'admin.studentHub.action.forum_post_deleted': "Forum cevabı silindi",
    'admin.studentHub.action.forum_accept_answer': "Forum cevabı kabul edildi",
    'admin.studentHub.action.admin_forum_moderate_thread': "Forum konusu denetlendi",
    'admin.studentHub.action.admin_forum_moderate_post': "Forum cevabı denetlendi",
    'admin.studentHub.action.lead_created': "Aday oluşturuldu",
    'admin.studentHub.action.lead_deleted': "Aday silindi",
    'admin.studentHub.action.lead_converted': "Aday öğrenciye dönüştürüldü",
    'admin.studentHub.action.leads_imported': "Adaylar içe aktarıldı",
    'admin.studentHub.action.lead_status_updated': "Aday durumu güncellendi",
    'admin.studentHub.action.leads_bulk_assigned': "Adaylar toplu atandı",
    'admin.studentHub.action.installment_collected': "Taksit tahsil edildi",
    'admin.studentHub.action.promissory_note_created': "Senet oluşturuldu",
    'admin.studentHub.action.promissory_collected': "Senet tahsil edildi",
    'admin.studentHub.action.taksit_guncellendi': "Taksit güncellendi",
    'admin.studentHub.action.referans_kodu_olusturuldu': "Referans kodu oluşturuldu",
    'admin.studentHub.action.referans_kodu_guncellendi': "Referans kodu güncellendi",
    'admin.studentHub.action.session_revoked': "Oturum sonlandırıldı",
    'admin.studentHub.action.sessions_revoked': "Oturumlar sonlandırıldı",
    'admin.studentHub.action.account_frozen': "Hesap donduruldu",
    'admin.studentHub.action.video_interview_start': "Video mülakat başlatıldı",
    'admin.studentHub.action.video_interview_delete': "Video mülakat silindi",
    'admin.studentHub.action.video_access_denied': "Video erişimi reddedildi",
    'admin.studentHub.action.embed_token_issued': "Video erişim anahtarı verildi",
    'admin.studentHub.action.vimeo_assigned': "Vimeo hesabı atandı",
    'admin.studentHub.action.voice_practice_evaluated': "Sesli pratik değerlendirildi",
    'admin.studentHub.action.voice_scenario_created': "Sesli senaryo oluşturuldu",
    'admin.studentHub.action.voice_scenario_updated': "Sesli senaryo güncellendi",
    'admin.studentHub.action.voice_scenario_deleted': "Sesli senaryo silindi",
    'admin.studentHub.action.placement_test_submitted': "Seviye tespit sınavı gönderildi",
    'admin.studentHub.action.bank_auto_match_triggered': "Banka otomatik eşleştirme tetiklendi",
    'admin.studentHub.action.bank_transaction_added': "Banka işlemi eklendi",
    'admin.studentHub.action.bank_transaction_matched': "Banka işlemi eşleştirildi",
    'admin.studentHub.action.admin_create_cabin_module': "Kabin modülü oluşturuldu",
    'admin.studentHub.action.admin_update_cabin_module': "Kabin modülü güncellendi",
    'admin.studentHub.action.impersonate_issued': "Öğrenci gibi giriş başlatıldı",
    'admin.studentHub.action.impersonate_exchange': "Öğrenci gibi giriş yapıldı",
    'admin.studentHub.action.magic_link_issued': "Sihirli bağlantı oluşturuldu",
    'admin.studentHub.payment_col_amount': "Tutar",
    'admin.studentHub.payment_col_status': "Durum",
    'admin.studentHub.payment_col_course': "Kurs",
    'admin.studentHub.payment_col_date': "Tarih",
    'admin.studentHub.kvkk_pending_label': "Silme Talep Edildi",
    'admin.studentHub.kvkk_scheduled': "Planlanan Tarih",
    'admin.studentHub.kvkk_grace': "Bitiş Tarihi",
    'admin.studentHub.kvkk_cancel_btn': "Silme Talebini İptal Et",
    'admin.studentHub.kvkk_delete_btn': "Hemen Sil",
    'admin.studentHub.kvkk_delete_title': "Hesabı Kalıcı Olarak Sil",
    // KVKK GERCEK DAVRANIS icin EN taraftaki ayni anahtarin ustundeki yorum aciklamasina bak.
    'admin.studentHub.kvkk_delete_desc': "Bu işlem kullanıcının kişisel verilerini (profil, etkinlik, öğrenme kayıtları) kalıcı olarak siler — bu geri alınamaz. Ödeme, taksit, fatura ve icra kayıtları vergi ve ticaret hukuku gereği saklanır; bunlar silinmez.",
    'admin.studentHub.kvkk_cancel_title': "Silme Talebini İptal Et",
    'admin.studentHub.kvkk_cancel_desc': "Bu işlem kullanıcının hesap silme talebini iptal edecektir.",
    'admin.studentHub.kvkk_no_pending': "Bekleyen silme talebi yok",
    'admin.studentHub.empty_title': "Öğrenci bulunamadı",
    'admin.studentHub.empty_desc': "Arama veya filtrelerinizi ayarlayın.",
    'admin.studentHub.loading': "Öğrenciler yükleniyor...",
    'admin.studentHub.new_user_title': "Yeni Kullanıcı Oluştur",
    'admin.studentHub.new_user_name': "Ad Soyad",
    'admin.studentHub.new_user_email': "E-posta",
    'admin.studentHub.new_user_phone': "Telefon",
    'admin.studentHub.new_user_password': "Şifre",
    'admin.studentHub.new_user_role': "Rol",
    'admin.studentHub.new_user_save': "Kullanıcı Oluştur",
    'admin.studentHub.new_user_saving': "Oluşturuluyor...",
    'admin.studentHub.new_user_created': "Kullanıcı oluşturuldu",
    'admin.studentHub.pw_reset_title': "Şifre Sıfırla",
    'admin.studentHub.pw_reset_new': "Yeni Şifre",
    'admin.studentHub.pw_reset_save': "Sıfırla",
    'admin.studentHub.pw_reset_success': "Şifre sıfırlandı",
    'admin.studentHub.ban_confirm_title': "Kullanıcıyı Engelle",
    'admin.studentHub.ban_confirm_desc': "Engelli kullanıcılar giriş yapamaz.",
    'admin.studentHub.unban_confirm_title': "Engeli Kaldır",
    'admin.studentHub.unban_confirm_desc': "Bu işlem giriş erişimini geri yükler.",
    'admin.studentHub.deactivate_confirm_title': "Hesabı Pasife Al",
    'admin.studentHub.deactivate_confirm_desc': "Öğrenci giriş yapamayacak. Hiçbir veri silinmez.",
    'admin.studentHub.reactivate_confirm_title': "Hesabı Aktife Al",
    'admin.studentHub.reactivate_confirm_desc': "Bu işlem öğrencinin giriş erişimini geri yükler.",
    'admin.studentHub.status_reason_label': "Gerekçe (zorunlu)",
    'admin.studentHub.status_reason_placeholder': "Bu durum değişikliği için bir gerekçe girin",
    'admin.studentHub.action_assign_level': "Seviye Ata",
    'admin.studentHub.level_modal_title': "Seviye Ata",
    'admin.studentHub.level_modal_desc': "Öğrencinin kabin ekibi ve/veya İngilizce seviyesini elle belirleyin. Denetim kaydı için gerekçe zorunludur.",
    'admin.studentHub.level_cabin_crew_label': "Kabin Ekibi Seviyesi",
    'admin.studentHub.level_english_label': "İngilizce Seviyesi",
    'admin.studentHub.level_keep_current': "Mevcut kalsın",
    'admin.studentHub.level_beginner': "Başlangıç",
    'admin.studentHub.level_intermediate': "Orta",
    'admin.studentHub.level_advanced': "İleri",
    'admin.studentHub.level_reason_label': "Gerekçe (zorunlu)",
    'admin.studentHub.level_reason_placeholder': "Bu seviye neden atanıyor?",
    'admin.studentHub.level_save': "Seviyeyi Kaydet",
    'admin.studentHub.level_saved': "Seviye güncellendi",
    'admin.studentHub.level_current_badge_cabin': "Kabin",
    'admin.studentHub.level_current_badge_english': "İngilizce",
    'admin.studentHub.cabin_program_badge_label': "Kabin Programı",
    'admin.studentHub.cabin_program_registered': "Kayıtlı",
    'admin.studentHub.cabin_program_not_registered': "Kayıtlı değil",
    'admin.studentHub.action_cabin_enroll': "Kabin programına kaydet",
    'admin.studentHub.action_cabin_end': "Kabin program kaydını sonlandır",
    'admin.studentHub.cabin_enroll_title': "Kabin Programına Kaydet",
    'admin.studentHub.cabin_enroll_desc': "Öğrenci kabin ekibi eğitim programına kaydedilecek.",
    'admin.studentHub.cabin_enroll_notes_label': "Not (opsiyonel)",
    'admin.studentHub.cabin_enroll_notes_placeholder': "Bu kayıtla ilgili bir iç not ekleyin",
    'admin.studentHub.cabin_enroll_no_level_hint': "Öğrencinin modüllere erişebilmesi için seviye de atanmalı.",
    'admin.studentHub.cabin_enroll_confirm': "Kaydet",
    'admin.studentHub.cabin_enroll_success': "Öğrenci kabin programına kaydedildi",
    'admin.studentHub.cabin_end_title': "Kabin Program Kaydını Sonlandır",
    'admin.studentHub.cabin_end_desc': "Öğrencinin kabin ekibi eğitim programına erişimi sona erecek.",
    'admin.studentHub.cabin_end_reason_label': "Gerekçe (zorunlu)",
    'admin.studentHub.cabin_end_reason_placeholder': "Bu kayıt neden sonlandırılıyor?",
    'admin.studentHub.cabin_end_confirm': "Kaydı Sonlandır",
    'admin.studentHub.cabin_end_success': "Kabin program kaydı sonlandırıldı",
    'admin.studentHub.videoProgress.title': "Video İzleme Detayı",
    'admin.studentHub.videoProgress.openBtn': "Video Detay",
    'admin.studentHub.videoProgress.lessonHeader': "Ders",
    'admin.studentHub.videoProgress.percentHeader': "İzleme",
    'admin.studentHub.videoProgress.lastWatchedHeader': "Son İzleme",
    'admin.studentHub.videoProgress.completedBadge': "Tamamlandı",
    'admin.studentHub.videoProgress.notStarted': "Başlanmadı",
    'admin.studentHub.videoProgress.summary': "Toplam: {{completed}}/{{total}} ders · ortalama %{{avg}}",
    'admin.studentHub.videoProgress.empty': "Bu kursta henüz izleme kaydı yok",
    'admin.studentHub.videoProgress.error': "Veri yüklenemedi",

    // ── StudentHub — Görüşmeler sekmesi ──────────────────────────────────────
    'admin.studentHub.tab_gorusmeler': "Görüşmeler",
    'admin.studentHub.tab_sinav': "Sınav Takibi",
    'admin.studentHub.gorusme_add_title': "Yeni Görüşme",
    'admin.studentHub.gorusme_kanal': "Kanal",
    'admin.studentHub.gorusme_konu': "Konu",
    'admin.studentHub.gorusme_not': "Not",
    'admin.studentHub.gorusme_tarih': "Tarih / Saat",
    'admin.studentHub.gorusme_btn_add': "Görüşme Ekle",
    'admin.studentHub.gorusme_adding': "Ekleniyor...",
    'admin.studentHub.gorusme_added': "Görüşme eklendi",
    'admin.studentHub.gorusme_empty': "Henüz görüşme kaydı yok",
    'admin.studentHub.gorusme_load_more': "Daha fazla yükle",
    'admin.studentHub.sms_history_title': "Gönderilen SMS'ler",
    'admin.studentHub.sms_history_empty': "Henüz SMS gönderilmemiş",
    'admin.studentHub.sms_purpose_otp': "Onay Kodu",
    'admin.studentHub.sms_purpose_credentials': "Giriş Bilgisi",
    'admin.studentHub.sms_purpose_info': "Bilgilendirme",
    'admin.studentHub.sms_purpose_reminder': "Hatırlatma",
    'admin.studentHub.sms_purpose_campaign': "Kampanya",
    'admin.studentHub.sms_purpose_lead': "Aday",
    'admin.studentHub.sms_purpose_test': "Test",
    'admin.studentHub.sms_status_sent': "Gönderildi",
    'admin.studentHub.sms_status_failed': "Başarısız",
    'admin.studentHub.sms_status_skipped': "Atlandı",
    'admin.studentHub.sms_status_pending': "Beklemede",
    'admin.studentHub.gorusme_lead_badge': "Lead dönemi",
    'admin.studentHub.gorusme_kanal_telefon': "Telefon",
    'admin.studentHub.gorusme_kanal_whatsapp': "WhatsApp",
    'admin.studentHub.gorusme_kanal_yuz_yuze': "Yüz Yüze",
    'admin.studentHub.gorusme_kanal_sms': "SMS",
    'admin.studentHub.gorusme_kanal_email': "E-posta",

    // ── StudentHub — Profil teyit rozetleri ──────────────────────────────────
    'admin.studentHub.teyit_edildi': "Teyit Edildi",
    'admin.studentHub.teyit_edilmedi': "Teyit Edilmedi!",
    'admin.studentHub.teyit_confirm_title': "Teyit Durumu Güncelle",
    'admin.studentHub.teyit_confirm_desc': "Teyit durumunu değiştirmek istiyor musunuz?",
    'admin.studentHub.teyit_tc': "TC Kimlik",
    'admin.studentHub.teyit_adres': "Adres",
    'admin.studentHub.teyit_dogum_tarihi': "Doğum Tarihi",
    'admin.studentHub.badge_active': "Aktif",
    'admin.studentHub.badge_inactive': "Pasif",
    'admin.studentHub.badge_verified': "Kimlik Doğrulandı",
    'admin.studentHub.badge_ek_ders': "Ek Ders Hakkı: {{n}}",

    // ── StudentHub — Aksiyon butonları ────────────────────────────────────────
    'admin.studentHub.btn_send_sms': "Bilgi SMS'i Gönder",
    'admin.studentHub.btn_pdf_form': "Kayıt Formu (PDF)",
    'admin.studentHub.btn_ek_ders': "Ek Ders Hakkı Kullan",
    'admin.studentHub.sms_confirm_title': "Bilgi SMS'i Gönder",
    'admin.studentHub.sms_confirm_desc': "Öğrenciye kayıt bilgilerini içeren SMS gönderilecek.",
    'admin.studentHub.sms_sent': "SMS gönderildi",
    'admin.studentHub.ek_ders_confirm_title': "Ek Ders Hakkı Kullan",
    'admin.studentHub.ek_ders_confirm_desc': "Bir ek ders hakkı tüketilecek.",
    'admin.studentHub.ek_ders_used': "Ek ders hakkı kullanıldı. Kalan: {{n}}",

    // ── StudentHub — Sınav sekmesi ────────────────────────────────────────────
    'admin.studentHub.sinav_my_groups': "Sınav Grupları",
    'admin.studentHub.sinav_suggestions': "Önerilen Gruplar",
    'admin.studentHub.sinav_no_groups': "Sınav grubuna üye değil",
    'admin.studentHub.sinav_no_suggestions': "Önerilen grup yok",
    'admin.studentHub.sinav_no_suggestions_desc': "Önerilecek sınav grubu bulunamadı — öğrencinin MEB onaylı ana eğitimi yoksa veya tüm gruplara zaten üyeyse öneri çıkmaz.",
    'admin.studentHub.sinav_btn_add_group': "Gruba Ekle",
    'admin.studentHub.sinav_adding': "Ekleniyor...",
    'admin.studentHub.sinav_added': "Gruba eklendi",
    'admin.studentHub.sinav_members': "{{n}} üye",
    'admin.studentHub.sinav_date': "Sınav: {{date}}",

    // ── AdminStudentDetailPage ─────────────────────────────────────────────────
    'admin.studentDetail.title': "Öğrenci Detayı",
    'admin.studentDetail.back_to_list': "Öğrenci Listesi",
    'admin.studentDetail.search_placeholder': "Ad, TC, telefon, e-posta ile ara...",
    'admin.studentDetail.tab_kimlik': "Kimlik Bilgileri",
    'admin.studentDetail.tab_enrollments': "Eğitimler",
    'admin.studentDetail.tab_payments': "Muhasebe",
    'admin.studentDetail.tab_payments_count_hint': "Taksit sayısı, tahsilat sayısı değil",
    'admin.studentDetail.tab_activity': "İstatistikler",
    'admin.studentDetail.tab_certificates': "Sertifikalar",
    'admin.studentDetail.tab_gorusmeler': "Görüşmeler",
    'admin.studentDetail.tab_sinav': "Sınav/Süpervizyon",
    'admin.studentDetail.tab_kvkk': "KVKK",
    'admin.studentDetail.tab_security': "Güvenlik",

    // ── AdminEnrollments ─────────────────────────────────────────────────────
    'admin.enrollments.title': "Kayıtlar",
    'admin.enrollments.subtitle': "Tüm öğrenci kurs kayıtları",
    'admin.enrollments.breadcrumb_admin': "Admin",
    'admin.enrollments.breadcrumb': "Kayıtlar",
    'admin.enrollments.btn_bulk_enroll': "Toplu Kayıt",
    'admin.enrollments.filter_search': "Kullanıcı veya kurs ara...",
    'admin.enrollments.filter_course': "Kurs",
    'admin.enrollments.filter_status': "Durum",
    'admin.enrollments.col_user': "Kullanıcı",
    'admin.enrollments.col_course': "Kurs",
    'admin.enrollments.col_progress': "İlerleme",
    'admin.enrollments.col_status': "Durum",
    'admin.enrollments.col_started': "Başlangıç",
    'admin.enrollments.action_delete': "Kaldır",
    'admin.enrollments.action_complete': "Tamamlandı Yap",
    'admin.enrollments.bulk_delete': "Seçilileri Sil",
    'admin.enrollments.bulk_complete': "Seçilileri Tamamla",
    'admin.enrollments.delete_title': "Kaydı Kaldır",
    'admin.enrollments.delete_desc': "Bu işlem öğrenciyi kurstan çıkarır.",
    'admin.enrollments.empty_title': "Kayıt bulunamadı",
    'admin.enrollments.empty_desc': "Filtrelerinizi ayarlayın.",
    'admin.enrollments.aggregate_note': "Kurs bazlı istatistikler gösteriliyor (öğrenci bazlı liste için backend /admin/enrollments gerekli)",
    'admin.enrollments.enrolled_count': "Kayıtlı",
    'admin.enrollments.est_revenue': "Tahmini Gelir",

    // ── AdminAnnouncements ────────────────────────────────────────────────────
    'admin.ann_page_title': "Duyurular",
    'admin.ann_page_subtitle': "Platform genelinde duyurular oluşturun ve yönetin",
    'admin.ann_new': "Yeni Duyuru",
    'admin.ann_title_en': "Başlık (EN)",
    'admin.ann_title_tr': "Başlık (TR)",
    'admin.ann_content': "İçerik...",
    'admin.ann_target_all': "Herkes",
    'admin.ann_target_students': "Öğrenciler",
    'admin.ann_target_instructors': "Eğitmenler",
    'admin.ann_priority_low': "Düşük",
    'admin.ann_priority_normal': "Normal",
    'admin.ann_priority_high': "Yüksek",
    'admin.ann_publish': "Yayınla",
    'admin.ann_publishing': "Yayınlanıyor...",
    'admin.ann_published': "Duyuru yayınlandı",
    'admin.ann_deleted': "Duyuru silindi",
    'admin.ann_activated': "Aktifleştirildi",
    'admin.ann_deactivated': "Pasifleştirildi",
    'admin.ann_validation': "Başlık ve içerik zorunlu",
    'admin.ann_empty_title': "Henüz duyuru yok",
    'admin.ann_empty_desc': "Kullanıcılarınızı bilgilendirmek için ilk duyuruyu oluşturun.",
    'admin.ann_delete_title': "Duyuruyu Sil",
    'admin.ann_delete_desc': "Bu duyuruyu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
    'admin.ann_active': "Aktif",
    'admin.ann_inactive': "Pasif",

    // ── AdminCampaigns ────────────────────────────────────────────────────────
    'admin.camp_page_title': "Kampanyalar",
    'admin.camp_page_subtitle': "Kupon kodları ve indirim kampanyalarını yönetin",
    'admin.camp_new': "Yeni Kampanya",
    'admin.camp_name': "Kampanya adı",
    'admin.camp_code': "Kupon kodu (örn: YENİ2025)",
    'admin.camp_description': "Açıklama",
    'admin.camp_discount': "İndirim %",
    'admin.camp_start': "Başlangıç",
    'admin.camp_end': "Bitiş",
    'admin.camp_max_usage': "Maks Kullanım",
    'admin.camp_create': "Oluştur",
    'admin.camp_creating': "Oluşturuluyor...",
    'admin.camp_created': "Kampanya oluşturuldu",
    'admin.camp_activated': "Aktifleştirildi",
    'admin.camp_deactivated': "Pasifleştirildi",
    'admin.camp_validation': "Kampanya adı ve kupon kodu zorunlu",
    'admin.camp_empty_title': "Henüz kampanya yok",
    'admin.camp_empty_desc': "Öğrencileri çekmek için ilk indirim kampanyanızı oluşturun.",
    'admin.camp_usage': "Kullanım",
    'admin.camp_active': "Aktif",
    'admin.camp_inactive': "Pasif",

    // ── AdminCampaigns — WhatsApp ─────────────────────────────────────────────
    'admin.wa.tab_discount': "İndirim Kampanyaları",
    'admin.wa.tab_whatsapp': "WhatsApp Kampanyaları",
    'admin.wa.step1_title': "1. Şablon Seç",
    'admin.wa.step2_title': "2. Alıcı Seç",
    'admin.wa.step3_title': "3. Onayla & Gönder",
    'admin.wa.step_result': "Sonuç",
    'admin.wa.template_search': "Şablon ara...",
    'admin.wa.template_empty': "Onaylı şablon bulunamadı.",
    'admin.wa.template_load_error': "Şablonlar yüklenemedi. Lütfen tekrar deneyin.",
    'admin.wa.template_preview': "Mesaj Önizleme",
    'admin.wa.template_selected': "Seçili Şablon",
    'admin.wa.recipients_tab_leads': "Lead'ler",
    'admin.wa.recipients_tab_students': "Öğrenciler",
    'admin.wa.recipients_tab_manual': "Manuel",
    'admin.wa.leads_status_filter': "Duruma göre filtrele",
    'admin.wa.leads_all_status': "Tüm durumlar",
    'admin.wa.search_placeholder': "Ara...",
    'admin.wa.manual_placeholder': "Her satıra bir numara (örn. +905551234567)",
    'admin.wa.selected_count': "Seçili",
    'admin.wa.confirm_summary': "şablonu",
    'admin.wa.confirm_summary2': "kişiye gönderilecek.",
    'admin.wa.confirm_checkbox': "Alıcı listesini kontrol ettim",
    'admin.wa.send_button': "Gönder",
    'admin.wa.sending': "Gönderiliyor...",
    'admin.wa.count_mismatch': "Liste değişmiş, lütfen yenileyip tekrar deneyin.",
    'admin.wa.no_recipients': "Geçerli alıcı bulunamadı.",
    'admin.wa.too_many': "Çok fazla alıcı (maks. 500).",
    'admin.wa.service_unavailable': "WhatsApp servisi şu an kullanılamıyor.",
    'admin.wa.send_error': "Gönderim sırasında bir hata oluştu.",
    'admin.wa.result_title': "Gönderim Sonucu",
    'admin.wa.result_sent': "Gönderildi",
    'admin.wa.result_failed': "Başarısız",
    'admin.wa.result_optout': "Opt-Out",
    'admin.wa.result_deduped': "Tekrar Kaldırıldı",
    'admin.wa.result_invalid': "Geçersiz Numaralar",
    'admin.wa.poll_status': "Teslimat izleniyor...",
    'admin.wa.view_report': "Raporu Gör",
    'admin.wa.report_phone': "Telefon",
    'admin.wa.report_status': "Durum",
    'admin.wa.report_sent_at': "Gönderildi",
    'admin.wa.report_delivered_at': "İletildi",
    'admin.wa.progress_sent': "Gönderildi",
    'admin.wa.progress_delivered': "İletildi",
    'admin.wa.progress_read': "Okundu",
    'admin.wa.progress_failed': "Başarısız",
    'admin.wa.new_campaign': "Yeni Kampanya",
    'admin.wa.step_back': "Geri",
    'admin.wa.step_next': "İleri",
    'admin.wa.no_template_selected': "Devam etmek için bir şablon seçin.",
    'admin.wa.no_recipients_selected': "En az bir alıcı seçin.",
    'admin.wa.loading_leads': "Lead'ler yükleniyor...",
    'admin.wa.loading_students': "Öğrenciler yükleniyor...",
    'admin.wa.select_all': "Tümünü Seç",
    'admin.wa.deselect_all': "Seçimi Kaldır",
    'admin.wa.approved_badge': "ONAYLANDI",
    'admin.wa.language_badge': "Dil",
    'admin.wa.category_badge': "Kategori",
    'admin.wa.manual_count_suffix': "numara",
    'admin.wa.result_send_initiated': "{count} alıcıya gönderilmeye başlandı",
    'admin.wa.result_invalid_count': "Geçersiz Numara",
    'admin.wa.poll_error': "Durum sorgulanamadı. Lütfen daha sonra kontrol edin.",
    'admin.wa.no_tracking': "Gönderim kabul edildi, durum takibi bu kampanya için kullanılamıyor.",
    'admin.wa.students_course_filter': "Tüm kurslar",
    'admin.wa.leads_education_filter': "İlgilenilen eğitim",
    'admin.wa.leads_education_placeholder': "örn. Kabin Görevlisi",
    'admin.wa.no_phone_badge': "Telefon yok",
    'admin.wa.select_all_with_phone': "Tümünü seç ({count})",
    'admin.wa.deselect_all_filtered': "Seçimi temizle",
    'admin.wa.filter_changed_reset': "Filtre değişti — seçim sıfırlandı.",
    'admin.wa.selection_limit_warning': "Seçim 500 alıcı sınırını aşıyor, filtreyi daraltın.",
    'admin.wa.phone_masked_label': "Telefon",
    'admin.wa.recipient_detail_label': "Detay",
    'admin.wa.recipients_total': "Toplam",
    'admin.wa.recipients_with_phone': "Telefonlu",
    'admin.wa.truncation_warning': "Toplam {total} kayıttan ilk {shown} listeleniyor — filtreyi daraltın",
    'admin.wa.select_all_disabled_truncated': "Liste kısaltılmışken Tümünü Seç devre dışı",

    // ── AdminCampaigns — Meta Şablonları sekmesi ─────────────────────────────
    'admin.wa.tab_templates': "Şablonlar",
    'admin.wa.tpl_list_title': "WhatsApp Şablonları",
    'admin.wa.tpl_list_subtitle': "Meta'ya kayıtlı mesaj şablonlarını yönet",
    'admin.wa.tpl_refresh': "Yenile",
    'admin.wa.tpl_refreshing': "Yenileniyor...",
    'admin.wa.tpl_status_approved': "Onaylı",
    'admin.wa.tpl_status_pending': "Meta onayı bekleniyor",
    'admin.wa.tpl_status_rejected': "Reddedildi",
    'admin.wa.tpl_body_preview': "Mesaj önizleme",
    'admin.wa.tpl_body_expand': "Tüm metni göster",
    'admin.wa.tpl_body_collapse': "Küçült",
    'admin.wa.tpl_delete': "Sil",
    'admin.wa.tpl_delete_confirm_title': "Şablon silinsin mi?",
    'admin.wa.tpl_delete_confirm_body': "Bu işlem geri alınamaz ve aynı adla 30 gün boyunca yeni şablon oluşturulamaz.",
    'admin.wa.tpl_delete_confirm_btn': "Evet, sil",
    'admin.wa.tpl_delete_cancel': "Vazgeç",
    'admin.wa.tpl_deleting': "Siliniyor...",
    'admin.wa.tpl_deleted_ok': "Şablon silindi.",
    'admin.wa.tpl_empty': "Şablon bulunamadı.",
    'admin.wa.tpl_load_error': "Şablonlar yüklenemedi.",
    'admin.wa.tpl_503': "WhatsApp servisi şu an kullanılamıyor. Meta API kimlik bilgilerini kontrol edin.",
    'admin.wa.tpl_create_title': "Yeni Şablon Oluştur",
    'admin.wa.tpl_name_label': "Şablon adı",
    'admin.wa.tpl_name_hint': "Yalnızca küçük harf, rakam ve alt çizgi",
    'admin.wa.tpl_name_preview': "Önizleme",
    'admin.wa.tpl_category_label': "Kategori",
    'admin.wa.tpl_category_marketing': "MARKETING – Tanıtım / Promosyon",
    'admin.wa.tpl_category_utility': "UTILITY – İşlemsel",
    'admin.wa.tpl_category_authentication': "AUTHENTICATION – Doğrulama",
    'admin.wa.tpl_language_label': "Dil",
    'admin.wa.tpl_header_label': "Başlık metni (opsiyonel)",
    'admin.wa.tpl_header_hint': "{count}/60 karakter",
    'admin.wa.tpl_body_label': "Gövde",
    'admin.wa.tpl_body_hint': "{count}/1024 karakter",
    'admin.wa.tpl_footer_label': "Alt bilgi metni (opsiyonel)",
    'admin.wa.tpl_footer_hint': "{count}/60 karakter",
    'admin.wa.tpl_submit': "Meta onayına gönder",
    'admin.wa.tpl_submitting': "Gönderiliyor...",
    'admin.wa.tpl_submit_ok': "Şablon Meta onayına gönderildi — onay genellikle dakikalar-saatler sürer, durumu listeden takip edebilirsin.",
    'admin.wa.tpl_submit_ok_short': "Şablon Meta'ya gönderildi",
    'admin.wa.tpl_error_invalid_name': "Şablon adı geçersiz. Yalnızca küçük harf, rakam ve alt çizgi kullanın.",
    'admin.wa.tpl_error_invalid_category': "Geçersiz kategori.",
    'admin.wa.tpl_error_body_too_long': "Gövde metni 1024 karakter sınırını aşıyor.",
    'admin.wa.tpl_info_note': "Onaylanan şablonlar kampanya sihirbazındaki listeye düşer (aynı WhatsApp Business hesabı). Not: sihirbaz listesi ayrı bir kaynaktan gelir; yeni oluşturulan şablonların orada görünmesi biraz zaman alabilir.",
    'admin.wa.tpl_has_buttons_badge': "Butonlu",
    'admin.wa.tpl_name_required': "Şablon adı zorunludur.",
    'admin.wa.tpl_body_required': "Gövde metni zorunludur.",
    'admin.wa.tpl_header_type_label': "Başlık tipi",
    'admin.wa.tpl_header_type_none': "Yok",
    'admin.wa.tpl_header_type_text': "Metin",
    'admin.wa.tpl_header_type_image': "Görsel",
    'admin.wa.tpl_header_image_hint': "JPEG veya PNG, maks. 5 MB",
    'admin.wa.tpl_header_image_uploading': "Yükleniyor...",
    'admin.wa.tpl_header_image_uploaded': "Yüklendi",
    'admin.wa.tpl_header_image_too_large': "Görsel 5 MB sınırını aşıyor.",
    'admin.wa.tpl_header_image_invalid_type': "Yalnızca JPEG ve PNG kabul edilir.",
    'admin.wa.tpl_header_image_upload_error': "Görsel yüklenemedi.",
    'admin.wa.tpl_header_image_app_id_missing': "WhatsApp App ID tanımlı değil — görsel başlık kullanılamıyor.",
    'admin.wa.tpl_header_image_badge': "Görselli başlık",
    'admin.wa.tpl_image_faz_b_note': "Görselli şablonların kampanya gönderimi doğrudan Meta geçişi (Faz B) tamamlanınca tam desteklenir.",

    // ── AdminCampaigns — Kariyer Takvimi Paylaşımı sekmesi ───────────────────
    'admin.wa.tab_kariyertakvimi': "Kariyer Takvimi Paylaşımı",
    'admin.ext.info_title': "Dış Kitle API",
    'admin.ext.info_endpoint': "Endpoint",
    'admin.ext.info_key_note': "API anahtarı .env dosyasında EXTERNAL_AUDIENCE_API_KEY olarak tanımlıdır (değer burada gösterilmez).",
    'admin.ext.info_prod_note': "Dış erişim yalnızca prod ortamında aktif olur.",
    'admin.ext.preview_title': "Paylaşılacak Veri Önizlemesi",
    'admin.ext.logs_title': "Erişim Günlüğü",
    'admin.ext.type_label': "Veri tipi",
    'admin.ext.type_leads': "Lead'ler",
    'admin.ext.type_students': "Öğrenciler",
    'admin.ext.status_label': "Durum",
    'admin.ext.status_all': "Tüm durumlar",
    'admin.ext.education_placeholder': "İlgilenilen eğitim (örn. Kabin Görevlisi)",
    'admin.ext.course_label': "Tüm kurslar",
    'admin.ext.search_placeholder': "Ad / telefon ara...",
    'admin.ext.summary': "Toplam {total} kayıt, {with_phone} telefonlu",
    'admin.ext.col_name': "Ad Soyad",
    'admin.ext.col_phone': "Telefon",
    'admin.ext.col_status': "Durum / Detay",
    'admin.ext.col_created': "Kayıt Tarihi",
    'admin.ext.info_note': "Kariyer Takvimi bu filtrelerle aynı listeyi API'den çekebilir.",
    'admin.ext.prev_btn': "Önceki",
    'admin.ext.next_btn': "Sonraki",
    'admin.ext.log_col_time': "Zaman",
    'admin.ext.log_col_ip': "IP",
    'admin.ext.log_col_auth': "Yetki",
    'admin.ext.log_col_filters': "Filtreler",
    'admin.ext.log_col_count': "Kayıt",
    'admin.ext.log_col_duration': "Süre (ms)",
    'admin.ext.log_auth_ok': "OK",
    'admin.ext.log_auth_fail': "Başarısız",
    'admin.ext.log_empty_title': "Kariyer Takvimi henüz veri çekmedi.",
    'admin.ext.log_empty_desc': "Dış sistem istekte bulununca erişim kayıtları burada görünür.",
    'admin.ext.refresh': "Yenile",
    'admin.ext.refreshing': "Yenileniyor...",

    // ── AdminReviews ──────────────────────────────────────────────────────────
    'admin.rev_page_title': "Yorum Yönetimi",
    'admin.rev_page_subtitle': "Öğrenci kurs yorumlarını moderasyon edin",
    'admin.rev_delete_title': "Yorumu Sil",
    'admin.rev_delete_desc': "Bu yorumu silmek istediğinize emin misiniz?",
    'admin.rev_deleted': "Yorum silindi",
    'admin.rev_empty_title': "Henüz yorum yok",
    'admin.rev_empty_desc': "Öğrenci kurs yorumları burada görünecek.",

    // ── AdminMessages ─────────────────────────────────────────────────────────
    'admin.msg_page_title': "Öğrenci Mesajları",
    'admin.msg_page_subtitle': "Öğrenci iletişim mesajlarını yanıtlayın",
    'admin.msg_reply': "Cevapla",
    'admin.msg_reply_placeholder': "Cevabınızı yazın...",
    'admin.msg_sent': "Cevap gönderildi",
    'admin.msg_reply_label': "Cevap",
    'admin.msg_validation': "Cevap metni boş olamaz",
    'admin.msg_empty_title': "Henüz mesaj yok",
    'admin.msg_empty_desc': "Öğrenci iletişim mesajları burada görünecek.",

    // ── AdminLogs ─────────────────────────────────────────────────────────────
    'admin.log_page_title': "Log Kayıtları",
    'admin.log_page_subtitle': "Admin ve kullanıcı işlem geçmişini görüntüleyin",
    'admin.log_filter': "İşlem filtrele...",
    'admin.log_date': "Tarih",
    'admin.log_user': "Kullanıcı",
    'admin.log_action': "İşlem",
    'admin.log_details': "Detay",
    'admin.log_empty_title': "Log kaydı bulunamadı",
    'admin.log_empty_desc': "Aktivite logları burada görünecek.",

    // ── AdminCertificates ─────────────────────────────────────────────────────
    'admin.cert_page_title': "Sertifika Yönetimi",
    'admin.cert_page_subtitle': "Sertifika türlerini ve kurumlarını yönetin",
    'admin.cert_new': "Sertifika Ekle",
    'admin.cert_types': "Sertifika Türleri",
    'admin.cert_name_tr': "Sertifika Adı (TR) *",
    'admin.cert_name_en': "Sertifika Adı (EN)",
    'admin.cert_provider': "Kurum / Üniversite *",
    'admin.cert_exam_fee': "Sınav Ücreti (TRY) *",
    'admin.cert_save': "Kaydet",
    'admin.cert_update': "Güncelle",
    'admin.cert_added': "Sertifika eklendi",
    'admin.cert_updated': "Sertifika güncellendi",
    'admin.cert_deleted': "Sertifika türü silindi",
    'admin.cert_delete_title': "Sertifika Türünü Sil",
    'admin.cert_delete_desc': "Emin misiniz? Bu sertifika türüne sahip öğrenciler etkilenebilir.",
    'admin.cert_empty_title': "Henüz sertifika türü eklenmemiş",
    'admin.cert_empty_desc': "Öğrencilerin kazanabileceği sertifika türlerini ekleyin.",
    'admin.cert_stat_types': "Sertifika Türü",
    'admin.cert_stat_institutions': "Kurum/Üniversite",
    'admin.cert_stat_avg_fee': "Ort. Sınav Ücreti (TRY)",
    'admin.cert_edit': "Sertifika Düzenle",
    'admin.cert_new_form': "Yeni Sertifika Türü",

    // ── AdminPayments ─────────────────────────────────────────────────────────
    'admin.pay_page_title': "Ödeme Takibi",
    'admin.pay_page_subtitle': "Tüm ödeme işlemlerini görüntüleyin",
    'admin.pay_revenue': "Toplam Gelir",
    'admin.pay_transactions': "İşlem Sayısı",
    'admin.pay_avg': "Ort. İşlem",
    'admin.pay_col_user': "Kullanıcı",
    'admin.pay_col_course': "Kurs",
    'admin.pay_col_amount': "Tutar",
    'admin.pay_col_status': "Durum",
    'admin.pay_col_date': "Tarih",
    'admin.pay_empty_title': "Henüz ödeme yok",
    'admin.pay_empty_desc': "Ödeme kayıtları burada görünecek.",

    // ── AdminAccounting v2 ────────────────────────────────────────────────────
    'admin.acc_page_title': "Mali İşler",
    'admin.acc_page_subtitle': "Muhasebe, taksitler, senetler ve banka hesapları",
    'admin.acc_tab_overview': "Genel Bakış",
    'admin.acc_tab_entries': "Hesap Hareketleri",
    'admin.acc_tab_installments': "Taksitler",
    'admin.acc_tab_notes': "Senetler",
    'admin.acc_tab_banks': "Banka Hesapları",
    'admin.acc_kpi_revenue': "Toplam Gelir",
    'admin.acc_kpi_expense': "Toplam Gider",
    'admin.acc_kpi_net': "Net Kâr",
    'admin.acc_kpi_pending': "Bekleyen Taksit",
    'admin.acc_kpi_bank_total': "Banka Toplam",
    'admin.acc_kpi_overdue': "Geciken",
    'admin.acc_col_user': "Öğrenci",
    'admin.acc_col_amount': "Tutar",
    'admin.acc_col_status': "Durum",
    'admin.acc_col_date': "Tarih",
    'admin.acc_col_due': "Vade Tarihi",
    'admin.acc_col_note_no': "Senet No",
    'admin.acc_col_bank': "Banka",
    'admin.acc_col_iban': "IBAN",
    'admin.acc_col_type': "Tip",
    'admin.acc_col_category': "Kategori",
    'admin.acc_col_desc': "Açıklama",
    'admin.acc_col_installment_no': "Taksit #",
    'admin.acc_collect': "Tahsil Et",
    // B2 fix — taksit iptal edilmişse kapalı "Tahsil Et" göstergesindeki tooltip/etiket.
    'admin.acc_collect_disabled_cancelled_hint': "Bu taksit iptal edildi; tahsil edilemez.",
    'admin.acc_collected': "Taksit tahsil edildi",
    'admin.acc_note_collected': "Senet tahsil edildi",
    'admin.acc_note_deleted': "Senet silindi",
    'admin.acc_bank_saved': "Hesap eklendi",
    'admin.acc_bank_updated': "Hesap güncellendi",
    'admin.acc_entry_saved': "Kayıt eklendi",
    'admin.acc_add_bank': "Hesap Ekle",
    'admin.acc_add_note': "Senet Ekle",
    'admin.acc_new_entry': "Yeni Kayıt",
    'admin.acc_collect_payment': "Tahsilat Yap",
    'admin.acc_select_student': "Öğrenci Seç",
    'admin.acc_note_number': "Senet No",
    'admin.acc_due_date': "Vade Tarihi",
    'admin.acc_bank_name': "Banka Adı *",
    'admin.acc_account_name': "Hesap Adı *",
    'admin.acc_iban_placeholder': "TR00 0000 0000 0000 0000 0000 00",
    'admin.acc_account_no': "Hesap No",
    'admin.acc_branch': "Şube",
    'admin.acc_opening_balance': "Başlangıç Bakiye",
    'admin.acc_iban_hint': "IBAN TR ile başlamalı, toplam 26 karakter olmalıdır",
    'admin.acc_iban_hint_optional': "Opsiyonel — girilirse IBAN TR ile başlamalı, toplam 26 karakter olmalıdır",
    'admin.acc_account_type': "Hesap Türü",
    'admin.acc_account_type_cash': "Nakit Kasa",
    'admin.acc_account_type_bank': "Banka",
    'admin.acc_account_type_pos': "POS (Sanal Pos, Moka)",
    'admin.acc_currency': "Para Birimi",
    'admin.acc_delete_note_title': "Senedi Sil",
    'admin.acc_delete_note_desc': "Bu senedi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
    'admin.acc_collect_note_title': "Tahsil Edildi Olarak İşaretle",
    'admin.acc_collect_note_desc': "Bu senedi tahsil edildi olarak işaretlemek istediğinize emin misiniz?",
    'admin.acc_note_invoice_id': "Borç Kaydı",
    'admin.acc_note_invoice_loading': "Borç kayıtları yükleniyor...",
    'admin.acc_note_invoice_select_student_first': "Önce öğrenci seçin",
    'admin.acc_note_invoice_none': "Bu öğrencinin borç kaydı yok",
    'admin.acc_note_invoice_select': "Borç kaydı seçin",
    'admin.acc_note_validation_invoice': "Senedin bağlı olduğu borç kaydını seçin",
    'admin.acc_note_collect_modal_title': "Senet Tahsilatı",
    'admin.acc_note_collect_bank_placeholder': "Banka/kasa seçin",
    'admin.acc_note_collect_bank_required': "Tahsilat için banka/kasa seçimi zorunludur",
    'admin.acc_collect_amount_locked_hint': "Tahsilat tutarı, taksidin (faiz dahil) tutarına eşit olmalıdır; kısmi tahsilat desteklenmiyor.",
    'admin.acc_collect_note_synced': "Bağlı senet de tahsil edildi olarak kapatıldı ({n}).",
    'admin.acc_collect_note_skipped': "{n} bağlı senet kapatılamadı; muhasebe mutabakatı gerekiyor.",
    'admin.acc_collect_installment_title': "Taksit Tahsil Et",
    'admin.acc_collect_installment_desc': "Bu taksiti tahsil etmek istediğinize emin misiniz?",
    'admin.acc_payment_method': "Ödeme Yöntemi",
    'admin.acc_cash': "Nakit",
    'admin.acc_card': "Kredi Kartı",
    'admin.acc_transfer': "Havale/EFT",
    'admin.acc_cash_register': "Kasa",
    'admin.acc_no_banks': "Banka eklenmemiş",
    'admin.acc_no_notes': "Senet kaydı yok",
    'admin.acc_no_installments': "Taksit kaydı yok",
    'admin.acc_no_entries': "Muhasebe kaydı yok",
    'admin.acc_income': "Gelir",
    'admin.acc_expense': "Gider",
    'admin.acc_validation_category': "Kategori ve açıklama zorunlu",
    'admin.acc_validation_amount': "Tutar 0'dan büyük olmalı",
    'admin.acc_validation_student': "Öğrenci seçin",
    'admin.acc_validation_due_date': "Vade tarihi zorunlu",
    'admin.acc_validation_bank_name': "Hesap adı zorunlu",
    'admin.acc_validation_iban': "IBAN zorunlu",
    'admin.acc_validation_iban_format': "Geçerli bir TR IBAN giriniz (TR ile başlamalı, toplam 26 karakter)",
    'admin.acc_new_bank': "Yeni Hesap",
    'admin.acc_edit_bank': "Hesap Düzenle",
    'admin.acc_saving': "Kaydediliyor...",
    'admin.acc_save': "Kaydet",
    'admin.acc_update': "Güncelle",
    'admin.acc_creating': "Oluşturuluyor...",
    'admin.acc_create': "Oluştur",
    'admin.acc_collecting': "Tahsil ediliyor...",
    'admin.acc_net': "Net",
    'admin.acc_banks': "Bankalar",
    'admin.acc_overdue_label': "Geciken",
    'admin.acc_overdue_banner': "{count} geciken ödeme — toplam {amount} TRY",
    'admin.acc_no_overdue': "Geciken ödeme yok",
    'admin.acc_no_payments': "Ödeme kaydı bulunamadı",
    'admin.acc_status_paid': "Ödendi",
    'admin.acc_status_pending': "Bekliyor",
    'admin.acc_status_overdue': "Gecikti",
    'admin.acc_status_collected': "Tahsil Edildi",
    'admin.acc_status_cancelled': "İptal",
    'admin.acc_status_active': "Aktif",
    'admin.acc_overdue_tab': "Geciken ({count})",

    // ── AdminAccounting — yeni sekmeler (Giderler / İadeler / Faiz Ayarları) ──
    'admin.acc_tab_expenses': "Giderler",
    'admin.acc_tab_refunds': "İadeler",
    'admin.acc_tab_interest': "Faiz Ayarları",

    // KPI yeni kartlar
    'admin.acc_kpi_gross_income': "Brüt Gelir",
    'admin.acc_kpi_refunded': "İade Edilen",
    'admin.acc_kpi_net_income': "Net Gelir",
    'admin.acc_kpi_net_profit': "Net Kâr",
    // FAZ 1 hizalama — net_profit < 0 iken acc_kpi_net_profit yerine gösterilir
    'admin.acc_kpi_net_loss': "Net Zarar",
    'admin.acc_kpi_overdue_count': "Geciken Taksit",
    'admin.acc_kpi_interest_count': "Faizli Taksit",

    // Giderler sekmesi
    'admin.acc_expense_new': "Yeni Gider",
    'admin.acc_expense_category': "Kategori",
    'admin.acc_expense_vendor': "Tedarikçi",
    'admin.acc_expense_doc_ref': "Belge Referansı",
    'admin.acc_expense_recurring': "Düzenli Gider",
    'admin.acc_expense_saved': "Gider kaydedildi",
    'admin.acc_expense_updated': "Gider güncellendi",
    'admin.acc_expense_deleted': "Gider silindi",
    'admin.acc_expense_summary': "Kategori Özeti",
    'admin.acc_no_expenses': "Gider kaydı yok",
    'admin.acc_expense_delete_title': "Gideri Sil",
    'admin.acc_expense_delete_desc': "Bu gideri silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
    'admin.acc_expense_edit': "Gider Düzenle",
    'admin.acc_cat_rent': "Kira",
    'admin.acc_cat_bank_fee': "Banka Masrafı",
    'admin.acc_cat_salary': "Maaş",
    'admin.acc_cat_utility': "Fatura/Aidat",
    'admin.acc_cat_marketing': "Pazarlama",
    'admin.acc_cat_tax': "Vergi",
    'admin.acc_cat_office': "Ofis",
    'admin.acc_cat_other': "Diğer",

    // İadeler sekmesi
    'admin.acc_refund_new': "Yeni İade",
    'admin.acc_refund_amount': "İade Tutarı",
    'admin.acc_refund_reason': "İade Gerekçesi",
    'admin.acc_refund_method': "İade Yöntemi",
    'admin.acc_refund_receipt': "Havale Dekontu",
    'admin.acc_refund_payment_id': "Ödeme ID",
    'admin.acc_refund_invoice_id': "Borç Kaydı ID",
    'admin.acc_refund_or': "VEYA",
    'admin.acc_refund_saved': "İade talebi oluşturuldu",
    'admin.acc_no_refunds': "İade kaydı yok",
    'admin.acc_refund_receipt_required': "Havale/EFT iadede dekont yüklemek zorunludur",
    'admin.acc_refund_validation_amount': "İade tutarı 0'dan büyük olmalı",
    'admin.acc_refund_validation_reason': "Gerekçe zorunlu",
    'admin.acc_refund_validation_method': "Yöntem zorunlu",
    'admin.acc_refund_validation_ref': "Ödeme ID veya Fatura ID girilmeli",
    'admin.acc_reason_student_request': "Öğrenci Talebi",
    'admin.acc_reason_course_cancel': "Kurs İptali",
    'admin.acc_reason_content_change': "İçerik Değişikliği",
    'admin.acc_reason_other': "Diğer",
    'admin.acc_method_transfer': "Havale/EFT",
    'admin.acc_method_cash': "Nakit",
    'admin.acc_method_moka_refund': "Moka İadesi (Otomatik)",
    'admin.acc_refund_status_requested': "Talep Edildi",
    'admin.acc_refund_status_approved': "Onaylandı",
    'admin.acc_refund_status_completed': "Tamamlandı",
    'admin.acc_refund_status_rejected': "Reddedildi",
    'admin.acc_refund_col_amount': "Tutar",
    'admin.acc_refund_col_reason': "Gerekçe",
    'admin.acc_refund_col_method': "Yöntem",
    'admin.acc_refund_col_status': "Durum",
    'admin.acc_refund_col_date': "Tarih",
    'admin.acc_refund_col_user': "Öğrenci",

    // Faiz ayarları sekmesi
    'admin.acc_interest_title': "Faiz ve Eskalasyon Ayarları",
    'admin.acc_interest_enabled': "Faiz Hesabını Etkinleştir",
    'admin.acc_interest_annual_rate': "Yıllık Faiz Oranı (%)",
    'admin.acc_interest_grace_period': "Muafiyet Süresi (gün)",
    'admin.acc_interest_warning_day_1': "1. Uyarı Eşiği (gün) — Geciken statüsüne geçiş",
    'admin.acc_interest_warning_day_2': "2. Uyarı Eşiği (gün) — Gecikme Uyarısı statüsüne geçiş",
    'admin.acc_interest_start_day': "Faiz Başlangıç Günü",
    'admin.acc_interest_legal_day': "İcra Başlangıç Günü",
    'admin.acc_interest_min_amount': "Min. Faiz Tutarı (TRY)",
    'admin.acc_interest_saved': "Faiz ayarları kaydedildi",
    'admin.acc_interest_job_run': "Faiz tahakkuk işlemi başlatıldı",
    'admin.acc_interest_job_running': "Çalışıyor...",
    'admin.acc_interest_run_job': "Faiz Tahakkuk İşlemini Şimdi Çalıştır",
    'admin.acc_interest_run_job_desc': "Tüm geciken taksitler için günlük faiz tahakkuk sürecini manuel başlatır.",
    'admin.acc_interest_rate_hint': "Örnek: 36 = %36 yıllık faiz. Günlük oran: yıllık_oran / 365",
    'admin.acc_interest_loading': "Faiz ayarları yükleniyor...",
    // #9: error states
    'admin.acc_expense_load_error': "Giderler yüklenemedi. Lütfen tekrar deneyin.",
    'admin.acc_refund_load_error': "İadeler yüklenemedi. Lütfen tekrar deneyin.",
    'admin.acc_interest_load_error': "Faiz ayarları yüklenemedi. Lütfen tekrar deneyin.",
    'admin.acc_retry': "Tekrar Dene",
    // #14: expense description validation
    'admin.acc_validation_description': "Açıklama zorunludur",
    // #1: refund invoice required
    'admin.acc_refund_validation_invoice': "Borç Kaydı ID zorunludur",
    // #16: payment_id optional label
    'admin.acc_refund_optional': "isteğe bağlı",
    // invoice dropdown UX
    'admin.acc_refund_invoice_select': "Borç Kaydı Seç",
    'admin.acc_refund_invoice_loading': "Borç kayıtları yükleniyor...",
    'admin.acc_refund_no_invoices': "Bu öğrenciye ait borç kaydı bulunamadı",
    'admin.acc_refund_select_student_first': "Önce öğrenci seçin",
    // #17: breadcrumb
    'admin.acc_breadcrumb_admin': "Admin",
    // #15: Moka notice user-friendly
    'admin.acc_refund_moka_notice': "Moka otomatik iadesi şu an aktif değil. Bu talep finans ekibi tarafından manuel işlenecektir.",

    // Taksit genişletilmiş kolonlar
    'admin.acc_col_interest': "Faiz",
    'admin.acc_col_total_due': "Toplam Borç",
    'admin.acc_col_days_overdue': "Gecikme (gün)",
    'admin.acc_status_overdue_warn': "Uyarı",
    'admin.acc_status_interest': "Faizli",
    'admin.acc_status_legal': "İcra",

    // ── AdminAccounting — Eşleşme Havuzu ─────────────────────────────────────
    'admin.acc_tab_match_pool': "Eşleşme Havuzu",
    'admin.acc_match_pool_title': "Banka Hareketi Eşleşme Havuzu",
    'admin.acc_match_pool_subtitle': "Havale ödemelerini bekleyen kayıtlarla eşleştirin",
    'admin.acc_match_new_tx': "Yeni Banka Hareketi",
    'admin.acc_match_csv_import': "CSV İçe Aktar",
    'admin.acc_match_col_date': "Tarih",
    'admin.acc_match_col_sender': "Gönderici",
    'admin.acc_match_col_amount': "Tutar",
    'admin.acc_match_col_bank': "Banka",
    'admin.acc_match_col_desc': "Açıklama",
    'admin.acc_match_col_suggested': "Önerilen Eşleşme",
    'admin.acc_match_col_actions': "İşlemler",
    'admin.acc_match_btn_match': "Eşleştir",
    'admin.acc_match_btn_delete': "Sil",
    'admin.acc_match_no_records': "Eşleşmemiş hareket yok",
    'admin.acc_match_no_records_desc': "Tüm havale hareketleri eşleştirilmiş.",
    'admin.acc_match_has_more': "200'den fazla eşleşmemiş hareket var. Yalnızca ilk 200 gösterilmektedir.",
    'admin.acc_match_loading': "Hareketler yükleniyor...",
    'admin.acc_match_load_error': "Hareketler yüklenemedi. Lütfen tekrar deneyin.",
    'admin.acc_match_form_title': "Yeni Banka Hareketi",
    'admin.acc_match_form_amount': "Tutar",
    'admin.acc_match_form_direction': "Yön",
    'admin.acc_match_form_direction_in': "Gelen (Para Girişi)",
    'admin.acc_match_form_direction_out': "Giden (Para Çıkışı)",
    'admin.acc_match_form_sender_name': "Gönderici Adı",
    'admin.acc_match_form_sender_iban': "Gönderici IBAN",
    'admin.acc_match_form_description': "Açıklama",
    'admin.acc_match_form_date': "İşlem Tarihi",
    'admin.acc_match_form_bank': "Banka Adı",
    'admin.acc_match_form_ext_ref': "Harici Referans (isteğe bağlı)",
    'admin.acc_match_form_save': "Hareketi Kaydet",
    'admin.acc_match_form_saving': "Kaydediliyor...",
    'admin.acc_match_saved': "Banka hareketi kaydedildi",
    'admin.acc_match_val_amount': "Tutar 0'dan büyük olmalı",
    'admin.acc_match_val_sender': "Gönderici adı zorunludur",
    'admin.acc_match_val_date': "İşlem tarihi zorunludur",
    'admin.acc_match_val_bank': "Banka adı zorunludur",
    'admin.acc_match_val_payment': "Eşleştirilecek ödemeyi seçin",
    'admin.acc_match_csv_title': "CSV İçe Aktarma",
    'admin.acc_match_csv_hint': "Banka hareketlerini içeren CSV dosyasını yükleyin",
    'admin.acc_match_csv_btn': "İçe Aktar",
    'admin.acc_match_csv_importing': "Aktarılıyor...",
    'admin.acc_match_csv_result': "Eklendi: {{imported}} | Atlandı (tekrar): {{skipped}} | Hata: {{errors}}",
    'admin.acc_match_csv_error_details': "Hata detayları",
    'admin.acc_match_modal_title': "Hareketi Ödemeyle Eşleştir",
    'admin.acc_match_modal_suggested': "Önerilen",
    'admin.acc_match_modal_payment_id': "Ödeme ID / Referans",
    'admin.acc_match_modal_placeholder': "Ödeme ID veya referans girin...",
    'admin.acc_match_modal_confirm': "Eşleştirmeyi Onayla",
    'admin.acc_match_modal_confirming': "Eşleştiriliyor...",
    'admin.acc_match_success': "Hareket başarıyla eşleştirildi",
    'admin.acc_match_mismatch_warn': "Tutar uyuşmazlığı — Banka: {{bank}}, Ödeme: {{payment}}, Fark: {{diff}}",
    'admin.acc_match_delete_title': "Hareketi Sil",
    'admin.acc_match_delete_desc': "Bu banka hareketi silinsin mi? Eşleştirilmiş hareketler silinemez.",
    'admin.acc_match_deleted': "Hareket silindi",
    'admin.acc_match_delete_409': "Eşleştirilmiş hareket silinemez",
    'admin.acc_match_dir_in': "Gelen",
    'admin.acc_match_dir_out': "Giden",
    // ── Match Modal FIX 1 (id vs ref) ────────────────────────────────────────
    'admin.acc_match_modal_use_suggestion': "Bu ödemeyle eşleştir",
    'admin.acc_match_modal_payment_id_manual': "Ödeme ID (manuel giriş)",
    'admin.acc_match_modal_id_hint': "Dahili pay_xxx ID'si. Öneri varsa yukarıdaki butonu kullanın.",
    // ── Havale Talebi Oluştur (FIX 2) ────────────────────────────────────────
    'admin.acc_havale_btn': "Havale Talebi Oluştur",
    'admin.acc_havale_form_title': "Havale Talebi Oluştur",
    'admin.acc_havale_form_desc': "Admin tarafından oluşturulur. Öğrenci için bekleyen ödeme (KO-STD referansı) kaydedilir.",
    'admin.acc_havale_course': "Kurs",
    'admin.acc_havale_course_select': "Kurs seçin...",
    'admin.acc_havale_courses_loading': "Kurslar yükleniyor...",
    'admin.acc_havale_bank_optional': "isteğe bağlı",
    'admin.acc_havale_bank_auto': "Otomatik banka seç",
    'admin.acc_havale_val_course': "Lütfen kurs seçin",
    'admin.acc_havale_create': "Talebi Oluştur",
    'admin.acc_havale_creating': "Oluşturuluyor...",
    'admin.acc_havale_created': "Havale talebi oluşturuldu",
    'admin.acc_havale_copy': "Kopyala",
    'admin.acc_havale_result_title': "Havale Talebi Oluşturuldu",
    'admin.acc_havale_result_ref': "KO-STD Referansı (öğrenciye iletin)",
    'admin.acc_havale_result_ref_hint': "Öğrenci bu referansı banka transferinde açıklama alanına yazmalıdır.",
    'admin.acc_havale_result_bank_info': "Transfer yapılacak hesap",
    'admin.acc_havale_result_instruction': "Referans kodunu ve IBAN'ı öğrenciye iletin. Banka hareketi geldiğinde 'Yeni Banka Hareketi' bölümünden girin ve bu ödeme ID'siyle eşleştirin.",

    // ── FIX FAZA-3: Güven Skoru + Otomatik Eşleştir + Banka Connector ────────
    'admin.acc_match_confidence_full': "Tam Eşleşme",
    'admin.acc_match_confidence_name_amount': "Önerili — Ad+Tutar",
    'admin.acc_match_confidence_amount_only': "Olası — Tutar",
    'admin.acc_match_confidence_unknown': "Tanımsız",
    'admin.acc_match_col_confidence': "Güven Skoru",
    'admin.acc_auto_match_btn': "Otomatik Eşleştir",
    'admin.acc_auto_match_running': "İşleniyor...",
    'admin.acc_auto_match_result': "{{approved}} otomatik onaylandı, {{suggested}} öneri oluştu ({{processed}} işlendi)",
    'admin.acc_auto_match_error': "Otomatik eşleştirme başarısız",
    'admin.acc_connector_panel_title': "Banka API Bağlantıları",
    'admin.acc_connector_panel_desc': "Banka API erişimi geldiğinde otomatik hareket çekme aktifleşir; şu an CSV/manuel besleme + otomatik eşleştirme çalışıyor.",
    'admin.acc_connector_status_connected': "Bağlı",
    'admin.acc_connector_status_pending': "Bekliyor — API başvurusu",

    // ── FIX 1: Birleşik Defter (Ledger) ──────────────────────────────────────
    'admin.acc_ledger_loading': "Defter yükleniyor...",
    'admin.acc_ledger_error': "Defter yüklenemedi. Lütfen tekrar deneyin.",
    'admin.acc_ledger_empty': "Defter kaydı yok",
    'admin.acc_ledger_current_balance': "Defter Bakiyesi (Net Akış)",
    // FAZ 1 hizalama — defter bakiyesi negatifken kartın altında gösterilir.
    // NOT: öğrenci borç defterinden farklı olarak buradaki negatif değer
    // öğrenciye borç DEĞİL — tüm kaynaklardaki kayıtlı çıkışların (gider+iade)
    // kayıtlı girişleri aştığı anlamına gelir. accounting.debt_ledger.
    // balance_creditor metnini burada KULLANMA.
    'admin.acc_ledger_balance_deficit_hint': "Kayıtlı toplam çıkış (gider + iade), tüm kaynaklardaki kayıtlı toplam girişi aşıyor.",
    'admin.acc_ledger_from': "Başlangıç",
    'admin.acc_ledger_to': "Bitiş",
    'admin.acc_ledger_col_date': "Tarih",
    'admin.acc_ledger_col_type': "Tip",
    'admin.acc_ledger_col_desc': "Açıklama",
    'admin.acc_ledger_col_debit': "Borç (−)",
    'admin.acc_ledger_col_credit': "Alacak (+)",
    'admin.acc_ledger_col_amount': "Tutar",
    'admin.acc_ledger_col_balance': "Bakiye",
    'admin.acc_ledger_prev': "Önceki sayfa",
    'admin.acc_ledger_next': "Sonraki sayfa",

    // ── Mali İşler liste sekmeleri — sunucu taraflı "Listele" (feature/mali-isler-liste-web) ──
    'admin.acc_list_date_range': "Tarih Aralığı",
    'admin.acc_list_btn_list': "Listele",
    'admin.acc_list_date_required': "Tarih aralığı seçip Listele'ye basın.",
    'admin.acc_list_empty_no_range': "Sonuçları görmek için tarih aralığı seçip Listele'ye basın.",
    'admin.acc_list_truncated_warning': "Sonuç sayısı çok fazla — liste kesildi. Tarih aralığını daraltın.",

    // ── Overview akış özeti (feature/mali-isler-ozet-tarih-web) ─────────────
    'admin.acc_overview_flow_title': "Akış Özeti — Tarih Aralığına Bağlı",
    'admin.acc_overview_range_label': "Seçili Aralık",

    // ── İade öğrenci arama (feature/mali-isler-ozet-tarih-web) ──────────────
    'admin.acc_refund_student_search_placeholder': "İsim veya e-posta ile ara (en az 2 karakter)",
    'admin.acc_refund_student_search_searching': "Aranıyor…",
    'admin.acc_refund_student_search_no_results': "Sonuç bulunamadı",

    // ── FIX 2: Banka Ekstre Modal ────────────────────────────────────────────
    'admin.acc_statement_title': "Banka Ekstresi",
    'admin.acc_statement_opening': "Başlangıç Bakiye",
    'admin.acc_statement_balance': "Güncel Bakiye",
    'admin.acc_statement_open': "Ekstre Görüntüle",
    // FAZ 1 hizalama — banka/kasa bakiyesi negatifken tutarın yanında gösterilir.
    // Negatif banka bakiyesi bir kasa açığıdır (kurumun kendi nakit pozisyonu),
    // öğrenciye olan bir borç DEĞİL — accounting.debt_ledger.balance_creditor
    // ("Öğrenciye İade Borcu") ile karıştırılmasın diye ayrı anahtar.
    'admin.acc_balance_deficit_badge': "Kasa Açığı",

    // ── FIX 3: Tıklanabilir KPI ──────────────────────────────────────────────
    'admin.acc_kpi_go_tab': "Sekmeye git",

    // ── Checkout — Havale ile Öde ─────────────────────────────────────────────
    'checkout.payment_method_group': "Ödeme Yöntemi",
    'checkout.method_wire': "Havale / EFT ile Öde",
    'checkout.method_wire_sub': "Banka havalesi ile ödeme yap",
    'checkout.wire_ref_title': "Havale Bilgileri",
    'checkout.wire_bank_name': "Banka",
    'checkout.wire_iban': "IBAN",
    'checkout.wire_account_no': "Hesap No",
    'checkout.wire_ref_no': "Referans No",
    'checkout.wire_amount': "Tutar",
    'checkout.wire_notice': "Önemli: Havale açıklamasına referans numarasını yazmayı unutmayın.",
    'checkout.wire_notice_sub': "Ödeme onaylandıktan sonra kaydınız aktifleştirilecektir (genellikle 1-2 iş günü).",
    'checkout.wire_copy': "Kopyala",
    'checkout.wire_copied': "Kopyalandı!",
    'checkout.wire_no_bank': "Havale ile ödeme şu an kullanılamıyor. Lütfen başka bir yöntem seçin.",
    'checkout.wire_loading': "Ödeme bilgileri yükleniyor...",
    'checkout.wire_error': "Havale bilgileri alınamadı. Lütfen tekrar deneyin.",
    'checkout.wire_retry': "Tekrar Dene",

    // ── AdminBulkOps ────────────────────────────────────────────��─────────────
    'admin.bulk.dashboard.title': "Toplu İşlemler",
    'admin.bulk.dashboard.subtitle': "Öğrenci, kurs ve ödeme verilerini toplu yönet",
    'admin.bulk.dashboard.lastAction': "Son işlem: {{action}} — {{time}}",

    'admin.bulk.tabs.students': "Öğrenciler",
    'admin.bulk.tabs.courses': "Kurslar",
    'admin.bulk.tabs.payments': "Ödemeler",
    'admin.bulk.tabs.export': "Dışa Aktar",

    'admin.bulk.actions.enroll': "Kursa Kaydet",
    'admin.bulk.actions.unenroll': "Kaydı Sil",
    'admin.bulk.actions.roleChange': "Rol Değiştir",
    'admin.bulk.actions.paymentUpdate': "Ödeme Güncelle",
    'admin.bulk.actions.notify': "Bildirim Gönder",
    'admin.bulk.actions.certificate': "Sertifika Ver",
    'admin.bulk.actions.export': "Dışa Aktar",
    'admin.bulk.actions.delete': "Seçilenleri Sil",

    'admin.bulk.bar.selected': "{{count}} kayıt seçildi",
    'admin.bulk.bar.selected_zero': "Kayıt seçilmedi",
    'admin.bulk.bar.clearSelection': "Seçimi temizle",

    'admin.bulk.confirm.cancel': "Vazgeç",
    'admin.bulk.confirm.confirm': "Onayla",

    'admin.bulk.confirm.deleteTitle': "Toplu Silme",
    'admin.bulk.confirm.deleteDesc': "**{{count}} kaydı** kalıcı olarak sileceksiniz. Bu işlem geri alınamaz.",
    'admin.bulk.confirm.deleteStep1': "Evet, devam et",
    'admin.bulk.confirm.deleteStep2Title': "Son Onay",
    'admin.bulk.confirm.deleteStep2Desc': "Onaylamak için aşağıya **SİL** yazın",
    'admin.bulk.confirm.deleteStep2Placeholder': "SİL",
    'admin.bulk.confirm.deleteConfirmBtn': "Kalıcı Sil",

    'admin.bulk.enroll.confirmTitle': "Toplu Kayıt",
    'admin.bulk.enroll.confirmDesc': "**{{count}} öğrenci** \"{{courseName}}\" kursuna kaydedilecek.",
    'admin.bulk.enroll.confirmBtn': "Kaydet",
    'admin.bulk.enroll.courseLabel': "Kurs seçin",
    'admin.bulk.enroll.typeLabel': "Kayıt türü",
    'admin.bulk.enroll.typeFree': "Ücretsiz",
    'admin.bulk.enroll.typePaid': "Ücretli",

    'admin.bulk.roleChange.confirmTitle': "Toplu Rol Değişikliği",
    'admin.bulk.roleChange.confirmDesc': "**{{count}} kullanıcının** rolü \"{{newRole}}\" yapılacak.",
    'admin.bulk.roleChange.confirmBtn': "Rolü Değiştir",
    'admin.bulk.roleChange.roleLabel': "Yeni rol",
    'admin.bulk.roleChange.adminWarning': "Admin rolü tam sistem erişimi verir. Dikkatli olun.",

    'admin.bulk.notify.sheetTitle': "Toplu Bildirim",
    'admin.bulk.notify.titleLabel': "Bildirim başlığı",
    'admin.bulk.notify.titlePlaceholder': "örn. Sınav hatırlatması",
    'admin.bulk.notify.titleRequired': "Başlık zorunludur",
    'admin.bulk.notify.messageLabel': "Mesaj",
    'admin.bulk.notify.messagePlaceholder': "Öğrencilere gönderilecek mesaj...",
    'admin.bulk.notify.channelsLabel': "Kanallar",
    'admin.bulk.notify.channelInApp': "Uygulama içi",
    'admin.bulk.notify.channelEmail': "E-posta",
    'admin.bulk.notify.channelWhatsapp': "WhatsApp",
    'admin.bulk.notify.previewCount': "{{count}} öğrenciye gönderilecek",
    'admin.bulk.notify.sendBtn': "Önizle ve Gönder",
    'admin.bulk.notify.confirmTitle': "Toplu Bildirim Gönder",
    'admin.bulk.notify.confirmDesc': "**{{count}} öğrenciye** {{channels}} üzerinden bildirim gönderilecek.",
    'admin.bulk.notify.confirmBtn': "Gönder",
    // URL onay uyarısı (security denetimi, 2026-08-04) — bkz.
    // utils/urlDetection.js. isSafeInternalDeeplink SADECE ayrı `deeplink`
    // alanını korur, serbest başlık/mesaj metnini KAPSAMAZ.
    'admin.bulk.notify.confirmTitleUrl': "Mesajınızda bir bağlantı var",
    'admin.bulk.notify.urlWarningDesc': "Şu bağlantı/adres tespit edildi: \"{{url}}\". Bazı cihazlarda bildirim metnindeki bağlantılar otomatik tıklanabilir hale gelir — göndermek istediğinize emin misiniz?",
    'admin.bulk.notify.urlWarningBtn': "Yine de Gönder",

    'admin.bulk.export.entityLabel': "Veri türü",
    'admin.bulk.export.entityUsers': "Öğrenciler",
    'admin.bulk.export.entityCourses': "Kurslar",
    'admin.bulk.export.entityPayments': "Ödemeler",
    'admin.bulk.export.fieldsLabel': "Alanlar",
    'admin.bulk.export.formatLabel': "Format",
    'admin.bulk.export.previewCount': "Filtre sonucu: {{count}} kayıt",
    'admin.bulk.export.btn': "Dışa Aktar",
    'admin.bulk.export.downloadReady': "Dosya hazır — indirme başladı",

    'admin.bulk.progress.title': "İşlem devam ediyor...",
    'admin.bulk.progress.status': "{{done}} / {{total}} tamamlandı",
    'admin.bulk.progress.ariaLabel': "Toplu işlem ilerleme göstergesi",
    'admin.bulk.progress.cancelBtn': "İptal et",

    'admin.bulk.result.title': "İşlem Tamamlandı",
    'admin.bulk.result.successCount': "{{count}} işlem başarılı",
    'admin.bulk.result.failedCount': "{{count}} işlem başarısız",
    'admin.bulk.result.errorListTitle': "Hata detayları",
    'admin.bulk.result.errorItem': "{{id}}: {{error}}",
    'admin.bulk.result.closeBtn': "Kapat",
    'admin.bulk.result.newActionBtn': "Yeni İşlem",

    'admin.bulk.result.pushTitle': "Push bildirim sonucu",
    'admin.bulk.result.pushSent': "{{count}} cihaza push bildirimi gönderildi.",
    'admin.bulk.result.pushFailed': "{{count}} cihaza push gönderilemedi.",
    'admin.bulk.result.pushSkippedNoPref': "{{count}} kişi push bildirim tercihini kapattığı için push almadı (uygulama içi bildirim yine gitti).",
    'admin.bulk.result.pushSkippedNoTokens': "{{count}} kişinin kayıtlı aktif cihazı olmadığı için push gitmedi (uygulama içi bildirim yine gitti).",
    'admin.bulk.result.pushSkippedCommercial': "{{count}} kişiye, ticari ileti kategorisinde olduğu için push gönderilmedi — bu bilinçli bir kısıt (ticari push için ayrı onay altyapısı henüz yok), hata değil. Uygulama içi bildirim yine gönderildi.",

    'admin.bulk.empty.title': "Kayıt bulunamadı",
    'admin.bulk.empty.desc': "Filtrelerinizi değiştirip tekrar deneyin",

    'admin.bulk.error.title': "Bağlantı kesildi",
    'admin.bulk.error.desc': "Veri yüklenemedi — tekrar deneyin",
    'admin.bulk.error.retryBtn': "Tekrar Dene",

    // ── Admin Güvenlik ────────────────────────────────────────────────────────
    'admin.security.tabTitle': "Güvenlik",
    'admin.security.activeSessions.title': "Aktif Oturumlar",
    'admin.security.activeSessions.desc': "Kullanıcıların aktif oturumlarını görüntüleyin ve sonlandırın",
    'admin.security.activeSessions.colUser': "Kullanıcı",
    'admin.security.activeSessions.colDevice': "Cihaz",
    'admin.security.activeSessions.colPlatform': "Platform",
    'admin.security.activeSessions.colIp': "IP Adresi",
    'admin.security.activeSessions.colLastSeen': "Son Görülme",
    'admin.security.activeSessions.actionRevoke': "Tüm Oturumları Sonlandır",
    'admin.security.activeSessions.filterSearch': "Kullanıcı ara...",
    'admin.security.activeSessions.filterPlatform': "Platform",
    'admin.security.activeSessions.filterCountry': "Ülke",
    'admin.security.activeSessions.emptyTitle': "Aktif oturum yok",
    'admin.security.activeSessions.emptyDesc': "Tüm kullanıcılar şu anda çevrimdışı.",
    'admin.security.activeSessions.loading': "Oturumlar yükleniyor...",
    'admin.security.anomalies.title': "Şüpheli Aktivite",
    'admin.security.anomalies.desc': "Tespit edilen anomaliler ve güvenlik uyarıları",
    'admin.security.anomalies.colRule': "Kural",
    'admin.security.anomalies.colSeverity': "Önem",
    'admin.security.anomalies.colDate': "Tarih",
    'admin.security.anomalies.colStatus': "Durum",
    'admin.security.anomalies.filterStatus': "Durum",
    'admin.security.anomalies.filterSeverity': "Önem Derecesi",
    'admin.security.anomalies.filterRule': "Kural",
    'admin.security.anomalies.emptyTitle': "Anomali tespit edilmedi",
    'admin.security.anomalies.emptyDesc': "Seçili filtreler için şüpheli aktivite bulunamadı.",
    'admin.security.anomalies.loading': "Anomaliler yükleniyor...",
    'admin.security.anomalies.detailTitle': "Anomali Detayı",
    'admin.security.anomalies.rawDetails': "Ham Veri (JSON)",
    'admin.security.anomalies.severity.low': "Düşük",
    'admin.security.anomalies.severity.medium': "Orta",
    'admin.security.anomalies.severity.high': "Yüksek",
    'admin.security.anomalies.rule.high_frequency': "Yüksek Frekans",
    'admin.security.anomalies.rule.multi_ip': "Çoklu IP",
    'admin.security.anomalies.rule.impossible_travel': "İmkansız Seyahat",
    'admin.security.anomalies.rule.rapid_sequential': "Hızlı Ardışık",
    'admin.security.anomalies.rule.off_hours': "Mesai Dışı Erişim",
    'admin.security.anomalies.status.open': "Açık",
    'admin.security.anomalies.status.reviewed': "İncelendi",
    'admin.security.anomalies.status.dismissed': "Görmezden Gelindi",
    'admin.security.action.detail': "Detay",
    'admin.security.action.dismiss': "Görmezden Gel",
    'admin.security.action.freezeAccount': "Hesabı Dondur",
    'admin.security.action.revokeSessions': "Oturumları Sonlandır",
    'admin.security.action.investigate': "İncelemeye Al",
    'admin.security.confirm.freezeTitle': "Hesabı Dondur",
    'admin.security.confirm.freezeMessage': "Kullanıcı giriş yapamayacak. Bu işlem Öğrenciler sayfasından geri alınabilir.",
    'admin.security.confirm.revokeAllTitle': "Tüm Oturumları Sonlandır",
    'admin.security.userDetail.tabSecurity': "Güvenlik",
    'admin.security.userDetail.accessPattern.title': "Saatlik Erişim Deseni",
    'admin.security.userDetail.accessPattern.noData': "Erişim verisi bulunamadı",

    // ── AdminVoicePracticeScenarios ────────────────────────────────────────────
    'admin.voicePractice.title': "Sesli Pratik Senaryoları",
    'admin.voicePractice.subtitle': "Kabin ekibi eğitimi için sözlü pratik senaryolarını yönetin",
    'admin.voicePractice.createBtn': "Yeni Senaryo",
    'admin.voicePractice.createTitle': "Senaryo Oluştur",
    'admin.voicePractice.editTitle': "Senaryo Düzenle",
    'admin.voicePractice.deleteTitle': "Senaryoyu Sil",
    'admin.voicePractice.deleteDesc': "Bu senaryo kalıcı olarak silinecek ve tüm aktif oturumlardan kaldırılacak.",
    'admin.voicePractice.createSuccess': "Senaryo oluşturuldu",
    'admin.voicePractice.updateSuccess': "Senaryo güncellendi",
    'admin.voicePractice.deleteSuccess': "Senaryo silindi",
    'admin.voicePractice.validationTitleRequired': "Başlık (TR) ve Başlık (EN) zorunludur",
    'admin.voicePractice.validationCategoryRequired': "Kategori seçimi zorunludur",
    'admin.voicePractice.colTitle': "Başlık",
    'admin.voicePractice.colCategory': "Kategori",
    'admin.voicePractice.colLevel': "Seviye",
    'admin.voicePractice.colDuration': "Süre",
    'admin.voicePractice.colActive': "Durum",
    'admin.voicePractice.colActions': "İşlemler",
    'admin.voicePractice.filterCategory': "Kategori",
    'admin.voicePractice.filterLevel': "Seviye",
    'admin.voicePractice.filterStatus': "Durum",
    'admin.voicePractice.fieldCategory': "Kategori",
    'admin.voicePractice.fieldLevel': "Seviye",
    'admin.voicePractice.fieldTitleTr': "Başlık (TR)",
    'admin.voicePractice.fieldTitleEn': "Başlık (EN)",
    'admin.voicePractice.fieldDescTr': "Açıklama (TR)",
    'admin.voicePractice.fieldDescEn': "Açıklama (EN)",
    'admin.voicePractice.fieldRefTextTr': "Referans Metin (TR)",
    'admin.voicePractice.fieldRefTextEn': "Referans Metin (EN)",
    'admin.voicePractice.fieldDuration': "Hedef Süre (saniye)",
    'admin.voicePractice.fieldDurationHint': "Önerilen pratik süresi (saniye cinsinden)",
    'admin.voicePractice.fieldTags': "Etiketler",
    'admin.voicePractice.tagsPlaceholder': "Etiket ekle (Enter veya virgül)",
    'admin.voicePractice.fieldActive': "Aktif",
    'admin.voicePractice.fieldInactive': "Pasif",
    'admin.voicePractice.emptyTitle': "Henüz senaryo yok",
    'admin.voicePractice.emptyDesc': "İlk sesli pratik senaryosunu oluşturun.",
    // kategoriler
    'admin.voicePractice.category.announce': "Anonslar",
    'admin.voicePractice.category.safety': "Güvenlik",
    'admin.voicePractice.category.service': "Yolcu Hizmetleri",
    'admin.voicePractice.category.emergency': "Acil Durum",
    'admin.voicePractice.category.general': "Genel",
    'admin.voicePractice.category.turbulence': "Türbülans",
    'admin.voicePractice.category.landing': "İniş",
    'admin.voicePractice.category.delay': "Gecikme",
    // yeni form alanları
    'admin.voicePractice.fieldOrder': "Sıra",
    'admin.voicePractice.fieldOrderTooltip': "Aynı kategori içinde küçük sıra önce gelir",
    'admin.voicePractice.fieldTipsTr': "İpuçları (TR)",
    'admin.voicePractice.fieldTipsEn': "İpuçları (EN)",
    'admin.voicePractice.tipsTrPlaceholder': "Türkçe ipucu ekle (Enter veya virgül)",
    'admin.voicePractice.tipsEnPlaceholder': "İngilizce ipucu ekle (Enter veya virgül)",
    'admin.voicePractice.tipsMaxError': "En fazla 10 ipucu eklenebilir",
    'admin.voicePractice.fieldPassingScore': "Geçme Eşiği",
    'admin.voicePractice.fieldPassingScoreHint': "Geçmek için gereken minimum puan (0–100)",
    'admin.voicePractice.validationPassingScore': "Geçme eşiği 0 ile 100 arasında olmalıdır",
    'admin.voicePractice.fieldIsDialogCapable': "Dialog Modu",
    'admin.voicePractice.fieldDialogRole': "Dialog Rolü",
    'admin.voicePractice.dialogRoleNone': "Yok / None",
    'admin.voicePractice.dialogRolePassenger': "Yolcu / Passenger",
    'admin.voicePractice.dialogRoleSupervisor': "Süpervizör / Supervisor",
    'admin.voicePractice.dialogRoleInterviewer': "Mülakatçı / Interviewer",
    'admin.voicePractice.validationDialogRole': "Dialog modu kapalıyken rol seçilemez",
    'admin.voicePractice.fieldAudioUrl': "TTS Ses URL'i (opsiyonel)",
    'admin.voicePractice.fieldAudioUrlHint': "M4 milestone'ında otomatik doldurulacak, manuel girmeyiniz.",
    'admin.voicePractice.validationAudioUrl': "Geçerli bir URL girilmelidir",
    'admin.voicePractice.fieldUnitId': "Müfredat Ünite ID (opsiyonel)",
    'admin.voicePractice.fieldUnitIdHint': "İleride müfredat sistemine bağlanacak.",
    'admin.voicePractice.sectionAdvanced': "Gelişmiş Ayarlar",
    'admin.voicePractice.sectionDialog': "Dialog Ayarları",
    'admin.voicePractice.fieldDialogOpenerTr': "Dialog Açılış Cümlesi (TR)",
    'admin.voicePractice.fieldDialogOpenerEn': "Dialog Açılış Cümlesi (EN)",
    'admin.voicePractice.fieldDialogOpenerHint': "AI'ın konuşmayı başlatacağı cümle. Boş bırakılırsa AI otomatik üretir (önerilmez).",
    'admin.voicePractice.fieldDialogContextTr': "Dialog Bağlamı (TR)",
    'admin.voicePractice.fieldDialogContextEn': "Dialog Bağlamı (EN)",
    'admin.voicePractice.fieldDialogContextHint': "AI'a verilecek senaryo bağlamı. Boş bırakılırsa açıklama (description) kullanılır.",
    'admin.voicePractice.fieldDialogMaxTurnsOverride': "Maksimum Tur Sayısı (Senaryo Bazlı)",
    'admin.voicePractice.fieldDialogMaxTurnsPlaceholder': "7 (varsayılan)",
    'admin.voicePractice.fieldDialogMaxTurnsHint': "Bu senaryoda izin verilen maksimum sohbet turu (2-20). Boş bırakılırsa global varsayılan (7) kullanılır.",
    'admin.voicePractice.validationDialogOpener': "Açılış cümlesi en fazla 500 karakter olabilir",
    'admin.voicePractice.validationDialogContext': "Bağlam en fazla 2000 karakter olabilir",
    'admin.voicePractice.validationDialogMaxTurns': "Max tur sayısı 2-20 arasında olmalı",
    // yeni tablo kolonları
    'admin.voicePractice.colOrder': "Sıra",
    'admin.voicePractice.colDialogCapable': "Dialog",
    'admin.voicePractice.colPassingScore': "Geçme Puanı",

    // ── AdminVoiceInterviewQuestions ───────────────────────────────────────────
    'admin.voiceInterview.title': "Sesli Mülakat Soruları",
    'admin.voiceInterview.subtitle': "Kabin ekibi adayları için sözlü mülakat soru bankasını yönetin",
    'admin.voiceInterview.createBtn': "Yeni Soru",
    'admin.voiceInterview.createTitle': "Soru Oluştur",
    'admin.voiceInterview.editTitle': "Soru Düzenle",
    'admin.voiceInterview.deleteTitle': "Soruyu Sil",
    'admin.voiceInterview.deleteDesc': "Bu soru kalıcı olarak silinecek.",
    'admin.voiceInterview.createSuccess': "Soru oluşturuldu",
    'admin.voiceInterview.updateSuccess': "Soru güncellendi",
    'admin.voiceInterview.deleteSuccess': "Soru silindi",
    'admin.voiceInterview.validationRequired': "Soru metni (TR ve EN) zorunludur",
    'admin.voiceInterview.colQuestion': "Soru",
    'admin.voiceInterview.colType': "Tür",
    'admin.voiceInterview.colDifficulty': "Zorluk",
    'admin.voiceInterview.colDuration': "Süre",
    'admin.voiceInterview.colActive': "Durum",
    'admin.voiceInterview.colActions': "İşlemler",
    'admin.voiceInterview.filterType': "Soru Türü",
    'admin.voiceInterview.filterDifficulty': "Zorluk",
    'admin.voiceInterview.filterStatus': "Durum",
    'admin.voiceInterview.fieldType': "Soru Türü",
    'admin.voiceInterview.fieldDifficulty': "Zorluk Derecesi",
    'admin.voiceInterview.fieldQuestionTr': "Soru (TR)",
    'admin.voiceInterview.fieldQuestionEn': "Soru (EN)",
    'admin.voiceInterview.fieldDuration': "Beklenen Süre (saniye)",
    'admin.voiceInterview.fieldSampleAnswerTr': "Örnek Cevap (TR)",
    'admin.voiceInterview.fieldSampleAnswerEn': "Örnek Cevap (EN)",
    'admin.voiceInterview.fieldActive': "Aktif",
    'admin.voiceInterview.emptyTitle': "Henüz soru yok",
    'admin.voiceInterview.emptyDesc': "Pratik oturumları için mülakat soruları oluşturun.",

    // ── AdminAviationEnglishScenarios ──────────────────────────────────────────
    'admin.aviationEnglish.title': "Aviation English",
    'admin.aviationEnglish.subtitle': "Havacılık İngilizcesi senaryoları ve quiz sorularını yönetin",
    'admin.aviationEnglish.tabScenarios': "Senaryolar",
    'admin.aviationEnglish.tabQuestions': "Sorular",
    'admin.aviationEnglish.createScenarioBtn': "Yeni Senaryo",
    'admin.aviationEnglish.createQuestionBtn': "Yeni Soru",
    'admin.aviationEnglish.createScenarioTitle': "Senaryo Oluştur",
    'admin.aviationEnglish.editScenarioTitle': "Senaryo Düzenle",
    'admin.aviationEnglish.createQuestionTitle': "Soru Oluştur",
    'admin.aviationEnglish.editQuestionTitle': "Soru Düzenle",
    'admin.aviationEnglish.deleteScenarioTitle': "Senaryoyu Sil",
    'admin.aviationEnglish.deleteScenarioDesc': "Bu senaryo silindiğinde ilgili sorular da kaldırılacak.",
    'admin.aviationEnglish.deleteQuestionTitle': "Soruyu Sil",
    'admin.aviationEnglish.deleteQuestionDesc': "Bu soru kalıcı olarak silinecek.",
    'admin.aviationEnglish.scenarioCreateSuccess': "Senaryo oluşturuldu",
    'admin.aviationEnglish.scenarioUpdateSuccess': "Senaryo güncellendi",
    'admin.aviationEnglish.scenarioDeleteSuccess': "Senaryo silindi",
    'admin.aviationEnglish.questionCreateSuccess': "Soru oluşturuldu",
    'admin.aviationEnglish.questionUpdateSuccess': "Soru güncellendi",
    'admin.aviationEnglish.questionDeleteSuccess': "Soru silindi",
    'admin.aviationEnglish.validationTitleRequired': "Başlık (TR ve EN) zorunludur",
    'admin.aviationEnglish.validationQuestionRequired': "Soru metni (TR ve EN) zorunludur",
    'admin.aviationEnglish.validationScenarioRequired': "Lütfen bir senaryo seçin",
    'admin.aviationEnglish.colTitle': "Başlık (TR)",
    'admin.aviationEnglish.colTitleEn': "Başlık (EN)",
    'admin.aviationEnglish.colLevel': "Seviye",
    'admin.aviationEnglish.colQuestions': "Sorular",
    'admin.aviationEnglish.colQuestion': "Soru",
    'admin.aviationEnglish.colQuestionType': "Tür",
    'admin.aviationEnglish.colCorrectAnswer': "Doğru Cevap",
    'admin.aviationEnglish.filterLevel': "Seviye",
    'admin.aviationEnglish.filterScenario': "Senaryo",
    'admin.aviationEnglish.fieldTitleTr': "Başlık (TR)",
    'admin.aviationEnglish.fieldTitleEn': "Başlık (EN)",
    'admin.aviationEnglish.fieldDescTr': "Açıklama (TR)",
    'admin.aviationEnglish.fieldDescEn': "Açıklama (EN)",
    'admin.aviationEnglish.fieldLevel': "Seviye",
    'admin.aviationEnglish.fieldActive': "Aktif",
    'admin.aviationEnglish.fieldScenario': "Senaryo",
    'admin.aviationEnglish.fieldQuestionType': "Soru Türü",
    'admin.aviationEnglish.fieldQuestionTr': "Soru (TR)",
    'admin.aviationEnglish.fieldQuestionEn': "Soru (EN)",
    'admin.aviationEnglish.fieldChoices': "Cevap Seçenekleri",
    'admin.aviationEnglish.fieldSampleAnswerTr': "Örnek Cevap (TR)",
    'admin.aviationEnglish.fieldSampleAnswerEn': "Örnek Cevap (EN)",
    'admin.aviationEnglish.emptyScenarios': "Henüz senaryo yok",
    'admin.aviationEnglish.emptyQuestions': "Henüz soru yok",
    'admin.aviationEnglish.emptyDesc': "Başlamak için içerik ekleyin.",
    'admin.aviationEnglish.colOrder': "Sıra",
    'admin.aviationEnglish.colPoints': "Puan",
    'admin.aviationEnglish.colActive': "Durum",
    'admin.aviationEnglish.fieldOrder': "Sıra",
    'admin.aviationEnglish.fieldPoints': "Puan",
    'admin.aviationEnglish.fieldContextTr': "Açıklama (TR)",
    'admin.aviationEnglish.addOption': "Seçenek Ekle",
    'admin.aviationEnglish.correctAnswerHint': "Doğru Cevap (yukarıdaki seçeneklerden biriyle aynı yazılmalı)",

    // ── AdminAnnounceTemplates ─────────────────────────────────────────────────
    'admin.announceTemplates.title': "Anons Şablonları",
    'admin.announceTemplates.subtitle': "Slot değişkenli çift dilli anons metinlerini yönetin",
    'admin.announceTemplates.createBtn': "Yeni Şablon",
    'admin.announceTemplates.createTitle': "Şablon Oluştur",
    'admin.announceTemplates.editTitle': "Şablon Düzenle",
    'admin.announceTemplates.deleteTitle': "Şablonu Sil",
    'admin.announceTemplates.deleteDesc': "Bu şablon kalıcı olarak silinecek.",
    'admin.announceTemplates.createSuccess': "Şablon oluşturuldu",
    'admin.announceTemplates.updateSuccess': "Şablon güncellendi",
    'admin.announceTemplates.deleteSuccess': "Şablon silindi",
    'admin.announceTemplates.validationTitleRequired': "Başlık (TR ve EN) zorunludur",
    'admin.announceTemplates.validationCategoryRequired': "Kategori seçimi zorunludur",
    'admin.announceTemplates.colTitle': "Başlık",
    'admin.announceTemplates.colCategory': "Kategori",
    'admin.announceTemplates.colSlots': "Slotlar",
    'admin.announceTemplates.colActive': "Durum",
    'admin.announceTemplates.filterCategory': "Kategori",
    'admin.announceTemplates.filterStatus': "Durum",
    'admin.announceTemplates.fieldCategory': "Kategori",
    'admin.announceTemplates.fieldTitleTr': "Başlık (TR)",
    'admin.announceTemplates.fieldTitleEn': "Başlık (EN)",
    'admin.announceTemplates.fieldTemplateTr': "Şablon Metni (TR)",
    'admin.announceTemplates.fieldTemplateEn': "Şablon Metni (EN)",
    'admin.announceTemplates.fieldSlots': "Slot Tanımları",
    'admin.announceTemplates.fieldActive': "Aktif",
    'admin.announceTemplates.emptyTitle': "Henüz şablon yok",
    'admin.announceTemplates.emptyDesc': "Kabin ekibi pratiği için anons şablonları oluşturun.",

    // ── AdminPaxCommSituations ─────────────────────────────────────────────────
    'admin.paxComm.title': "Yolcu İletişimi",
    'admin.paxComm.subtitle': "Ton bazlı yanıtlarla yolcu etkileşim senaryolarını yönetin",
    'admin.paxComm.createBtn': "Yeni Durum",
    'admin.paxComm.createTitle': "Durum Oluştur",
    'admin.paxComm.editTitle': "Durumu Düzenle",
    'admin.paxComm.deleteTitle': "Durumu Sil",
    'admin.paxComm.deleteDesc': "Bu durum kalıcı olarak silinecek.",
    'admin.paxComm.createSuccess': "Durum oluşturuldu",
    'admin.paxComm.updateSuccess': "Durum güncellendi",
    'admin.paxComm.deleteSuccess': "Durum silindi",
    'admin.paxComm.validationTitleRequired': "Başlık (TR ve EN) zorunludur",
    'admin.paxComm.validationCategoryRequired': "Kategori seçimi zorunludur",
    'admin.paxComm.validationResponseRequired': "En az bir yanıt girilmelidir",
    'admin.paxComm.colTitle': "Başlık",
    'admin.paxComm.colCategory': "Kategori",
    'admin.paxComm.colResponses': "Yanıtlar",
    'admin.paxComm.colActive': "Durum",
    'admin.paxComm.filterCategory': "Kategori",
    'admin.paxComm.filterStatus': "Durum",
    'admin.paxComm.fieldCategory': "Kategori",
    'admin.paxComm.fieldTitleTr': "Başlık (TR)",
    'admin.paxComm.fieldTitleEn': "Başlık (EN)",
    'admin.paxComm.fieldContextTr': "Bağlam / Durum (TR)",
    'admin.paxComm.fieldContextEn': "Bağlam / Durum (EN)",
    'admin.paxComm.fieldResponses': "Yanıtlar (tone bazlı)",
    'admin.paxComm.fieldTips': "İletişim İpuçları",
    'admin.paxComm.fieldActive': "Aktif",
    'admin.paxComm.emptyTitle': "Henüz durum yok",
    'admin.paxComm.emptyDesc': "Eğitim için yolcu iletişim durumları oluşturun.",

    // ── CabinCrew AviationEnglish Hybrid ─────────────────────────────────────
    'cabinCrew.aviationEnglish.staticContentHeading': "Hazır Şablonlar",
    'cabinCrew.aviationEnglish.staticContentHeadingPax': "Hazır Senaryolar",
    'cabinCrew.aviationEnglish.generateAi': "AI ile Soru Üret",
    'cabinCrew.aviationEnglish.noStaticContent': "Henüz şablon eklenmemiş, AI ile başla",
    'cabinCrew.aviationEnglish.loadingStatic': "İçerik yükleniyor...",
    'cabinCrew.aviationEnglish.staticCategory': "Kategori",
    'cabinCrew.aviationEnglish.templateText': "Anons Metni",
    'cabinCrew.aviationEnglish.contextLabel': "Durum",
    'cabinCrew.aviationEnglish.tipsLabel': "İpuçları",
    'cabinCrew.aviationEnglish.responsesLabel': "Örnek Yanıtlar",
    'cabinCrew.aviationEnglish.orDivider': "veya",
    'cabinCrew.aviationEnglish.aiSectionHeading': "AI Alıştırmaları",
    'cabinCrew.aviationEnglish.backToTemplates': "Şablonlara Dön",

    // ── CabinCrew Announce Practice ───────────────────────────────────────────
    'cabinCrew.announce.listen': "Dinle",
    'cabinCrew.announce.stopListen': "Durdur",
    'cabinCrew.announce.practice': "Pratik Yap",
    'cabinCrew.announce.recording': "Kaydediyor...",
    'cabinCrew.announce.stopRecord': "Kaydı Durdur",
    'cabinCrew.announce.evaluating': "Değerlendiriliyor...",
    'cabinCrew.announce.score': "Skor",
    'cabinCrew.announce.tryAgain': "Tekrar Dene",
    'cabinCrew.announce.transcript': "Söylediğin",
    'cabinCrew.announce.startRecord': "Kayıt başlatmak için tıklayın",
    'cabinCrew.announce.pronunciationLabel': "Telaffuz",
    'cabinCrew.announce.clarityLabel': "Netlik",
    'cabinCrew.announce.paceLabel': "Hız",
    'cabinCrew.announce.accuracyLabel': "Doğruluk",
    'cabinCrew.announce.overallLabel': "Genel",
    'cabinCrew.announce.aiFeedback': "AI Geri Bildirim",
    'cabinCrew.announce.micError': "Mikrofon izni gerekli!",
    'cabinCrew.announce.evalError': "Değerlendirme başarısız. Tekrar deneyin.",
    'cabinCrew.announce.practiceTitle': "Anons Pratiği",
    'cabinCrew.announce.noTrVoice': "Bu tarayıcıda Türkçe ses bulunamadı; en yakın dile geçildi.",
    'cabinCrew.announce.fillSlots': "Boşlukları doldur",
    'cabinCrew.announce.slotPlaceholder': "değer gir",

    // ── VoicePracticeModule — geri bildirim eylemleri ────────────────────────
    'cabinCrew.voice.tryAgain': "Tekrar Dene",
    'cabinCrew.voice.nextExercise': "Sonraki Soru",
    'cabinCrew.voice.close': "Kapat",
    'cabinCrew.voice.allDone': "Tüm egzersizleri tamamladın!",
    'cabinCrew.voice.finishAll': "Tamamla",

    // ── AviationEnglishModule — filtre çubuğu ────────────────────────────────
    'common.all': "Tümü",
    'common.back': "Geri",
    'cabinCrew.filterBy': "Filtrele",
    'cabinCrew.categories.welcome': "Karşılama",
    'cabinCrew.categories.safety': "Güvenlik",
    'cabinCrew.categories.emergency': "Acil Durum",
    'cabinCrew.categories.weather': "Hava Durumu",
    'cabinCrew.categories.landing': "İniş",
    'cabinCrew.categories.service': "Servis",
    'cabinCrew.categories.greeting': "Selam",
    'cabinCrew.categories.request': "İstek",
    'cabinCrew.levelGate.title': "Seviyeniz Henüz Atanmadı",
    'cabinCrew.levelGate.desc': "Seviyeniz henüz atanmadı. Kariyer Online ile iletişime geçin.",

    // ── AdminCabinAcademyHub ──────────────────────────────────────────────────
    'admin.cabinAcademy.title': "Kabin Akademisi Yönetimi",
    'admin.cabinAcademy.subtitle': "Tüm kabin ekibi eğitim modülleri için merkezi yönetim",
    'admin.cabinAcademy.modulesSection': "Modüller",
    'admin.cabinAcademy.metric.activeModules': "Aktif Modül",
    'admin.cabinAcademy.metric.totalAcrossModules': "Toplam İçerik",
    'admin.cabinAcademy.metric.mostActive': "En Aktif Modül",
    'admin.cabinAcademy.metric.lastUpdated': "Son Güncelleme",
    'admin.cabinAcademy.level.beginner': "Baş",
    'admin.cabinAcademy.level.intermediate': "Orta",
    'admin.cabinAcademy.level.advanced': "İleri",
    'admin.cabinAcademy.module.voicePractice.title': "Sesli Pratik",
    'admin.cabinAcademy.module.voicePractice.desc': "Kabin ekibi eğitim oturumları için sözlü pratik senaryoları",
    'admin.cabinAcademy.module.voicePractice.totalLabel': "senaryo",
    'admin.cabinAcademy.module.voicePractice.manageBtn': "Yönet",
    'admin.cabinAcademy.module.voicePractice.quickCreateBtn': "Yeni Senaryo",
    'admin.cabinAcademy.module.voiceInterview.title': "Sesli Mülakat",
    'admin.cabinAcademy.module.voiceInterview.desc': "Kabin ekibi adayları için sözlü mülakat soru bankası",
    'admin.cabinAcademy.module.voiceInterview.totalLabel': "soru",
    'admin.cabinAcademy.module.voiceInterview.manageBtn': "Yönet",
    'admin.cabinAcademy.module.voiceInterview.quickCreateBtn': "Yeni Soru",
    'admin.cabinAcademy.module.aviationEnglish.title': "Aviation English",
    'admin.cabinAcademy.module.aviationEnglish.desc': "Havacılık İngilizcesi senaryoları ve quiz soruları",
    'admin.cabinAcademy.module.aviationEnglish.totalLabel': "senaryo",
    'admin.cabinAcademy.module.aviationEnglish.questionCount': "{n} quiz sorusu",
    'admin.cabinAcademy.module.aviationEnglish.manageBtn': "Yönet",
    'admin.cabinAcademy.module.aviationEnglish.quickCreateBtn': "Yeni Senaryo",
    'admin.cabinAcademy.module.announceTemplates.title': "Anons Şablonları",
    'admin.cabinAcademy.module.announceTemplates.desc': "Pratik için slot değişkenli iki dilli anons metinleri",
    'admin.cabinAcademy.module.announceTemplates.totalLabel': "şablon",
    'admin.cabinAcademy.module.announceTemplates.manageBtn': "Yönet",
    'admin.cabinAcademy.module.announceTemplates.quickCreateBtn': "Yeni Şablon",
    'admin.cabinAcademy.module.paxComm.title': "Yolcu İletişimi",
    'admin.cabinAcademy.module.paxComm.desc': "Ton bazlı yanıtlarla yolcu etkileşim senaryoları",
    'admin.cabinAcademy.module.paxComm.totalLabel': "durum",
    'admin.cabinAcademy.module.paxComm.manageBtn': "Yönet",
    'admin.cabinAcademy.module.paxComm.quickCreateBtn': "Yeni Durum",
    'admin.cabinAcademy.module.cabinAiCoach.title': "Kabin AI Koç",
    'admin.cabinAcademy.module.cabinAiCoach.desc': "Kabin ekibi için yapay zeka destekli koçluk modülü (statik içerik, Faz 2)",
    'admin.cabinAcademy.cabinAiCoach.faz2Notice': "Bu modül Faz 2'de admin yönetimine açılacak",

    // Flashcard Dersi
    'flashcardLesson.iKnow': "Biliyorum",
    'flashcardLesson.iDontKnow': "Bilmiyorum",
    'flashcardLesson.next': "Sonraki",
    'flashcardLesson.prev': "Önceki",
    'flashcardLesson.completed': "Tamamlandı!",
    'flashcardLesson.restart': "Tekrar Et",
    'flashcardLesson.wordCount': "kelime",
    'flashcardLesson.progressLabel': "İlerleme",
    'flashcardLesson.tapToFlip': "Çevirmek için dokun",
    'flashcardLesson.score': "Puan",
    'flashcardLesson.knownCount': "Biliyor",
    'flashcardLesson.unknownCount': "Bilmiyor",
    'flashcardLesson.noContent': "Bu ders için flashcard içeriği bulunamadı.",

    // Eşleştirme Dersi
    'matchingLesson.matched': "eşleşti",
    'matchingLesson.mistakes': "Hatalar",
    'matchingLesson.completed': "Hepsi Eşleşti!",
    'matchingLesson.restart': "Tekrar Oyna",
    'matchingLesson.hint': "İpucu",
    'matchingLesson.score': "Puan",
    'matchingLesson.noContent': "Bu ders için eşleştirme çifti bulunamadı.",
    'matchingLesson.selectLeft': "Sol taraftan bir kelime seç",
    'matchingLesson.selectRight': "Şimdi eşleşen kelimeyi seç",

    // Sürükle-Bırak Dersi
    'dragDropLesson.check': "Kontrol Et",
    'dragDropLesson.correct': "Doğru!",
    'dragDropLesson.incorrect': "Tekrar Dene",
    'dragDropLesson.nextSentence': "Sonraki Cümle",
    'dragDropLesson.retry': "Yeniden Dene",
    'dragDropLesson.submitAll': "Gönder",
    'dragDropLesson.completed': "Tüm Cümleler Tamamlandı!",
    'dragDropLesson.score': "Puan",
    'dragDropLesson.noContent': "Bu ders için sürükle-bırak içeriği bulunamadı.",
    'dragDropLesson.dropHere': "Kelimeleri buraya sürükle...",
    'dragDropLesson.sentence': "Cümle",

    // ── DialogueLesson (Faz 4d) ──────────────────────────────────────────────
    'dialogueLesson.yourTurn': "Sıra Sende",
    'dialogueLesson.correct': "Doğru!",
    'dialogueLesson.incorrect': "Tam değil.",
    'dialogueLesson.continue': "Devam",
    'dialogueLesson.score': "Puan",
    'dialogueLesson.correctAnswers': "doğru",
    'dialogueLesson.completed': "Diyalog Tamamlandı!",
    'dialogueLesson.restart': "Tekrar Dene",
    'dialogueLesson.step': "Adım",
    'dialogueLesson.noContent': "Bu ders için diyalog içeriği bulunamadı.",

    // ── FillInBlankLesson (Faz 4d) ───────────────────────────────────────────
    'fillInBlankLesson.check': "Kontrol Et",
    'fillInBlankLesson.correct': "Doğru!",
    'fillInBlankLesson.incorrect': "Yanlış",
    'fillInBlankLesson.correctAnswerLabel': "Doğru cevap",
    'fillInBlankLesson.next': "Sonraki",
    'fillInBlankLesson.retry': "Tekrar Dene",
    'fillInBlankLesson.score': "Puan",
    'fillInBlankLesson.hint': "İpucu Göster",
    'fillInBlankLesson.completed': "Tamamlandı!",
    'fillInBlankLesson.question': "Soru",
    'fillInBlankLesson.noContent': "Bu ders için boşluk doldurma içeriği bulunamadı.",

    // ── SwipeVocabularyLesson (Faz 4d) ───────────────────────────────────────
    'swipeLesson.iKnow': "Biliyorum",
    'swipeLesson.reviewLater': "Tekrar Et",
    'swipeLesson.known': "Biliyor",
    'swipeLesson.remaining': "kaldı",
    'swipeLesson.swipeInstruction': "Biliyorsan sağa, tekrar etmek istersen sola kaydır",
    'swipeLesson.completed': "Tamamlandı!",
    'swipeLesson.score': "Puan",
    'swipeLesson.wordsKnown': "kelime biliniyor",
    'swipeLesson.restart': "Tekrar Dene",
    'swipeLesson.noContent': "Bu ders için kelime içeriği bulunamadı.",

    // ── TimeRaceLesson (Faz 4d) ──────────────────────────────────────────────
    'timeRaceLesson.title': "Zaman Yarışı!",
    'timeRaceLesson.questions': "soru",
    'timeRaceLesson.start': "Yarışı Başlat",
    'timeRaceLesson.perCorrect': "doğru başına",
    'timeRaceLesson.perWrong': "yanlış başına",
    'timeRaceLesson.timeLeft': "Kalan Süre",
    'timeRaceLesson.correct': "Doğru",
    'timeRaceLesson.incorrect': "Yanlış",
    'timeRaceLesson.points': "puan",
    'timeRaceLesson.timeUp': "Süre Doldu!",
    'timeRaceLesson.allDone': "Tamamlandı!",
    'timeRaceLesson.timeUsed': "Kullanılan Süre",
    'timeRaceLesson.finalScore': "Toplam Puan",
    'timeRaceLesson.restart': "Tekrar Oyna",
    'timeRaceLesson.noContent': "Bu ders için zaman yarışı içeriği bulunamadı.",

    // Ortak ders anahtarları
    'common.completeLesson': "Dersi Tamamla",
    'common.score': "Puan",
    'common.tryAgain': "Tekrar Dene",
    'common.lessonCompleted': "Ders Tamamlandı",

    // Admin nav
    'admin.nav.dashboard': "Genel Bakış",
    'admin.nav.education': "Eğitim",
    'admin.nav.courses': "Kurslar",
    'admin.nav.vimeo': "Vimeo Kütüphanesi",
    'admin.nav.placementTests': "Seviye Sınavları",
    'admin.nav.reviews': "Yorumlar",
    'admin.nav.students': "Öğrenciler",
    'admin.nav.danismanData': "Danışman & Data",
    // "Danışman & Data" altındaki 4 alt drawer başlığı
    'admin.nav.danismanGunlukIs': "Günlük İş",
    'admin.nav.danismanDataOnay': "Data & Onay",
    'admin.nav.danismanRaporlar': "Raporlar",
    'admin.nav.danismanEkip': "Ekip Yönetimi",
    'admin.nav.studentHub': "Öğrenci Merkezi (360)",
    'admin.nav.studentsPage': "Öğrenciler",
    'admin.nav.usersPage': "Kullanıcılar",
    'admin.nav.enrollments': "Tüm Kayıtlar",
    'admin.newUser.title': "Yeni Kullanıcı Oluştur",
    'admin.newUser.subtitle': "Oluşturmak istediğiniz kullanıcı tipini seçin",
    'admin.newUser.role.student': "Öğrenci",
    'admin.newUser.role.instructor': "Eğitmen",
    'admin.newUser.role.admin': "Admin",
    'admin.newUser.role.student.desc': "Kurs, sertifika ve ödeme akışıyla tam kayıt",
    'admin.newUser.role.instructor.desc': "Yalnızca kişisel bilgiler — kurs akışı yok",
    'admin.newUser.role.admin.desc': "Admin yetkisiyle kullanıcı oluştur",
    'admin.newUser.step.personalInfo': "Kişisel Bilgi",
    'admin.newUser.step.mainCourse': "Ana Kurs",
    'admin.newUser.step.additional': "Ek Kurslar",
    'admin.newUser.step.certificate': "Sertifika",
    'admin.newUser.step.summary': "Özet",
    'admin.newUser.success.title': "Kayıt Başarılı!",
    'admin.newUser.success.newRegistration': "Yeni Kayıt",
    'admin.newUser.success.backToStudents': "Öğrenci Listesi",
    'admin.newUser.success.backToUsers': "Kullanıcı Listesi",
    'admin.nav.cabinCrewEnrollments': "Kabin Ekibi Programları",
    'admin.nav.cabinCrewGroups': "Gruplar",
    'admin.nav.bulkOps': "Toplu İşlemler",
    'admin.nav.cabinAcademy': "Kabin Akademisi",
    'admin.nav.cabinHub': "Akademi Merkezi",
    'admin.nav.cabinCurriculum': "Müfredat Yönetimi",
    'admin.nav.voicePractice': "Sesli Pratik",
    'admin.nav.voiceInterview': "Sesli Mülakat Soruları",
    'admin.nav.aviationEnglish': "Aviation English",
    'admin.nav.announceTemplates': "Anons Şablonları",
    'admin.nav.paxComm': "Yolcu İletişimi",
    'admin.nav.documents': "Belgeler & Hukuk",
    'admin.nav.certificates': "Sertifikalar",
    'admin.nav.documentsList': "Belgeler",
    'admin.nav.contracts': "Sözleşmeler",
    'admin.nav.kvkk': "KVKK Talepleri",
    'admin.nav.legalContent': "Yasal İçerik",
    'admin.nav.geoContent': "GEO İçerik",
    'admin.nav.finance': "Finans",
    'admin.nav.payments': "Ödemeler",
    'admin.nav.accounting': "Muhasebe",
    'admin.nav.invoices': "Satış Kayıtları",

    // ── AdminInvoices — detail modal & PDF ───────────────────────────────────
    'admin.inv.detail_title': "Borç Kaydı Detayı",
    'admin.inv.close': "Kapat",
    'admin.inv.type_sale': "Satış",
    'admin.inv.type_refund': "İade",
    'admin.inv.type_proforma': "Proforma",
    'admin.inv.field_invoice_no': "Kayıt No",
    'admin.inv.field_type': "Tür",
    'admin.inv.field_status': "Durum",
    'admin.inv.field_customer': "Müşteri",
    'admin.inv.field_tc': "T.C. No",
    'admin.inv.field_issued_at': "Düzenleme Tarihi",
    'admin.inv.field_payment_method': "Ödeme Yöntemi",
    'admin.inv.lines_title': "Fatura Kalemleri",
    'admin.inv.col_desc': "Açıklama",
    'admin.inv.col_qty': "Adet",
    'admin.inv.col_unit_price': "Birim Fiyat",
    'admin.inv.col_vat_rate': "KDV %",
    'admin.inv.col_vat_amount': "KDV",
    'admin.inv.col_line_total': "Tutar",
    'admin.inv.subtotal': "Ara Toplam",
    'admin.inv.vat_total': "KDV Toplam",
    'admin.inv.grand_total': "Genel Toplam",
    'admin.inv.paid_amount': "Ödenen",
    'admin.inv.remaining_amount': "Kalan",
    'admin.inv.payments_title': "Ödemeler",
    'admin.inv.installments_title': "Taksitler",
    'admin.inv.no_lines': "Kalem bulunamadı.",
    'admin.inv.no_payments': "Kayıtlı ödeme yok.",
    'admin.inv.no_installments': "Taksit bulunamadı.",
    'admin.inv.pdf_download': "PDF İndir",
    'admin.inv.pdf_error': "PDF indirilemedi.",
    'admin.inv.loading': "Kayıt yükleniyor...",
    'admin.inv.load_error': "Kayıt yüklenemedi.",
    'admin.inv.detail_btn': "Detay",
    'admin.inv.pay_date': "Tarih",
    'admin.inv.pay_amount': "Tutar",
    'admin.inv.pay_method': "Yöntem",
    'admin.inv.inst_no': "#",
    'admin.inv.inst_due': "Vade Tarihi",
    'admin.inv.inst_amount': "Tutar",
    'admin.inv.inst_status': "Durum",

    // ── AdminMuhasebeListesi — Öğrenci Muhasebe Listesi (Faz L) ───────────────
    'admin.nav.muhasebeListesi': "Öğrenci Muhasebe Listesi",
    'admin.nav.parasutMapping': "Paraşüt Eşleştirme",
    'admin.muhasebeListesi.page_title': "Öğrenci Muhasebe Listesi",
    'admin.muhasebeListesi.page_subtitle': "Taksit (borç) ve eşleşen tahsilat satırlarının filtrelenebilir listesi.",
    'admin.muhasebeListesi.filter_date_type': "Tarih Tipi",
    'admin.muhasebeListesi.date_type_odeme': "Ödeme Tarihine Göre",
    'admin.muhasebeListesi.date_type_vade': "Vade Tarihine Göre",
    'admin.muhasebeListesi.date_type_kayit': "Kayıt Tarihine Göre",
    'admin.muhasebeListesi.filter_senet_durumu': "Senet Durumu",
    'admin.muhasebeListesi.senet_durumu_tum': "Tümü",
    'admin.muhasebeListesi.senet_durumu_borclu': "Borçlu",
    'admin.muhasebeListesi.senet_durumu_odenen': "Ödenen",
    'admin.muhasebeListesi.filter_payment_method': "Ödeme Yöntemi",
    'admin.muhasebeListesi.payment_method_hepsi': "Hepsi",
    'admin.muhasebeListesi.payment_method_cash': "Nakit",
    'admin.muhasebeListesi.payment_method_credit_card': "Kredi Kartı",
    'admin.muhasebeListesi.payment_method_bank_transfer': "Havale",
    'admin.muhasebeListesi.filter_only_moka': "Yalnızca Moka ödemeleri",
    'admin.muhasebeListesi.filter_only_legal': "Yalnızca avukat takibindekiler",
    'admin.muhasebeListesi.filter_course': "Ders",
    'admin.muhasebeListesi.filter_certificate': "Sertifika",
    'admin.muhasebeListesi.filter_danisman': "Danışman",
    'admin.muhasebeListesi.filter_bank': "Banka",
    'admin.muhasebeListesi.filter_muhasebe_tipi': "Muhasebe Tipi",
    'admin.muhasebeListesi.filter_all': "Tümü",
    'admin.muhasebeListesi.filter_date_range': "Başlangıç / Bitiş Tarihi",
    'admin.muhasebeListesi.btn_list': "Listele",
    'admin.muhasebeListesi.btn_export': "Excel'e Aktar",
    'admin.muhasebeListesi.summary_total_debt': "Toplam Borç",
    'admin.muhasebeListesi.summary_total_paid': "Toplam Ödenen",
    'admin.muhasebeListesi.summary_total_transferred': "Toplam Ödenen (Aktarılan)",
    'admin.muhasebeListesi.summary_remaining': "Kalan",
    'admin.muhasebeListesi.summary_total_vat': "Toplam KDV",
    'admin.muhasebeListesi.summary_vat_free_transferred': "KDV'siz Aktarılan",
    'admin.muhasebeListesi.col_student': "Öğrenci",
    'admin.muhasebeListesi.col_debt_date': "Borç Tarihi",
    'admin.muhasebeListesi.col_debt_amount': "Borç Tutarı",
    'admin.muhasebeListesi.col_payment_date': "Ödeme Tarihi",
    'admin.muhasebeListesi.col_payment_amount': "Ödeme Tutarı",
    'admin.muhasebeListesi.col_payment_transferred': "Ödeme Tutarı (Aktarılan)",
    'admin.muhasebeListesi.col_vat_amount': "KDV Tutarı",
    'admin.muhasebeListesi.col_bank': "Banka",
    'admin.muhasebeListesi.col_senet_no': "Senet No",
    'admin.muhasebeListesi.col_lawyer': "Avukat",
    'admin.muhasebeListesi.empty_title': "Kayıt bulunamadı",
    'admin.muhasebeListesi.empty_desc': "Seçilen filtrelerle eşleşen satır yok.",
    'admin.muhasebeListesi.empty_desc_no_range': "Tarih aralığı seçip Listele'ye basın.",
    'admin.muhasebeListesi.warning_date_range_required': "Listelemek veya Excel'e aktarmak için başlangıç ve bitiş tarihi seçmelisiniz.",

    'admin.nav.definitions': "Tanımlar",
    'admin.nav.examGroups': "Sınav Grupları",
    'admin.nav.certPrint': "Sertifika Basım & Kargo",
    'admin.nav.dijitalRozet': "Dijital Rozet",
    'admin.nav.leads': "Data (Lead)",
    'admin.nav.dataManagement': "Data Yönetimi",
    'admin.nav.personelYonetimi': "Personel Yönetimi",
    'admin.nav.danismanYonetimi': "Danışman Yönetimi",
    'admin.nav.satisOnay': "Profil Onayları",
    'admin.nav.ciroDashboard': "Ciro Dashboard",
    'admin.nav.satisRaporlama': "Satış Raporlama",
    'admin.nav.primKurallari': "Prim Kuralları",
    'admin.nav.izinTalepleri': "İzin Talepleri",
    'admin.nav.campaigns': "Kampanyalar",
    'admin.nav.reports': "Raporlar",
    'admin.nav.system': "Sistem & Güvenlik",
    'admin.nav.messages': "Mesajlar",
    'admin.nav.announcements': "Duyurular",
    'admin.nav.logs': "Log Kayıtları",
    'admin.nav.activeSessions': "Aktif Oturumlar",
    'admin.nav.anomalies': "Şüpheli Aktivite",
    'admin.nav.bugReports': "Sorun Bildirimleri",
    'admin.nav.danismanCockpit': "Danışman Paneli",
    'admin.nav.danismanAnaSayfa': "Danışman Ana Sayfası",
    'admin.nav.danismanIstatistik': "Danışman İstatistikleri",
    'admin.nav.mesaiMolaTakip': "Mesai & Mola Takibi",
    'admin.nav.notificationJobs': "Bildirim Görevleri",
    'admin.nav.appVersion': "Uygulama Sürümü",

    // ── Admin bildirim zamanlanmış görevleri (aç/kapa) ──────────────────────
    'admin.notifJobs.title': "Bildirim Görevleri",
    'admin.notifJobs.subtitle': "Zamanlanmış in-app bildirim görevlerini açıp kapatın.",
    'admin.notifJobs.loadError': "Bildirim görevleri yüklenemedi.",
    'admin.notifJobs.retry': "Tekrar dene",
    'admin.notifJobs.emptyTitle': "Henüz ayarlanabilir görev yok",
    'admin.notifJobs.emptyDesc': "Gerçek bir aç/kapa bayrağı olan görevler eklendikçe burada listelenecek.",
    'admin.notifJobs.lastRun': "Son çalışma",
    'admin.notifJobs.lastRunNever': "Henüz hiç çalışmadı",
    'admin.notifJobs.notifiedOf': "{{notified}} bildirim / {{checked}} kontrol",
    'admin.notifJobs.statusDisabled': "Atlandı (kapalı)",
    'admin.notifJobs.toggleOn': "Açık",
    'admin.notifJobs.toggleOff': "Kapalı",
    'admin.notifJobs.toggleSuccess': "Görev güncellendi",
    'admin.notifJobs.toggleError': "Görev güncellenemedi",
    'admin.notifJobs.infoNote': "Burada sadece backend'de gerçek bir bayrağı olan görevler listelenir. Diğer zamanlanmış hatırlatmalar (ör. çalışma inaktivitesi, mülakat uygunluğu) her zaman çalışır ve henüz açıp kapatılamaz.",

    // ── Admin mobil uygulama sürüm yönetimi (zorunlu güncelleme kapısı) ─────
    'admin.appVersion.title': "Mobil Uygulama Sürüm Yönetimi",
    'admin.appVersion.subtitle': "Platform bazında minimum desteklenen sürümü ve son sürümü yönetin.",
    'admin.appVersion.loadError': "Sürüm ayarları yüklenemedi.",
    'admin.appVersion.retry': "Tekrar dene",
    'admin.appVersion.tabAndroid': "Android",
    'admin.appVersion.tabIos': "iOS",
    'admin.appVersion.sectionVersions': "Sürümler",
    'admin.appVersion.sectionVersionsDesc': "Semver formatında girin (ör. 1.2.0 veya 1.2).",
    'admin.appVersion.fieldMinVersion': "Minimum Desteklenen Sürüm",
    'admin.appVersion.fieldMinVersionHelp': "Bu sürümün altındaki kullanıcılar, güncelleme yapana kadar uygulamadan tamamen kilitlenir.",
    'admin.appVersion.fieldLatestVersion': "Son Sürüm",
    'admin.appVersion.fieldLatestVersionHelp': "Bu sürümün altındaki kullanıcılara kapatılabilir bir güncelleme hatırlatması gösterilir.",
    'admin.appVersion.fieldStoreUrl': "Mağaza URL'si",
    'admin.appVersion.fieldStoreUrlPlaceholder': "https://play.google.com/store/apps/details?id=...",
    'admin.appVersion.fieldStoreUrlHint': "Boş bırakabilir, ya da şunlardan birine https:// ile başlayan bir link girebilirsiniz: play.google.com, apps.apple.com, itunes.apple.com, kariyeronline.tr.",
    'admin.appVersion.errorInvalidSemver': "Geçerli bir sürüm numarası olmalı (ör. 1.2.0)",
    'admin.appVersion.errorInvalidStoreUrl': "Mağaza URL'si play.google.com, apps.apple.com, itunes.apple.com veya kariyeronline.tr adreslerinden birine https:// ile giden bir link olmalıdır — bu link, kullanıcıların kapatamayacağı bir ekranda açılır.",
    'admin.appVersion.errorMinGtLatest': "Minimum desteklenen sürüm, son sürümden büyük olamaz",
    'admin.appVersion.warningLockTitle': "Uyarı — bu işlem kullanıcıları kilitleyecek",
    'admin.appVersion.warningLockBody': "Minimum desteklenen sürümü değiştirmek, {{version}} altındaki sürümü kullanan TÜM kullanıcıları anında uygulamadan kilitler; güncelleme yapmadan devam edemezler.",
    'admin.appVersion.sectionOverride': "Opsiyonel Güncelleme Mesajı Override'ı",
    'admin.appVersion.sectionOverrideDesc': "Boş bırakırsanız uygulamanın varsayılan yerelleştirilmiş mesajı kullanılır.",
    'admin.appVersion.fieldTitle': "Başlık",
    'admin.appVersion.fieldMessage': "Mesaj",
    'admin.appVersion.fieldReleaseNotes': "Sürüm Notları",
    'admin.appVersion.confirmTitle': "Minimum sürüm değişikliğini onayla",
    'admin.appVersion.confirmBody': "Aşağıya yeni minimum sürümü yazarak, {{version}} altındaki TÜM kullanıcıları uygulamadan kilitleyeceğini anladığını teyit et.",
    'admin.appVersion.confirmInputLabel': "Onaylamak için sürümü yazın",
    'admin.appVersion.confirmMismatch': "Eşleşmiyor — tam olarak \"{{version}}\" yazmalısınız",
    'admin.appVersion.confirmButton': "Anladım, bu sürüme kilitle",
    'admin.appVersion.cancel': "İptal",
    'admin.appVersion.saveButton': "Kaydet",
    'admin.appVersion.saving': "Kaydediliyor...",
    'admin.appVersion.saveSuccess': "Sürüm ayarları güncellendi.",
    'admin.appVersion.saveError': "Sürüm ayarları güncellenemedi.",
    'admin.appVersion.lastUpdated': "Son güncelleme",
    'admin.appVersion.lastUpdatedNever': "Henüz güncellenmedi (güvenli varsayılanlar kullanılıyor).",
    'admin.appVersion.sectionDistribution': "Sürüm Dağılımı",
    'admin.appVersion.sectionDistributionDesc': "Son {{days}} günde gözlemlenen gerçek istemci sürümleri.",
    'admin.appVersion.window7d': "7 gün",
    'admin.appVersion.window30d': "30 gün",
    'admin.appVersion.windowRange': "{{from}} – {{to}} arası",
    'admin.appVersion.totalRequests': "{{count}} istek",
    'admin.appVersion.daysSeenLabel': "{{days}}/{{window}} gün görüldü",
    'admin.appVersion.otherVersionsLabel': "Diğer ({{count}} sürüm)",
    'admin.appVersion.configuredBadge': "Ayarlı",
    'admin.appVersion.observedMaxBadge': "Önerilen",
    'admin.appVersion.distributionLoadError': "Sürüm dağılımı yüklenemedi.",
    'admin.appVersion.distributionEmptyTitle': "Bu pencerede henüz veri yok",
    'admin.appVersion.distributionEmptyBody': "Seçili tarih aralığında hiçbir istemciden sürüm bilgisi kaydedilmedi.",
    'admin.appVersion.staleConfirmedTitle': "Yapılandırılan son sürüm güncel değil",
    'admin.appVersion.staleConfirmedBody': "Gözlemlenen en yüksek sürüm {{observedVersion}} (%{{percent}}, {{days}} gün, {{requests}} istek) — yapılandırılan son sürüm ise {{configuredVersion}}.",
    'admin.appVersion.staleConfirmedFillCta': "Önerilen sürümü doldur",
    'admin.appVersion.staleFillPendingHint': "Dolduruldu — Kaydet'e basmayı unutma",
    'admin.appVersion.staleHintBody': "{{observedVersion}} sürümünde küçük bir kullanım görüldü (%{{percent}}, {{days}} gün) — muhtemelen tek bir test cihazı veya erken erişim kurulumu. Şimdilik bir işlem gerekmiyor.",
    'admin.appVersion.dataQualityCapHit': "Bu pencerede {{days}} gün, günlük sürüm çeşitliliği sınırına ({{max}}) ulaşıldı — o günlerin dağılımı eksik olabilir.",
    'admin.appVersion.dataQualityCapHitDates': "Etkilenen günler: {{dates}}",

    // ── Cabin Module — M5.2 ──────────────────────────────────────────────────
    'cabin.nav.aria_label': "Kabin modülü navigasyonu",
    'cabin.nav.toggle_label': "Menüyü aç/kapat",
    'cabin.nav.dashboard': "Dashboard",
    'cabin.nav.modules': "Modüller",
    'cabin.nav.roadmap': "Yol Haritası",
    'cabin.nav.progress': "İlerleme",
    'cabin.nav.achievements': "Başarılar",
    'cabin.nav.leaderboard': "Başarı Sıralaması",
    'cabin.nav.video_lessons': "Video Dersler",
    'cabin.nav.interview': "Mülakat",
    'cabin.nav.spinWheel': "Günlük Çark",
    'cabin.nav.more': "Daha Fazla",
    'cabin.nav.back_to_academy': "Kariyer Online'a Dön",

    // ── Ders sayfası (kabin modu) ─────────────────────────────────────────────
    'cabin.lesson.description_title': "Ders Açıklaması",
    'cabin.lesson.description_empty': "Bu ders için açıklama eklenmemiş.",

    // ── Kabin Udemy düzeni ─────────────────────────────────────────────────────
    'cabin.lesson.tab_overview': "Genel Bakış",
    'cabin.lesson.tab_notes': "Notlar",
    'cabin.lesson.progress_label': "{done}/{total} tamamlandı",
    'cabin.lesson.sidebar_toggle_open': "Kurs içeriğini aç",
    'cabin.lesson.sidebar_toggle_close': "Kurs içeriğini kapat",
    'cabin.lesson.course_content': "Kurs İçeriği",
    'cabin.lesson.duration_min': "{n} dak",
    'cabin.lesson.students': "{n} öğrenci",
    'cabin.lesson.notes_title': "Notlarım",
    'cabin.lesson.notes_empty': "Bu ders için henüz not yok.",
    'cabin.lesson.notes_placeholder': "Notunuzu yazın...",
    'cabin.lesson.notes_save': "Kaydet",
    'cabin.lesson.notes_delete': "Sil",
    'cabin.lesson.notes_loading': "Notlar yükleniyor...",
    'cabin.lesson.notes_error': "Notlar yüklenemedi.",
    'cabin.lesson.back_to_course': "Kursa dön",

    // ── Video Lessons sayfası ─────────────────────────────────────────────────
    'cabin.video_lessons.title': "Video Dersler",
    'cabin.video_lessons.subtitle': "Kabin Memurluğu Eğitimi",
    'cabin.video_lessons.done_label': "tamamlandı",
    'cabin.video_lessons.completed_label': "Tamamlandı",
    'cabin.video_lessons.empty.title': "Henüz ders eklenmedi",
    'cabin.video_lessons.empty.sub': "Dersler yakında yayına alınacak.",
    'cabin.video_lessons.empty.cta': "Modüllere Git",

    // ── Bildirimler ───────────────────────────────────────────────────────────
    'cabin.notifications.title': "Bildirimler",
    'cabin.notifications.panel_label': "Bildirimler paneli",
    'cabin.notifications.bell_label': "Bildirimler",
    'cabin.notifications.bell_label_count': "Bildirimler, {count} okunmamış",
    'cabin.notifications.sr_unread': "okunmamış bildirim",
    'cabin.notifications.mark_all_read': "Tümünü okundu işaretle",
    'cabin.notifications.unread_dot': "Okunmamış",
    'cabin.notifications.empty': "Henüz bildirim yok",
    'cabin.notifications.error': "Bildirimler yüklenemedi",
    'cabin.notifications.markAllError': "Tümünü okundu işaretlenemedi",
    'cabin.notifications.bucket.today': "Bugün",
    'cabin.notifications.bucket.this_week': "Bu Hafta",
    'cabin.notifications.bucket.older': "Daha Önce",
    'cabin.notifications.empty.title': "Henüz bildirimin yok",
    'cabin.notifications.empty.sub': "Ders hatırlatmaları, mülakat sonuçları ve duyurular burada görünecek.",
    'cabin.notifications.empty_filtered.title': "Hepsini okudun",
    'cabin.notifications.empty_filtered.sub': "Yeni bildirim geldiğinde burada göreceksin.",
    'cabin.notifications.filter.all': "Tümü",
    'cabin.notifications.filter.unread': "Okunmamış",
    'cabin.notifications.filter.aria_label': "Bildirim filtresi",
    'cabin.notifications.mark_read_one': "Okundu işaretle",
    'cabin.notifications.view_all': "Tümünü Gör",
    'cabin.notifications.page.title': "Bildirimler",
    'cabin.notifications.page.subtitle': "Ders hatırlatmaların, mülakat sonuçların ve duyuruların burada.",
    'cabin.notifications.sr_live_unread': "{count} okunmamış bildirim",
    'cabin.notifications.sr_live_all_read': "Okunmamış bildirim yok",
    'cabin.notifications.list_aria_label': "Bildirim listesi",
    'cabin.notifications.time.just_now': "az önce",

    'cabin.spinWheel.title': "Günlük Çark",
    'cabin.spinWheel.subtitle': "Her gün bir kez çevir, ödülünü kap!",
    'cabin.spinWheel.spin': "Çevir",
    'cabin.spinWheel.spinning': "Çevriliyor...",
    'cabin.spinWheel.alreadyUsed': "Bugünkü hakkını kullandın!",
    'cabin.spinWheel.comeBack': "Sayaç sıfırlandığında geri gel.",
    'cabin.spinWheel.congrats': "Tebrikler!",
    'cabin.spinWheel.youWon': "Bir ödül kazandın! Harika gidiyorsun.",
    'cabin.spinWheel.awesome': "Harika!",

    'cabin.dashboard.greeting': "Merhaba, {name}!",
    'cabin.dashboard.subtitle': "Kabin Memurluğu Eğitimi",
    'cabin.dashboard.current_level': "Mevcut Seviye",
    'cabin.dashboard.target_level': "Hedef",
    'cabin.dashboard.progress_label': "{completed}/{total} ders tamamlandı",
    'cabin.dashboard.progress_pct': "Kabin Memurluğuna %{pct}",
    'cabin.dashboard.footer_quote': "Her ders seni kabin kapısına bir adım daha yaklaştırır.",
    'cabin.dashboard.modules_title': "Eğitim Modülleri",
    'cabin.dashboard.view_all': "Tümünü Gör",
    'cabin.dashboard.show_more': "{count} modül daha göster",
    'cabin.dashboard.show_less': "Daha az göster",
    'cabin.dashboard.roadmap_banner_title': "Öğrenme Yol Haritası",
    'cabin.dashboard.roadmap_banner_sub': "3 Aşama: Başlangıç → Orta → İleri · 16 Modül",
    'cabin.dashboard.roadmap_banner_cta': "Haritayı Gör",

    'cabin.stats.total_miles': "Toplam Mil",
    'cabin.stats.streak': "Gün Serisi",
    'cabin.stats.completed': "Tamamlanan",
    'cabin.stats.level': "Seviye",
    'cabin.stats.streak_days': "{count} günlük seri",

    'cabin.continue.title': "Kaldığın Yerden Devam Et",
    'cabin.continue.remaining': "~{min} dk kaldı",

    'cabin.module.cta.start': "Başla",
    'cabin.module.cta.continue': "Devam Et",
    'cabin.module.cta.retry': "Tekrar Et",
    'cabin.module.cta.locked': "Kilitli",
    'cabin.module.locked_tooltip': "{prerequisite} bittikten sonra açılır",
    'cabin.module.hours': "{time} saat",
    'cabin.module.miles_reward': "{miles} Mil",

    'cabin.daily_tasks.title': "Günlük Görevler",
    'cabin.daily_tasks.coming_soon': "Yakında aktif olacak",

    'cabin.leaderboard.title': "Haftalık Liderler",
    'cabin.leaderboard.you': "Sen",
    'cabin.leaderboard.rank': "Sıra: {rank}",

    'cabin.activity.title': "Son Aktiviteler",

    'cabin.roadmap.title': "Öğrenme Yol Haritası",
    'cabin.roadmap.subtitle': "Kabin memurluğuna adım adım ilerle",
    'cabin.roadmap.stage_counter': "Aşama {current}/{total}",
    'cabin.roadmap.stage_required': "{count} modül · %{pct} tamamlama gerekli",
    'cabin.roadmap.locked_tooltip': "{prerequisite} aşamasını tamamladıktan sonra açılır",
    'cabin.roadmap.current_badge': "Şu An Burada",
    'cabin.roadmap.module_time': "Tahmini: {time}",
    'cabin.roadmap.module_miles': "{miles} Mil kazanılacak",
    'cabin.roadmap.module_units': "{units} ünite · {lessons} ders",
    'cabin.roadmap.detail_sheet_title': "Modül Detayı",
    'cabin.roadmap.empty.title': "Yol haritası yükleniyor...",
    'cabin.roadmap.error.title': "Yol haritası yüklenemedi",
    'cabin.roadmap.error.sub': "Bağlantını kontrol et",
    'cabin.roadmap.theme.foundations': "Temeller",
    'cabin.roadmap.theme.interview': "Mülakat Hazırlık",
    'cabin.roadmap.theme.sector': "Sektör & Uzmanlık",
    'cabin.roadmap.theme.foundations.sub': "Modül 1-4 · Temel konular",
    'cabin.roadmap.theme.interview.sub': "Modül 5-10 · Hazırlık becerileri",
    'cabin.roadmap.theme.sector.sub': "Modül 11-16 · Sektör uzmanlığı",
    'cabin.roadmap.theme_progress': "%{pct} tamamlandı",
    'cabin.roadmap.modules_count': "{completed}/{total} modül",
    'cabin.roadmap.hero.total_miles': "Toplam Mil",
    'cabin.roadmap.hero.overall_progress': "Genel İlerleme",
    'cabin.roadmap.hero.stage_chip_pct': "%{pct}",

    'cabin.progress.title': "İlerleme Durumum",
    'cabin.progress.subtitle': "Kabin memurluğu eğitimindeki gelişimini takip et",
    'cabin.progress.level_card.current': "Mevcut Seviye",
    'cabin.progress.level_card.next': "Sıradaki Seviye",
    'cabin.progress.level_card.miles_to_next': "{miles} Mil daha kazan",
    'cabin.progress.level_card.max': "Maksimum seviyeye ulaştınız!",
    'cabin.progress.next_level': "Sonraki Seviye",
    'cabin.progress.next_level_miles': "Sonraki seviyeye {miles} Mil",
    'cabin.progress.tabs.aria_label': "İlerleme sekmeleri",
    'cabin.progress.tabs.overview': "Genel Bakış",
    'cabin.progress.tabs.modules': "Modüller",
    'cabin.progress.tabs.skills': "Beceriler",
    'cabin.progress.tabs.roadmap': "Yol Haritası",
    'cabin.progress.stats.total_miles': "Toplam Mil",
    'cabin.progress.stats.streak': "Günlük Seri",
    'cabin.progress.stats.completed': "Tamamlanan Ders",
    'cabin.progress.stats.time': "Toplam Süre",
    'cabin.progress.weekly.title': "Haftalık Dersler",
    'cabin.progress.weekly.aria_label': "Haftalık ders tablosu",
    'cabin.progress.weekly.tooltip_label': "Ders",
    'cabin.progress.module.completed_label': "Tamamlandı",
    'cabin.progress.module.in_progress_label': "Devam Ediyor",
    'cabin.progress.attempt.score': "%{score} başarı",
    'cabin.progress.attempt.date': "{date}",
    'cabin.progress.skills.title': "Beceri Alanları",
    'cabin.progress.achievements.title': "Başarılarım",
    'cabin.progress.achievements.coming_soon': "M5.4 ile gelecek",
    'cabin.progress.export.title': "PDF Rapor İndir",
    'cabin.progress.export.coming_soon': "Yakında aktif olacak",
    'cabin.progress.empty.title': "Henüz ders bitirmediniz",
    'cabin.progress.empty.sub': "Dashboard'dan bir ders seçin ve ilerlemenizi görün",
    'cabin.progress.empty.cta': "Derse Git",
    'cabin.progress.tab.overview': "Genel Bakış",
    'cabin.progress.tab.modules': "Modüller",
    'cabin.progress.tab.themes': "Temalar",
    'cabin.progress.total_completed': "{completed}/{total} ders tamamlandı",
    'cabin.progress.weekly_activity': "Haftalık Aktivite",
    'cabin.progress.today_summary': "Bugünün Özeti",
    'cabin.progress.top_modules': "En Çok İlerleme",
    'cabin.progress.empty': "Henüz ders tamamlamadın, ilk modüle başla!",
    'cabin.progress.completion_ring.label': "Genel Tamamlanma",
    'cabin.progress.today.xp': "Bugün XP",
    'cabin.progress.today.miles': "Bugün Mil",
    'cabin.progress.today.lessons': "Bugün Ders",
    'cabin.progress.theme.foundations': "Temeller",
    'cabin.progress.theme.interview': "Mülakat Hazırlık",
    'cabin.progress.theme.sector': "Sektör & Uzmanlık",
    'cabin.progress.theme.modules_done': "{done}/{total} modül",
    'cabin.progress.last_activity': "{time} önce aktifti",
    'cabin.progress.streak.current': "Mevcut seri",
    'cabin.progress.streak.longest': "En uzun seri",
    'cabin.progress.badges.unlocked': "{count} rozet kazanıldı",
    'cabin.progress.go_to_module': "Devam Et",

    'cabin.empty.title': "Kabin yolculuğunuza başlayın!",
    'cabin.empty.sub': "İlk dersinizi seçin ve ilk milinizi kazanın.",
    'cabin.empty.cta': "İlk Derse Başla",

    'cabin.error.title': "Bağlantı koptu",
    'cabin.error.sub': "Veriler yüklenemedi. Tekrar dene.",
    'cabin.error.cta': "Tekrar Dene",

    'cabin.milestone.beginner': "Başlangıç",
    'cabin.milestone.student': "Öğrenci",
    'cabin.milestone.practitioner': "Pratikçi",
    'cabin.milestone.master': "Master",
    'cabin.milestone.expert': "Uzman",

    'cabin.level.beginner': "Başlangıç",
    'cabin.level.student': "Öğrenci",
    'cabin.level.candidate': "Aday",
    'cabin.level.intermediate': "Orta Seviye",
    'cabin.level.advanced': "İleri Seviye",
    'cabin.level.expert': "Uzman",
    'cabin.level.master': "Master",
    'cabin.level.professional': "Profesyonel",

    'cabin.a11y.total_miles': "{count} mil kazanıldı",
    'cabin.a11y.streak': "{days} günlük seri aktif",
    'cabin.a11y.module_card': "Modül {order}, {title}, %{progress} tamamlandı",
    'cabin.a11y.module_card_locked': "Modül {order}, {title}, {prerequisite} bittikten sonra açılır",
    'cabin.a11y.level_progress': "Seviye ilerlemesi: %{pct}",
    'cabin.a11y.sidebar_expanded': "Menü genişletildi",
    'cabin.a11y.sidebar_collapsed': "Menü daraltıldı",

    // ── M5.3 Lesson Player + Exercises ───────────────────────────────────────
    'lesson.loading': "Ders yükleniyor...",
    'lesson.error_title': "Ders yüklenemedi",
    'lesson.error_desc': "Bağlantını kontrol et ve tekrar dene.",
    'lesson.exit': "Dersten çık",
    'lesson.start': "Başla",
    'lesson.resume_notice': "Kaldığınız yerden devam ediyorsunuz",
    'lesson.key_takeaways': "Bu derste öğrenecekleriniz:",
    'lesson.intro_image_alt': "Ders görseli",
    'lesson.locked': "Kilitli",
    'lesson.minutes': "dk",
    'lesson.coming_soon_title': "Yakında Geliyor",
    'lesson.coming_soon_desc': "Bu egzersiz tipi M5.5 güncellemesinde eklenecek.",
    'lesson.skip_phase': "Atla ve Devam Et",
    'lesson.exitConfirm.title': "Dersten Çık?",
    'lesson.exitConfirm.desc': "İlerlemeniz kaydedildi. Kaldığınız yerden devam edebilirsiniz.",
    'lesson.exitConfirm.cancel': "Devam Et",
    'lesson.exitConfirm.exit': "Çık",

    'exercise.question_counter': "Soru {current} / {total}",
    'exercise.max_xp': "Maks. {xp} XP",
    'exercise.submit': "Kontrol Et",
    'exercise.checking': "Kontrol ediliyor...",
    'exercise.next': "Sonraki Soru",
    'exercise.finish': "Bitir",
    'exercise.finish_lesson': "Dersi Tamamla",
    'exercise.correct': "Doğru!",
    'exercise.wrong': "Yanlış!",
    'exercise.correct_answer': "Doğru cevap:",
    'exercise.correct_translation': "Doğru çeviri:",
    'exercise.correct_order': "Doğru sıralama:",
    'exercise.hint': "İpucu",
    'exercise.hint_used': "İpucu kullanıldı, puan düşük olabilir.",
    'exercise.fill_placeholder': "Cevabınızı yazın...",
    'exercise.fill_input_label': "Cevap girişi",
    'exercise.blank_label': "Boşluk",
    'exercise.word_pool_label': "Kelime havuzu",
    'exercise.match_instruction': "Her iki sütundan eşleşen öğelere tıklayın",
    'exercise.match_aria_label': "Eşleştirme egzersizi",
    'exercise.matched': "eşleşti",
    'exercise.all_matched': "Tüm eşleşmeler tamamlandı!",
    'exercise.drag_instruction': "Kelimeleri doğru sıraya sürükleyin",
    'exercise.word_order_area': "Kelime sıralama alanı",
    'exercise.reset': "Sıfırla",
    'exercise.card_front': "Kartın ön yüzü",
    'exercise.card_back': "Kartın arkası",
    'exercise.tap_to_flip': "Çevirmek için tıklayın",
    'exercise.flip': "Çevir",
    'exercise.prev_card': "Önceki kart",
    'exercise.next_card': "Sonraki kart",
    'exercise.play_audio': "Sesi oynat",
    'exercise.image_question_alt': "Soru görseli",
    'exercise.card_image': "Kart görseli",
    'exercise.card_image_upload': "Görsel yükle",
    'exercise.card_image_remove': "Görseli kaldır",
    'exercise.known_btn': "Bildim",
    'exercise.unknown_btn': "Bilmedim",
    'exercise.known': "Bildi",
    'exercise.unknown': "Bilmedi",
    'exercise.flashcard_done': "Kart destesi tamamlandı!",
    'exercise.flashcard_known': "Bildiğim",
    'exercise.flashcard_total': "Toplam",
    'exercise.progress_label': "Kart ilerlemesi",
    'exercise.language_tr': "Türkçe",
    'exercise.language_en': "İngilizce",
    'exercise.translate_to': "{lang} çevirisi",
    'exercise.translation_placeholder': "Çevirinizi yazın...",
    'exercise.translation_input_label': "Çeviri girişi",
    'exercise.enter_to_submit': "Enter: gönder, Shift+Enter: yeni satır",
    'exercise.continue': "Devam Et",
    'teaching.continue': "Devam Et",

    'complete.title': "Ders Tamamlandı!",
    'complete.score_label': "Başarı Puanı",
    'complete.xp_label': "Deneyim Puanı",
    'complete.miles_label': "Havacılık Mili",
    'complete.badges_unlocked': "Yeni Rozetler!",
    'complete.continue': "Sonraki Derse Devam Et",
    'complete.next_lesson': "Sonraki Derse Geç",
    'complete.backToModule': "Modüle Dön",
    'complete.failTitle': "Bu sefer olmadı",
    'complete.failDesc': "Geçmek için en az %{{threshold}} gerekiyor. Tekrar dene!",
    'complete.retry': "Tekrar Dene",
    'complete.module_finished_title': "Modül Tamamlandı!",
    'complete.module_finished_desc': "Bu modüldeki tüm dersleri bitirdiniz.",

    'module.units': "ünite",
    'module.lessons': "ders",
    'module.hours': "saat",
    'module.miles': "Mil",
    'module.no_units_title': "Henüz ünite yok",
    'module.no_units_desc': "Bu modül için ünite ve dersler yakında eklenecek.",
    'unit.lessons_done': "ders tamamlandı",
    'unit.no_lessons': "Bu ünitede henüz ders yok.",
    'common.go_back': "Geri Dön",
    'common.close': "Kapat",
    'common.loading': "Yükleniyor...",

    // ── M5.4 Gamification ────────────────────────────────────────────────────
    'cabin.gamification.streak.label': "Günlük Seri",
    'cabin.gamification.streak.tooltip': "{count} günlük seri aktif, son aktivite {time} önce",
    'cabin.gamification.streak.fireModeLabel': "Yangın Modu",
    'cabin.gamification.streak.aria_updated': "Seri güncellendi: {days} gün",
    'cabin.gamification.streak.tooltip_active': "{days} günlük seri aktif",
    'cabin.gamification.streak.tooltip_last': "Son aktivite: {when}",
    'cabin.gamification.streak.tooltip_longest': "En uzun: {days} gün",
    'cabin.gamification.streak.last_activity_now': "Az önce aktif",
    'cabin.gamification.streak.last_activity_hours': "{hours} saat önce",
    'cabin.gamification.dailyTasks.title': "Günlük Görevler",
    'cabin.gamification.dailyTasks.allCompleted': "Bugünün tüm görevleri tamamlandı!",
    'cabin.gamification.dailyTasks.claim': "Ödülü Al",
    'cabin.gamification.dailyTasks.claimed': "Tamamlandı",
    'cabin.gamification.dailyTasks.progress': "{current}/{total} görev",
    'cabin.gamification.leaderboard.title': "Başarı Sıralaması",
    'cabin.gamification.leaderboard.weekly': "Haftalık",
    'cabin.gamification.leaderboard.monthly': "Aylık",
    'cabin.gamification.leaderboard.alltime': "Tüm Zamanlar",
    'cabin.gamification.leaderboard.yourRank': "Senin sıran: #{rank}",
    'cabin.gamification.leaderboard.empty': "Henüz sıralama yok",
    'cabin.gamification.leaderboard.my_rank_label': "Sıralamadaki Yerin",
    'cabin.gamification.leaderboard.miles_unit': "Mil",
    'cabin.gamification.leaderboard.level': "Seviye",
    'cabin.gamification.leaderboard.you': "Sen",
    'cabin.gamification.leaderboard.error': "Sıralama yüklenemedi",
    'cabin.gamification.leaderboard.top_n': "İlk {n} kullanıcı gösteriliyor",
    'cabin.gamification.leaderboard.period_tabs_aria': "Dönem seçimi",
    'cabin.gamification.achievements.title': "Başarımlar",
    'cabin.gamification.achievements.filterAll': "Tümü",
    'cabin.gamification.achievements.filterUnlocked': "Açılanlar",
    'cabin.gamification.achievements.filterLocked': "Kilitliler",
    'cabin.gamification.achievements.unlockedOn': "{date} tarihinde kazanıldı",
    'cabin.gamification.achievements.progress': "{current}/{threshold}",
    'cabin.gamification.achievements.allUnlocked': "Tebrikler! Tüm rozetleri açtın 🏆",
    'cabin.gamification.achievements.empty.title': "Henüz rozet yok",
    'cabin.gamification.achievements.empty.sub': "İlk dersi tamamla, ilk rozeti aç!",
    'cabin.modules.empty.title': "Henüz modül eklenmedi",
    'cabin.modules.empty.sub': "Modüller yakında yayına alınacak.",
    'cabin.gamification.badgeUnlock.congrats': "Tebrikler!",
    'cabin.gamification.badgeUnlock.newBadge': "Yeni rozet kazandın!",
    'cabin.gamification.badgeUnlock.continue': "Harika!",
    'cabin.sidebar.leaderboard': "Liderlik",
    'cabin.sidebar.achievements': "Başarımlar",

    // ── M5.6 Interview System ─────────────────────────────────────────────────
    'cabin.interview.hub.title': "Mülakat Pratiği",
    'cabin.interview.hub.subtitle': "AI destekli kabin memuru mülakat simülasyonu",
    'cabin.interview.hub.difficulty': "Zorluk",
    'cabin.interview.hub.language': "Dil",
    'cabin.interview.hub.category': "Kategori",
    'cabin.interview.hub.select_mode': "Mod Seçin",
    'cabin.interview.hub.text_mode_title': "Yazılı Mülakat",
    'cabin.interview.hub.text_mode_desc': "Yazılı mülakat pratiği, AI ile sohbet",
    'cabin.interview.hub.voice_mode_title': "Sesli Mülakat",
    'cabin.interview.hub.voice_mode_desc': "Sesli mülakat, gerçekçi simülasyon",
    'cabin.interview.hub.video_mode_title': "Video Mülakat",
    'cabin.interview.hub.video_mode_desc': "Self-record video + AI değerlendirme",
    'cabin.interview.hub.video_badge': "KVKK",
    'cabin.interview.hub.recent_sessions': "Son Oturumlar",
    'cabin.interview.hub.view_all': "Tümünü Gör",
    'cabin.interview.hub.quick_access': "Hazırlık",
    'cabin.interview.hub.cv_profile_title': "CV Profilim",
    'cabin.interview.hub.cv_profile_desc': "Profilini tamamla, mülakat sorularını sana göre özelleştirelim",
    'cabin.interview.hub.readiness_title': "Hazırlık Raporum",
    'cabin.interview.hub.readiness_desc': "Mülakat hazırlık skorunu ve önerilerini incele",
    'cabin.interview.hub.last_session': "SON OTURUM",
    'cabin.interview.hub.difficulty_short': "Zorluk",
    'cabin.interview.hub.pending_eval': "Değerlendirme bekleniyor",
    'cabin.interview.hub.progress_summary': "İLERLEMEM",
    'cabin.interview.hub.stat_total': "Toplam",
    'cabin.interview.hub.stat_scored': "Puanlandı",
    'cabin.interview.hub.stat_pending': "Bekliyor",
    'cabin.interview.hub.avg_score': "Ort. skor",
    'cabin.interview.session.status.scored': "Değerlendirildi",
    'cabin.interview.session.status.pending_review': "İnceleniyor",
    'cabin.interview.hub.badge.pending': "Değerlendirme Bekleniyor",
    'cabin.interview.hub.badge.in_progress': "Devam Ediyor",
    'cabin.interview.hub.pending_hint': "Değerlendirici inceleme alıyor",
    'cabin.interview.hub.in_progress_hint': "Oturum tamamlanmadı",
    'cabin.interview.hub.result.positive': "Olumlu Sonuç",
    'cabin.interview.hub.result.reserve': "İhtiyatlı Sonuç",
    'cabin.interview.hub.result.negative': "Olumsuz Sonuç",
    'cabin.interview.hub.no_session_title': "Henüz mülakat yapmadın",
    'cabin.interview.hub.no_session_sub': "İlk simülasyonunu başlat ve hazırlığını ölç",

    'cabin.interview.text.loading': "Mülakat başlatılıyor...",
    'cabin.interview.text.back': "Hub'a dön",
    'cabin.interview.text.turns': "tur",
    'cabin.interview.text.score': "Skor",
    'cabin.interview.text.end': "Bitir",
    'cabin.interview.text.typing_label': "AI değerlendiriyor...",
    'cabin.interview.text.chat_aria': "Mülakat sohbeti",
    'cabin.interview.text.textarea_aria': "Cevabınız",
    'cabin.interview.text.placeholder': "Cevabınızı yazın...",
    'cabin.interview.text.send': "Cevabı gönder",
    'cabin.interview.text.send_hint': "Göndermek için Ctrl+Enter",
    'cabin.interview.text.start_error': "Mülakat başlatılamadı",
    'cabin.interview.text.turn_error': "Cevap gönderilemedi",

    'cabin.interview.voice.loading': "Sesli mülakat hazırlanıyor...",
    'cabin.interview.voice.start_error': "Sesli mülakat başlatılamadı",
    'cabin.interview.voice.turn_error': "Kayıt gönderilemedi",
    'cabin.interview.voice.tts_play': "Soruyu sesli oku",
    'cabin.interview.voice.record': "Kaydet",
    'cabin.interview.voice.stop': "Durdur",
    'cabin.interview.voice.retry': "Tekrar Dene",
    'cabin.interview.voice.submit': "Cevabı Gönder",
    'cabin.interview.voice.transcribing': "AI işliyor...",
    'cabin.interview.voice.perm_denied': "Bu mod için mikrofon erişimi gereklidir",
    'cabin.interview.voice.transcript_label': "Transkript",
    'cabin.interview.voice.feedback_label': "AI Geri Bildirimi",

    'cabin.interview.video.loading': "Video mülakat hazırlanıyor...",
    'cabin.interview.video.start_error': "Video mülakat başlatılamadı",
    'cabin.interview.video.turn_error': "Video yüklenemedi",
    'cabin.interview.video.kvkk_title': "Video Kayıt Bildirimi",
    'cabin.interview.video.kvkk_body1': "Videonuz değerlendirme amacıyla kaydedilecek ve AI tarafından işlenecektir.",
    'cabin.interview.video.kvkk_body2': "Devam ederek gizlilik politikamız kapsamında (KVKK) bu kaydı onaylıyorsunuz.",
    'cabin.interview.video.kvkk_accept': "Onaylıyorum",
    'cabin.interview.video.kvkk_decline': "İptal",
    'cabin.interview.video.record': "Kaydı Başlat",
    'cabin.interview.video.stop': "Durdur",
    'cabin.interview.video.retry': "Tekrar Kaydet",
    'cabin.interview.video.submit': "Gönder ve Devam Et",
    'cabin.interview.video.uploading': "Yükleniyor...",
    'cabin.interview.video.perm_denied': "Kamera ve mikrofon erişimi gereklidir",
    'cabin.interview.video.question_list': "Sorular",
    'cabin.interview.video.done': "Tamamlandı",

    'cabin.interview.final.title': "Mülakat Tamamlandı!",
    'cabin.interview.final.strengths': "Güçlü Yönler",
    'cabin.interview.final.improvements': "Geliştirilecek Alanlar",
    'cabin.interview.final.badges': "Kazanılan Rozetler",
    'cabin.interview.final.retry': "Tekrar Dene",
    'cabin.interview.final.history': "Geçmiş",

    'cabin.interview.history.title': "Mülakat Geçmişi",
    'cabin.interview.history.no_sessions': "Henüz oturum yok",
    'cabin.interview.history.start_first': "İlk Mülakatını Başlat",
    'cabin.interview.history.detail_title': "Oturum Detayı",
    'cabin.interview.history.close': "Kapat",
    'cabin.interview.history.no_detail': "Detay mevcut değil",
    'cabin.interview.history.duration': "Süre",
    'cabin.interview.history.turns': "Tur",
    'cabin.interview.history.status_active': "Devam Ediyor",
    'cabin.interview.history.pending_hint': "Değerlendirme bekleniyor — eğitmenin incelemesi tamamlandığında sonucun görünecek",

    // ── Interview Simulation (feat/interview-simulation-web) ─────────────────
    'interview.sim.title': "Mülakat Simülasyonu",
    'interview.sim.hub.subtitle': "Cevaplarınızı kaydedin, eğitmen değerlendirsin",
    'interview.sim.begin_btn': "Simülasyonu Başlat",
    'interview.sim.start_error': "Oturum başlatılamadı",
    'interview.sim.submit_error': "Video gönderilemedi",

    'interview.sim.instructions.title': "Mülakata Başlamadan Önce",
    'interview.sim.instructions.subtitle': "Lütfen bu talimatları dikkatlice okuyun",
    'interview.sim.instructions.dress': "Giyiminiz düzgün ve profesyonel olsun",
    'interview.sim.instructions.camera': "Kameranızı yüzünüzü net gösterecek şekilde ayarlayın",
    'interview.sim.instructions.lighting': "İyi aydınlatılmış bir ortamda olun",
    'interview.sim.instructions.quiet': "Arka plan gürültüsünün az olduğu sessiz bir yer seçin",
    'interview.sim.instructions.natural': "Göz teması kurun ve doğal olun",
    'interview.sim.kvkk_notice': "Video kaydınız güvenli biçimde saklanacak ve yalnızca eğitmenler tarafından incelenecektir. Devam ederek gizlilik politikamız (KVKK) kapsamında bu kaydı onaylıyorsunuz.",
    'interview.sim.question_count': "Bu simülasyonda {{n}} soru bulunmaktadır",
    'interview.sim.camera_ready': "Kamera hazır",
    'interview.sim.camera_preview_label': "Kamera Önizleme",
    'interview.sim.no_questions': "Sorular yükleniyor…",
    'interview.sim.camera_checking': "Kamera kontrol ediliyor…",
    'interview.sim.camera_denied': "Kamera erişimi gerekli — tarayıcı ayarlarından izin verin ve sayfayı yenileyin.",
    'interview.sim.start_interview': "Mülakata Başla",
    'interview.sim.flow_note': "5 soruyu tek bir kesintisiz kayıt oturumunda yanıtlayacaksınız. Tüm soruları bitirince 'Kaydı Bitir'e basın.",
    'interview.sim.question_lang_label': "Soru Dili",
    'interview.sim.cat.experience': "Deneyim",
    'interview.sim.cat.motivation': "Motivasyon",
    'interview.sim.cat.teamwork': "Ekip Çalışması",
    'interview.sim.cat.stress': "Stres Yönetimi",
    'interview.sim.cat.customer_service': "Müşteri Hizmetleri",
    'interview.sim.cat.general': "Genel",
    'interview.sim.cat.english': "İngilizce",
    'interview.sim.cat.safety': "Güvenlik",
    'interview.sim.cat.communication': "İletişim",
    'interview.sim.cat.professionalism': "Profesyonellik",
    'interview.sim.cat.scenario': "Senaryo",
    'interview.sim.cat.aviation_specific': "Havacılık",
    'interview.sim.cat.situational': "Durumsal",
    'interview.sim.cat.emergency': "Acil Durum",
    'interview.sim.cat.introduction': "Tanışma",

    // Kariyer Online Shell i18n keys
    'kariyer.lang.tr': "Türkçe",
    'kariyer.lang.en': "İngilizce",

    // Interview band labels (kariyer.* namespace)
    'kariyer.band_ccri_very_high': "Çok Yüksek Hazırlık",
    'kariyer.band_ccri_high': "Yüksek Hazırlık",
    'kariyer.band_ccri_mid': "Orta Düzey Hazırlık",
    'kariyer.band_ccri_low': "Düşük Hazırlık",
    'kariyer.band_ars_outstanding': "Olağanüstü",
    'kariyer.band_ars_strong': "Güçlü Aday",
    'kariyer.band_ars_adequate': "Yeterli Aday",
    'kariyer.band_ars_developing': "Gelişim Gerekli",
    'kariyer.band_ars_insufficient': "Yetersiz",

    'interview.sim.countdown': "Hazır olun!",
    'interview.sim.recording': "Kayıt Yapılıyor",
    'interview.sim.finish_recording': "Kaydı Bitir",
    'interview.sim.retake': "Tekrar Çek",
    'interview.sim.submit': "Videoyu Gönder",

    'interview.sim.preview_title': "Kaydınızı İnceleyin",
    'interview.sim.preview_subtitle': "Kaydınızı izleyin, gönderin ya da tekrar çekin",

    'interview.sim.submitted_title': "Mülakat Gönderildi!",
    'interview.sim.waiting_review': "Mülakatınız eğitmene iletildi. Değerlendirme sonrası burada görüntülenecek.",
    'interview.sim.pending': "Değerlendirme Bekleniyor",
    'interview.sim.pending_desc': "Eğitmeniniz videonuzu inceleyerek puan ve geri bildirim verecektir. Bu işlem 1-3 iş günü sürebilir.",
    'interview.sim.view_history': "Oturumlarımı Gör",

    'interview.sim.status_pending': "Değerlendirme Bekleniyor",
    'interview.sim.status_scored': "Değerlendirildi",

    'interview.sim.result.total': "Toplam Puan",
    'interview.sim.result.criteria_title': "DEĞERLENDİRME KRİTERLERİ",
    'interview.sim.result.feedback': "Eğitmen Geri Bildirimi",
    'interview.sim.result.scored_by': "Değerlendiren: {{name}}",
    'interview.sim.result.criteria.communication': "İletişim",
    'interview.sim.result.criteria.professionalism': "Profesyonellik",
    'interview.sim.result.criteria.content': "İçerik",
    'interview.sim.result.criteria.confidence': "Özgüven",
    'interview.sim.result.criteria.overall_impression': "Genel İzlenim",

    // ── EnglishInterviewPlayer keys ───────────────────────────────────────────
    'ei.question_counter': "Soru {current} / {total}",
    'ei.textarea_placeholder': "İngilizce cevabınızı yazın...",
    'ei.word_count': "{count} kelime",
    'ei.char_count': "{count}/5 karakter",
    'ei.submit_btn': "Cevapla",
    'ei.result.correct': "Güzel!",
    'ei.result.incorrect': "Geliştirilebilir",
    'ei.result.grammar': "Gramer",
    'ei.result.relevance': "İlgililik",
    'ei.result.tone': "Ton",
    'ei.result.better_version_label': "DAHA İYİ VERSİYON",
    'ei.next_question': "Sonraki Soru",
    'ei.see_results': "Sonuçları Gör",
    'ei.completion.interview_complete': "Mülakat Tamamlandı!",
    'ei.completion.out_of_lives': "Canlarınız Bitti",
    'ei.completion.summary': "Özet",
    'ei.completion.correct_count': "{correct} / {total} doğru",
    'ei.completion.finish': "Bitir",

    'instructor.reviews.title': "Mülakat Değerlendirmeleri",
    'instructor.reviews.subtitle': "Öğrenci mülakat simülasyonlarını inceleyin ve puanlayın",
    'instructor.reviews.stats.pending': "Bekleyen",
    'instructor.reviews.stats.total': "Toplam Oturum",
    'instructor.reviews.tab_pending': "Bekleyenler",
    'instructor.reviews.tab_all': "Tüm Oturumlar",
    'instructor.reviews.no_pending': "Bekleyen değerlendirme yok",
    'instructor.reviews.no_sessions': "Henüz oturum yok",
    'instructor.reviews.inspect': "İncele",
    'instructor.reviews.detail_title': "Oturumu İncele",
    'instructor.reviews.video_label': "Öğrenci mülakat videosu",
    'instructor.reviews.video_unavailable': "Video henüz mevcut değil",
    'instructor.reviews.video_loading': "Video yükleniyor...",
    'instructor.reviews.video_error': "Video yüklenemedi",
    'interview.sim.result.total_score_label': "Toplam Puan",

    // ── Interview Simulation — Cooldown UI ────────────────────────────────────
    'interview.sim.cooldown.banner_title': "Mülakatın tamamlandı, aferin!",
    'interview.sim.cooldown.banner_sub': "Yeni mülakat: {{date}}",
    'interview.sim.cooldown.relative': "{{days}} gün sonra",
    'interview.sim.cooldown.relative_today': "Bugün açılıyor — az kaldı!",
    'interview.sim.cooldown.btn_caption': "Bekleme süresinde",
    'interview.sim.cooldown.toast': "Bir sonraki mülakatın {{date}} tarihinde açılıyor",
    'interview.sim.cooldown.a11y_hint': "{{date}} tarihine kadar kullanılamıyor",

    // ── Interview Simulation — Admin cooldown reset ───────────────────────────
    'interview.sim.admin.reset_cooldown_btn': "Cooldown'u Sıfırla",
    'interview.sim.admin.reset_cooldown_confirm_title': "Mülakat Cooldown'unu Sıfırla",
    'interview.sim.admin.reset_cooldown_confirm_desc': "Bu öğrenci hemen yeni mülakat başlatabilecek.",
    'interview.sim.admin.reset_cooldown_success': "Cooldown sıfırlandı. Öğrenci hemen yeni mülakat başlatabilir.",
    'interview.sim.admin.reset_cooldown_error': "Cooldown sıfırlanamadı. Lütfen tekrar deneyin.",

    'instructor.reviews.criteria_title': "Puanlama Kriterleri (her biri 1-5)",
    'instructor.reviews.feedback_label': "Gerekçe ve Notlar",
    'instructor.reviews.feedback_placeholder': "Puan gerekçenizi ve notlarınızı yazın...",
    'instructor.reviews.submit_score': "Puanla ve Gönder",
    'instructor.reviews.scoring': "Puan gönderiliyor...",
    'instructor.reviews.score_success': "Puan başarıyla gönderildi",
    'instructor.reviews.score_error': "Puan gönderilemedi",
    'instructor.reviews.result_required': "Göndermeden önce lütfen bir sonuç seçin (Olumlu / Yedek / Olumsuz).",

    // ── Rubric v2 — section headings ─────────────────────────────────────────
    'instructor.reviews.section1_title': "BÖLÜM I — GENEL İZLENİM",
    'instructor.reviews.section2_title': "BÖLÜM II — İLETİŞİM VE DİL YETKİNLİKLERİ",
    'instructor.reviews.section3_title': "BÖLÜM III — MESLEKİ YETERLİLİK VE UYUM",
    'instructor.reviews.section4_title': "Bölüm IV — Genel Değerlendirme",

    // ── Rubric v2 — criteria labels ──────────────────────────────────────────
    'instructor.reviews.criteria.appearance_hygiene': "Kılık kıyafet, hijyen",
    'instructor.reviews.criteria.posture_body_language': "Profesyonel duruş ve beden dili",
    'instructor.reviews.criteria.friendliness': "Güler yüzlülük / olumlu yaklaşım",
    'instructor.reviews.criteria.first_impression': "İlk izlenim",
    'instructor.reviews.criteria.turkish_communication': "Türkçe iletişim becerileri",
    'instructor.reviews.criteria.english_level': "İngilizce seviyesi",
    'instructor.reviews.criteria.voice_diction': "Ses tonu / diksiyon",
    'instructor.reviews.criteria.empathy_customer_focus': "Empati ve müşteri odaklılık",
    'instructor.reviews.criteria.teamwork': "Takım çalışmasına yatkınlık",
    'instructor.reviews.criteria.stress_performance': "Stres altında performans",
    'instructor.reviews.criteria.flexible_hours': "Esnek çalışma saatlerine uyum",
    'instructor.reviews.criteria.passenger_satisfaction': "Yolcu memnuniyetine yaklaşımı",
    'instructor.reviews.criteria.difficult_passengers': "Zor yolcularla başa çıkma becerisi",

    // ── Rubric v2 — result options ───────────────────────────────────────────
    'instructor.reviews.result_label': "Sonuç Kararı",
    'instructor.reviews.result.positive': "Olumlu",
    'instructor.reviews.result.reserve': "Yedek Aday",
    'instructor.reviews.result.negative': "Olumsuz",

    // ── Rubric v2 — textarea labels ──────────────────────────────────────────
    'instructor.reviews.strengths_label': "Gözlemlenen Güçlü Yönler",
    'instructor.reviews.strengths_placeholder': "Adayın güçlü yönlerini not edin...",
    'instructor.reviews.development_areas_label': "GELİŞİM GEREKTİREN ALANLAR",
    'instructor.reviews.development_areas_placeholder': "Adayın gelişim alanlarını not edin...",

    // ── F4 24-criteria scoring — TR ──────────────────────────────────────────
    // Kriter etiketleri
    'instructor.reviews.f4.criteria.kp1': "Kameraya bakış",
    'instructor.reviews.f4.criteria.kp2': "Gülümseme",
    'instructor.reviews.f4.criteria.kp3': "Duruş",
    'instructor.reviews.f4.criteria.kp4': "Beden dili",
    'instructor.reviews.f4.criteria.kp5': "Enerji",
    'instructor.reviews.f4.criteria.kp6': "Profesyonel görünüm",
    'instructor.reviews.f4.criteria.il1': "Diksiyon",
    'instructor.reviews.f4.criteria.il2': "Ses tonu",
    'instructor.reviews.f4.criteria.il3': "Akıcılık",
    'instructor.reviews.f4.criteria.il4': "Net cevap verme",
    'instructor.reviews.f4.criteria.il5': "Gereksiz uzatmama",
    'instructor.reviews.f4.criteria.il6': "İkna edicilik",
    'instructor.reviews.f4.criteria.ck1': "Soruyu anlama",
    'instructor.reviews.f4.criteria.ck2': "Örnekle cevap",
    'instructor.reviews.f4.criteria.ck3': "Meslek farkındalığı",
    'instructor.reviews.f4.criteria.ck4': "Özgüven",
    'instructor.reviews.f4.criteria.ck5': "Samimiyet",
    'instructor.reviews.f4.criteria.ck6': "Ezbersiz cevap",
    'instructor.reviews.f4.criteria.in1': "Telaffuz",
    'instructor.reviews.f4.criteria.in2': "Kelime dağarcığı",
    'instructor.reviews.f4.criteria.in3': "Cümle kurma",
    'instructor.reviews.f4.criteria.in4': "İngilizce Akıcılık",
    'instructor.reviews.f4.criteria.in5': "Anlaşılabilirlik",
    'instructor.reviews.f4.criteria.in6': "İngilizce özgüven",
    // Grup başlıkları
    'instructor.reviews.f4.group_kp_title': "KAMERA PERFORMANSI",
    'instructor.reviews.f4.group_il_title': "İLETİŞİM",
    'instructor.reviews.f4.group_ck_title': "CEVAP KALİTESİ",
    'instructor.reviews.f4.group_in_title': "İNGİLİZCE",
    // Skor rozetleri / notlar
    'instructor.reviews.f4.score_excluded_badge': "Skora dahil değil",
    'instructor.reviews.f4.score_excluded_hint': "Bu kriter değerlendirme sürecinde kullanılır ancak CCRI/ARS puanına eklenmez",
    'instructor.reviews.f4.score_preview_note': "CCRI ve ARS submit sonrası hesaplanır",
    'instructor.reviews.f4.unscored_warning': "{{count}} kriter varsayılan değerde (5) — göndermeden önce gözden geçirin.",
    // CV paneli
    'instructor.reviews.f4.cv_panel_label': "CV Özeti",
    'instructor.reviews.f4.cv_approved_badge': "Onaylı",
    'instructor.reviews.f4.cv_panel_empty': "CV henüz onaylanmadı",
    'instructor.reviews.f4.cv_section_kisisel': "KİŞİSEL",
    'instructor.reviews.f4.cv_section_egitim': "EĞİTİM",
    'instructor.reviews.f4.cv_section_deneyim': "DENEYİM",
    'instructor.reviews.f4.cv_section_dil': "DİL",
    'instructor.reviews.f4.cv_section_motivasyon': "MOTİVASYON",
    'instructor.reviews.f4.cv_section_no_exp': "Deneyim yok",
    // CV skoru bölümü
    'instructor.reviews.f4.cv_score_section_title': "CV / KARİYER SKORU",
    'instructor.reviews.f4.cv_score_label': "Onaylı CV'ye göre skor",
    'instructor.reviews.f4.cv_score_hint': "Öğrencinin onaylı CV'sini inceleyerek 0–10 arasında bir değer gir",
    'instructor.reviews.f4.cv_score_ars_note': "Bu skor CCRI hesabına (CV/Kariyer alanı) dahildir",
    // Red-flag bölümü
    'instructor.reviews.f4.flag_section_title': "Olumsuz Gözlemler",
    'instructor.reviews.f4.flag_disclaimer': "Bu alanlar değerlendirme notlarına girer; CCRI veya ARS puanını doğrudan etkilemez.",
    'instructor.reviews.f4.flag_note_label': "Ek not (opsiyonel)",
    'instructor.reviews.f4.flag_note_placeholder': "Gözlemlerinizi buraya yazın...",
    // Red-flag enum etiketleri
    'instructor.reviews.f4.flag.poor_posture': "Kötü duruş",
    'instructor.reviews.f4.flag.grooming': "Bakımsız görünüm",
    'instructor.reviews.f4.flag.unprofessional_appearance': "Profesyonel olmayan kıyafet",
    'instructor.reviews.f4.flag.eye_contact_avoidance': "Göz temasından kaçınma",
    'instructor.reviews.f4.flag.low_energy': "Düşük enerji",
    'instructor.reviews.f4.flag.monotone_voice': "Monoton ses tonu",
    'instructor.reviews.f4.flag.language_barrier': "Dil engeli",
    'instructor.reviews.f4.flag.memorized_answers': "Ezberci cevaplar",
    'instructor.reviews.f4.flag.nervousness_excessive': "Aşırı gerginlik",
    'instructor.reviews.f4.flag.attitude': "Olumsuz tutum",
    'instructor.reviews.f4.flag.dishonesty_signs': "Dürüstlük sorunu işaretleri",
    'instructor.reviews.f4.flag.safety_unawareness': "Güvenlik farkındalığı eksik",
    'instructor.reviews.f4.flag.customer_service_gap': "Müşteri hizmetleri açığı",
    'instructor.reviews.f4.flag.teamwork_concern': "Takım çalışması endişesi",
    'instructor.reviews.f4.flag.weak_motivation': "Zayıf motivasyon",
    'instructor.reviews.f4.flag.punctuality': "Dakiklik sorunu",
    // Sonuç paneli
    'instructor.reviews.f4.result_ccri_title': "CCRI — Kabin Kursiyeri Hazırlık Endeksi",
    'instructor.reviews.f4.result_ars_title': "ARS — Aday Değerlendirme Skoru",
    'instructor.reviews.f4.result_out_of_100': "100 üzerinden",
    'instructor.reviews.f4.result_breakdown_title': "ALAN KIRILIMI",
    // CCRI band'leri
    'instructor.reviews.f4.band_ccri_very_high': "Çok Yüksek Hazırlık",
    'instructor.reviews.f4.band_ccri_high': "Yüksek Hazırlık",
    'instructor.reviews.f4.band_ccri_mid': "Orta Düzey Hazırlık",
    'instructor.reviews.f4.band_ccri_low': "Düşük Hazırlık",
    // ARS band'leri
    'instructor.reviews.f4.band_ars_outstanding': "Olağanüstü Performans",
    'instructor.reviews.f4.band_ars_strong': "Güçlü Aday",
    'instructor.reviews.f4.band_ars_adequate': "Yeterli Aday",
    'instructor.reviews.f4.band_ars_developing': "Gelişim Gerekli",
    'instructor.reviews.f4.band_ars_insufficient': "Yetersiz",
    // Alan kırılımı key'leri
    'instructor.reviews.f4.breakdown.fiziksel_sunum': "Fiziksel Sunum",
    'instructor.reviews.f4.breakdown.iletisim_diksiyon': "İletişim & Diksiyon",
    'instructor.reviews.f4.breakdown.ingilizce': "İngilizce",
    'instructor.reviews.f4.breakdown.motivasyon': "Motivasyon",
    'instructor.reviews.f4.breakdown.kriz': "Kriz Yönetimi",
    'instructor.reviews.f4.breakdown.ekip': "Ekip Çalışması",
    'instructor.reviews.f4.breakdown.cv_kariyer': "CV / Kariyer",
    'instructor.reviews.f4.breakdown.kamera_durusu': "Kamera & Duruş",
    'instructor.reviews.f4.breakdown.cevap_kalitesi': "Cevap Kalitesi",
    'instructor.reviews.f4.breakdown.ozguven': "Özgüven",
    'instructor.reviews.f4.breakdown.havayolu_kulturu': "Havayolu Kültürü",
    'instructor.reviews.f4.breakdown.ingilizce_cevap': "İngilizce Cevap",
    'instructor.reviews.f4.breakdown.stres': "Stres Yönetimi",
    'instructor.reviews.f4.breakdown.profesyonel_imaj': "Profesyonel İmaj",
    // Salt-okunur eski kayıt etiketi
    'instructor.reviews.f4.legacy_readonly_title': "Puanlama detayları (salt okunur)",
    'instructor.reviews.f4.legacy_v1_label': "5-kriter puanı",
    'instructor.reviews.f4.legacy_v2_label': "13-kriter puanı",
    'instructor.reviews.unknown_schema_title': "Bilinmeyen değerlendirme şeması",
    'instructor.reviews.unknown_schema_desc': "Bu oturum tanınmayan bir puanlama şemasıyla kaydedilmiş. Detay için yöneticinize başvurun.",
    // v2 düzenleme engeli
    'instructor.reviews.v2_edit_disabled_title': "Bu değerlendirme düzenlenemez",
    'instructor.reviews.v2_edit_disabled_desc': "Bu değerlendirme eski formatta (v2) kaydedilmiş olup F4 kriterleri içermiyor. Düzenleme işlemi orijinal puanların varsayılan değerlerle üzerine yazılmasına yol açar. Yeniden puanlamak için oturumu tekrar puanlama seçeneğini kullanın.",
    'instructor.reviews.v2_edit_rescore_hint': "Mevcut F4 rubriğiyle yeniden puanlamak için inceleme listesine dönün ve bu oturum için \"Puanla\" seçeneğini seçin.",
    // Genel boolean
    'cabin.common.yes': "Evet",
    'cabin.common.no': "Hayır",

    // ── M5.5 Lesson phases ───────────────────────────────────────────────────
    'lesson.teaching_title': "Ders",
    'lesson.teaching_header_label': "Ders içeriği",
    'lesson.sections': "{count} bölüm",
    'lesson.read_and_continue': "Oku ve devam et",
    'lesson.no_content': "İçerik bulunamadı.",
    'lesson.intro_header_label': "Ders tanıtımı",
    'lesson.takeaway_label_fact': "Gerçek",
    'lesson.takeaway_label_item': "Madde",
    'exercise.mini_question': "Mini Soru",

    // ── M5.5 VocabularyTimeRace ──────────────────────────────────────────────
    'cabin.exercise.timeRace.title': "Zaman Yarışı",
    'cabin.exercise.timeRace.cards': "{count} kart",
    'cabin.exercise.timeRace.rule1': "{time} saniyelik bir yarış",
    'cabin.exercise.timeRace.rule2': "Her doğru cevap puan kazandırır",
    'cabin.exercise.timeRace.rule3': "{lives} hakkın var",
    'cabin.exercise.timeRace.rule4': "Hızlı olan kazanır",
    'cabin.exercise.timeRace.start': "Başla",
    'cabin.exercise.timeRace.time_left': "Kalan süre",
    'cabin.exercise.timeRace.lives': "Hak",
    'cabin.exercise.timeRace.score': "Puan",
    'cabin.exercise.timeRace.finish': "Bitir",
    'cabin.exercise.timeRace.combo': "Kombo",
    'cabin.exercise.timeRace.great': "Harika!",
    'cabin.exercise.timeRace.good': "İyi iş!",
    'cabin.exercise.timeRace.tryAgain': "Pratik yapmaya devam et!",
    'cabin.exercise.timeRace.gameOver': "Yarış bitti — işte sonuçların",
    'cabin.exercise.timeRace.correct': "Doğru",
    'cabin.exercise.timeRace.maxCombo': "Maks. Kombo",
    'cabin.exercise.timeRace.accuracy': "Doğruluk",
    'cabin.exercise.timeRace.playAgain': "Tekrar Oyna",

    // ── M5.5 SwipeVocabularyCard ─────────────────────────────────────────────
    'cabin.exercise.swipeCard.perfect': "Mükemmel!",
    'cabin.exercise.swipeCard.great': "Harika!",
    'cabin.exercise.swipeCard.keepGoing': "Devam et!",
    'cabin.exercise.swipeCard.resultDesc': "{known}/{total} kart bilindi",
    'cabin.exercise.swipeCard.knownIt': "Bildim",
    'cabin.exercise.swipeCard.dontKnow': "Bilmedim",
    'cabin.exercise.swipeCard.accuracy': "Doğruluk",
    'cabin.exercise.swipeCard.swipeHint': "Kaydır veya butonları kullan",

    // ── M5.5 StoryMode ───────────────────────────────────────────────────────
    'cabin.exercise.storyMode.outcome': "Sonuç",
    'cabin.exercise.storyMode.points': "Puan",
    'cabin.exercise.storyMode.scenesVisited': "{n} sahne görüldü",
    'cabin.exercise.storyMode.finish': "Bitir",
    'cabin.exercise.storyMode.scene': "Sahne {n}",
    'cabin.exercise.storyMode.playAudio': "Sesi oynat",
    'cabin.exercise.storyMode.choose': "Bir yanıt seç",
    'cabin.exercise.storyMode.next': "Sonraki",

    // ── M5.5 WordHunt ────────────────────────────────────────────────────────
    'cabin.exercise.wordHunt.title': "Kelime Avı",
    'cabin.exercise.wordHunt.sentences': "cümle",
    'cabin.exercise.wordHunt.howToPlay': "Nasıl oynanır",
    'cabin.exercise.wordHunt.rule1': "Cümleyi oku ve eksik kelimeyi bul",
    'cabin.exercise.wordHunt.rule2': "Cevabını kutuya yaz",
    'cabin.exercise.wordHunt.rule3': "{time} saniyeniz var",
    'cabin.exercise.wordHunt.startBtn': "Başla",
    'cabin.exercise.wordHunt.finished': "Tamamlandı!",
    'cabin.exercise.wordHunt.wordsFound': "kelime bulundu",
    'cabin.exercise.wordHunt.points': "Puan",
    'cabin.exercise.wordHunt.foundAll': "Tüm kelimeler bulundu!",
    'cabin.exercise.wordHunt.keepPracticing': "Pratik yapmaya devam et!",
    'cabin.exercise.wordHunt.find': "Bul",
    'cabin.exercise.wordHunt.hint': "İpucu",
    'cabin.exercise.wordHunt.sentenceLabel': "Cümle {n}",
    'cabin.exercise.wordHunt.correctWas': "Doğrusu şuydu",
    'cabin.exercise.wordHunt.nextSentence': "Sonraki Cümle",
    'cabin.exercise.wordHunt.seeResults': "Sonuçları Gör",
    // -- Landing v2 (Duo redesign) --------------------------------------
    'landing.duo.hero.eyebrow': "Kabin memurluğu kariyerin buradan başlıyor.",
    'landing.duo.hero.title': "İnteraktif eğitimler ile iş dünyasına hazırlan.",
    'landing.duo.hero.subtitle': "Sesli pratikten mülakat simülasyonuna kadar her şey burada. 5.000+ aday zaten yolda, sen de başla.",
    'landing.duo.hero.ctaPrimary': "Hemen Başla",
    'landing.duo.hero.ctaSecondary': "Giriş Yap",
    'landing.duo.hero.statsCourses': "kurs",
    'landing.duo.hero.statsStudents': "öğrenci",
    'landing.duo.hero.statsLessons': "ders",
    'landing.duo.hero.mascotAlt': "Akademi maskotu",
    'landing.duo.feature1.title': "Ömür boyu erişim.",
    'landing.duo.feature1.desc': "Bir kez ödersin, eğitimler sonsuza dek senin olur. Yeni dersler, güncellemeler ve özellikler dahil. Ek ücret yok.",
    'landing.duo.feature1.alt': "Ömür boyu erişim illüstrasyonu",
    'landing.duo.feature2.title': "Bilimin desteğiyle.",
    'landing.duo.feature2.desc': "Aralıklı tekrar ve yapay zeka geri bildirimi ile her seans seni mülakata bir adım daha yaklaştırıyor. Sektör uzmanları tarafından tasarlandı.",
    'landing.duo.feature2.alt': "Bilimsel öğrenme illüstrasyonu",
    'landing.duo.feature3.title': "Motivasyon hep yüksek.",
    'landing.duo.feature3.desc': "Günlük seriler, XP puanları ve sıralama tablosu ile hedefine odaklı kal. Her gün bir adım, mülakat günü bir sıçrama.",
    'landing.duo.feature3.alt': "Motivasyon ve seri günler illüstrasyonu",
    'landing.duo.feature4.title': "Tamamen sana özel.",
    'landing.duo.feature4.desc': "Akıllı seviye testiyle tam seviyenden başlarsın. Eksiklerini kapatırsın, güçlü yönlerini pekiştirirsin. Zaman kaybetmeden mülakata hazırlanırsın.",
    'landing.duo.feature4.alt': "Kişiselleştirilmiş öğrenme illüstrasyonu",
    'landing.duo.cta.title': "Hayalindeki kariyere giden yol buradan başlıyor.",
    'landing.duo.cta.desc': "5.000'den fazla aday zaten aramızda. Sen de yerini al.",
    'landing.duo.cta.button': "Hemen Başla",
    'landing.duo.langStrip.title': "programlarımız",
    'landing.duo.langStrip.cabinCrew': "Kabin Memurluğu",
    'landing.duo.langStrip.aviationEnglish': "Havacılık İngilizcesi",
    'landing.duo.langStrip.groundOps': "Yer Hizmetleri",
    'landing.duo.langStrip.interview': "Mülakat Hazırlığı",
    'landing.duo.langStrip.hr': "İK Uzmanlığı",
    'landing.duo.langStrip.signLanguage': "İşaret Dili",
    'landing.duo.langStrip.spiritualCoaching': "Manevi Danışmanlık",
    'landing.duo.footer.tagline': "Kabin memurluğu kariyerine en doğru hazırlık.",
    'landing.duo.footer.product': "Ürün",
    'landing.duo.footer.company': "Şirket",
    'landing.duo.footer.support': "Destek",
    'landing.duo.footer.about': "Hakkımızda",
    'landing.duo.footer.careers': "Kariyer",
    'landing.duo.footer.contact': "İletişim",
    'landing.duo.footer.faq': "SSS",
    'landing.duo.footer.privacy': "Gizlilik",
    'landing.duo.footer.terms': "Şartlar",
    'landing.duo.footer.rights': "Tüm hakları saklıdır.",
    // -- Landing Interview Prova section ------------------------------------
    'landing.interview.eyebrow': "Mülakat Hazırlığı",
    'landing.interview.title.line1': "Sektör uzmanlarıyla,",
    'landing.interview.title.highlight': "Mülakat Provası",
    'landing.interview.subtitle': "Gerçek mülakat ortamını deneyimleyin. Profesyonel eğitmenler eşliğinde, birebir mülakat simülasyonları ile kendinizi test edin ve güveninizi artırın.",
    'landing.interview.feature1': "Gerçekçi mülakat senaryoları",
    'landing.interview.feature2': "Birebir geri bildirim",
    'landing.interview.feature3': "Beden dili ve iletişim koçluğu",
    'landing.interview.feature4': "Video kayıt ve analiz",
    'landing.interview.cta': "Detaylı Bilgi Al",
    'landing.interview.video.title': "Mülakat Provası",
    'landing.interview.video.subtitle': "Tanıtım Videosu",
    'landing.interview.video.badge': "Mülakat Eğitimi",
    'landing.interview.live': "CANLI",
    'landing.interview.scene1.q': "Neden kabin memuru olmak istiyorsun?",
    'landing.interview.scene1.a': "İnsanlara hizmet etmeyi ve seyahat etmeyi seviyorum.",
    'landing.interview.scene2.q': "Stresle nasıl başa çıkarsın?",
    'landing.interview.scene2.a': "Baskı altında sakin ve odaklanırım.",
    'landing.interview.scene3.q': "Bir takım çalışması deneyimini anlat.",
    'landing.interview.scene3.a': "Son işimde bir grup projesini başarıyla yönettim.",
    // -- Auth v2 (Duo redesign) -----------------------------------------
    'auth.duo.brand': "Kariyer Online",
    'auth.duo.welcomeBack': "Tekrar hoş geldin!",
    'auth.duo.createAccount': "Hesabını oluştur",
    'auth.duo.login.title': "Giriş yap",
    'auth.duo.register.title': "Kayıt ol",
    'auth.duo.register.intro': "Ücretsiz, eğlenceli ve etkili.",
    'auth.duo.forgot.title': "Şifreni mi unuttun?",
    'auth.duo.forgot.subtitle': "E-postanı gir, sana sıfırlama linki gönderelim.",
    'auth.duo.forgot.cta': "Sıfırlama linki gönder",
    'auth.duo.forgot.sending': "Gönderiliyor...",
    'auth.duo.forgot.sentTitle': "E-postanı kontrol et",
    'auth.duo.forgot.sentDesc': "{email} adresine sıfırlama linki gönderdik. Spam klasörünü de kontrol etmeyi unutma.",
    'auth.duo.forgot.backToLogin': "Girişe dön",
    'auth.duo.forgot.rememberedQ': "Şifreni hatırladın mı?",
    'auth.duo.reset.title': "Yeni şifre belirle",
    'auth.duo.reset.subtitle': "Güçlü ve aklında kalacak bir şifre seç.",
    'auth.duo.reset.newPassword': "Yeni şifre",
    'auth.duo.reset.confirmPassword': "Şifre tekrarı",
    'auth.duo.reset.placeholderNew': "en az 8 karakter",
    'auth.duo.reset.placeholderConfirm': "şifre tekrarı",
    'auth.duo.reset.cta': "Şifreyi güncelle",
    'auth.duo.reset.saving': "Kaydediliyor...",
    'auth.duo.reset.doneTitle': "Tamamdır!",
    'auth.duo.reset.doneDesc': "Şifren güncellendi. Şimdi yeni şifrenle giriş yap.",
    'auth.duo.reset.goToLogin': "Girişe git",
    'auth.duo.reset.invalid': "Bu sıfırlama linki geçersiz veya süresi dolmuş.",
    'auth.duo.reset.passwordsDontMatch': "Şifreler eşleşmiyor",
    'auth.duo.reset.passwordTooShort': "Şifre en az 8 karakter olmalı",
    'auth.duo.email': "E-posta",
    'auth.duo.emailPlaceholder': "ornek@eposta.com",
    'auth.duo.password': "Şifre",
    'auth.duo.passwordPlaceholder': "Şifre",
    'auth.duo.name': "Adın",
    'auth.duo.namePlaceholder': "Adın soyadın",
    'auth.duo.continueGoogle': "Google ile devam et",
    'auth.duo.or': "veya",
    'auth.duo.forgotLink': "Şifremi unuttum",
    'auth.duo.haveAccount': "Zaten bir hesabın var mı?",
    'auth.duo.noAccount': "Hesabın yok mu?",
    'auth.duo.signUpLink': "Kayıt ol",
    'auth.duo.noAccountInfo': "Hesabın yok mu? Eğitim yöneticinle iletişime geç.",
    'auth.duo.loginLink': "Giriş yap",
    'auth.duo.submitting': "Bir saniye...",
    'auth.duo.success': "Aramıza hoş geldin!",
    'auth.duo.termsHint': "Kayıt olarak Kullanım Koşulları ve Gizlilik Politikası'nı kabul etmiş olursun.",
    'auth.duo.login.toastSuccess': "Giriş başarılı",
    'auth.duo.login.googleDisabled': "Google ile giriş şu an kapalı. Lütfen e-posta / şifre ile giriş yap.",
    'auth.duo.register.toastSuccess': "Kayıt başarılı",
    'auth.duo.register.googleDisabled': "Google ile kayıt şu an kapalı. Lütfen e-posta / şifre ile kayıt ol.",
    'auth.duo.forgot.emailRequired': "E-posta adresi zorunlu",
    // -- Navbar v2 (Duo) -------------------------------------------------
    'navDuo.brand': "Kariyer Online",
    'navDuo.ctaStart': "Başla",
    'navDuo.ctaLogin': "Giriş yap",

    // ── StudentRegistrationWizard ─────────────────────────────────────────────
    'admin.wizard.title': "Yeni Öğrenci Kaydı",
    'admin.wizard.stepsAriaLabel': "Kayıt sihirbazı adımları",
    'admin.wizard.stepOf': "{current}. Adım / {total}",
    'admin.wizard.close': "Sihirbazı kapat",
    'admin.wizard.loading': "Veriler yükleniyor...",

    'admin.wizard.step1.short': "Bilgiler",
    'admin.wizard.step2.short': "Ana Kurs",
    'admin.wizard.step3.short': "Yan Kurslar",
    'admin.wizard.step4.short': "Sertifika",
    'admin.wizard.step5.short': "Ödeme",

    'admin.wizard.step1.title': "Kişisel Bilgiler",
    'admin.wizard.step2.title': "Ana Kurs",
    'admin.wizard.step3.title': "Yan Kurslar",
    'admin.wizard.step4.title': "Sertifika Seçimi",
    'admin.wizard.step5.title': "Özet ve Ödeme",

    'admin.wizard.step1.alreadyRegistered': "Öğrenci 1. adımda zaten kaydedildi. Buradaki değişiklikler mevcut kaydı güncellemez.",
    'admin.wizard.step2.hint': "Öğrenci için bir ana kurs seçin.",
    'admin.wizard.step3.hint': "İsteğe bağlı olarak ek kurslar ekleyebilirsiniz. Bu adımı atlayabilirsiniz.",
    'admin.wizard.step4.hint': "Her kurs için isteğe bağlı sertifika tipi belirleyin.",
    'admin.wizard.step4.noCourses': "Henüz kurs seçilmedi.",
    'admin.wizard.step4.certLabel': "Sertifika Tipi",
    'admin.wizard.step4.certNone': "Sertifika yok",
    'admin.wizard.step4.examFeeInfo': "Varsayılan sınav ücreti",

    'admin.wizard.step5.studentSection': "Öğrenci",
    'admin.wizard.step5.coursesSection': "Kurslar ve Sertifikalar",
    'admin.wizard.step5.paymentSection': "Ödeme",
    'admin.wizard.step5.courseTotal': "Kurs toplamı",
    'admin.wizard.step5.discount': "İndirim",
    'admin.wizard.step5.certFeeTotal': "Sertifika ücretleri",
    'admin.wizard.step5.grandTotal': "Genel toplam",
    'admin.wizard.step5.certFeeAmount': "Sınav ücreti",
    'admin.wizard.step5.certFeeInclude': "Ödemeye dahil et",

    'admin.wizard.field.firstName': "Ad",
    'admin.wizard.field.lastName': "Soyad",
    'admin.wizard.field.email': "E-posta",
    'admin.wizard.field.phone': "Telefon",
    'admin.wizard.field.tcNo': "TC Kimlik No",
    'admin.wizard.field.birthDate': "Doğum Tarihi",
    'admin.wizard.field.address': "Adres",
    'admin.wizard.field.city': "Şehir",
    'admin.wizard.field.district': "İlçe",
    'admin.wizard.field.postalCode': "Posta Kodu",
    'admin.wizard.field.emergencyContact': "Acil Durum Kişisi",
    'admin.wizard.field.emergencyPhone': "Acil Durum Telefonu",
    'admin.wizard.field.notes': "Not",
    'admin.wizard.field.password': "Şifre",

    'admin.wizard.pw.autoGen': "Otomatik üret",
    'admin.wizard.pw.manual': "Elle gir",
    'admin.wizard.pw.regenerate': "Yeni şifre üret",
    'admin.wizard.pw.placeholder': "Şifre girin...",
    'admin.wizard.pw.show': "Şifreyi göster",
    'admin.wizard.pw.hide': "Şifreyi gizle",

    'admin.wizard.kvkk.label': "Öğrenci KVKK kapsamında kişisel verilerinin işlenmesine onay veriyor (zorunlu)",

    'admin.wizard.search.placeholder': "Kurs ara...",
    'admin.wizard.courses.empty': "Kurs bulunamadı",
    'admin.wizard.courses.free': "Ücretsiz",
    'admin.wizard.courses.primaryLabel': "ANA KURS",
    'admin.wizard.courses.secondaryLabel': "YAN KURS",
    'admin.wizard.courses.secondarySelected': "kurs seçildi",
    'admin.wizard.courses.priceLabel': "Fiyat (TRY)",

    'admin.wizard.payment.free': "Ücretsiz",
    'admin.wizard.payment.paid': "Ücretli",
    'admin.wizard.payment.method': "Ödeme Yöntemi",
    'admin.wizard.payment.cash': "Nakit",
    'admin.wizard.payment.card': "Kredi Kartı",
    'admin.wizard.payment.transfer': "Banka Havalesi",
    'admin.wizard.payment.installments': "Taksit Sayısı",
    'admin.wizard.payment.discount': "İndirim (%)",
    'admin.wizard.payment.notes': "Ödeme Notu",
    'admin.wizard.payment.notesPlaceholder': "İsteğe bağlı not...",

    'admin.wizard.btn.back': "Geri",
    'admin.wizard.btn.cancel': "İptal",
    'admin.wizard.btn.next': "İleri",
    'admin.wizard.btn.registerAndNext': "Kaydet ve Devam Et",
    'admin.wizard.btn.registering': "Kaydediliyor...",
    'admin.wizard.btn.enrolling': "Atanıyor...",
    'admin.wizard.btn.complete': "Kaydı Tamamla",

    'admin.wizard.val.required': "Bu alan zorunludur",
    'admin.wizard.val.email': "Geçerli bir e-posta adresi girin",
    'admin.wizard.val.tcNo': "TC Kimlik No 11 haneli olmalıdır",
    'admin.wizard.val.kvkk': "KVKK onayı zorunludur",
    'admin.wizard.val.primaryRequired': "Lütfen bir ana kurs seçin",

    'admin.wizard.registerSuccess': "Öğrenci başarıyla kaydedildi",
    'admin.wizard.enrollSuccess': "Kurs atamaları başarıyla kaydedildi",
    'admin.wizard.error.register': "Kayıt işlemi başarısız",
    'admin.wizard.error.enroll': "Kurs atama başarısız",
    'admin.wizard.error.loadData': "Kurs verileri yüklenemedi",
    'admin.wizard.error.noUserId': "Kayıt adımı tamamlanmadı",

    'admin.wizard.success.title': "Öğrenci Kaydı Tamamlandı!",
    'admin.wizard.success.sub': "Öğrenci sisteme eklendi ve seçilen kurslara kaydedildi.",
    'admin.wizard.success.cta': "Kapat ve Yenile",

    'admin.wizard.openBtn': "Yeni Öğrenci (Sihirbaz)",

    // ── AdminLegal — Hukuk & İcra ─────────────────────────────────────────────
    'admin.nav.legal': "Hukuk & İcra",
    'admin.legal.subtitle': "İcra dosyalarını, takip süreçlerini ve avukatları yönetin",
    'admin.legal.tab.cases': "İcra Dosyaları",
    'admin.legal.tab.ready': "İcraya Hazır Taksitler",
    'admin.legal.tab.lawyers': "Avukatlar",

    'admin.legal.col.caseNo': "Dosya No",
    'admin.legal.col.student': "Öğrenci",
    'admin.legal.col.totalDebt': "Toplam Borç",
    'admin.legal.col.status': "Durum",
    'admin.legal.col.lawyer': "Avukat",
    'admin.legal.col.date': "Tarih",
    'admin.legal.col.installment': "Taksit",
    'admin.legal.col.amount': "Tutar",
    'admin.legal.col.dueDate': "Vade Tarihi",
    'admin.legal.col.overdueDays': "Gecikme",

    'admin.legal.status.pending': "Beklemede",
    'admin.legal.status.notice_sent': "İhtar Gönderildi",
    'admin.legal.status.legal_started': "Hukuki Süreç Başladı",
    'admin.legal.status.payment_order': "Ödeme Emri",
    'admin.legal.status.seizure': "Haciz",
    'admin.legal.status.collected': "Tahsil Edildi",
    'admin.legal.status.closed': "Kapatıldı",

    'admin.legal.filter.status': "Duruma göre filtrele",
    'admin.legal.filter.lawyer': "Avukata göre filtrele",
    'admin.legal.filter.allStatuses': "Tüm Durumlar",
    'admin.legal.filter.allLawyers': "Tüm Avukatlar",
    'admin.legal.filter.selectStatus': "Sonraki durumu seçin…",

    'admin.legal.btn.detail': "Detay",
    'admin.legal.btn.downloadNotice': "İhtar PDF İndir",
    'admin.legal.btn.updateStatus': "Durum Güncelle",
    'admin.legal.btn.uploadDoc': "Belge Yükle",
    'admin.legal.btn.addNote': "Not Ekle",
    'admin.legal.btn.addPayment': "Tahsilat Ekle",
    'admin.legal.btn.openCase': "İcraya Gönder",
    'admin.legal.btn.addLawyer': "Avukat Ekle",
    'admin.legal.btn.viewDoc': "Belgeyi görüntüle",

    'admin.legal.detail.title': "İcra Dosyası Detayı",
    'admin.legal.detail.overview': "Genel Bakış",
    'admin.legal.detail.idMissing': "Bu kaydın dosya kimliği okunamadı; statü, not, belge ve tahsilat işlemleri kapatıldı. Pencereyi kapatıp listeyi yenileyin ve tekrar deneyin.",
    'admin.legal.detail.docs': "Belgeler",
    'admin.legal.detail.notes': "Notlar",
    'admin.legal.detail.payments': "Tahsilatlar",
    'admin.legal.detail.debtBreakdown': "Borç Dökümü",
    'admin.legal.detail.updateStatus': "Durum Güncelle",
    'admin.legal.detail.uploadDoc': "Belge Yükle",
    'admin.legal.detail.addPayment': "Tahsilat Kaydet",

    'admin.legal.debt.principal': "Ana Borç",
    'admin.legal.debt.interest': "Faiz",
    'admin.legal.debt.fees': "Masraflar",
    'admin.legal.debt.total': "Toplam",
    'admin.legal.debt.collected': "Tahsil Edilen",
    'admin.legal.debt.remaining': "Kalan",

    'admin.legal.doc.type': "Belge Türü",
    'admin.legal.doc.typePlaceholder': "örn. ihtar, icra emri",
    'admin.legal.doc.desc': "Açıklama",
    'admin.legal.doc.descPlaceholder': "Kısa açıklama",
    'admin.legal.doc.selectFile': "Yüklenecek dosyayı seçin",

    'admin.legal.note.label': "Not",
    'admin.legal.note.placeholder': "Not metnini girin…",

    'admin.legal.pay.amount': "Tutar (₺)",
    'admin.legal.pay.date': "Tahsilat Tarihi",
    'admin.legal.pay.method': "Yöntem",
    'admin.legal.pay.method.transfer': "Banka Havalesi",
    'admin.legal.pay.method.cash': "Nakit",
    'admin.legal.pay.method.card': "Kredi Kartı",
    'admin.legal.pay.notes': "Notlar",
    'admin.legal.pay.notesPlaceholder': "Tahsilat notu…",

    'admin.legal.cases.empty': "İcra dosyası bulunamadı",
    'admin.legal.cases.idMissing': "Dosya kimliği okunamadı",
    'admin.legal.docs.empty': "Belge yüklenmemiş",
    'admin.legal.notes.empty': "Not eklenmemiş",
    'admin.legal.payments.empty': "Tahsilat kaydı yok",
    'admin.legal.lawyers.empty': "Avukat tanımlanmamış",
    'admin.legal.ready.empty': "İcraya hazır taksit bulunamadı",
    'admin.legal.ready.emptySub': "Durumu 'legal' olan ve henüz dosyaya bağlanmamış taksitler burada görünür",
    'admin.legal.ready.hint': "İcra dosyası açmak için taksit seçin",
    'admin.legal.ready.selectedCount': "{n} taksit seçildi",
    'admin.legal.ready.days': "gün",
    'admin.legal.ready.selectAll': "Tümünü seç",
    'admin.legal.ready.selectRow': "Satırı seç",

    'admin.legal.openCase.title': "İcra Dosyası Aç",
    'admin.legal.openCase.selectedInstallments': "Seçili Taksitler",
    'admin.legal.openCase.noLawyer': "Avukat yok (sonra atanabilir)",
    'admin.legal.openCase.debtDerivedHint': "Anapara borç, seçilen taksitlerden sunucu tarafında türetilir; elle girilemez.",
    'admin.legal.openCase.installmentIdMissing': "Seçili satırların en az birinde taksit kimliği okunamadı, dosya açılamaz. Listeyi yenileyip tekrar deneyin.",
    'admin.legal.openCase.lawyerIdMissing': "{n} avukat kaydı kimliği okunamadığı için listelenmedi. İlgili kayıtları Avukatlar sekmesinden düzeltin.",

    'admin.legal.lawyer.name': "Ad Soyad",
    'admin.legal.lawyer.firm': "Hukuk Bürosu",
    'admin.legal.lawyer.phone': "Telefon",
    'admin.legal.lawyer.email': "E-posta",
    'admin.legal.lawyer.barNumber': "Baro Sicil No",
    'admin.legal.lawyer.specialization': "Uzmanlık Alanı",
    'admin.legal.lawyer.commissionRate': "Komisyon %",
    'admin.legal.lawyer.status': "Durum",
    'admin.legal.lawyer.statusActive': "Aktif",
    'admin.legal.lawyer.statusInactive': "Pasif",
    'admin.legal.lawyer.nameRequired': "Avukat adı zorunludur",
    'admin.legal.lawyer.deleteConfirm': "Bu avukatı silmek istediğinizden emin misiniz?",
    'admin.legal.lawyer.idMissing': "Avukat kimliği okunamadı",
    'admin.legal.lawyer.addTitle': "Avukat Ekle",
    'admin.legal.lawyer.editTitle': "Avukatı Düzenle",

    'admin.legal.msg.statusUpdated': "Durum güncellendi",
    'admin.legal.msg.noteAdded': "Not eklendi",
    'admin.legal.msg.docUploaded': "Belge yüklendi",
    'admin.legal.msg.paymentAdded': "Tahsilat kaydedildi",
    'admin.legal.msg.caseOpened': "İcra dosyası açıldı",
    'admin.legal.msg.lawyerAdded': "Avukat eklendi",
    'admin.legal.msg.lawyerUpdated': "Avukat güncellendi",
    'admin.legal.msg.lawyerDeleted': "Avukat silindi",
    'admin.legal.msg.invalidAmount': "Lütfen sıfırdan büyük geçerli bir tutar girin",

    'admin.legal.pagination': "{from}–{to} / {total}",
    'admin.legal.prev': "Önceki",
    'admin.legal.next': "Sonraki",

    // ── AdminLegalContent (KVKK/Gizlilik/Şartlar editörü) ───────────────────────
    'admin.legalContent.subtitle': "Kullanıcılara gösterilen KVKK, gizlilik politikası ve kullanım şartları içeriğini yönetin",
    'admin.legalContent.matrix.docType': "Belge",
    'admin.legalContent.docType.kvkk': "KVKK Aydınlatma Metni",
    'admin.legalContent.docType.privacy': "Gizlilik Politikası",
    'admin.legalContent.docType.terms': "Kullanım Şartları",
    'admin.legalContent.lang.tr': "Türkçe",
    'admin.legalContent.lang.en': "İngilizce",
    'admin.legalContent.status.draft': "Taslak",
    'admin.legalContent.status.published': "Yayında",
    'admin.legalContent.status.empty': "Henüz içerik yok",
    'admin.legalContent.field.title': "Başlık",
    'admin.legalContent.field.titlePlaceholder': "ör. Kişisel Verilerin Korunması Aydınlatma Metni",
    'admin.legalContent.field.format': "Format",
    'admin.legalContent.field.content': "İçerik",
    'admin.legalContent.field.contentPlaceholder': "Yasal metni buraya yazın (markdown veya HTML)...",
    'admin.legalContent.field.effectiveDate': "Yürürlük tarihi",
    'admin.legalContent.format.markdown': "Markdown",
    'admin.legalContent.format.html': "HTML",
    'admin.legalContent.btn.save': "Taslağı kaydet",
    'admin.legalContent.btn.publish': "Yayınla",
    'admin.legalContent.btn.unpublish': "Yayından kaldır",
    'admin.legalContent.btn.preview': "Önizle",
    'admin.legalContent.btn.hidePreview': "Önizlemeyi gizle",
    'admin.legalContent.preview.title': "Önizleme",
    'admin.legalContent.preview.empty': "Önizlenecek içerik yok",
    'admin.legalContent.hint.needContent': "Yayınlamadan önce içerik ekleyin",
    'admin.legalContent.hint.contract': "doc_type: kvkk / privacy / terms · lang: tr / en — kaydetmek belgeyi taslak olarak tutar, yayına almak için Yayınla'yı kullanın",
    'admin.legalContent.msg.saved': "Taslak kaydedildi",
    'admin.legalContent.msg.published': "Belge yayınlandı",
    'admin.legalContent.msg.unpublished': "Belge yayından kaldırıldı",

    // ── AdminGeoContent / AdminGeoContentDetail (GEO makale onayı) ─────────────
    'admin.geoContent.pageTitle': "GEO İçerik Onayı",
    'admin.geoContent.pageSubtitle': "Otomasyonun ürettiği makaleleri incele, yayınla veya reddet",
    'admin.geoContent.col.title': "Başlık",
    'admin.geoContent.col.slug': "Slug",
    'admin.geoContent.col.pillar': "Alan",
    'admin.geoContent.col.status': "Durum",
    'admin.geoContent.col.createdAt': "Geliş tarihi",
    'admin.geoContent.col.updatedAt': "Son güncelleme",
    'admin.geoContent.col.source': "Kaynak servis",
    'admin.geoContent.status.draft': "Taslak",
    'admin.geoContent.status.review': "İncelemede",
    'admin.geoContent.status.published': "Yayında",
    'admin.geoContent.status.rejected': "Reddedildi",
    'admin.geoContent.status.archived': "Arşivlendi",
    'admin.geoContent.empty.title': "İncelenecek makale yok",
    'admin.geoContent.empty.desc': "Otomasyon yeni bir makale gönderdiğinde burada görünecek. Henüz bağlanmadıysa GEO içerik API anahtarını kontrol et.",
    'admin.geoContent.emptyFiltered.title': "Kayıt bulunamadı",
    'admin.geoContent.emptyFiltered.desc': "Bu filtreye uyan makale yok",
    'admin.geoContent.btn.publish': "Yayınla",
    'admin.geoContent.btn.reject': "Reddet",
    'admin.geoContent.btn.archive': "Yayından Kaldır",
    'admin.geoContent.btn.viewLive': "Canlı sayfayı görüntüle",
    'admin.geoContent.btn.retry': "Tekrar Dene",
    'admin.geoContent.btn.viewDetail': "Detayı görüntüle",
    'admin.geoContent.publishConfirm.title': "Makaleyi yayınla",
    'admin.geoContent.publishConfirm.desc': "Bu makale kariyeronline.tr/makale/{slug} adresinde herkese ve AI arama motorlarına açık hale gelecek. Devam etmeden önce içeriği tam okuduğundan emin ol.",
    'admin.geoContent.archiveConfirm.title': "Yayından kaldır",
    'admin.geoContent.archiveConfirm.desc': "Makale kariyeronline.tr/makale/{slug} adresinden kaldırılacak ve bu işlem geri alınamaz — yeniden yayınlama bu sürümde desteklenmiyor.",
    'admin.geoContent.reject.title': "Makaleyi reddet",
    'admin.geoContent.reject.desc': "Bu makale yayınlanmayacak. Reddetme nedenini üretici otomasyonun görebileceği şekilde yaz.",
    'admin.geoContent.reject.label': "Reddetme nedeni",
    'admin.geoContent.reject.placeholder': "Örn. Kaynak doğrulanamadı, istatistik güncel değil",
    'admin.geoContent.reject.confirm': "Reddet",
    'admin.geoContent.toast.published': "Makale yayınlandı",
    'admin.geoContent.toast.rejected': "Makale reddedildi",
    'admin.geoContent.toast.archived': "Makale yayından kaldırıldı",
    'admin.geoContent.toast.conflict': "Bu makale başka bir sekmede güncellendi — sayfa yenileniyor",
    'admin.geoContent.toast.rateLimited': "Çok fazla işlem yaptın, birkaç saniye bekle ve tekrar dene",
    'admin.geoContent.signal.tldr': "TL;DR",
    'admin.geoContent.signal.tldrYes': "Var",
    'admin.geoContent.signal.tldrNo': "Yok",
    'admin.geoContent.signal.h2': "H2 başlık",
    'admin.geoContent.signal.table': "Tablo",
    'admin.geoContent.signal.sources': "Kaynak",
    'admin.geoContent.signal.dataPoints': "Veri noktası",
    'admin.geoContent.signal.targetQuestions': "Hedef soru",
    'admin.geoContent.signal.entities': "Varlık",
    'admin.geoContent.tab.rendered': "Yayın Görünümü",
    'admin.geoContent.tab.markdown': "Ham Markdown",
    'admin.geoContent.draftNote': "Bu makale henüz incelemeye gönderilmedi",
    'admin.geoContent.rejectedNote': "Bu makale reddedildi, yeniden değerlendirme bu turda desteklenmiyor",
    'admin.geoContent.archivedNote': "Bu makale arşivlendi, yeniden yayınlama bu turda desteklenmiyor",
    'admin.geoContent.pillar.cabinCrew': "Kabin Memurluğu",
    'admin.geoContent.pillar.career': "Kariyer",
    'admin.geoContent.meta.description': "Meta açıklama",
    'admin.geoContent.meta.chars': "karakter",
    'admin.geoContent.meta.targetQuestions': "Hedef sorular",
    'admin.geoContent.meta.sources': "Kaynaklar",
    'admin.geoContent.meta.dataPoints': "Veri noktaları",
    'admin.geoContent.meta.entities': "Varlıklar",
    'admin.geoContent.meta.technicalDetails': "Teknik detaylar",
    'admin.geoContent.meta.authorName': "Yazar",
    'admin.geoContent.meta.subtopic': "Alt konu",
    'admin.geoContent.meta.topicId': "Topic ID",
    'admin.geoContent.meta.idempotencyKey': "Idempotency key",
    'admin.geoContent.meta.contentHash': "İçerik hash'i",

    // İçerik düzenleme — AdminGeoContentDetail.jsx Düzenleme Modu (görev 2026-07-30)
    'admin.geoContent.btn.edit': "Düzenle",
    'admin.geoContent.btn.cancelEdit': "Vazgeç",
    'admin.geoContent.btn.save': "Kaydet",
    'admin.geoContent.saving': "Kaydediliyor…",
    'admin.geoContent.editRejectedNote': "Reddedilen makale bu turda düzenlenemez — otomasyon yeniden üretmeli.",
    'admin.geoContent.editArchivedNote': "Arşivlenmiş makaleler düzenlenemez — yeniden yayınlama bu turda desteklenmiyor.",
    'admin.geoContent.field.title': "Başlık",
    'admin.geoContent.field.metaDescription': "Meta açıklama",
    'admin.geoContent.field.metaDescriptionHint': "Arama sonuçlarında görünmesi için 150-160 karakter ideal",
    'admin.geoContent.field.bodyMd': "Makale gövdesi (Markdown)",
    'admin.geoContent.field.entities': "Varlıklar — satır başına bir tane",
    'admin.geoContent.field.targetQuestions': "Hedef sorular — satır başına bir tane",
    'admin.geoContent.field.sources': "Kaynaklar — satır başına bir URL",
    'admin.geoContent.field.internalLinks': "İç bağlantılar — satır başına bir tane",
    'admin.geoContent.field.pillar': "Alan",
    'admin.geoContent.field.dataPoints': "Veri noktaları",
    'admin.geoContent.field.dataPointLabel': "Etiket",
    'admin.geoContent.field.dataPointValue': "Değer",
    'admin.geoContent.field.dataPointSource': "Kaynak URL (opsiyonel)",
    'admin.geoContent.btn.addDataPoint': "Veri noktası ekle",
    'admin.geoContent.edit.dataPoints.removeAria': "{label} veri noktasını sil",
    'admin.geoContent.edit.publishedWarning': "Bu makale şu anda yayında ve kariyeronline.tr/makale/{slug} adresinde herkese açık. Kaydettiğin an canlı içerik değişir.",
    'admin.geoContent.editPublishConfirm.title': "Yayındaki makaleyi güncelle",
    'admin.geoContent.editPublishConfirm.desc': "Değişiklikler hemen canlıya yansıyacak. Devam edilsin mi?",
    'admin.geoContent.editPublishConfirm.confirm': "Kaydet ve yayınla",
    'admin.geoContent.unsavedConfirm.title': "Kaydedilmemiş değişiklikler var",
    'admin.geoContent.unsavedConfirm.desc': "Bu sayfadan ayrılırsan yaptığın düzenlemeler kaybolur. Yine de ayrılmak istiyor musun?",
    'admin.geoContent.unsavedConfirm.confirm': "Ayrıl",
    'admin.geoContent.unsavedConfirm.cancel': "Kalmaya devam et",
    'admin.geoContent.toast.saved': "Makale kaydedildi",
    'admin.geoContent.editConflict.banner': "Bu makale başka bir yerde güncellendi. Değişikliklerini gözden geçirip tekrar kaydedebilirsin, ama üzerine yazacağını unutma.",
    'admin.geoContent.edit.previewUpdating': "Önizleme güncelleniyor…",
    'admin.geoContent.edit.previewUnavailable': "Önizleme şu anda oluşturulamıyor — içeriğin render motoru geçici olarak kullanılamıyor. Kaydetmeye devam edebilirsin, önizleme düzelince tekrar dener.",
    'admin.geoContent.edit.previewToggleShow': "Önizlemeyi göster",
    'admin.geoContent.edit.previewToggleHide': "Önizlemeyi gizle",
    'admin.geoContent.signal.directAnswer': "Doğrudan cevap",

    // PUBLIC yasal belge sayfası — /legal/:docType (LegalPage.jsx)
    'legal.docType.kvkk': "KVKK Aydınlatma Metni",
    'legal.docType.privacy': "Gizlilik Politikası",
    'legal.docType.terms': "Kullanım Şartları",
    'legal.loading': "Belge yükleniyor...",
    'legal.error.title': "Bir şeyler ters gitti",
    'legal.error.retry': "Tekrar dene",
    'legal.notFound.title': "Belge bulunamadı",
    'legal.notFound.body': "Bu yasal belge bağlantısı mevcut değil. Lütfen adresi kontrol edin.",
    'legal.unavailable.body': "Bu içerik yakında yayınlanacaktır. Lütfen daha sonra tekrar kontrol edin.",
    'legal.effectiveDate': "Yürürlük tarihi",
    'legal.updatedAt': "Son güncelleme",
    'legal.backHome': "Ana sayfaya dön",
    'legal.langToggle.label': "Dil",
    'legal.langToggle.tr': "TR",
    'legal.langToggle.en': "EN",

    // ── AdminVimeo ─────────────────────────────────────────────────────────────
    'admin.vimeo_page_title': "Vimeo Kütüphanesi",
    'admin.vimeo_page_subtitle': "Video klasörlerini yönetin ve kurslara atayın",

    // ── AdminContracts ─────────────────────────────────────────────────────────
    'admin.con_page_title': "Sözleşmeler & Uyum",
    'admin.con_page_subtitle': "Tedarikçi sözleşmeleri, öğrenci anlaşmaları, iptaller ve inaktif öğrenciler",

    // ── AdminReviews extra columns ─────────────────────────────────────────────
    'admin.rev_col_rating': "Puan",
    'admin.rev_col_comment': "Yorum",

    // ── Announcements enum labels ──────────────────────────────────────────────
    'admin.ann_label_all': "Herkes",
    'admin.ann_label_students': "Öğrenciler",
    'admin.ann_label_instructors': "Eğitmenler",
    'admin.ann_label_normal': "Normal",
    'admin.ann_label_high': "Yüksek",
    'admin.ann_label_low': "Düşük",

    // ── AdminNewIdentityPage ───────────────────────────────────────────────────
    'admin.identity.title': "Yeni Kimlik Oluştur",
    'admin.identity.subtitle': "Yeni öğrenci kimliği ve sistem kaydı oluşturun",
    'admin.identity.breadcrumb': "Yeni Kimlik",
    'admin.identity.section.identity': "Kimlik Bilgileri",
    'admin.identity.section.contact': "İletişim Bilgileri",
    'admin.identity.section.education': "Eğitim & Meslek",
    'admin.identity.section.system': "Sistem Ayarları",
    'admin.identity.field.ad': "Ad",
    'admin.identity.field.soyad': "Soyad",
    'admin.identity.field.cinsiyet': "Cinsiyet",
    'admin.identity.field.tc_vatandasi': "Vatandaşlık",
    'admin.identity.field.tc_no': "T.C. Kimlik No",
    'admin.identity.field.dogum_tarihi': "Doğum Tarihi",
    'admin.identity.field.dogum_yeri': "Doğum Yeri",
    'admin.identity.field.anne_adi': "Anne Adı",
    'admin.identity.field.baba_adi': "Baba Adı",
    'admin.identity.field.email': "E-posta (Opsiyonel)",
    'admin.identity.field.telefon': "Cep Telefonu",
    'admin.identity.field.telefon2': "Cep Telefonu 2",
    'admin.identity.field.yurtdisi_tel': "Yurt Dışı Telefon",
    'admin.identity.field.sehir': "Şehir",
    'admin.identity.field.adres': "Adres",
    'admin.identity.field.meslek': "Meslek",
    'admin.identity.field.egitim_durumu': "Eğitim Durumu",
    'admin.identity.field.lisans_bolumu': "Lisans Bölümü",
    'admin.identity.field.egitim_aciklama': "Eğitim Açıklama",
    'admin.identity.field.reklam_turu': "Reklam Türü",
    'admin.identity.field.ogrenci_kodu_ozel': "Özel Öğrenci Kodu",
    'admin.identity.field.aktiflik_suresi': "Aktiflik Süresi",
    'admin.identity.field.aktiflik_suresi_hint': "Boş bırakılabilir — eğitime göre satışta belirlenir",
    'admin.identity.field.aciklama': "Açıklama",
    'admin.identity.field.pasif': "Pasif Öğrenci",
    'admin.identity.field.fatura_talebi': "Fatura Talebi",
    'admin.identity.verify.btn': "Kimliği Doğrula",
    'admin.identity.verify.hint': "TC No (11 hane), Ad, Soyad ve Doğum Tarihi gerekli",
    'admin.identity.verify.success': "Doğrulandı",
    'admin.identity.verify.fail': "Doğrulanamadı",
    'admin.identity.submit': "Kimliği Oluştur",
    'admin.identity.submitting': "Kaydediliyor...",
    'admin.identity.success.title': "Kimlik Oluşturuldu!",
    'admin.identity.success.subtitle': "Öğrenci sisteme başarıyla kaydedildi.",
    'admin.identity.success.warning': "Aşağıdaki bilgiler bir daha gösterilmeyecek. Lütfen kopyalayın veya SMS gönderin.",
    'admin.identity.success.ogrenci_kodu': "Öğrenci No",
    'admin.identity.success.gecici_sifre': "Geçici Şifre",
    'admin.identity.success.onay_kodu': "Onay Kodu",
    'admin.identity.success.send_approval': "Onay Kodunu SMS Gönder",
    'admin.identity.success.send_creds': "Giriş Bilgilerini SMS Gönder",
    'admin.identity.success.profile': "Öğrenci Profiline Git",
    'admin.identity.success.new': "Yeni Kayıt",
    'admin.identity.prefill_notice': "Lead verisinden ön doldurma yapıldı",
    // ── ChangePasswordPage ────────────────────────────────────────────────────
    'auth.changePw.title': "Şifrenizi Değiştirin",
    'auth.changePw.subtitle': "İlk girişinizde şifrenizi değiştirmeniz gerekmektedir.",
    'auth.changePw.current': "Mevcut Şifre",
    'auth.changePw.new': "Yeni Şifre",
    'auth.changePw.confirm': "Yeni Şifre (Tekrar)",
    'auth.changePw.minLength': "(min. 8 karakter)",
    'auth.changePw.mismatch': "Şifreler eşleşmiyor",
    'auth.changePw.tooShort': "Yeni şifre en az 8 karakter olmalıdır",
    'auth.changePw.submit': "Şifremi Değiştir",
    'auth.changePw.submitting': "Kaydediliyor...",
    'auth.changePw.done.title': "Şifre Değiştirildi!",
    'auth.changePw.done.redirect': "Yönlendiriliyorsunuz...",
    // ── AutoLoginPage ─────────────────────────────────────────────────────────
    'auth.autoLogin.loading.title': "Giriş yapılıyor...",
    'auth.autoLogin.loading.subtitle': "Lütfen bekleyin.",
    'auth.autoLogin.success.title': "Giriş başarılı!",
    'auth.autoLogin.success.subtitle': "Yönlendiriliyorsunuz...",
    'auth.autoLogin.error.title': "Giriş Başarısız",
    'auth.autoLogin.error.backToLogin': "Giriş Sayfasına Dön",
    // ── StudentSecurityTab — impersonate ──────────────────────────────────────
    'admin.security.access.title': "Yönetici Erişim Araçları",
    'admin.security.access.impersonate': "Öğrenci Gibi Gir",
    'admin.security.access.magicLink': "Giriş Linki Oluştur",
    'admin.security.access.copy': "Kopyala",
    'admin.security.access.copied': "Kopyalandı",
    'admin.security.access.sendSms': "SMS ile Gönder",
    'admin.security.access.smsSent': "SMS Gönderildi ✓",
    'admin.security.access.validFor': "Geçerlilik:",

    // ── AdminStudentSalePage ──────────────────────────────────────────────────
    'admin.sale.title': "Satış",
    'admin.sale.breadcrumb': "Satış",
    'admin.sale.subtitle': "Satış sayfası — eğitim seçimi, ödeme planı ve onaylama",
    'admin.sale.btn_go_to_sale': "Satışa Geç",
    'admin.sale.btn_new_sale': "Yeni Satış",
    'admin.sale.panel_courses': "Eğitim Seçimi",
    'admin.sale.panel_sales_info': "Satış Bilgileri",
    'admin.sale.panel_payment': "Ödeme Planı",
    'admin.sale.panel_approval': "Onaylama",
    'admin.sale.course_type_main': "ANA EĞİTİM",
    'admin.sale.course_type_extra': "EK EĞİTİM",
    'admin.sale.meb_warning': "MEB onaylı — yalnızca ana eğitim",
    'admin.sale.cart_empty': "Henüz eğitim seçilmedi",
    'admin.sale.search_placeholder': "Kurs ara...",
    'admin.sale.label_referral_code': "Referans Kodu (Opsiyonel)",
    'admin.sale.btn_query': "Sorgula",
    'admin.sale.referral_valid': "Geçerli kod",
    'admin.sale.referral_invalid': "Geçersiz kod",
    'admin.sale.label_discount': "İndirim",
    'admin.sale.label_list_price': "Liste Tutarı",
    'admin.sale.label_sale_total': "Toplam Satış Tutarı",
    'admin.sale.label_in_plan': "Ödeme Tablosuna Eklenen",
    'admin.sale.label_difference': "Fark",
    'admin.sale.diff_warning': "Ödeme planı toplamı satış tutarıyla eşleşmiyor — tüm taksitleri düzenleyerek farkı sıfırlayın.",
    'admin.sale.label_payment_method': "Ödeme Türü",
    'admin.sale.method_cash': "Nakit",
    'admin.sale.method_card': "Kredi Kartı",
    'admin.sale.method_transfer': "Banka Havalesi",
    'admin.sale.label_generate_plan': "Eşit Taksit Oluştur",
    'admin.sale.label_installment_count': "Taksit Sayısı",
    'admin.sale.label_first_due': "İlk Taksit Tarihi",
    'admin.sale.btn_generate': "EKLE",
    'admin.sale.btn_clear': "TEMİZLE",
    'admin.sale.col_amount': "Tutar",
    'admin.sale.col_due_date': "Vade Tarihi",
    'admin.sale.plan_empty_hint': "Taksit planı oluşturun veya peşin kaydedin.",
    'admin.sale.label_extra_lessons': "Ek Ders Sonra Seçilecek",
    'admin.sale.label_extra_lessons_count': "Adet",
    'admin.sale.label_notes': "Kayıt Açıklaması",
    'admin.sale.label_sales_staff': "Satış Yapan Personel",
    'admin.sale.staff_placeholder': "Personel seçiniz...",
    'admin.sale.label_approval_code': "Onay Kodu",
    'admin.sale.approval_code_placeholder': "Öğrenciden alınan kod...",
    'admin.sale.btn_send_sms': "SMS Gönder",
    'admin.sale.sms_sent': "Gönderildi",
    'admin.sale.error_no_primary': "En az bir Ana Eğitim seçilmeli",
    'admin.sale.error_no_staff': "Satış personeli seçilmeli",
    'admin.sale.error_diff_not_zero': "Ödeme planı farkı sıfır olmalı",
    'admin.sale.btn_confirm': "SATIŞI ONAYLA",
    'admin.sale.confirm_title': "Satışı Onayla",
    'admin.sale.confirm_desc': "Kayıt oluşturulacak. Bu işlem geri alınamaz.",
    'admin.sale.confirm_btn': "Onayla ve Kaydet",
    'admin.sale.success_title': "Satış Kaydedildi!",
    'admin.sale.success_subtitle': "Kayıt başarıyla tamamlandı.",
    'admin.sale.success_invoice': "Fatura No",
    'admin.sale.success_plan': "Taksit Planı",
    'admin.sale.btn_student_profile': "Öğrenci Profiline Git",
    'admin.sale.btn_back_to_list': "Öğrenci Listesine Dön",
    'admin.sale.error_meb_extra': "MEB onaylı kurs yalnızca ana eğitim olarak eklenebilir",
    'admin.sale.error_already_in_cart': "Bu kurs zaten sepette",
    'admin.sale.cert_label': "Sertifika Tipi",
    'admin.sale.exam_date_label': "Sınav Tarihi",
    'admin.sale.cert_select_placeholder': "Seçiniz...",
    'admin.sale.selected_courses': "Seçilen Eğitimler",

    // ── CV Wizard (F2) ────────────────────────────────────────────────────────
    'cv.wizard.title': "CV Profili",
    'cv.wizard.subtitle': "Kabin memurluğu aday profilinizi doldurun",
    'cv.step.kisisel': "Kişisel",
    'cv.step.egitim': "Eğitim",
    'cv.step.is_deneyimi': "Deneyim",
    'cv.step.dil': "Dil",
    'cv.step.motivasyon': "Motivasyon",
    'cv.step_desc.kisisel': "Temel iletişim ve kimlik bilgileri (zorunlu)",
    'cv.step_desc.egitim': "Eğitim geçmişi (opsiyonel)",
    'cv.step_desc.is_deneyimi': "İş geçmişi ve yetkinlikler (opsiyonel)",
    'cv.step_desc.dil': "Dil bilgileri ve seviyesi (opsiyonel)",
    'cv.step_desc.motivasyon': "Havacılık motivasyonunuz (opsiyonel)",
    'cv.nav.back': "Geri",
    'cv.nav.next': "İleri",
    'cv.nav.skip': "Şimdilik Atla",
    'cv.nav.submit': "CV'yi Gönder",
    'cv.autosave.saving': "Kaydediliyor...",
    'cv.autosave.saved': "Kaydedildi",
    'cv.autosave.error': "Kayıt başarısız",
    'cv.kvkk.title': "Kişisel Verilerin İşlenmesi (KVKK)",
    'cv.kvkk.read_text': "KVKK Metnini Oku",
    'cv.kvkk.full_text': "Bu formda girilen kişisel verileriniz, Kariyer Online tarafından 6698 sayılı KVKK kapsamında, kabin memurluğu aday değerlendirmesi amacıyla işlenecektir. Verileriniz yetkili eğitmenlerle paylaşılacak olup rızanız alınmadan üçüncü taraflara aktarılmayacaktır. Verilerinize erişim, düzeltme ve silinmesini talep etme hakkına sahipsiniz. KVKK talep formu aracılığıyla dilediğiniz zaman silme talebinde bulunabilirsiniz.",
    'cv.kvkk.consent_label': "Kişisel verilerimin KVKK kapsamında işlenmesini okudum ve onaylıyorum.",
    'cv.kvkk.accept_btn': "Onaylıyorum, Devam Et",
    'cv.kvkk.reject_btn': "Onaylamıyorum",
    'cv.field.ad': "Ad",
    'cv.field.soyad': "Soyad",
    'cv.field.dogum_tarihi': "Doğum Tarihi",
    'cv.field.cinsiyet': "Cinsiyet",
    'cv.field.boy_cm': "Boy (cm)",
    'cv.field.kilo_kg': "Kilo (kg)",
    'cv.field.sehir': "Şehir",
    'cv.field.telefon': "Telefon",
    'cv.field.eposta': "E-posta",
    'cv.field.pasaport_var': "Geçerli pasaportum var",
    'cv.field.seyahat_engeli': "Seyahat engelim var",
    'cv.field.seyahat_engeli_aciklama': "Seyahat engeli açıklaması",
    'cv.field.adli_sicil': "Adli sicil kaydım var",
    'cv.field.adli_sicil_aciklama': "Adli sicil açıklaması",
    'cv.field.onceki_havayolu_basvurusu': "Daha önce bir havayoluna başvurdum",
    'cv.field.mulakate_girdi_mi': "Mülakata çağrıldım",
    'cv.field.elendigi_asama': "Elendiğim aşama",
    'cv.field.instagram': "Instagram",
    'cv.field.linkedin': "LinkedIn",
    'cv.field.mezuniyet_durumu': "Mezuniyet Durumu",
    'cv.field.okul': "Okul / Üniversite",
    'cv.field.bolum': "Bölüm",
    'cv.field.mezuniyet_yili': "Mezuniyet Yılı",
    'cv.field.devam_eden_egitim': "Hâlâ eğitime devam ediyorum",
    'cv.field.yabanci_dil_egitimi': "Yabancı dil eğitimi",
    'cv.field.sertifikalar': "Sertifikalar",
    'cv.field.kabin_memurlugu_egitimi': "Kabin memurluğu eğitimi aldım",
    'cv.field.ilk_yardim_egitimi': "İlk yardım eğitimi aldım",
    'cv.field.diksiyon_egitimi': "Diksiyon / konuşma eğitimi aldım",
    'cv.field.calistimi': "İş deneyimim var",
    'cv.field.son_is_yeri': "Son işyeri",
    'cv.field.pozisyon': "Pozisyon",
    'cv.field.sure': "Süre",
    'cv.field.musteri_iliskileri_deneyimi': "Müşteri ilişkileri deneyimi",
    'cv.field.satis_deneyimi': "Satış deneyimi",
    'cv.field.yogun_tempo_deneyimi': "Yoğun tempolu ortam deneyimi",
    'cv.field.vardiya_deneyimi': "Vardiyalı çalışma deneyimi",
    'cv.field.ekip_deneyimi': "Ekip çalışması deneyimi",
    'cv.field.zor_musteri_deneyimi': "Zor müşteri deneyimi",
    'cv.field.ingilizce_seviye': "İngilizce seviyesi",
    'cv.field.konusma_pratigi': "Konuşma pratiği",
    'cv.field.ingilizce_mulakat_tecrubesi': "İngilizce mülakat tecrübesi",
    'cv.field.diller': "Ek Diller",
    'cv.field.dil_adi': "Dil Adı",
    'cv.field.dil_seviye': "Yeterlilik Seviyesi",
    'cv.action.dil_ekle': "Dil Ekle",
    'cv.validation.dil_seviye_birlikte': "Dil adı ve seviye birlikte girilmelidir",
    'cv.validation.dil_mukerrer': "Bu dil zaten eklenmiş",
    'cv.field.neden_kabin_memuru': "Neden kabin memuru olmak istiyorsunuz?",
    'cv.field.ne_zamandir_ister': "Bu kariyeri ne zamandan beri istiyorsunuz?",
    'cv.field.zorluk_farkindaligi': "Zorlukların farkındalığı",
    'cv.field.uzun_ucus_uygunlugu': "Uzun uçuşlara uygunluk",
    'cv.field.vardiya_uygunlugu': "Vardiyalı çalışmaya uygunluk",
    'cv.field.sehir_degisimi_uygunlugu': "Şehir değişikliğine açıklık",
    'cv.field.uniforma_disiplini': "Üniforma ve görünüm standardlarını kabul ediyorum",
    'cv.field.havayolu_beklentisi': "Havayolundan beklentileriniz",
    'cv.field.bes_yil_sonra': "5 yıl sonra kendinizi nerede görüyorsunuz?",
    'cv.placeholder.ad': "örn. Ayşe",
    'cv.placeholder.soyad': "örn. Yılmaz",
    'cv.placeholder.sehir': "örn. İstanbul",
    'cv.placeholder.aciklama': "Kısa bir açıklama...",
    'cv.placeholder.aciklama_opsiyonel': "Opsiyonel — deneyiminizi kısaca anlatın...",
    'cv.placeholder.select': "Seçiniz...",
    'cv.placeholder.okul': "örn. Anadolu Üniversitesi",
    'cv.placeholder.bolum': "örn. Turizm İşletmeciliği",
    'cv.placeholder.sertifikalar': "örn. IATA, CPR Sertifikası",
    'cv.placeholder.son_is_yeri': "örn. THY Yer Hizmetleri",
    'cv.placeholder.pozisyon': "örn. Müşteri Temsilcisi",
    'cv.placeholder.sure': "örn. 2 yıl",
    'cv.placeholder.elendigi_asama': "örn. Final aşaması",
    'cv.placeholder.neden_kabin_memuru': "Bu kariyeri neden seçtiğinizi anlatın...",
    'cv.placeholder.ne_zamandir_ister': "örn. 2019'dan beri",
    'cv.placeholder.bes_yil_sonra': "5 yıllık planınızı anlatın...",
    'cv.placeholder.yabanci_dil_egitimi': "örn. IELTS, İngilizce dil kursu",
    'cv.hint.comma_separated': "Birden fazla giriş için virgülle ayırın",
    'cv.section.pasaport_seyahat': "Pasaport & Seyahat",
    'cv.section.adli_sicil': "Adli Sicil",
    'cv.section.onceki_basvuru': "Önceki Başvurular",
    'cv.section.ek_egitimler': "Ek Eğitimler",
    'cv.section.deneyim_aciklamalari': "Deneyim Açıklamaları",
    'cv.section.uygunluk': "Çalışma Koşullarına Uygunluk",
    'cv.option.kadin': "Kadın",
    'cv.option.erkek': "Erkek",
    'cv.option.diger': "Diğer",
    'cv.option.ortaogretim': "Ortaöğretim",
    'cv.option.onlisans': "Ön Lisans",
    'cv.option.lisans': "Lisans",
    'cv.option.lisansustu': "Lisansüstü",
    'cv.option.baslangic': "Başlangıç (A1-A2)",
    'cv.option.orta': "Orta (B1)",
    'cv.option.iyi': "İyi (B2)",
    'cv.option.cok_iyi': "Çok İyi (C1)",
    'cv.option.akici': "Akıcı (C2)",
    'cv.validation.ad_required': "Ad zorunludur",
    'cv.validation.soyad_required': "Soyad zorunludur",
    'cv.validation.dogum_required': "Doğum tarihi zorunludur",
    'cv.validation.dogum_age': "Adayların 18-40 yaş aralığında olması gerekmektedir",
    'cv.validation.telefon_required': "Telefon numarası zorunludur",
    'cv.validation.telefon_format': "+905XXXXXXXXX formatında girin (10 hane)",
    'cv.validation.eposta_required': "E-posta zorunludur",
    'cv.validation.eposta_format': "Geçerli bir e-posta adresi girin",
    'cv.validation.boy_min': "Boy en az 140 cm olmalıdır",
    'cv.validation.boy_max': "Boy 210 cm'yi geçemez",
    'cv.validation.kilo_min': "Kilo en az 40 kg olmalıdır",
    'cv.validation.kilo_max': "Kilo 150 kg'ı geçemez",
    'cv.validation.yil_min': "Yıl 1980'den önce olamaz",
    'cv.validation.yil_max': "Yıl çok ilerisi olamaz",
    'cv.validation.hakkimda_max': "Profesyonel özet en fazla 600 karakter olabilir",
    'cv.validation.foto_size': "Fotoğraf 2 MB'dan küçük olmalıdır",
    'cv.validation.foto_format': "Yalnızca JPEG ve PNG dosyaları kabul edilmektedir",
    'cv.error.submit_failed': "CV kaydedilemedi. Lütfen tekrar deneyin.",
    'cv.error.foto_upload_failed': "Fotoğraf yüklenemedi. Lütfen tekrar deneyin.",
    'cv.error.foto_delete_failed': "Fotoğraf silinemedi. Lütfen tekrar deneyin.",
    'cv.success.title': "CV Gönderildi!",
    'cv.success.sub': "Profiliniz kaydedildi. Eğitmeniniz mülakat öncesinde geçmişinizi inceleyebilecek.",
    'cv.success.cta_interview': "Mülakat Pratiğine Git",
    'cv.success.cta_dashboard': "Panele Dön",

    // ── CV Step 6 — Profesyonel Çıktı ────────────────────────────────────────
    'cv.step.cv_ciktisi': "CV Çıktısı",
    'cv.step_desc.cv_ciktisi': "Unvan, özet, iş geçmişi ve fotoğraf (opsiyonel)",
    'cv.field.unvan': "Profesyonel Unvan",
    'cv.field.hakkimda': "Hakkımda / Profesyonel Özet",
    'cv.field.foto': "Profil Fotoğrafı",
    'cv.field.is_deneyimleri': "İş Deneyimi Kayıtları",
    'cv.field.is_pozisyon': "Pozisyon",
    'cv.field.is_sirket': "Şirket",
    'cv.field.is_baslangic': "Başlangıç Tarihi",
    'cv.field.is_bitis': "Bitiş Tarihi",
    'cv.field.is_devam_ediyor': "Hâlâ bu işyerinde çalışıyorum",
    'cv.field.is_maddeler': "Maddeler",
    'cv.placeholder.unvan': "örn. Aday Kabin Memuru",
    'cv.placeholder.hakkimda': "Kendiniz hakkında kısa bir profesyonel özet yazın (max 600 karakter)...",
    'cv.placeholder.is_pozisyon': "örn. Müşteri Temsilcisi",
    'cv.placeholder.is_sirket': "örn. ABC Havayolları",
    'cv.placeholder.is_baslangic': "örn. 06/2021",
    'cv.placeholder.is_bitis': "örn. 08/2023",
    'cv.placeholder.is_madde': "örn. Her gün 200+ yolcuya mükemmel hizmet sundum",
    'cv.section.is_deneyimleri': "İş Deneyimi Kayıtları",
    'cv.section.foto': "Profil Fotoğrafı",
    'cv.section.referans_not': "Referanslar",
    'cv.hint.hakkimda_counter': "{count}/600 karakter",
    'cv.hint.referans': "Referanslar talep üzerine sunulacaktır.",
    'cv.btn.deneyim_ekle': "Deneyim Ekle",
    'cv.btn.deneyim_sil': "Kaldır",
    'cv.btn.madde_ekle': "Madde ekle",
    'cv.btn.madde_sil': "Sil",
    'cv.btn.foto_yukle': "Fotoğraf Yükle",
    'cv.btn.foto_kaldir': "Fotoğrafı Kaldır",
    'cv.btn.foto_uploading': "Yükleniyor...",
    'cv.btn.foto_deleting': "Kaldırılıyor...",
    'cv.btn.dil_sil': "Dili sil",
    'cv.btn.sil': "Sil",
    'cv.foto.preview_alt': "CV profil fotoğrafı önizleme",
    'cv.pdf.download_photo': "CV İndir (fotoğraflı)",
    'cv.pdf.download_no_photo': "ATS Uyumlu İndir (fotoğrafsız)",
    'cv.pdf.downloading': "İndiriliyor...",

    // ── CV Status Screen (F4 — cv-onay) ──────────────────────────────────────
    'cv.status.page_title': "CV İnceleme Durumu",
    'cv.status.loading': "Durum yükleniyor...",
    'cv.status.error': "Durum yüklenemedi",
    'cv.status.error_retry': "Tekrar Dene",
    'cv.status.no_cv': "Henüz CV gönderilmedi",
    'cv.status.no_cv_sub': "Profilinizi inceleme için göndermek üzere CV sihirbazını tamamlayın.",
    'cv.status.no_cv_cta': "CV Sihirbazına Git",
    'cv.status.review_round': "İnceleme turu {round}",
    'cv.status.submitted_at': "Gönderildi: {date}",

    'cv.status.pending_review.badge': "İnceleme Bekliyor",
    'cv.status.pending_review.title': "Eğitmen incelemesi bekleniyor",
    'cv.status.pending_review.sub': "Profiliniz gönderildi. Bir eğitmen kısa süre içinde inceleyecek.",

    'cv.status.in_review.badge': "İnceleniyor",
    'cv.status.in_review.title': "Eğitmen inceliyor",
    'cv.status.in_review.sub': "Bir eğitmen CV'nizi incelemeye başladı. İnceleme tamamlandığında bildirim alacaksınız.",

    'cv.status.approved.badge': "Onaylandı",
    'cv.status.approved.title': "CV'niz onaylandı!",
    'cv.status.approved.sub': "Tebrikler! Profiliniz onaylandı. Onaylanan CV'nizi PDF olarak indirebilirsiniz.",
    'cv.status.approved.download': "PDF İndir",
    'cv.status.approved.downloading': "İndiriliyor...",
    'cv.status.approved.download_error': "PDF indirilemedi. Lütfen tekrar deneyin.",

    'cv.status.changes_requested.badge': "Düzenleme Gerekiyor",
    'cv.status.changes_requested.title': "Eğitmen düzenleme istedi",
    'cv.status.changes_requested.sub': "Eğitmen CV'nizi inceledi ve bazı değişiklikler istedi. Aşağıdaki geri bildirimi inceleyin ve yeniden gönderin.",
    'cv.status.changes_requested.message_label': "Eğitmen geri bildirimi:",
    'cv.status.changes_requested.edit_cta': "Düzenle ve Yeniden Gönder",

    // ── CV Wizard — Europass 5-adım iskelet (Faz 2) ──────────────────────────
    'cv_wizard.steps.welcome': "Başla",
    'cv_wizard.steps.select': "Seç",
    'cv_wizard.steps.edit': "Düzenle",
    'cv_wizard.steps.template': "Şablon",
    'cv_wizard.steps.save': "Kaydet",
    'cv_wizard.welcome.title': "CV'nizi Oluşturun",
    'cv_wizard.welcome.subtitle': "Kabin memurluğu aday profilinizi Europass formatında hazırlayın",
    'cv_wizard.welcome.profile_summary': "Profilinizde {n} bölüm mevcut",
    'cv_wizard.welcome.no_name': "İsimsiz profil",
    'cv_wizard.welcome.empty_warning': "Profilinizde henüz hiç bilgi yok. Devam etmeden önce profilinizi doldurun.",
    'cv_wizard.welcome.go_to_profile': "Profile Git",
    'cv_wizard.welcome.cta': "Başlayalım",
    'cv_wizard.welcome.kvkk_title': "KVKK Onayı Gereklidir",
    'cv_wizard.placeholder.coming_soon': "Bu adım yakında kullanılabilir olacak",
    'cv_wizard.placeholder.save_coming_soon': "CV kaydetme ve gönderme bir sonraki fazda eklenecek",

    // ── CV Wizard — Select adımı (Faz 3) ─────────────────────────────────────
    'cv_wizard.select.subtitle': "CV'nize hangi bölüm ve kayıtların ekleneceğini seçin",
    'cv_wizard.select.counter': "{n} bölüm · {m} kayıt seçili",
    'cv_wizard.select.section.kisisel': "Kişisel Bilgiler",
    'cv_wizard.select.section.kisisel_locked_desc': "CV'nize her zaman dahil edilir",
    'cv_wizard.select.section.is_deneyimi': "İş Deneyimi",
    'cv_wizard.select.section.egitim': "Eğitim",
    'cv_wizard.select.sertifikalar_note': "Sertifikalar eğitim bölümüne dahildir",
    'cv_wizard.select.section.dil': "Diller",
    'cv_wizard.select.section.hakkimda': "Hakkımda",
    'cv_wizard.select.section.referanslar': "Referanslar",
    'cv_wizard.select.referanslar_note': "KVKK gereği varsayılan olarak çıkarılmış",
    'cv_wizard.select.empty_section': "Bu bölümde henüz kayıt yok",
    'cv_wizard.select.go_to_profile': "Profili Düzenle",

    // ── CV Wizard — Edit adımı (Faz 4) ───────────────────────────────────────
    'cv_wizard.edit.subtitle': "Düzenlemek için herhangi bir alana tıklayın",
    'cv_wizard.edit.preview_title': "CV Önizleme",
    'cv_wizard.edit.instructions_title': "İpuçları",
    'cv_wizard.edit.instructions': "Düzenlemek için alana tıklayın. Enter ile kaydet, Escape ile iptal edin. Textarea: Shift+Enter yeni satır, Escape iptal.",
    'cv_wizard.edit.empty_field': "Henüz eklenmedi",
    'cv_wizard.edit.unsaved_title': "Kaydedilmemiş Değişiklik",
    'cv_wizard.edit.unsaved_body': "Kaydedilmemiş bir değişiklik var. Ne yapmak istersiniz?",
    'cv_wizard.edit.unsaved_back': "Geri Dön",
    'cv_wizard.edit.unsaved_continue': "Kaydetmeden Devam Et",
    'cv_wizard.edit.hakkimda_counter': "{count}/600 karakter",
    'cv_wizard.edit.section.kisisel': "Kişisel",
    'cv_wizard.edit.section.is_deneyimi': "İş Deneyimleri",
    'cv_wizard.edit.section.egitim': "Eğitim",
    'cv_wizard.edit.section.dil': "Diller",
    'cv_wizard.edit.section.hakkimda': "Hakkımda",
    'cv_wizard.edit.field.name': "Ad Soyad",
    'cv_wizard.edit.field.unvan': "Unvan",
    'cv_wizard.edit.field.hakkimda': "Hakkımda",
    'cv_wizard.edit.field.okul': "Okul",
    'cv_wizard.edit.field.bolum': "Bölüm",
    'cv_wizard.edit.field.mezuniyet': "Mezuniyet Yılı",
    'cv_wizard.edit.field.ingilizce_seviye': "İngilizce Seviyesi",
    'cv_wizard.edit.required': "Bu alan boş bırakılamaz",
    'cv_wizard.edit.mobile_edit_btn': "Bu Bölümü Düzenle",
    'cv_wizard.edit.mobile_sheet_close': "Kapat",
    'cv_wizard.edit.mobile_sheet_save': "Kaydet",
    'cv_wizard.edit.entry.pozisyon': "Pozisyon",
    'cv_wizard.edit.entry.sirket': "Şirket",
    'cv_wizard.edit.entry.tarih': "Tarihler",

    // ── CV Wizard Faz 4 — A4 CVCanvas ───────────────────────────────────────
    'cv_wizard.canvas.aria_label': "CV önizleme alanı",

    // ── CV Wizard Faz 5 — Canvas Inline Edit ────────────────────────────────
    'cv_wizard.canvas.placeholder_adsoyad': "Ad Soyad",
    'cv_wizard.canvas.placeholder_unvan': "Unvan / Pozisyon",
    'cv_wizard.canvas.placeholder_eposta': "E-posta",
    'cv_wizard.canvas.placeholder_telefon': "Telefon",
    'cv_wizard.canvas.placeholder_sehir': "Şehir",
    'cv_wizard.canvas.placeholder_hakkimda': "Kendiniz hakkında kısa bir özet yazın...",
    'cv_wizard.canvas.placeholder_mez_yili': "Yıl",
    'cv_wizard.canvas.placeholder_dil_adi': "Dil adı",
    'cv_wizard.canvas.placeholder_dil_seviye': "Seviye",
    'cv_wizard.canvas.empty_deneyim': "Henüz iş deneyimi eklenmedi.",
    'cv_wizard.canvas.empty_dil': "Henüz dil eklenmedi.",
    'cv_wizard.canvas.add_deneyim': "+ İş Deneyimi Ekle",
    'cv_wizard.canvas.add_dil': "+ Dil Ekle",
    'cv_wizard.canvas.aria_madde': "Madde",
    // Faz 6 — toolbar + mobil sekmeler
    'cv_wizard.canvas.toolbar_label': "Şablon",
    'cv_wizard.canvas.template_classic': "Klasik",
    'cv_wizard.canvas.template_modern': "Modern",
    'cv_wizard.canvas.template_minimal': "Minimal",
    'cv_wizard.canvas.mobile_tabs_label': "CV Aksiyonları",
    'cv_wizard.canvas.mobile_preview_tab': "Önizleme",
    'cv_wizard.canvas.mobile_edit_tab': "Düzenle",

    // ── CV Wizard Faz 7 — Başvuru Bilgileri Dialog (application_info) ────────
    'cv_wizard.application_info.btn_label': "Başvuru Bilgileri",
    'cv_wizard.application_info.dialog_title': "Başvuru Arka Plan Bilgileri",
    'cv_wizard.application_info.mobile_section_label': "Başvuru Bilgileri",
    'cv_wizard.application_info.group_a': "Fiziksel Nitelikler / Uygunluk",
    'cv_wizard.application_info.group_b': "Başvuru Geçmişi",
    'cv_wizard.application_info.group_c': "Dil Deneyimi",
    'cv_wizard.application_info.group_d': "Ek Eğitimler",
    'cv_wizard.application_info.group_e': "İş Deneyimi Niteleme",
    'cv_wizard.application_info.group_f': "Motivasyon",
    'cv_wizard.application_info.motivation_note': "Bu cevaplar CV'de görünmez — eğitmeniniz değerlendirmede kullanır",
    'cv_wizard.application_info.save': "Kaydet",
    'cv_wizard.application_info.cancel': "İptal",
    // ── CV Wizard Faz 7 — Canvas yeni alan yer tutucuları ───────────────────
    'cv_wizard.canvas.placeholder_dogum_tarihi': "Doğum tarihi",
    'cv_wizard.canvas.placeholder_instagram': "@kullanici_adi",
    'cv_wizard.canvas.placeholder_linkedin': "linkedin.com/in/...",
    'cv_wizard.canvas.placeholder_mez_durumu': "Mezuniyet durumu",
    'cv_wizard.canvas.placeholder_yabanci_dil': "Yabancı dil eğitimi",
    'cv_wizard.canvas.placeholder_ing_seviye': "Seviye",
    'cv_wizard.canvas.placeholder_sertifika': "Sertifika",
    'cv_wizard.canvas.add_sertifika': "+ Sertifika Ekle",
    'cv_wizard.canvas.empty_sertifikalar': "Henüz sertifika eklenmedi.",
    // ── CV Wizard Faz 7 — Canvas edit alan etiketleri ───────────────────────
    'cv_wizard.edit.field.dogum_tarihi': "Doğum Tarihi",
    'cv_wizard.edit.field.instagram': "Instagram",
    'cv_wizard.edit.field.linkedin': "LinkedIn",
    'cv_wizard.edit.field.mez_durumu': "Mezuniyet Durumu",
    'cv_wizard.edit.field.devam_eden_egitim': "Devam Ediyor",
    'cv_wizard.edit.field.yabanci_dil_egitimi': "Yabancı Dil Eğitimi",
    'cv_wizard.edit.field.sertifikalar': "Sertifikalar",
    'cv_wizard.edit.section_toggle_show': "CV'ye ekle",
    'cv_wizard.edit.section_toggle_hide': "CV'den çıkar",

    // ── CV Wizard Faz 5-6 — Template & Save ─────────────────────────────────
    'cv_wizard.template.subtitle': "CV'niz için bir düzen seçin",
    'cv_wizard.template.selected_badge': "Seçili",
    'cv_wizard.template.names.classic': "Klasik",
    'cv_wizard.template.names.modern': "Modern",
    'cv_wizard.template.names.minimal': "Minimal",
    'cv_wizard.template.names.classic_desc': "Geleneksel düzen, ATS dostu",
    'cv_wizard.template.names.modern_desc': "İki sütun görsel kenar çubuğu",
    'cv_wizard.template.names.minimal_desc': "Sade ve temiz, yüksek okunabilirlik",
    'cv_wizard.save.subtitle': "CV'nizi gözden geçirin ve eğitmeninize gönderin",
    'cv_wizard.save.preview_title': "CV Özeti",
    'cv_wizard.save.template_label': "Şablon",
    'cv_wizard.save.selected_sections': "{n} bölüm seçili",
    'cv_wizard.save.pdf_photo': "PDF İndir (Fotoğraflı)",
    'cv_wizard.save.pdf_no_photo': "PDF İndir (ATS/Fotoğrafsız)",
    'cv_wizard.save.pdf_disabled': "PDF onay sonrası indirilir",
    'cv_wizard.save.send_btn': "Eğitmene Gönder",
    'cv_wizard.save.send_modal_title': "Eğitmene Gönder",
    'cv_wizard.save.send_modal_body': "Profiliniz incelemeye gönderilecektir. Eğitmeniniz mülakattan önce değerlendirecektir.",
    'cv_wizard.save.send_modal_cancel': "İptal",
    'cv_wizard.save.send_modal_confirm': "Gönder",
    'cv_wizard.save.sending': "Gönderiliyor...",

    // ── Instructor CV Reviews (F4 — cv-onay) ─────────────────────────────────
    'instructor.cv.page_title': "CV İnceleme Kuyruğu",
    'instructor.cv.page_sub': "Öğrenci CV profillerini inceleyin ve işleyin",
    'instructor.cv.filter.all': "Tümü",
    'instructor.cv.filter.pending_review': "İnceleme Bekliyor",
    'instructor.cv.filter.in_review': "İnceleniyor",
    'instructor.cv.filter.approved': "Onaylandı",
    'instructor.cv.filter.changes_requested': "Düzenleme İstendi",
    'instructor.cv.queue.empty': "CV bulunamadı",
    'instructor.cv.queue.empty_sub': "Mevcut filtreyle eşleşen kayıt yok.",
    'instructor.cv.queue.loading': "Kuyruk yükleniyor...",
    'instructor.cv.queue.error': "Kuyruk yüklenemedi",
    'instructor.cv.list.round': "Tur {round}",
    'instructor.cv.list.submitted': "Gönderildi: {date}",
    'instructor.cv.select_prompt': "İncelemek için listeden bir CV seçin",

    'instructor.cv.panel.original_toggle': "Orijinali göster",
    'instructor.cv.panel.merged_toggle': "Birleşimi göster",
    'instructor.cv.panel.tabs_label': "CV Bölümleri",
    'instructor.cv.panel.section.kisisel': "Kişisel Bilgiler",
    'instructor.cv.panel.section.egitim': "Eğitim",
    'instructor.cv.panel.section.is_deneyimi': "İş Deneyimi",
    'instructor.cv.panel.section.dil': "Dil Bilgisi",
    'instructor.cv.panel.section.motivasyon': "Motivasyon",
    'instructor.cv.panel.section.assessment': "Değerlendirme",
    'instructor.cv.panel.note_label': "Dahili not (öğrenciye görünmez)",
    'instructor.cv.panel.note_placeholder': "Dahili kullanım için not ekle...",
    'instructor.cv.panel.message_label': "Öğrenciye mesaj",
    'instructor.cv.panel.message_placeholder': "Hangi değişikliklerin yapılması gerektiğini açıklayın...",
    'instructor.cv.panel.message_required': "Öğrenciye mesaj zorunludur.",
    'instructor.cv.panel.save_edits': "Düzenlemeleri Kaydet",
    'instructor.cv.panel.saving': "Kaydediliyor...",
    'instructor.cv.panel.saved': "Kaydedildi",
    'instructor.cv.panel.view_toggle_label': "Görünüm modu",
    'instructor.cv.panel.view_form': "Form Görünümü",
    'instructor.cv.panel.view_preview': "CV Önizleme",

    'instructor.cv.action.claim': "Üstlen",
    'instructor.cv.action.claiming': "Üstleniliyor...",
    'instructor.cv.action.unclaim': "Bırak",
    'instructor.cv.action.approve': "Onayla",
    'instructor.cv.action.approving': "Onaylanıyor...",
    'instructor.cv.action.request_changes': "Revize İste",
    'instructor.cv.action.requesting': "Gönderiliyor...",

    // Pasif eylem gerekçeleri — aktif olmayan Kaydet/Onayla/Değişiklik İste/Bırak
    // butonlarının yanında sürekli gösterilir (bkz. InstructorCVReviews.jsx).
    'instructor.cv.action.locked_reason.pending': "Bu işlem için önce CV'yi üstlenin.",
    'instructor.cv.action.locked_reason.other_reviewer': "Bu CV başka bir eğitmen tarafından inceleniyor.",
    'instructor.cv.action.locked_reason.completed': "Bu inceleme tamamlandı.",
    'instructor.cv.action.locked_reason.unavailable': "Bu işlem şu anda kullanılamıyor.",

    'instructor.cv.status.pending_review': "İnceleme Bekliyor",
    'instructor.cv.status.in_review': "İnceleniyor",
    'instructor.cv.status.approved': "Onaylandı",
    'instructor.cv.status.changes_requested': "Düzenleme İstendi",

    'instructor.cv.toast.claimed': "CV başarıyla üstlenildi.",
    'instructor.cv.toast.unclaimed': "CV bırakıldı.",
    'instructor.cv.toast.approved': "CV başarıyla onaylandı.",
    'instructor.cv.toast.changes_sent': "Değişiklik isteği öğrenciye gönderildi.",
    'instructor.cv.toast.edits_saved': "Düzenlemeler kaydedildi.",
    'instructor.cv.toast.error': "İşlem başarısız. Lütfen tekrar deneyin.",
    'instructor.cv.toast.review_forbidden': "Bu CV üzerinde bu işlemi yapma yetkiniz yok.",
    'instructor.cv.toast.invalid_state': "Bu işlem CV'nin mevcut durumunda yapılamaz.",

    // ── F3 Interview Simulation — per-question countdown ─────────────────────
    'interviewSim.progress.questionLabel': "Soru {current} / {total}",
    'interviewSim.progress.timeLeft': "kalan",
    'interviewSim.questionTransition.saved': "Cevabınız kaydedildi.",
    'interviewSim.questionTransition.sectionTitle': "{section} bölümüne geçiliyor",
    'interviewSim.questionTransition.remaining': "Bu bölümde {count} soru daha var",
    'interviewSim.allQuestionsAnswered.title': "Tüm sorular tamamlandı",
    'interviewSim.allQuestionsAnswered.sub': "İstersen birkaç saniye daha konuşabilirsin ya da kaydı bitir.",
    'interviewSim.allQuestionsAnswered.cta': "Kaydı Bitir",
    'interviewSim.nextQuestion': "Sonraki Soru",
    'interviewSim.previousQuestion': "Önceki Soru",
    'interviewSim.errors.questionsLoadFailed.title': "Sorular yüklenemedi",
    'interviewSim.errors.questionsLoadFailed.sub': "Bağlantını kontrol et ve tekrar dene.",
    'interviewSim.errors.emptyQuestions.title': "Soru bulunamadı",
    'interviewSim.errors.emptyQuestions.sub': "Bu oturum için soru oluşturulamadı. Lütfen tekrar başla.",
    'interviewSim.errors.emptyQuestions.cta': "Geri Dön",
    'interviewSim.sections.personal_intro': "Tanıtım",
    'interviewSim.sections.motivation': "Motivasyon",
    'interviewSim.sections.behavioral': "Davranışsal",
    'interviewSim.sections.crisis_management': "Kriz Yönetimi",
    'interviewSim.sections.english': "İngilizce",
    'interviewSim.questionTransition.lastInSection': "Bu bölümün son sorusu",

    // ── Global timer + auto-submit (feature/mulakat-toplam-sure) ─────────────
    'interviewSim.globalTimer.label': "Mülakat",
    'interviewSim.globalTimer.a11y': "Mülakat geri sayımı: {{time}}",
    'interviewSim.timeUp.modalTitle': "Süre Doldu",
    'interviewSim.timeUp.modalBody': "Mülakatın otomatik kaydedildi ve gönderildi.",
    'interviewSim.timeUp.uploadingBody': "Videon yükleniyor, lütfen bekle…",
    'interviewSim.timeUp.errorTitle': "Gönderim başarısız",
    'interviewSim.timeUp.errorBody': "Bağlantı kesildi — tekrar dene.",
    'interviewSim.timeUp.cta': "Tamam",
    'interviewSim.timeUp.retryCta': "Tekrar Dene",
    'interviewSim.progress.questionProgress': "Soru ilerlemesi",

    // ── F2 CV nav (cabin dashboard card) ─────────────────────────────────────
    'cabin.dashboard.cv_card.title': "CV / Aday Profili",
    'cabin.dashboard.cv_card.sub': "Mülakata girmeden önce profilini tamamla",
    'cabin.dashboard.cv_card.cta': "CV'ye Git",

    // ── Bilgi Sınavı kartı (cabin dashboard) ─────────────────────────────────
    'cabin.dashboard.exam_card.title': "Bilgi Sınavı",
    'cabin.dashboard.exam_card.sub': "Kabin memurluğu bilgi testini çöz, sertifikanı kazan",
    'cabin.dashboard.exam_card.cta': "Sınava Başla",

    // ── F4 FIX 2 — Legacy v1 kriter etiketleri (5-kriter, /50) ──────────────
    'instructor.reviews.criteria.communication': "İletişim",
    'instructor.reviews.criteria.professionalism': "Profesyonellik",
    'instructor.reviews.criteria.content': "İçerik",
    'instructor.reviews.criteria.confidence': "Özgüven",
    'instructor.reviews.criteria.overall_impression': "Genel İzlenim",

    // ── F5 — Öğrenci Hazırlık Raporu (ReadinessReport) ───────────────────────
    // Sayfa başlığı & yönlendirme
    'readinessReport.pageTitle': "Hazırlık Raporun",
    'readinessReport.pageSubtitle': "Mülakat simülasyonuna dayalı değerlendirme raporun",
    'readinessReport.backToHistory': "Mülakat Geçmişi",
    // Boş / yükleniyor / hata durumları
    'readinessReport.empty.title': "Rapor Henüz Hazır Değil",
    'readinessReport.empty.sub': "Mülakatın eğitmen tarafından değerlendiriliyor.",
    'readinessReport.empty.no_scored_title': "Henüz Değerlendirilmiş Mülakatın Yok",
    'readinessReport.empty.no_scored_sub': "Hazırlık raporunu görmek için en az bir eğitmen değerlendirmesine ihtiyacın var. Bir simülasyon tamamla ve değerlendirme bekle.",
    'readinessReport.empty.pending_title': "Değerlendiriliyor",
    'readinessReport.empty.pending_sub': "Eğitmenin mülakatını inceliyor. Değerlendirme tamamlandığında hazırlık raporun burada görüntülenecek.",
    'readinessReport.loading': "Rapor yükleniyor...",
    'readinessReport.error.title': "Rapor Yüklenemedi",
    'readinessReport.error.sub': "Bağlantını kontrol et ve tekrar dene.",
    'readinessReport.error.retry': "Tekrar Dene",
    // Verdict banner
    'readinessReport.verdict.HAZIR.title': "Hazırsın!",
    'readinessReport.verdict.HAZIR.sub': "Kabin memurluğu mülakatı için gereken yetkinlikleri gösterdin.",
    'readinessReport.verdict.KOSULLU.title': "Koşullu Hazır",
    'readinessReport.verdict.KOSULLU.sub': "Hedef seviyeye çok yakınsın. Birkaç alanı güçlendirmek seni oraya götürecek.",
    'readinessReport.verdict.DEGIL.title': "Güçlenmek için birkaç adım kaldı",
    'readinessReport.verdict.DEGIL.sub': "Güçlü yönlerin var — gelişim önceliklerine odaklanmak seni mülakata hazırlayacak.",
    // Bölüm başlıkları — büyük harf değer içinde (CSS uppercase kaldırıldı, TR locale güvenli)
    'readinessReport.section.overview': "GENEL DEĞERLENDİRME",
    'readinessReport.section.ccriBreakdown': "CCRI ALAN KIRILIMI",
    'readinessReport.section.arsBreakdown': "ARS ALAN KIRILIMI",
    'readinessReport.section.strengths': "GÜÇLÜ YÖNLERİN",
    'readinessReport.section.development': "GELİŞİM ÖNCELİKLERİN",
    'readinessReport.section.cv': "CV DEĞERLENDİRMESİ",
    'readinessReport.section.groups': "KAMERA PERFORMANSI VE İNGİLİZCE",
    'readinessReport.section.instructorNote': "EĞİTMEN NOTU",
    'readinessReport.section.reinterview': "TEKRAR MÜLAKAT",
    // CCRI & ARS özet kartları
    'readinessReport.ccri.title': "CCRI",
    'readinessReport.ccri.subtitle': "KABİN KURSİYERİ HAZIRLIK ENDEKSİ",
    'readinessReport.ars.title': "ARS",
    'readinessReport.ars.subtitle': "ADAY DEĞERLENDİRME SKORU",
    'readinessReport.score.outOf100': "100 üzerinden",
    // Tier etiketleri
    'readinessReport.tier.GUCLU': "Güçlü",
    'readinessReport.tier.ORTA': "Yeterli",
    'readinessReport.tier.GELISTIRILECEK': "Geliştirilecek",
    // Skor kademe etiketleri — 3 kademe yüzde kuralı (mobil ile ortak, namespace: interview.scoreTier.*)
    'interview.scoreTier.high.label': "Çok İyi",
    'interview.scoreTier.mid.label': "Geliştirilmeli",
    'interview.scoreTier.low.label': "Yetersiz",
    'interview.scoreTier.high.areaLabel': "Güçlü",
    'interview.scoreTier.mid.areaLabel': "Geliştirilmeli",
    'interview.scoreTier.low.areaLabel': "Yetersiz",
    // Band a11y (ekran okuyucu)
    'readinessReport.band.a11y.ccri': "CCRI puanı: 100 üzerinden {{score}}. Band: {{band}}.",
    'readinessReport.band.a11y.ars': "ARS puanı: 100 üzerinden {{score}}. Band: {{band}}.",
    // Risk / gelişim alanı önem seviyesi
    'readinessReport.severity.KRITIK': "Öncelikli",
    'readinessReport.severity.AGIR': "Önemli",
    'readinessReport.severity.DIGER': "Not",
    // Güçlü yönler / gelişim alanı öğeleri — büyük harf değer içinde (TR locale güvenli)
    'readinessReport.strengths.derived.heading': "EN GÜÇLÜ ALANLAR",
    'readinessReport.development.derived.heading': "ODAKLANILACAK ALANLAR",
    'readinessReport.strengths.instructor.heading': "EĞİTMEN GÖZLEMİ",
    'readinessReport.development.instructor.heading': "EĞİTMEN ÖNERİSİ",
    // CV değerlendirme etiketleri
    'readinessReport.cv.approved': "CV Onaylandı",
    'readinessReport.cv.pending': "CV İncelemede",
    'readinessReport.cv.notSubmitted': "CV Gönderilmedi",
    'readinessReport.cv.status': "Durum",
    'readinessReport.cv.careerScore': "Kariyer Skoru",
    // KP / IN grubu — büyük harf değer içinde (TR locale güvenli)
    'readinessReport.group.kp.title': "KAMERA PERFORMANSI GRUBU",
    'readinessReport.group.in.title': "İNGİLİZCE GRUBU",
    'readinessReport.group.avgPct': "Grup Ortalaması",
    'readinessReport.group.notApplicable': "Bu mülakatta değerlendirilmedi",
    // Eğitmen notu
    'readinessReport.instructorNote.scoredBy': "{{name}} tarafından değerlendirildi",
    'readinessReport.instructorNote.scoredAt': "{{date}} tarihinde",
    // Tekrar mülakat
    'readinessReport.reinterview.title': "Tekrar Mülakat Önerisi",
    'readinessReport.reinterview.sub': "Eğitmenin bir mülakat simülasyonu daha yapmanı öneriyor.",
    // PDF butonu
    'readinessReport.pdf.download': "PDF Raporu İndir",
    'readinessReport.pdf.downloading': "İndiriliyor...",
    'readinessReport.pdf.error': "PDF oluşturulamadı. Lütfen tekrar dene.",
    'readinessReport.pdf.error_auth': "Oturum süresi doldu. Lütfen tekrar giriş yapın.",
    'readinessReport.pdf.error_server': "Sunucu hatası. Lütfen daha sonra tekrar deneyin.",
    // Dashboard giriş noktası
    'readinessReport.entry.title': "Hazırlık Raporun",
    'readinessReport.entry.available': "Değerlendirmeni görüntüle",
    'readinessReport.entry.pending': "Değerlendiriliyor...",

    // ── F2/F3 — Oturum seçici + karşılaştırma ────────────────────────────────
    'readinessReport.sessionSelector.label': "Oturum Seç",
    'readinessReport.sessionSelector.latest': "En son oturum",
    'readinessReport.compare.title': "Gelişim Karşılaştırması",
    'readinessReport.compare.subtitle': "Önceki ve son mülakat",
    'readinessReport.compare.ccriLabel': "CCRI Değişimi",
    'readinessReport.compare.arsLabel': "ARS Değişimi",
    'readinessReport.compare.verdictChange': "Durum Değişimi",
    'readinessReport.compare.prev': "Önceki",
    'readinessReport.compare.latest': "Son",
    'readinessReport.compare.delta.up': "+{{n}} yükseldi",
    'readinessReport.compare.delta.down': "{{n}} düştü",
    'readinessReport.compare.delta.same': "Değişmedi",
    'readinessReport.compare.empty.single.title': "Karşılaştırmak için ikinci mülakatı tamamla",
    'readinessReport.compare.empty.single.sub': "İlk değerlendirmen hazır. İkinci mülakattan sonra gelişimini burada karşılaştırabilirsin.",
    'readinessReport.compare.empty.single.cta': "Mülakata Başla",
    'readinessReport.compare.entryBanner.title': "Gelişim Karşılaştırması",
    'readinessReport.compare.entryBanner.sub': "Önceki ve son mülakat sonuçlarını karşılaştır",
    'readinessReport.compare.entryBanner.disabled': "İkinci mülakatın tamamlanınca aktif olacak",
    'readinessReport.compare.history.sessionLabel': "Oturum",
    'readinessReport.compare.regression.major': "Bu alanda belirgin gerileme var — eğitmenine danışmanı öneririz.",
    'readinessReport.compare.regression.minor': "Bu alan biraz geride kaldı — odaklanmaya devam et.",
    'readinessReport.compare.fetchError': "Raporlardan biri veya ikisi yüklenemedi. Lütfen tekrar dene.",
    'readinessReport.studentPanelEyebrow': "ÖĞRENCİ PANELİ",
    'readinessReport.empty.not_v3_schema_title': "Bu Oturum Desteklenmiyor",
    'readinessReport.empty.not_v3_schema_sub': "Bu mülakatın sonucu eski formatta. Güncel hazırlık raporunu görmek için yeni bir mülakat tamamla.",

    // ===== Landing v2 (havacılık lacivert/altın yeniden tasarım) =====
    'landing2.common.error': "Bir şeyler ters gitti, lütfen tekrar deneyin.",
    'landing2.common.retry': "Tekrar dene",
    'landing2.common.lessonsSuffix': "ders",
    'landing2.common.studentsSuffix': "öğrenci",
    'landing2.common.academyLabel': "Kariyer Online Akademi",

    'landing2.hero.emptyTitle': "Kurslar yakında burada",

    // ---- Hero slider v2 (3 renkli slayt, edX/Harvard tarzı) ----
    'landing2.hero.slide1.eyebrow': "Türkiye'nin Havacılık Akademisi",
    'landing2.hero.slide1.titleLine1': "Kariyerini",
    'landing2.hero.slide1.titleHighlight': "uçur.",
    'landing2.hero.slide1.body': "Uzman eğitmenlerden kabin memurluğu eğitimi al, MEB onaylı sertifikanı kazan ve yapay zekâ destekli mülakat provasıyla güvenle işe adım at.",
    'landing2.hero.slide1.cta': "Programları Keşfet",

    'landing2.hero.slide2.eyebrow': "Yapay Zekâ Destekli Mülakat",
    'landing2.hero.slide2.titleLine1': "Mülakata",
    'landing2.hero.slide2.titleHighlight': "hazır çık.",
    'landing2.hero.slide2.body': "Gerçek mülakat senaryolarında yapay zekâ destekli asistanla pratik yap, anlık geri bildirim al ve kabin memurluğu mülakatına özgüvenle hazırlan.",
    'landing2.hero.slide2.cta': "Provayı Başlat",
    'landing2.hero.slide2.note': "İlk provan ücretsiz.",

    'landing2.hero.slide3.eyebrow': "Yeni & Popüler Dersler",
    'landing2.hero.slide3.titleLine1': "Yeni sezonun dersleri",
    'landing2.hero.slide3.titleHighlight': "burada.",
    'landing2.hero.slide3.body': "Havacılık, yabancı dil ve kariyer kategorilerinde yeni eklenen dersleri incele; sana en uygun programı bugün seç ve öğrenmeye hemen başla.",
    'landing2.hero.slide3.cta': "Dersleri Gör",

    // ---- Küratörlü hero kartları (landing-v2-duzeltmeler madde 4/6) ----
    'landing2.hero.curated.groundHostess.title': "Yer Hostesliği Eğitimi",
    'landing2.hero.curated.cabinSim.title': "Mock-up Uçak İçi Simülasyon Eğitimi",
    'landing2.hero.curated.aiLiteracy.title': "Yapay Zekâ Okuryazarlığı",
    'landing2.hero.curated.exploreFallback.title': "Programları Keşfet",

    'landing2.hero.nav.prevSlide': "Önceki slayt",
    'landing2.hero.nav.nextSlide': "Sonraki slayt",
    'landing2.hero.nav.goToSlideLabel': "Slayta git",
    'landing2.hero.nav.carouselRoleDesc': "slayt gösterisi",
    'landing2.hero.nav.slideRoleDesc': "slayt",

    'landing2.marquee.item1': "MEB ONAYLI ÖZEL ÖĞRETİM KURUMU",
    'landing2.marquee.item2': "YAPAY ZEKÂ DESTEKLİ MÜLAKAT PRATİĞİ",
    'landing2.marquee.item3': "SEKTÖR UZMANI EĞİTMENLER",
    'landing2.marquee.item4': "GERÇEK MÜLAKAT SİMÜLASYONLARI",
    'landing2.marquee.item5': "MEB ONAYLI SERTİFİKA",
    'landing2.marquee.item6': "KABİN MEMURLUĞU EĞİTİM PROGRAMI",

    'landing2.trending.titleStart': "Kariyer Online'da",
    'landing2.trending.titleHighlight': "trend",
    'landing2.trending.tab.aviation': "Havacılık",
    'landing2.trending.tab.languages': "Diller",
    'landing2.trending.tab.career': "Kariyer",
    'landing2.trending.tab.health': "Sağlık",
    'landing2.trending.tab.tech': "Teknoloji",
    'landing2.trending.tab.general': "Program",
    'landing2.trending.tab.all': "Tüm Programlar",
    'landing2.trending.levelAll': "Tümü",
    'landing2.trending.emptyTitle': "Bu kategoride yakında yeni içerikler eklenecek",
    'landing2.trending.featureBadge': "Özellik",
    'landing2.trending.featureInterviewTitle': "Mülakat Simülasyonu",
    'landing2.trending.featureInterviewDesc': "Yapay zekâ destekli mülakat provasıyla kendini test et",
    'landing2.trending.featureCvTitle': "CV & Kariyer Hazırlığı",
    'landing2.trending.featureCvDesc': "Havacılığa uygun CV'ni oluştur ve başvuruya hazırlan",

    'landing2.popular.title': "Şimdi popüler",
    'landing2.popular.viewAll': "Tümü",
    'landing2.popular.empty': "Henüz gösterilecek kurs yok",
    'landing2.popular.curatedSubtitle': "Programı keşfet",

    'landing2.curriculum.eyebrow': "Müfredat",
    'landing2.curriculum.title': "Eğitim içerikleri",
    'landing2.curriculum.subtitle': "Havacılık terminolojisinden mülakat provasına kadar, Kabin Memurluğu programımızın gerçek modülleri.",
    'landing2.curriculum.mod1.bullet1': "Havacılık sektörüne ve havayolu operasyonlarına genel bakış",
    'landing2.curriculum.mod1.bullet2': "Sektördeki kariyer fırsatlarını tanı",
    'landing2.curriculum.mod2.bullet1': "Havayollarının işe alım sürecini ve beklentilerini öğren",
    'landing2.curriculum.mod2.bullet2': "Mülakat sorularına ve senaryolarına hazırlan",
    'landing2.curriculum.mod3.bullet1': "Kabin içi görev ve sorumlulukları öğren",
    'landing2.curriculum.mod3.bullet2': "Uçuş operasyon prosedürlerini kavra",
    'landing2.curriculum.mod4.bullet1': "Yolcu memnuniyeti ve hizmet standartlarını öğren",
    'landing2.curriculum.mod4.bullet2': "Profesyonel iletişim tekniklerini geliştir",
    'landing2.curriculum.mod5.bullet1': "Kriz anında doğru karar verme becerisi kazan",
    'landing2.curriculum.mod5.bullet2': "Ekip içi iş birliği ve acil durum yönetimini öğren",
    'landing2.curriculum.card6.title': "CV, Mülakat ve Kariyer Hazırlığı",
    'landing2.curriculum.card6.bullet1': "Havacılığa uygun CV oluşturma sihirbazı",
    'landing2.curriculum.card6.bullet2': "Yapay zekâ destekli mülakat simülasyonu",
    'landing2.curriculum.card6.bullet3': "Anlık geri bildirim ve gelişim takibi",

    'landing2.formats.title': "Sana uygun formatı seç",
    'landing2.formats.subtitle': "Nasıl öğrenmek istediğine göre yolunu çiz.",
    'landing2.formats.card1.title': "Tekil Dersler",
    'landing2.formats.card1.desc': "İstediğin konuyu, istediğin an öğren.",
    'landing2.formats.card1.meta1': "Kendi hızında ilerle",
    'landing2.formats.card1.meta2': "Sertifika kazan",
    'landing2.formats.card1.meta3': "Hızlı erişim",
    'landing2.formats.card1.cta': "Dersleri Keşfet",
    'landing2.formats.card2.title': "Sertifika Programı",
    'landing2.formats.card2.desc': "Dersleri birleştir, Kabin Memurluğu programını uçtan uca tamamla.",
    'landing2.formats.card2.meta1': "MEB onaylı sertifika",
    'landing2.formats.card2.meta2': "105 video ders",
    'landing2.formats.card2.meta3': "Derinlemesine ustalık",
    'landing2.formats.card2.cta': "Programı İncele",
    'landing2.formats.card3.title': "Mülakat Simülasyonu",
    'landing2.formats.card3.desc': "Yapay zekâ ile gerçek mülakat provası yap.",
    'landing2.formats.card3.meta1': "Sınırsız pratik",
    'landing2.formats.card3.meta2': "Anlık geri bildirim",
    'landing2.formats.card3.meta3': "Özgüven kazan",
    'landing2.formats.card3.cta': "Simülasyonu Dene",
    'landing2.formats.card4.title': "CV & Kariyer Hazırlığı",
    'landing2.formats.card4.desc': "Havacılığa uygun CV'ni oluştur, başvuruya hazır ol.",
    'landing2.formats.card4.meta1': "Adım adım CV sihirbazı",
    'landing2.formats.card4.meta2': "İK gözünden güçlü profil",
    'landing2.formats.card4.meta3': "Başvuru stratejisi",
    'landing2.formats.card4.cta': "CV Hazırlığına Başla",

    'landing2.roadmap.eyebrow': "Adım Adım",
    'landing2.roadmap.title': "Kayıttan kariyere yolculuğun",
    'landing2.roadmap.subtitle': "İlk dersten mülakata kadar, her aşamada yanındayız.",
    'landing2.roadmap.step1.title': "Eğitime Başla",
    'landing2.roadmap.step1.desc': "Sana uygun programa giriş yap, öğrenme yolculuğun başlasın.",
    'landing2.roadmap.step2.title': "Video Dersleri İzle",
    'landing2.roadmap.step2.desc': "Uzman eğitmenlerin hazırladığı dersleri kendi hızında tamamla.",
    'landing2.roadmap.step3.title': "İlerlemeni Takip Et",
    'landing2.roadmap.step3.desc': "Yol haritanda modül modül ilerle, rozetler ve puanlar kazan.",
    'landing2.roadmap.step4.title': "Mülakat Provası Yap",
    'landing2.roadmap.step4.desc': "Yapay zekâ destekli simülasyonla mülakat becerilerini geliştir.",
    'landing2.roadmap.step5.title': "Sertifikanı Al",
    'landing2.roadmap.step5.desc': "Eğitim sonunda MEB onaylı ve CPD kayıtlı sertifikanı kazan.",
    'landing2.roadmap.step6.title': "Başvurularını Yap",
    'landing2.roadmap.step6.desc': "CV'ni güçlendir, iş ilanlarına başvur ve mülakatlara katıl.",
    'landing2.roadmap.step7.title': "Kariyerine Adım At",
    'landing2.roadmap.step7.desc': "Kazandığın donanım ve özgüvenle işe başlama sürecine hazır hisset.",

    'landing2.testimonials.title': "Öğrenciler neden seviyor",
    'landing2.testimonials.t1.role': "Kabin Memurluğu Adayı",
    'landing2.testimonials.t1.quote': "Mülakat simülasyonu sayesinde gerçek görüşmede hiç heyecanlanmadım. Sorulara hazırlıklı gittim.",
    'landing2.testimonials.t2.role': "Kariyer Değiştiren Aday",
    'landing2.testimonials.t2.quote': "Farklı bir sektörden geliyordum, geç kaldığımı sanıyordum. Adım adım yol haritası her şeyi netleştirdi.",
    'landing2.testimonials.t3.role': "Havacılık İngilizcesi Öğrencisi",
    'landing2.testimonials.t3.quote': "İngilizcem engel sanıyordum. Küçük adımlarla ilerleyen dersler beni rahatlattı.",
    'landing2.testimonials.t4.role': "Yeni Mezun Aday",
    'landing2.testimonials.t4.quote': "Kursu bitirdikten sonra bile derslere geri dönüp tekrar izleyebiliyorum, bu harika.",

    'landing2.faq.title': "Sıkça sorulan sorular",
    'landing2.faq.q1': "Kariyer Online Aviation Academy nedir?",
    'landing2.faq.a1': "Ankara Çankaya merkezli, Milli Eğitim Bakanlığı'na bağlı resmi bir özel öğretim kurumuyuz. Kabin memurluğu başta olmak üzere havacılık ve kariyer alanında profesyonel eğitim veriyoruz.",
    'landing2.faq.q2': "Boy / kilo kriterlerim sınırda, uygun muyum?",
    'landing2.faq.a2': "Boy, uzanma ve fiziksel kriterler her hava yoluna göre değişir. Eğitim içeriğimizde bu kriterler ve hangi hava yollarının neye önem verdiği ayrıntılı olarak anlatılır.",
    'landing2.faq.q3': "İngilizce seviyem yeterli mi?",
    'landing2.faq.a3': "İngilizce beklentisi hava yoluna göre değişir. İngilizce ve İletişim modülümüz temel kalıplardan başlar; sandığından daha az İngilizce ile yola çıkabilirsin.",
    'landing2.faq.q4': "Hangi hava yolu bana uygun?",
    'landing2.faq.a4': "Her hava yolunun kendi kriterleri var. Türk Havayolları Şirketleri ve Dünya Havayolları modüllerimizde güncel sektörel bilgiyle profilini değerlendirmeyi öğrenirsin.",
    'landing2.faq.q5': "Mülakat simülasyonu nasıl çalışır?",
    'landing2.faq.a5': "Gerçek mülakat sorularıyla yapay zekâ destekli prova yaparsın; anında geri bildirim alır, beden dili ve stres yönetimi üzerine çalışırsın.",
    'landing2.faq.q6': "Eğitim bittikten sonra da erişimim devam eder mi?",
    'landing2.faq.a6': "Kaydolduğun kurslara platform üzerinden erişmeye devam edersin; dersleri istediğin zaman tekrar izleyebilirsin.",

    'landing2.whyUs.eyebrow': "Neden bizi tercih ediyorlar",
    'landing2.whyUs.title': "Farkı hisset",
    'landing2.whyUs.statBadge': "MEB Onaylı Özel Öğretim Kurumu",
    'landing2.whyUs.statNote': "Ankara Çankaya merkezli, resmi özel öğretim kurumu.",
    'landing2.whyUs.card1.title': "MEB Onaylı Sertifika",
    'landing2.whyUs.card1.desc': "Eğitimini tamamladığında resmi ve CPD kayıtlı sertifikanı alırsın.",
    'landing2.whyUs.card2.title': "Güncel Sektörel Bilgi",
    'landing2.whyUs.card2.desc': "Hava yollarının güncel kriterlerine hâkim içerik.",
    'landing2.whyUs.card3.title': "Gerçek Mülakat Simülasyonları",
    'landing2.whyUs.card3.desc': "Teoriyi pratiğe döker, heyecanını yenersin.",
    'landing2.whyUs.card4.title': "Yapay Zekâ Destekli Mülakat Pratiği",
    'landing2.whyUs.card4.desc': "Sesli pratik ve anlık geri bildirimle kendini geliştir.",

    'landing2.finalCta.title': "Hayalindeki kariyer bir tık uzakta.",
    'landing2.finalCta.subtitle': "İlk adımı bugün at.",
    'landing2.finalCta.ctaPrimary': "Ücretsiz Başla",
    'landing2.finalCta.ctaSecondary': "Giriş Yap",
    'landing2.finalCta.statsLessons': "Ders",

    'landing2.footer.interviewSim': "Mülakat Simülasyonu",

    // DÜZELTME (reviewer MERGE BLOKERİ): "(2 Kur)" ibaresi kaldırıldı — DB'de
    // bu kategoride gerçek bir "2 kur" yapısı yok, var olmayan ürün vaadiydi.
    'landing2.trending.langFrench.title': "Fransızca",
    'landing2.trending.langArabic.title': "Arapça",

    'landing2.curriculum.fallback.mod1.title': "Temel Kabin Memurluğu",
    'landing2.curriculum.fallback.mod1.bullet1': "Havacılık terminolojisi ve operasyon mantığı",
    'landing2.curriculum.fallback.mod1.bullet2': "Kabin içi görev ve sorumluluklar",
    'landing2.curriculum.fallback.mod1.bullet3': "Yolcu hizmet standartları",
    'landing2.curriculum.fallback.mod1.bullet4': "Acil durum temel prosedürleri",
    'landing2.curriculum.fallback.mod2.title': "Cabin Personality & Mülakat",
    'landing2.curriculum.fallback.mod2.bullet1': "Cabin personality fit mantığı ve değerlendirme kriterleri",
    'landing2.curriculum.fallback.mod2.bullet2': "Mülakat simülasyonları (İK soru-cevap)",
    'landing2.curriculum.fallback.mod2.bullet3': "Diksiyon, beden dili ve ilk izlenim yönetimi",
    'landing2.curriculum.fallback.mod2.bullet4': "Stres altında doğru iletişim teknikleri",
    'landing2.curriculum.fallback.mod3.title': "İngilizce Kabin İletişimi",
    'landing2.curriculum.fallback.mod3.bullet1': "Havacılık İngilizcesi temel kalıplar",
    'landing2.curriculum.fallback.mod3.bullet2': "Yolcu karşılama ve hizmet diyalogları",
    'landing2.curriculum.fallback.mod3.bullet3': "Acil durum iletişim cümleleri",
    'landing2.curriculum.fallback.mod3.bullet4': "Akıcı ve anlaşılır konuşma pratiği",
    'landing2.curriculum.fallback.mod4.title': "Kriz ve Stres Yönetimi",
    'landing2.curriculum.fallback.mod4.bullet1': "Baskı altında doğru karar verme",
    'landing2.curriculum.fallback.mod4.bullet2': "Zor yolcu yönetimi teknikleri",
    'landing2.curriculum.fallback.mod4.bullet3': "Uçuş içi kriz senaryoları",
    'landing2.curriculum.fallback.mod4.bullet4': "Sakinlik ve profesyonellik refleksi geliştirme",
    'landing2.curriculum.fallback.mod5.title': "Hizmet ve Yolcu Deneyimi",
    'landing2.curriculum.fallback.mod5.bullet1': "\"Servis değil deneyim\" yaklaşımı",
    'landing2.curriculum.fallback.mod5.bullet2': "Yolcu memnuniyeti psikolojisi",
    'landing2.curriculum.fallback.mod5.bullet3': "Kabin içi iletişim dili ve üslup",
    'landing2.curriculum.fallback.mod5.bullet4': "Marka temsil bilinci",

    // Documents — upload redirect (Kariyer Paneli)
    'documents.redirect.title': "Belge yükleme yeri değişti",
    'documents.redirect.body': "Belgelerini artık buradan değil, Kariyer Paneli üzerinden yükleyebilirsin.",
    'documents.redirect.cta': "Kariyer Paneli'ne Git",
    'documents.redirect.ariaLabel': "Kariyer Paneli'ne git (yeni sekmede açılır)",
    'documents.redirect.note': "Daha önce yüklediğin belgeler kaybolmadı.",
    'documents.redirect.thirdParty': "Kariyer Paneli, Academy Pro'dan bağımsız üçüncü taraf bir hizmettir. Yüklediğin belgeler o platformun kendi gizlilik politikası kapsamında işlenir.",

    // Admin — Dijital Rozet, KVKK silme kuyruğu özeti. Entegrasyon kapalıyken
    // (DIJITAL_ROZET_ENABLED=false) de görünür, çünkü kuyruk tam da o zaman
    // sessizce büyür — bu bir "olsa iyi olur" sayacı değil, KVKK uyumluluk
    // riskini gösteren bir gösterge.
    'admin.dijitalRozet.silmeKuyrugu.title': "KVKK Silme Kuyruğu",
    'admin.dijitalRozet.silmeKuyrugu.error': "Silme kuyruğu özeti yüklenemedi.",
    'admin.dijitalRozet.silmeKuyrugu.retry': "Tekrar dene",
    'admin.dijitalRozet.silmeKuyrugu.emptyTitle': "Kuyruk temiz",
    'admin.dijitalRozet.silmeKuyrugu.emptyDescription': "Bekleyen KVKK silme talebi yok.",
    'admin.dijitalRozet.silmeKuyrugu.pendingBadge': "{{count}} bekliyor",
    'admin.dijitalRozet.silmeKuyrugu.oldestAgeBadge': "en eski: {{days}} gün",
    'admin.dijitalRozet.silmeKuyrugu.failedBadge': "{{count}} başarısız",
    'admin.dijitalRozet.silmeKuyrugu.thresholdWarning': "En eski talep {{days}} gündür bekliyor, {{threshold}} günlük sınır aşıldı. KVKK'ya göre silme talepleri en geç 30 gün içinde sonuçlandırılmalıdır — Dijital Rozet worker'ı kapalı olduğu için bu süre şu anda aşılmış durumda.",
    'admin.dijitalRozet.silmeKuyrugu.pendingNote': "Dijital Rozet worker'ı (şu an kapalı) çalışana kadar bu kayıtlar Bakanlık sisteminde kalmaya devam eder.",
    'admin.dijitalRozet.silmeKuyrugu.failedWarning': "{{count}} kayıt kalıcı olarak başarısız oldu ve elle incelenmesi gerekiyor.",

    // Muhasebe — AccountingTab (Faz 3.8, plan §11). Ekran bilesenleri henuz
    // yazilmadi (Faz 3.6, sonraki tur); bu anahtarlar altyapi olarak eklendi.
    'accounting.tabs.summary': "Özet",
    'accounting.tabs.debt_ledger': "Borç & Tahsilat",
    'accounting.tabs.promissory_notes': "Senetler",
    'accounting.tabs.documents': "Belgeler",

    'accounting.summary.mixed_down_payment': "Karma/Peşinat",
    'accounting.summary.empty_title': "Ödeme geçmişi yok",
    'accounting.summary.empty_description': "Bu öğrenci için taksit veya ödeme kaydı bulunmuyor.",
    'accounting.summary.installments_heading': "Taksitler",
    'accounting.summary.due_label': "Vade",
    'accounting.summary.installment_unit': "taksit",
    'accounting.summary.payment_history_heading': "Ödeme Geçmişi",
    'accounting.payment_status.completed': "Tamamlandı",
    'accounting.payment_status.pending': "Bekliyor",
    'accounting.payment_status.failed': "Başarısız",
    'accounting.payment_status.refunded': "İade Edildi",
    'accounting.payment_status.cancelled': "İptal Edildi",

    'accounting.status.pending': "Bekliyor",
    'accounting.status.due': "Vadesi Bugün",
    'accounting.status.overdue': "Gecikmiş",
    'accounting.status.overdue_warn': "Son Uyarı",
    'accounting.status.interest': "Faiz İşliyor",
    'accounting.status.legal': "Hukuki Takip",
    'accounting.status.paid': "Ödendi",
    'accounting.status.cancelled': "İptal Edildi",
    'accounting.status.split': "Parçalandı",
    'accounting.status.merged': "Birleştirildi",
    'accounting.status.collected': "Tahsil Edildi",

    'accounting.columns.course': "Eğitim",
    'accounting.columns.debt_date': "Borç Tarihi",
    'accounting.columns.debt_amount': "Borç Tutarı",
    'accounting.columns.remaining': "Kalan",
    'accounting.columns.status': "Durum",
    'accounting.columns.action': "Aksiyon",
    'accounting.columns.payment_date': "Ödeme Tarihi",
    'accounting.columns.payment_amount': "Ödeme Tutarı",
    'accounting.columns.promissory_note_no': "Senet No",
    'accounting.columns.recorded_by': "Kaydeden",
    'accounting.columns.parasut_status': "Paraşüt Faturası",
    'accounting.columns.net_settled_amount': "Hesaba Geçen Tutar",
    'accounting.columns.vat_amount': "KDV Tutarı",
    'accounting.columns.amount_excl_vat': "KDV'siz Tutarı",
    // FAZ 1-B (Seçenek C, 2026-08-24) — collection_rows[].interest_amount_row,
    // installment tahsilatlarında dolu. columnDefs.js "optional" (Sütun Seç).
    'accounting.columns.interest_amount': "Faiz Tutarı",
    'accounting.columns.bank': "Banka",
    'accounting.columns.notes': "Açıklamalar",
    'accounting.columns.kind': "Ödeme Türü",

    'accounting.column_selector.label': "Sütun Seç",
    'accounting.details_popover.label': "Detaylar",

    'accounting.actions.collect': "Tahsilat",
    'accounting.actions.reverse_payment': "Ödemeyi Geri Al",
    'accounting.actions.issue_invoice': "Fatura Kes",
    'accounting.actions.print_notice': "İhtarname Yazdır",
    'accounting.actions.print_statement': "Döküm Yazdır",

    'accounting.notes_tabs.split_merge': "Senet Parçala/Birleştir",
    'accounting.notes_tabs.add': "Senet Ekle",
    'accounting.notes_tabs.cancel': "Senet İptal",
    'accounting.notes_tabs.legal_ops': "Avukat İşlemleri",
    'accounting.notes_tabs.edit': "Senet Düzenleme",

    'accounting.fields.net_settled_amount.label': "Hesaba Geçen Tutar",
    'accounting.fields.net_settled_amount.help': "Banka/POS komisyonu düşüldükten sonra hesabımıza geçen net tutar. Farklıysa elle düzeltin.",
    'accounting.fields.net_settled_amount.tooltip': "Örnek: öğrenci kredi kartıyla ₺1.000 ödedi, POS komisyonu ₺25 ise hesaba geçen ₺975'tir.",

    'accounting.reverse_payment.title': "Ödemeyi Geri Al",
    'accounting.reverse_payment.warning': "Bu işlem geri alınamaz — yeni bir ters kayıt oluşturulur, mevcut kayıt silinmez.",
    'accounting.reverse_payment.reason_label': "Gerekçe",
    'accounting.reverse_payment.reason_placeholder': "Bu işlemi neden geri alıyorsunuz?",
    'accounting.reverse_payment.reason_min_error': "Gerekçe en az 10 karakter olmalı.",
    'accounting.reverse_payment.confirm_button': "Geri Al",
    'accounting.reverse_payment.second_confirm_title': "Emin misiniz?",
    'accounting.reverse_payment.second_confirm_desc': "Bu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?",
    'accounting.reverse_payment.payment_summary': "{amount} · {date} tahsilatı",
    'accounting.reverse_payment.reason_counter': "{count}/10 karakter",
    'accounting.reverse_payment.continue_button': "Devam Et",
    'accounting.reverse_payment.submitting': "Gönderiliyor…",
    'accounting.reverse_payment.request_success': "Geri alma talebi oluşturuldu — başka bir yönetici onayladığında tamamlanacak.",
    'accounting.reverse_payment.pending_badge': "Onay Bekliyor",
    'accounting.reverse_payment.pending_list_label': "Onay Bekliyor ({count})",
    'accounting.reverse_payment.pending_list_empty': "Bekleyen geri alma talebi yok.",
    'accounting.reverse_payment.reversed_label': "Geri Alındı",
    'accounting.reverse_payment.approve_button': "Onayla",
    'accounting.reverse_payment.reject_button': "Reddet",
    'accounting.reverse_payment.approve_success': "Geri alma talebi onaylandı, ödeme geri alındı.",
    'accounting.reverse_payment.reject_success': "Geri alma talebi reddedildi.",
    'accounting.reverse_payment.note_linked_warning': "Bu tahsilat bağlı bir senedi kapatmıştı. Geri alındığında taksit yeniden açılır, bağlı senet aktif duruma döner.",
    'accounting.reverse_payment.direct_note_warning': "Bu, bir senedin doğrudan tahsilat kaydı. Geri alındığında senet yeniden aktif duruma döner.",
    'accounting.reverse_payment.moka_warning': "Bu tahsilat Moka üzerinden yapılmış. Geri alma yalnızca kayıt düzeltmesidir; Moka'daki ödeme iade edilmez, iadeyi Moka panelinden yapın.",
    'accounting.reverse_payment.parasut_invoice_warning': "Bu tahsilat için Paraşüt'te satış faturası zaten kesilmiş. Geri alma bu faturayı OTOMATİK iptal etmez/düzeltmez — Paraşüt'te manuel bir iade faturası (fatura düzeltme) gerekebilir.",

    // Muhasebe FAZ 4 (Tur 2, 2026-09-29) — Paraşüt fatura durum rozeti
    'accounting.parasut_status.pending': "Gönderilmeyi bekliyor",
    'accounting.parasut_status.in_progress': "Gönderiliyor",
    'accounting.parasut_status.awaiting_job': "Paraşüt işliyor",
    'accounting.parasut_status.done': "Tamamlandı",
    'accounting.parasut_status.failed': "Hata",
    'accounting.parasut_status.missing_tax_number': "Öğrencinin TC'si eksik",
    'accounting.parasut_status.missing_product_mapping': "Ürün eşlemesi eksik",
    'accounting.parasut_status.missing_account_mapping': "Hesap eşlemesi eksik",
    'accounting.parasut_status.blocked_waiting': "Önceki adımı bekliyor",
    'accounting.parasut_status.needs_verification': "Kontrol gerekli",
    'accounting.parasut_status.unknown': "Durum belirsiz",
    'accounting.parasut_status.cancelled': "İptal edildi",
    'accounting.parasut_status.refunded_badge': "İade edildi",
    'accounting.parasut_status.failed_hint': "Fatura taslağı oluşturulamadı.",
    'accounting.parasut_status.go_to_outbox': "Fatura kuyruğunda incele",
    'accounting.parasut_status.retry_note_label': "Not (zorunlu)",
    'accounting.parasut_status.retry_note_required': "Tekrar göndermek için bir not zorunludur.",
    'accounting.parasut_status.retry_button': "Tekrar Gönder",
    'accounting.parasut_status.retry_pending': "Gönderiliyor…",
    'accounting.parasut_status.retry_success': "Tekrar gönderildi — durum kısa süre içinde güncellenecek.",
    'accounting.parasut_status.manual_credit_note_warning': "Bu tahsilat geri alındı/iade edildi ama Paraşüt'teki fatura otomatik iptal edilmedi — manuel bir iade faturası (fatura düzeltme) kesmeniz gerekiyor.",
    'accounting.parasut_status.mark_refunded_note_label': "Not (zorunlu)",
    'accounting.parasut_status.mark_refunded_note_required': "İade işareti için bir not zorunludur.",
    'accounting.parasut_status.mark_refunded_button': "İade edildi olarak işaretle",
    'accounting.parasut_status.mark_refunded_pending': "İşaretleniyor…",
    'accounting.parasut_status.mark_refunded_success': "İade edildi olarak işaretlendi.",
    'accounting.parasut_status.missing_tc_top_warning': "Bu öğrencinin T.C. kimlik numarası eksik — Paraşüt cari kaydı ve otomatik fatura kesilemez.",
    'accounting.parasut_status.missing_tc_goto_profile': "Kimlik sekmesine git",

    'accounting.errors.odeme_zaten_geri_alindi': "Bu ödeme zaten geri alınmış.",
    'accounting.errors.ters_kayit_geri_alinamaz': "Bir ters kayıt tekrar geri alınamaz.",
    'accounting.errors.ters_kayit_gerekce_zorunlu': "Gerekçe zorunludur.",
    'accounting.errors.maker_checker_ihlali': "Bu talebi siz oluşturdunuz; onay başka bir yönetici tarafından yapılmalı.",

    'accounting.empty.title': "Kayıt yok",
    'accounting.empty.description': "Bu öğrenci için muhasebe kaydı bulunmuyor.",
    'accounting.error.title': "Bir şeyler ters gitti",
    'accounting.error.description': "Muhasebe verileri yüklenemedi. Lütfen tekrar deneyin.",
    'accounting.error.retry': "Tekrar dene",

    // Muhasebe FAZ 3.6 — DebtLedgerTable / CollectionForm (AccountingTab)
    'accounting.methods.cash': "Nakit",
    'accounting.methods.bank_transfer': "Banka Havale/EFT",
    'accounting.methods.postal': "Posta/PTT",
    'accounting.methods.credit_card': "Kredi Kartı",

    'accounting.kind.manual': "Manuel Tahsilat",
    'accounting.kind.down_payment': "Peşinat",
    'accounting.kind.sale': "Peşin Satış",
    'accounting.kind.installment': "Taksit Tahsilatı",
    'accounting.kind.promissory': "Senet Tahsilatı",
    'accounting.kind.refund': "İade",
    'accounting.kind.reversal': "Ters Kayıt",

    'accounting.collect_form.title': "Tahsilat Ekle",
    'accounting.collect_form.subtitle': "Bu öğrenci için manuel tahsilat veya peşinat kaydedin.",
    'accounting.collect_form.amount_label': "Tutar",
    'accounting.collect_form.bank_label': "Banka",
    'accounting.collect_form.bank_placeholder': "Banka seçin...",
    'accounting.collect_form.bank_required_hint': "Her ödeme yönteminde zorunludur.",
    'accounting.collect_form.method_label': "Ödeme Yöntemi",
    'accounting.collect_form.kind_label': "Tahsilat Türü",
    'accounting.collect_form.course_label': "Eğitim",
    'accounting.collect_form.course_general': "Genel",
    'accounting.collect_form.muhasebe_tipi_label': "Muhasebe Tipi",
    'accounting.collect_form.muhasebe_tipi_none': "Yok",
    'accounting.collect_form.vat_override_label': "KDV Tutarı (elle düzeltme)",
    'accounting.collect_form.vat_override_help': "Opsiyonel. Ödeme tutarını aşamaz.",
    'accounting.collect_form.description_label': "Açıklama",
    'accounting.collect_form.description_placeholder': "Ödeme açıklaması...",
    'accounting.collect_form.submit': "Tahsilatı Kaydet",
    'accounting.collect_form.submitting': "Kaydediliyor…",
    'accounting.collect_form.success': "Tahsilat kaydedildi",
    'accounting.collect_form.errors.amount_invalid': "Sıfırdan büyük bir tutar girin.",
    'accounting.collect_form.errors.bank_required': "Banka seçimi zorunludur.",
    'accounting.collect_form.errors.vat_exceeds_amount': "KDV tutarı, ödeme tutarını aşamaz.",
    // F1-B fix — backend'in `tahsilat_tutari_kalani_asiyor` kontrolünün
    // istemci tarafı aynası; satır bazlı "Tahsilat" belirli bir faturadan
    // (row_type="invoice") açıldığında devreye girer. Tutar faturanın
    // kalanıyla ön-doldurulur ama düzenlenebilir kalır — sunucuya gitmeden
    // önce tutarın çok büyük olduğunu bildirir.
    'accounting.collect_form.errors.amount_exceeds_invoice_remaining': "Tutar, borç kaydının kalan bakiyesini aşamaz.",
    'accounting.collect_form.invoice_amount_hint': "Borç kaydının kalan bakiyesiyle ({remaining}) ön dolduruldu. Düzenlenebilir, ancak bunu aşamaz.",

    // F1 fix (fix/tahsilat-taksit-baglama) — CollectionForm taksit modu
    // (satır bazlı "Tahsilat" -> POST /admin/installments/{id}/collect).
    // Tutar taksidin kalanına sabittir ve salt-okunur gösterilir — API kısmi
    // tahsilatı desteklemiyor.
    'accounting.collect_form.installment_title': "Taksit Tahsilatı",
    'accounting.collect_form.installment_subtitle': "Taksitin tamamı tahsil edilecek.",
    'accounting.collect_form.installment_amount_readonly_hint': "Taksit tutarının tamamı tahsil edilir; kısmi tahsilat desteklenmiyor.",

    // Backend error_code -> kullaniciya anlamli mesaj (add-payment / ledger).
    // Listede olmayan kod icin getApiErrorMessage() backend'in kendi
    // message_tr/message_en'ini dogrudan kullanir (accountingErrorMessage yardimcisi).
    'accounting.errors.manuel_odeme_tutari_gecersiz': "Geçerli bir tutar girin.",
    'accounting.errors.manuel_odeme_banka_zorunlu': "Manuel tahsilat/peşinat için banka seçimi zorunludur.",
    'accounting.errors.banka_bulunamadi': "Seçilen banka bulunamadı veya aktif değil.",
    'accounting.errors.muhasebe_tipi_gecersiz': "Geçersiz muhasebe tipi.",
    'accounting.errors.manuel_odeme_kdv_tutari_gecersiz': "Geçersiz KDV tutarı.",
    'accounting.errors.manuel_odeme_istek_govdesi_gecersiz': "İstek gövdesi geçersiz.",
    'accounting.errors.ogrenci_bulunamadi': "Öğrenci bulunamadı.",

    // F1-B fix (fix/tahsilat-taksit-baglama, reviewer Yüksek bulgusu) —
    // taksitsiz (row_type="invoice") açık borç satırında manuel tahsilat
    // artık en eski açık faturaya sessizce bağlanmıyor, çağıranın belirttiği
    // invoice_id'ye hedeflenir.
    'accounting.errors.fatura_secimi_gerekli': "Öğrencinin birden fazla açık faturası var. Tahsil edilecek faturayı seçin.",
    'accounting.errors.tahsilat_tutari_kalani_asiyor': "Tahsilat tutarı, faturanın kalan bakiyesini aşamaz.",
    'accounting.errors.manuel_odeme_fatura_bulunamadi': "Belirtilen fatura bu öğrenciye ait değil veya bulunamadı.",
    'accounting.errors.manuel_odeme_fatura_acik_degil': "Belirtilen fatura açık (tahsil edilebilir) durumda değil.",

    // FAZ 1 satış sadeleştirme (2026-09-25) — 5 satış formu ortak metinleri.
    // Bank/account seçimi + kart-vadeli bilgi notu + taksit önizleme
    // (components/sales/InstallmentPreview.jsx). Backend error_code'ları
    // ayrı anahtar olarak listelenmez — backend zaten message_tr/message_en
    // döner, getApiErrorMessage() doğrudan onu kullanır (satır ~5193 notu).
    'sales.bank_label': "Tahsilat Hesabı",
    'sales.bank_placeholder': "Kasa/banka hesabı seçin...",
    'sales.bank_required_hint': "Satış anında tahsil edilen nakit ödeme için zorunludur (POS hesapları listelenmez — kart ödemesi sonradan Moka ödeme linkiyle alınır).",
    'sales.bank_none_available': "Aktif kasa/banka hesabı bulunamadı. Önce bir admin hesap eklemeli.",
    'sales.card_deferred_notice': "Kart ödemesi satış anında değil, sonradan ödeme linkiyle tahsil edilir.",
    'sales.installmentPreview.title': "Taksit Takvimi (önizleme)",
    'sales.installmentPreview.loading': "Hesaplanıyor…",
    'sales.installmentPreview.col_no': "Taksit",
    'sales.installmentPreview.col_due_date': "Vade Tarihi",
    'sales.installmentPreview.col_amount': "Tutar",
    'sales.installmentPreview.total': "Toplam",
    // FAZ 1 düzeltme turu — madde F2 (2026-09-25): düzenlenebilir taksit planı.
    'sales.installmentPreview.reset_btn': "Eşit Böl",
    'sales.installmentPreview.diff_label': "Kalan Fark",
    'sales.installmentPreview.diff_warning': "Taksit tutarları toplamı satış tutarıyla eşleşmiyor. Kaydetmeden önce farkı sıfırlayın.",
    // Madde 6 — kayıtlı öğrenci aynı kursu tekrar seçerse anlık uyarı.
    'sales.course_already_enrolled': "Bu öğrenci bu kursa zaten kayıtlı.",

    // FAZ 1 karma ödeme turu (2026-09-25) — components/sales/PaymentPlanFields.jsx.
    'sales.paymentPlan.down_payment_title': "Peşinat",
    'sales.paymentPlan.down_payment_amount_label': "Peşinat Tutarı (opsiyonel)",
    'sales.paymentPlan.down_payment_method_label': "Peşinat Yöntemi",
    'sales.paymentPlan.method_cash': "Nakit",
    'sales.paymentPlan.method_bank_transfer': "Havale",
    'sales.paymentPlan.down_payment_date_label': "Peşinat Tarihi",
    'sales.paymentPlan.down_payment_danisman_note': "Peşinat öğrenciden tahsil edildiğinde admin muhasebeden girer.",
    'sales.paymentPlan.remaining_title': "Kalan Tutar",
    'sales.paymentPlan.remaining_amount_label': "Kalan Tutar",
    'sales.paymentPlan.remaining_method_label': "Kalan Ödeme Yöntemi",
    'sales.paymentPlan.remaining_method_bank_transfer': "Havale Taksitleri",
    'sales.paymentPlan.remaining_method_credit_card': "Kredi Kartı (Moka)",
    'sales.paymentPlan.installments_count_label': "Taksit Sayısı",
    'sales.paymentPlan.first_due_date_label': "İlk Taksit Tarihi",
    'sales.paymentPlan.card_due_date_label': "Çekim Tarihi",
    'sales.paymentPlan.card_notice': "Kalan tutar Moka'ya tek çekim olarak aktarılır; taksiti öğrenci Moka'da seçer.",
    'sales.paymentPlan.full_down_payment_notice': "Peşinat toplam tutara eşit — kalan bölüm gizlendi.",

    // FAZ 1 karma ödeme turu (2026-09-25, madde 3) — danışman formu artık
    // ücretli katalogdan (GET /admin/certificates/types) okur.
    'sales.certificate_type_label': "Sertifika Türü",
    'sales.certificate_fee_included_label': "Fiyata sertifika ücreti dahil",
    'sales.certificate_fee_included_hint': "İşaretliyse kurs fiyatı sertifika sınav ücretini zaten içerir, toplama eklenmez. İşaretsizse ücret toplama eklenir.",

    'accounting.debt_ledger.total_debt': "Toplam Borç",
    'accounting.debt_ledger.total_collected': "Toplam Tahsilat",
    'accounting.debt_ledger.balance': "Kalan Bakiye",
    // B2 fix
    'accounting.debt_ledger.collect_blocked_terminal': "Bu borç satırı iptal edilmiş veya zaten ödenmiş; tahsil edilemez.",

    // F1 fix (fix/tahsilat-taksit-baglama) — satır dışındaki genel "Tahsilat"
    // butonu önceden sessizce en eski açık faturaya bağlanıyordu. Öğrencinin
    // açık taksit satırı varsa artık önce hangi taksite tahsilat yapılacağı
    // sorulur.
    'accounting.debt_ledger.pick_installment_title': "Taksit Seçin",
    'accounting.debt_ledger.pick_installment_hint': "Bu öğrencinin açık taksitleri var. Tahsil edilecek taksidi seçin.",
    'accounting.debt_ledger.pick_installment_label': "Taksit {no}/{total}",

    // F1-B fix (fix/tahsilat-taksit-baglama) — genel "Tahsilat" butonunun
    // seçim penceresi artık taksitlerin yanı sıra taksitsiz (row_type=
    // "invoice") açık borç satırlarını da listeler. Bir fatura satırı
    // seçilirse form o invoice_id'ye bağlı açılır — backend en eski açık
    // faturayı TAHMİN ETMEZ.
    'accounting.debt_ledger.pick_debt_row_title': "Borç Satırı Seçin",
    'accounting.debt_ledger.pick_debt_row_hint': "Bu öğrencinin açık borç satırları var. Tahsil edilecek satırı seçin.",
    'accounting.debt_ledger.pick_invoice_label': "Borç Kaydı",

    // FAZ 1-B (Seçenek B/C, 2026-08-24) — iade sonrası negatif bakiye
    // (öğrenci alacaklı) ve toplamlara dahil edilmeyen tahsil edilen faiz.
    // DebtLedgerSubTab.jsx ve AccountingSummarySubTab.jsx AYNI anahtarı
    // paylaşır — iki ekran tutarlı okunsun diye.
    'accounting.debt_ledger.balance_creditor': "Öğrenciye İade Borcu",
    'accounting.debt_ledger.total_interest': "Tahsil Edilen Faiz",
    'accounting.debt_ledger.interest_hint': "Toplam Tahsilat tutarına dahil değildir; öğrenciden alınan gerçek nakit toplamı Tahsilat + Faiz'dir.",

    'accounting.summary.total_billed': "Toplam Borç",

    'accounting.promissory_notes.empty_title': "Senet yok",
    'accounting.promissory_notes.empty_description': "Bu öğrenci için senet kaydı bulunmuyor.",
    'accounting.promissory_notes.status_active': "Aktif",
    'accounting.promissory_notes.no_number': "Senet numarası yok",

    'accounting.docs.empty_title': "Belgeler yakında",
    'accounting.docs.empty_description': "Fatura, ihtarname ve döküm PDF geçmişi ileride bu alanda görüntülenecek.",

    // ── Muhasebe FAZ 4.8 — senet yaşam döngüsü (5 iç sekme) ───────────────────
    'accounting.status.collecting': "Tahsil ediliyor",
    'accounting.status.splitting': "Parçalanıyor",
    'accounting.status.merging': "Birleştiriliyor",

    'accounting.notes.select_placeholder': "Senet seçin...",
    'accounting.notes.note_required': "Bir senet seçin.",
    'accounting.notes.submitting': "Kaydediliyor…",
    'accounting.notes.irreversible': "Bu işlem geri alınamaz.",
    'accounting.notes.confirm_continue': "Evet, devam et",
    'accounting.notes.reconcile_title': "İşlem yarıda kaldı",
    'accounting.notes.reconcile_desc': "Senet geçici bir durumda bırakıldı, muhasebe mutabakatı gerekli. Düzeltilene kadar aksiyonlar kapalıdır.",
    'accounting.notes.amount_label': "Tutar",
    'accounting.notes.amount_invalid': "Sıfırdan büyük bir tutar girin.",
    'accounting.notes.due_label': "Vade Tarihi",
    'accounting.notes.due_invalid': "Vade tarihi YYYY-AA-GG biçiminde olmalıdır.",
    'accounting.notes.number_label': "Senet No",
    'accounting.notes.number_hint': "Opsiyonel. Boş bırakılırsa sistem numara üretir.",
    'accounting.notes.course_label': "Eğitim",
    'accounting.notes.bank_label': "Banka",
    'accounting.notes.bank_hint': "Burada opsiyonel, ancak senet tahsil edilirken zorunludur.",
    'accounting.notes.bank_none': "Yok",
    'accounting.notes.banks_error': "Banka listesi yüklenemedi. Kaydedebilirsiniz; banka yalnızca tahsilat anında zorunludur.",
    'accounting.notes.description_label': "Açıklama",
    'accounting.notes.description_placeholder': "Opsiyonel açıklama...",

    'accounting.notes.split.mode': "Parçala",
    'accounting.notes.split.title': "Senet Parçala",
    'accounting.notes.split.description': "Aktif bir senedi (ve bağlı taksidini) 2–60 yeni senede böler.",
    'accounting.notes.split.note_label': "Parçalanacak senet",
    'accounting.notes.split.no_active_notes': "Parçalanabilecek aktif senet yok.",
    'accounting.notes.split.count_label': "Parça sayısı",
    'accounting.notes.split.count_hint': "2 ile 60 arasında olmalıdır.",
    'accounting.notes.split.count_error': "2 ile 60 arasında bir parça sayısı girin.",
    'accounting.notes.split.manual_label': "Tutarları elle gireceğim",
    'accounting.notes.split.equal_hint': "Tutarlar eşit bölünür; kuruş artığının TAMAMI SON parçaya eklenir.",
    'accounting.notes.split.preview_title': "Parça önizlemesi",
    'accounting.notes.split.part': "Parça",
    'accounting.notes.split.due_label': "Vade",
    'accounting.notes.split.amount_label': "Tutar",
    'accounting.notes.split.total_label': "Toplam",
    'accounting.notes.split.due_required': "Her parçanın vade tarihi zorunludur.",
    'accounting.notes.split.amount_required': "Her parçaya tutar girin (veya eşit bölmeye dönün).",
    'accounting.notes.split.total_mismatch': "Parça tutarlarının toplamı senet tutarına eşit değil.",
    'accounting.notes.split.submit': "Senedi Parçala",
    'accounting.notes.split.confirm_title': "Senet parçalansın mı?",
    'accounting.notes.split.confirm_description': "Senet ve bağlı taksidi yerine yeni parçalar geçecek.",
    'accounting.notes.split.success': "Senet parçalandı.",

    'accounting.notes.merge.mode': "Birleştir",
    'accounting.notes.merge.title': "Senet Birleştir",
    'accounting.notes.merge.description': "Aynı faturaya ait birden fazla aktif senedi tek senette toplar.",
    'accounting.notes.merge.select_label': "Birleştirilecek senetler",
    'accounting.notes.merge.select_hint': "Yalnızca aynı faturaya ait aktif senetler birleştirilebilir; ilk seçimden sonra liste daralır.",
    'accounting.notes.merge.no_eligible': "Birleştirilebilecek aktif senet yok.",
    'accounting.notes.merge.min_error': "En az 2 senet seçin.",
    'accounting.notes.merge.total_label': "Birleşecek toplam",
    'accounting.notes.merge.due_label': "Yeni vade tarihi",
    'accounting.notes.merge.due_hint': "Seçili senetlerin en geç vadesiyle ön dolduruldu — gerekirse değiştirin. Sistem kendiliğinden tarih seçmez.",
    'accounting.notes.merge.due_required': "Yeni vade tarihi zorunludur.",
    'accounting.notes.merge.due_invalid': "Vade tarihi YYYY-AA-GG biçiminde olmalıdır.",
    'accounting.notes.merge.past_warning_title': "Vadesi geçmiş tarih",
    'accounting.notes.merge.past_warning': "Vadesi geçmiş bir tarih seçtiniz: senet birleştiği anda gecikmiş sayılacak. Yine de devam edebilirsiniz.",
    'accounting.notes.merge.submit': "Senetleri Birleştir",
    'accounting.notes.merge.confirm_title': "Senetler birleştirilsin mi?",
    'accounting.notes.merge.confirm_description': "Seçili senetler ve taksitleri tek senette toplanacak.",
    'accounting.notes.merge.success': "Senetler birleştirildi.",

    'accounting.notes.add.title': "Senet Ekle",
    'accounting.notes.add.description': "Öğrencinin mevcut borç kayıtlarından birine manuel senet kaydeder.",
    'accounting.notes.add.no_invoice': "Bu öğrencinin henüz borç kaydı yok. Senet bir borç kaydına bağlanmak zorunda olduğu için oluşturulamıyor.",
    'accounting.notes.add.invoice_label': "Borç Kaydı",
    'accounting.notes.add.invoice_hint': "Zorunlu: senedin tahsilatı bu borç kaydına işlenir.",
    'accounting.notes.add.invoice_placeholder': "Borç kaydı seçin...",
    'accounting.notes.add.invoice_required': "Borç kaydı seçimi zorunludur.",
    'accounting.notes.add.submit': "Senedi Kaydet",
    'accounting.notes.add.success': "Senet oluşturuldu.",
    'accounting.notes.add.installment_word': "Taksit",
    'accounting.notes.add.installment_label': "Taksit bağı (opsiyonel)",
    'accounting.notes.add.installment_hint': "Borç taksitte durur; senet o taksidin hukuki temsilidir. Yalnızca seçili borç kaydının açık ve başka bir senetle temsil edilmeyen taksitleri listelenir.",
    'accounting.notes.add.installment_placeholder': "Taksite bağlama",
    'accounting.notes.add.installment_pick_invoice': "Önce borç kaydı seçin: taksitler borç kaydına göre listelenir.",
    'accounting.notes.add.installment_none_eligible': "Bu faturada bağlanabilir taksit yok. Kapanmış (ödenmiş/iptal) taksitler ve zaten aktif bir senetle temsil edilen taksitler listelenmez.",
    'accounting.notes.add.installment_interest_title': "Faiz tahakkuku nedeniyle listelenmeyen taksit var",
    'accounting.notes.add.installment_interest_desc': "taksitte gecikme faizi tahakkuk ettiği için listeye alınmadı. Senet tutarı yalnızca anaparaya eşit olacağından faiz sessizce kaybolurdu; önce faiz mutabakatı yapılmalıdır.",
    'accounting.notes.add.unlinked_title': "Bu senet bağsız oluşacak",
    'accounting.notes.add.unlinked_desc': "Bu senet hiçbir taksite bağlanmayacak; borç takibi taksit üzerinden yapılamaz.",
    'accounting.notes.add.linked_title': "Taksite bağlanacak",
    'accounting.notes.add.linked_desc': "Tutar ve vade taksitten dolduruldu. Senet bu taksidi temsil eder; tahsil edildiğinde taksit kapanır.",
    'accounting.notes.add.amount_locked_hint': "Taksit seçili olduğu için kilitli: senet tutarı taksit tutarına eşit olmak zorundadır. Elle tutar girmek için taksit seçimini kaldırın.",
    'accounting.notes.add.due_locked_hint': "Taksitten alındı ve kilitlendi: senedin vadesi bağlı taksidin vadesiyle birebir aynı olmak zorundadır.",

    'accounting.notes.cancel.title': "Senet İptal",
    'accounting.notes.cancel.description': "Aktif bir senedi gerekçesiyle iptal eder. Senet silinmez.",
    'accounting.notes.cancel.effect_title': "İptal ne yapar, ne yapmaz",
    'accounting.notes.cancel.effect_desc': "Borç SİLİNMEZ: bağlı taksit açık kalır ve tahsil edilmeye devam eder. Yalnızca senet geçersiz kılınır ve senedin takside sağladığı faiz muafiyeti kalkar; taksit normal gecikme faizine tabi olur.",
    'accounting.notes.cancel.result_title': "Senet iptal edildi",
    'accounting.notes.cancel.result_exemption_lifted': "Bağlı taksidin faiz muafiyeti kalktı; taksit açık kalmaya devam ediyor ve artık normal gecikme faizine tabi.",
    'accounting.notes.cancel.result_no_installment': "Senet iptal edildi. Bağlı taksit bulunmadığı için kaldırılacak bir faiz muafiyeti yoktu.",
    'accounting.notes.cancel.note_label': "İptal edilecek senet",
    'accounting.notes.cancel.no_active_notes': "İptal edilebilecek aktif senet yok.",
    'accounting.notes.cancel.reason_label': "Gerekçe",
    'accounting.notes.cancel.reason_hint': "Denetim kaydına yazılır; senedin neden geçersiz kılındığını yazın.",
    'accounting.notes.cancel.reason_placeholder': "Bu senet neden iptal ediliyor?",
    'accounting.notes.cancel.reason_required': "Senet iptali için gerekçe zorunludur.",
    'accounting.notes.cancel.submit': "Senedi İptal Et",
    'accounting.notes.cancel.confirm_title': "Senet iptal edilsin mi?",
    'accounting.notes.cancel.confirm_description': "Senet gerekçenizle geçersiz kılınacak; taksit borcu açık kalacak.",
    'accounting.notes.cancel.success': "Senet iptal edildi.",

    'accounting.notes.legal.title': "Avukat İşlemleri",
    'accounting.notes.legal.description': "Seçilen senetleri icra dosyasına devreder.",
    'accounting.notes.legal.select_label': "Devredilecek senetler",
    'accounting.notes.legal.select_hint': "Yalnızca aktif senetler devredilebilir; hepsi bu öğrenciye aittir.",
    'accounting.notes.legal.no_active_notes': "Devredilebilecek aktif senet yok.",
    'accounting.notes.legal.min_error': "En az bir senet seçin.",
    'accounting.notes.legal.total_label': "Devredilecek toplam",
    'accounting.notes.legal.total_hint': "Yalnızca bilgi amaçlıdır — borç tutarı seçilen senetlerden sunucu tarafında hesaplanır, elle girilemez.",
    'accounting.notes.legal.lawyers_error': "Avukat listesi yüklenemedi. Avukat atamadan devam edebilirsiniz.",
    'accounting.notes.legal.lawyer_label': "Avukat",
    'accounting.notes.legal.lawyer_hint': "Opsiyonel; sonradan atanabilir.",
    'accounting.notes.legal.lawyer_none': "Atanmadı",
    'accounting.notes.legal.interest_label': "Faiz tutarı",
    'accounting.notes.legal.fees_label': "İcra masrafı",
    'accounting.notes.legal.amount_invalid': "Sıfır veya pozitif bir tutar girin.",
    'accounting.notes.legal.notes_label': "Dosya notu",
    'accounting.notes.legal.notes_placeholder': "Dosya açılış notu...",
    'accounting.notes.legal.submit': "İcraya Devret",
    'accounting.notes.legal.confirm_title': "Senetler icraya devredilsin mi?",
    'accounting.notes.legal.confirm_description': "İcra dosyası açılacak ve seçili senetler hukuki takip durumuna geçecek.",
    'accounting.notes.legal.success': "Senetler icraya devredildi.",

    'accounting.notes.edit.title': "Senet Düzenleme",
    'accounting.notes.edit.description': "Mevcut bir senedi düzenler veya tahsil edildi olarak işaretler.",
    'accounting.notes.edit.note_label': "Senet",
    'accounting.notes.edit.locked_title': "Bu senet artık aktif değil",
    'accounting.notes.edit.locked_desc': "Senet muhasebe zincirinin parçası olduğu için tutar ve vade kilitli. Yalnızca açıklama düzenlenebilir.",
    'accounting.notes.edit.field_locked_hint': "Kilitli — yalnızca senet aktifken düzenlenebilir.",
    'accounting.notes.edit.installment_locked_hint': "Kilitli — bu senet bir taksidi temsil ediyor; tutar değişimi borç değişimidir.",
    'accounting.notes.edit.collect_locked_hint': "Tahsilat sırasında kilitli — tahsil edilen tutar senedin kayıtlı tutarıdır.",
    'accounting.notes.edit.installment_locked_title': "Tutar/vade kilitli",
    'accounting.notes.edit.installment_locked_desc': "Bu senet bir taksidin hukuki temsilidir. Tutar ya da vade değişimi borcun kendisini değiştirmek demektir ve bu ekrandan yapılamaz: senet ile taksidi birlikte güncelleyen parçalama veya birleştirme kullanılmalıdır.",
    'accounting.notes.edit.delete_result_title': "Silme sonucu",
    'accounting.notes.edit.delete_result_exemption_lifted': "Senet silindi (kayıt saklandı). Bağlı taksidin faiz muafiyeti kalktı; taksit açık kalmaya devam ediyor ve artık normal gecikme faizine tabi.",
    'accounting.notes.edit.delete_result_no_installment': "Senet silindi (kayıt saklandı). Bağlı taksit bulunmadığı için kaldırılacak bir faiz muafiyeti yoktu.",
    'accounting.notes.edit.collect_label': "Tahsil edildi olarak işaretle",
    'accounting.notes.edit.collect_warning': "Tahsilat bir ödeme kaydı oluşturur ve faturaya işler. Bu ekrandan geri alınamaz.",
    'accounting.notes.edit.bank_required': "Senet tahsilatı için banka seçimi zorunludur.",
    'accounting.notes.edit.paid_date_label': "Ödeme tarihi",
    'accounting.notes.edit.collect_confirm_title': "Senet tahsil edildi olarak işaretlensin mi?",
    'accounting.notes.edit.collect_confirm_description': "Ödeme kaydı oluşturulacak ve faturaya işlenecek.",
    'accounting.notes.edit.delete': "Sil",
    'accounting.notes.edit.delete_confirm_title': "Senet silinsin mi?",
    'accounting.notes.edit.delete_confirm_description': "Silme kayıtta gerekçe bırakmaz — gerekçeli iptali tercih edin.",
    'accounting.notes.edit.delete_success': "Senet silindi.",
    'accounting.notes.edit.cancel_note': "İptal Et",
    'accounting.notes.edit.cancel_unavailable_hint': "Yalnızca aktif senet iptal edilebilir; bu senet muhasebe zincirinin parçası.",
    'accounting.notes.edit.submit': "Kaydet",
    'accounting.notes.edit.success': "Senet güncellendi.",

    // FAZ 4 backend error_code -> mesaj (getApiErrorMessage fallback'i yerine)
    'accounting.errors.senet_bulunamadi': "Senet bulunamadı.",
    'accounting.errors.senet_durum_gecisi_gecersiz': "Senedin mevcut durumunda bu işlem yapılamaz.",
    'accounting.errors.senet_alani_kilitli': "Senet aktif olmaktan çıktıktan sonra tutar ve vade değiştirilemez.",
    'accounting.errors.senet_tutar_gecersiz': "Senet tutarı geçersiz.",
    'accounting.errors.senet_invoice_id_zorunlu': "Senet için fatura seçimi zorunludur.",
    'accounting.errors.senet_invoice_bulunamadi': "Seçilen fatura bulunamadı veya silinmiş.",
    'accounting.errors.senet_invoice_kullanici_uyusmuyor': "Fatura başka bir öğrenciye ait.",
    'accounting.errors.senet_tahsilat_banka_zorunlu': "Senet tahsilatı için banka seçimi zorunludur.",
    'accounting.errors.senet_silinemez': "Bu durumdaki senet silinemez; gerekçeli olarak iptal edilmelidir.",
    'accounting.errors.senet_iptal_gerekce_zorunlu': "Senet iptali için gerekçe zorunludur.",
    'accounting.errors.senet_parcalama_adet_gecersiz': "Parçalama için 2-60 arası parça gereklidir.",
    'accounting.errors.senet_parcalama_parca_gecersiz': "Parçalardan biri geçersiz.",
    'accounting.errors.senet_parcalama_vade_zorunlu': "Her parçanın vade tarihi zorunludur.",
    'accounting.errors.senet_parcalama_tutar_eksik': "Tutarlar ya tüm parçalarda verilmeli ya da hiçbirinde verilmemeli.",
    'accounting.errors.senet_parcalama_toplam_uyusmuyor': "Parça tutarlarının toplamı senet tutarına eşit değil.",
    'accounting.errors.senet_taksidi_bulunamadi': "Senede bağlı taksit bulunamadı.",
    'accounting.errors.senet_taksit_durumu_uygun_degil': "Bağlı taksidin durumu bu işlem için uygun değil.",
    'accounting.errors.senet_taksit_tutari_uyusmuyor': "Senet tutarı ile bağlı taksit tutarı uyuşmuyor.",
    'accounting.errors.senet_taksit_kapatilamadi': "İşlem yarıda kaldı, muhasebe mutabakatı gerekli.",
    'accounting.errors.senet_taksit_baglantisi_tutarsiz': "Senet/taksit bağlantıları tutarsız. Ya tamamı bir taksite bağlı, ya tamamı bağsız senet seçin.",
    'accounting.errors.senet_taksit_faturasi_uyusmuyor': "Bağlı taksitler farklı faturalara ait.",
    'accounting.errors.senet_installment_id_gecersiz': "Taksit bağı geçersiz.",
    'accounting.errors.senet_taksit_kullanici_uyusmuyor': "Taksit başka bir öğrenciye ait; senet bu taksite bağlanamaz.",
    'accounting.errors.senet_taksit_faiz_tahakkuk_etmis': "Takside gecikme faizi tahakkuk etmiş; senede bağlamadan önce faiz mutabakatı yapılmalıdır.",
    'accounting.errors.senet_taksidi_zaten_bagli': "Bu taksit zaten aktif bir senetle temsil ediliyor; aynı taksit için ikinci senet oluşturulamaz.",
    'accounting.errors.senet_birlestirme_adet_gecersiz': "Birleştirme için 2-60 arası senet gereklidir.",
    'accounting.errors.senet_birlestirme_tekrarli_id': "Aynı senet birden fazla kez gönderilemez.",
    'accounting.errors.senet_birlestirme_durum_gecersiz': "Yalnızca aktif senetler birleştirilebilir.",
    'accounting.errors.senet_birlestirme_fatura_uyusmuyor': "Yalnızca aynı faturaya ait senetler birleştirilebilir.",
    'accounting.errors.senet_birlestirme_kullanici_uyusmuyor': "Yalnızca aynı öğrenciye ait senetler birleştirilebilir.",
    'accounting.errors.senet_birlestirme_vade_zorunlu': "Birleştirilen senet için yeni vade tarihi zorunludur.",
    'accounting.errors.senet_birlestirme_vade_gecersiz': "Vade tarihi YYYY-AA-GG biçiminde olmalıdır.",
    'accounting.errors.senet_icra_id_zorunlu': "İcraya devir için en az bir senet gereklidir.",
    'accounting.errors.senet_icra_tekrarli_id': "Aynı senet birden fazla kez gönderilemez.",
    'accounting.errors.senet_icra_kullanici_uyusmuyor': "Tüm senetler aynı öğrenciye ait olmalıdır.",
    'accounting.errors.legal_case_validation_error': "İcra dosyası verilen değerlerle oluşturulamadı.",
    // FAZ 4 PAKET I/K — sikilastirilan sozlesmenin yeni/degisen kodlari
    'accounting.errors.senet_taksite_bagli_alan_duzenlenemez': "Bu senet bir taksidi temsil ediyor; tutar ve vadesi bu ekrandan değiştirilemez. Borç ve senedi birlikte güncelleyen parçalama veya birleştirme kullanılmalıdır.",
    'accounting.errors.senet_tahsilatinda_alan_duzenlenemez': "Tahsilat isteğiyle aynı gövdede tutar, vade, fatura veya taksit alanı gönderilemez. Önce alanı düzeltin, sonra tahsil edin.",
    'accounting.errors.senet_tahsilati_devam_ediyor': "Bu senedin tahsilatı sürüyor ya da önceki bir deneme yarıda kalmış; ikinci tahsilat başlatılamaz. Önce muhasebe mutabakatı yapılmalıdır.",
    'accounting.errors.taksit_senedi_tahsil_ediliyor': "Bu taksidin senedi şu anda tahsil ediliyor; o işlem bitmeden taksit tahsil edilemez.",
    'accounting.errors.taksit_senedi_durumu_degisti': "Bu taksidin senedi araya giren başka bir işlemle değişti; tahsilat yapılmadı. Listeyi yenileyip tekrar deneyin.",
    'accounting.errors.taksit_senedi_icrada': "Bu taksidin senedi icraya devredilmiş; tahsilat icra dosyası üzerinden yapılmalıdır.",
    'accounting.errors.taksit_senedi_zaten_tahsil_edildi': "Bu borç senet üzerinden zaten tahsil edilmiş; taksidin ayrıca tahsil edilmesi aynı parayı iki kez gelir yazardı.",
    'accounting.errors.taksit_tahsilat_banka_zorunlu': "Taksit tahsilatı için banka/kasa seçimi zorunludur.",
    'accounting.errors.taksit_tahsilat_tutari_eslesmiyor': "Tahsilat tutarı, taksidin (faiz dahil) tutarına eşit olmalıdır. Kısmi tahsilat desteklenmiyor.",
    // F1 fix (fix/tahsilat-taksit-baglama)
    'accounting.errors.taksit_tahsilat_tutari_gecersiz': "Geçerli bir tutar girin.",
    'accounting.errors.taksit_odeme_yontemi_gecersiz': "Geçersiz ödeme yöntemi.",
    'accounting.errors.taksit_net_tutari_gecersiz': "Geçersiz aktarılan (net) tutar.",
    'accounting.errors.taksit_kdv_tutari_gecersiz': "Geçersiz KDV tutarı.",
    'accounting.errors.taksit_durumu_tahsilata_kapali': "Bu taksit mevcut durumunda tahsil edilemez.",
    'accounting.errors.senet_vade_gecersiz': "Vade tarihi zorunludur ve YYYY-AA-GG biçiminde olmalıdır.",
    'accounting.errors.senet_alan_tipi_gecersiz': "Gönderilen alanlardan birinin tipi geçersiz.",
    'accounting.errors.senet_parcalama_vade_gecersiz': "Parçalardan birinin vade tarihi geçersiz; YYYY-AA-GG biçimi kullanılmalıdır.",
    'accounting.errors.senet_parcalama_tutar_cok_kucuk': "Senet tutarı bu kadar parçaya bölünemeyecek kadar küçük.",
    'accounting.errors.senet_birlestirme_id_gecersiz': "Birleştirme için gönderilen senet kimlikleri geçersiz.",
    'accounting.errors.senet_icra_id_gecersiz': "İcraya devir için gönderilen senet kimlikleri geçersiz.",
    'accounting.errors.senet_taksit_vadesi_uyusmuyor': "Senedin vadesi, bağlı taksidin vadesiyle birebir aynı olmalıdır.",
    'accounting.errors.senet_disi_taksit_karisimi': "Seçilen senetlerin temsil etmediği taksitler aynı dosyaya eklenemez; onlar için ayrı dosya açın.",
    'accounting.errors.senet_sahiplik_uyusmuyor': "Senetler bu öğrenciye ait değil.",
    'accounting.errors.missing_debt_reference': "İcra dosyası açmak için en az bir taksit veya senet seçilmelidir.",
    'accounting.errors.invalid_id_list': "Gönderilen kimlik listesi geçersiz. Listeyi yenileyip seçimi tekrarlayın.",
    'accounting.errors.unknown_with_code': "İşlem tamamlanamadı ({code}). Lütfen tekrar deneyin; sorun sürerse bu kodu destek ekibiyle paylaşın.",

    // === FAZ5 (brüt/net) i18n — başlangıç ===
    'accounting.collect_form.net_settled_amount_help': "Girdiğiniz tutar öğrencinin ödediği brüt tutardır. POS kesintisi sonrası hesaba geçen net tutar daha az olabilir; boş bırakılırsa brüt tutar esas alınır.",
    'accounting.collect_form.net_settled_amount_help_emphasis': "Prim yine brüt tutar üzerinden hesaplanır.",
    'accounting.collect_form.errors.net_settled_amount_invalid': "Geçerli bir tutar girin (negatif olamaz).",
    'accounting.collect_form.errors.net_settled_amount_exceeds_amount': "Hesaba geçen tutar, girilen (brüt) tutardan fazla olamaz.",
    // === FAZ5 (brüt/net) i18n — bitiş ===

    // === POS aktarılan tutar zorunluluğu i18n — başlangıç ===
    'accounting.collect_form.net_settled_amount_required_label': "Aktarılan Tutar (POS için zorunlu)",
    'accounting.collect_form.net_settled_amount_optional_hint': "Boş bırakılırsa ödenen tutar kabul edilir.",
    'accounting.collect_form.errors.net_settled_amount_required_pos': "POS hesabına yapılan tahsilatta aktarılan tutar zorunludur.",
    'accounting.collect_form.errors.net_settled_amount_zero': "Aktarılan tutar sıfırdan büyük olmalıdır.",
    'accounting.collect_form.commission_label': "Komisyon",
    // === POS aktarılan tutar zorunluluğu i18n — bitiş ===
    // === BELGELER i18n — başlangıç ===
    'accounting.docs.invoice_pdf.label': "Satış Kaydı PDF İndir",
    'accounting.docs.invoice_pdf.aria_label': "Satış kaydını PDF indir — {course}",
    'accounting.docs.invoice_pdf.loading': "Hazırlanıyor…",
    'accounting.docs.invoice_pdf.success': "Satış kaydı PDF indirildi.",
    'accounting.docs.invoice_pdf.error': "Satış kaydı PDF indirilemedi. Lütfen tekrar deneyin.",
    'accounting.docs.invoice_pdf.disabled_tooltip': "Bu satıra bağlı satış kaydı yok.",
    'accounting.docs.statement_pdf.label': "Döküm PDF İndir",
    'accounting.docs.statement_pdf.aria_label': "Cari hesap dökümünü PDF indir",
    'accounting.docs.statement_pdf.loading': "Hazırlanıyor…",
    'accounting.docs.statement_pdf.success': "Döküm PDF indirildi.",
    'accounting.docs.statement_pdf.error': "Döküm PDF indirilemedi. Lütfen tekrar deneyin.",
    'accounting.docs.notice_pdf.label': "İhtar PDF İndir",
    'accounting.docs.notice_pdf.aria_label': "İhtarname PDF indir",
    'accounting.docs.notice_pdf.loading': "Kontrol ediliyor…",
    'accounting.docs.notice_pdf.success': "İhtarname PDF indirildi.",
    'accounting.docs.notice_pdf.error': "İhtarname PDF indirilemedi. Lütfen tekrar deneyin.",
    'accounting.docs.notice_pdf.no_case_title': "Açık icra dosyası yok",
    'accounting.docs.notice_pdf.no_case_desc': "İhtarname yalnızca bir icra dosyasına bağlı üretilebilir. Önce öğrenci için icra dosyası açın.",
    'accounting.docs.notice_pdf.no_case_link': "İcra & Avukat ekranına git",
    'accounting.docs.notice_pdf.picker_title': "Hangi icra dosyası için?",
    'accounting.docs.notice_pdf.picker_hint': "Öğrencinin birden fazla icra dosyası var — birini seçin.",
    // === BELGELER i18n — bitiş ===

  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('preferred_language') || 'tr';
  });

  const toggleLanguage = useCallback(() => {
    setLang(prev => {
      const next = prev === 'tr' ? 'en' : 'tr';
      localStorage.setItem('preferred_language', next);
      return next;
    });
  }, []);

  // WCAG 3.1.1 (Language of Page): <html lang> aktif dile senkron tutulur —
  // ekran okuyucular içeriği doğru telaffuz etsin diye.
  useEffect(() => {
    document.documentElement.lang = lang === 'tr' ? 'tr' : 'en';
  }, [lang]);

  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations['en']?.[key] || key;
  }, [lang]);

  const getField = useCallback((obj, field) => {
    if (!obj) return '';
    if (lang === 'tr') {
      // TR: field_tr → field (bare/orijinal) → field_en → ''
      return obj[`${field}_tr`] || obj[field] || obj[`${field}_en`] || '';
    }
    // EN: field_en → field (bare/orijinal, genellikle EN) → field_tr → ''
    // Böylece _en özel alanı olan kayıtlar (cabin) EN başlık gösterir;
    // _en olmayan kurslar (name, description, title vb.) bare alana düşer.
    return obj[`${field}_en`] || obj[field] || obj[`${field}_tr`] || '';
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, toggleLanguage, t, getField }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
